import { t, setCatalog, getLang, detectLang, LangContext, LangProvider, useLang, useTranslated } from "./i18n.js";
import { guardPrompt, ignorePairKey, IGNORE_KEY } from "./project-match.js";
import { addTodo, toggleTodo, removeTodo, sortTodos, todoCounts } from "./todos.js";
import { reconnectTick } from "./reconnect-logic.js";
// Inline follow-up marker helpers: ONE shared copy with the server (pure CJS,
// bundled by esbuild), so the browser and hub strip/parse it identically.
import { stripFollowupForDisplay, stripFollowupFromMessage, stripFollowupFromSession } from "../cloud/followup.js";
import {PROVIDER_LOGO_PATHS, ProviderLogo, ICON_PATHS, Icon} from "./components/icons.jsx";
import {SOUND_LIBRARY, ERROR_SOUND, SOUND_PREFS_KEY, DEFAULT_SOUND_PREFS, migrateSoundPrefs,
  loadSoundPrefs, saveSoundPrefs, audioCtx, unlockAudio, playSound, VOICE_SOUND, soundLabel,
  speakAnnouncement, playEventSound, fireCue, repeatSecsFor, startRepeat, stopRepeat,
  loadNotifMuted, saveNotifMuted} from "./components/sounds.js";
import {escapeHtml, mdToHtml, HEB_RE, dirOf, Md, copyText, chatToText, CopyBtn, SHELL_LANGS,
  isShellLang, splitCodeBlocks, CodeBlock, AssistantText} from "./components/markdown.jsx";
import {StatusDot, ExecutionLine, BrandMark, stateClass} from "./components/motif.jsx";

const {useState,useEffect,useRef,useCallback,forwardRef,useImperativeHandle} = React;

// SHARED UI: this single file is the source for BOTH the internal hub
// (public/app.js) and Heygents (cloud/public/app.js) — there is no separate
// cloud fork. Cloud-only behaviour is gated at runtime by the CLOUD flag below
// (window.HUB_CLOUD, set only by cloud/src/gate.js): `CLOUD &&` shows a feature
// only on the cloud, `!CLOUD &&` hides a server-only feature there. Data-layer
// differences live in cloud/src/shim.js — NOT here. Edit this file once and run
// `npm run build && npm run build:cloud` to update both bundles.
const CLOUD=typeof window!=="undefined"&&!!window.HUB_CLOUD;

const PRIORITY_OPTS=[["high","High"],["med","Medium"],["low","Low"]];
const fmt=(s)=>{if(s==null)return"—";const m=Math.floor(s/60),h=Math.floor(m/60),d=Math.floor(h/24);return d?d+"d":h?h+"h":m+"m";};
function providerName(provider){return provider==="codex"?"Codex":provider==="gemini"?"Gemini":"Claude";}
// The URL hash identifies the open project and, when present, the exact chat:
// #/project-slug/chat/chat-id. Keeping the chat ID in the route lets a Home row
// survive the project-history load (and makes the opened conversation shareable).
function chatRouteFromHash(h){
  try{
    const parts=(String(h!=null?h:location.hash)||"").replace(/^#\/?/,"").split("/");
    const slug=decodeURIComponent(parts[0]||"")||null;
    const chatId=parts[1]==="chat"&&parts[2]?decodeURIComponent(parts.slice(2).join("/")):null;
    return{slug,chatId};
  }catch(e){return{slug:null,chatId:null};}
}
function slugFromHash(){return chatRouteFromHash().slug;}
function chatHash(slug,chatId){return "#/"+encodeURIComponent(slug)+(chatId?"/chat/"+encodeURIComponent(chatId):"");}
// Per-chat composer drafts. The chat panel has ONE `input` state shared by every
// chat, so switching tabs used to blank whatever you had typed but not sent.
// These keep a chatId -> text map: stash the chat you leave, restore the one you
// open. Whitespace-only text is dropped so an untouched chat opens clean.
function stashDraft(drafts,id,text){
  const next={...drafts};
  if(id==null)return next;
  if(text&&text.trim())next[id]=text;else delete next[id];
  return next;
}
function readDraft(drafts,id){return (id!=null&&drafts&&drafts[id])||"";}
// VAPID public keys are URL-safe base64; PushManager.subscribe needs raw bytes.
function urlBase64ToUint8Array(base64String){
  const padding="=".repeat((4-(base64String.length%4))%4);
  const base64=(base64String+padding).replace(/-/g,"+").replace(/_/g,"/");
  const raw=atob(base64);
  const out=new Uint8Array(raw.length);
  for(let i=0;i<raw.length;i++)out[i]=raw.charCodeAt(i);
  return out;
}
// True when a browser push subscription was minted under the VAPID key we still
// sign with. Rotating HUB_VAPID_* orphans every existing subscription: the server
// prunes its rows (403 / VapidPkHashMismatch) but the BROWSER keeps its old one,
// so getSubscription() keeps returning a subscription that can never receive a
// push — the settings UI shows "subscribed" while the server has nobody to send
// to, and re-subscribing throws InvalidStateError until the stale one is dropped.
function subKeyMatches(sub,vapidPublicKey){
  try{
    const raw=sub&&sub.options&&sub.options.applicationServerKey;
    if(!raw||!vapidPublicKey)return false;
    const have=new Uint8Array(raw);
    const want=urlBase64ToUint8Array(vapidPublicKey);
    if(have.length!==want.length)return false;
    for(let i=0;i<have.length;i++)if(have[i]!==want[i])return false;
    return true;
  }catch(e){return false;}
}

// "n/a" / "not found" / "" are "this signal does not apply to this project"
// (not managed by PM2 / not a container), NOT a failure. Never colour them red.
const NOT_APPL=(v)=>v==null||v===""||v==="n/a"||v==="unknown"||v==="not found";
// `reachable` only says the ORIGIN answered below 500 - it does not say the
// answer belongs to this project. A 404/410 is exactly the "the domain resolves
// to some wildcard vhost / parked host / wrong route" case, so it must never
// read as serving. Under 400 is real service; 401/403/429 and other 4xx mean
// "up but we cannot tell", which stays unknown rather than counting as proof.
// Derived from http_status so old collectors and stored rows get this too; an
// explicit `serving` field from a probe (which knows the post-redirect URL)
// wins when present.
function servingState(h){
  if(h.serving!=null)return h.serving;
  if(h.http_status==null)return null;
  if(h.http_status===404||h.http_status===410)return false;
  return h.http_status<400?true:null;
}
// Mirror of the server's probeUrls filter (cloud/server.js): a project has a
// real, connected website only when at least one of its urls normalizes to a
// non-placeholder http(s) host. Wildcards / localhost / *.test etc. are not a
// configured site. Until such a URL exists (typed by the user, or discovered by
// the AI details run scanning the project files) the live status shows nothing.
const PLACEHOLDER_HOST_RE=/^(\*|.*\*.*|localhost|127\.0\.0\.1|0\.0\.0\.0|(www\.)?(your|my)[-.]?domain\..*|placeholder\..*|changeme\..*|todo\..*)$/i;
const PLACEHOLDER_TLD_RE=/\.(test|local|localhost|invalid)$/i;
const BARE_HOST_RE=/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(:\d{2,5})?(\/.*)?$/i;
function hasSiteUrl(p){
  const urls=p&&Array.isArray(p.urls)?p.urls:[];
  return urls.some(u=>{
    let s=String(u&&u.url||"").trim();
    if(!s)return false;
    if(!/^https?:\/\//i.test(s)){if(!BARE_HOST_RE.test(s))return false;s="https://"+s;}
    try{const host=new URL(s).hostname;return!PLACEHOLDER_HOST_RE.test(host)&&!PLACEHOLDER_TLD_RE.test(host);}catch{return false;}
  });
}
function primaryUrl(p){
  const urls=p&&Array.isArray(p.urls)?p.urls:[];
  for(const u of urls){
    let s=String(u&&u.url||"").trim();
    if(!s)continue;
    if(!/^https?:\/\//i.test(s)){if(!BARE_HOST_RE.test(s))continue;s="https://"+s;}
    return s;
  }
  return "";
}
function health(slug,st){
  const h=st&&st.projects&&st.projects[slug];
  if(!h)return{c:"var(--warn)",t:"Not monitored"};
  // A live HTTP response (status < 500 → reachable) is authoritative: the site is
  // actively serving traffic, so never flag the whole project "Down". Bad process
  // signals (pm2/container/port) while the URL still answers are a degraded state,
  // not an outage — surface them amber rather than falsely red.
  const badProc=["stopped","errored"].includes(h.pm2_status)||h.container_status==="exited";
  // A local port not listening is weak evidence on its own (the app may be behind
  // a proxy/container/remote host), so it only degrades — it never turns red.
  const softBad=badProc||h.port_listening===false;
  // Positive proof the project itself runs, independent of the URL probe. With no
  // working dir configured the collector matches neither pm2 (by cwd) nor docker
  // (by slug) and reports no port, so every one of these is absent and the URL
  // probe is the only vote - which is how a 404 used to render green.
  const goodProc=h.pm2_status==="online"||h.container_status==="running"||h.port_listening===true;
  const srv=servingState(h);
  // A 404/410 is never healthy, whether or not the origin counted as reachable:
  // the host answers but this project is not served there. Degraded, not green.
  if(srv===false)return{c:"var(--warn)",t:"Not serving"};
  if(h.reachable===true){
    if(softBad)return{c:"var(--warn)",t:"Degraded"};
    if(srv==null&&!goodProc)return{c:"var(--warn)",t:"Unverified"};
    return{c:"var(--ok)",t:"Healthy"};
  }
  // Never probed (nothing connected: no working dir and no real URL) → we simply
  // don't know. Don't claim an outage, and never fall through to "Healthy".
  if(h.reachable==null){
    if(badProc)return{c:"var(--err)",t:"Down"};
    if(goodProc)return softBad?{c:"var(--warn)",t:"Degraded"}:{c:"var(--ok)",t:"Healthy"};
    return{c:"var(--warn)",t:"Not monitored"};
  }
  if(h.reachable===false)return{c:"var(--err)",t:"Down"};
  return{c:"var(--warn)",t:"Partial"};
}
// colour a 0-100 metric: green ok / amber warn / red bad
function lvlColor(pct,warn,bad){if(pct==null)return null;return pct>=bad?"var(--err)":pct>=warn?"var(--warn)":"var(--ok)";}
// one header health stat: label + colour-coded value + a click-to-open popover
// that stays open (close with ✕, click-outside, or Esc) so you can click the
// commands and "Ask Claude" buttons inside it.
function HostStat({label,value,color,tip,cls,titleHint,series,ariaLabel}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  // The tip is positioned as a viewport-clamped fixed element (measured off the
  // button) rather than absolutely anchored to the stat. Header stats sit on the
  // right of the header, so a left:0 / fixed-width popover would spill off the
  // right edge on phones — clamping the computed left keeps it fully on-screen.
  const[pos,setPos]=useState(null);
  const ref=useRef(null);
  const btnRef=useRef(null);
  React.useLayoutEffect(()=>{
    if(!open){setPos(null);return;}
    const place=()=>{
      const b=btnRef.current;if(!b)return;
      const r=b.getBoundingClientRect();
      const w=Math.min(300,window.innerWidth-20);
      setPos({left:Math.max(10,Math.min(r.left,window.innerWidth-w-10)),top:r.bottom+8,width:w});
    };
    place();
    window.addEventListener("resize",place);window.addEventListener("scroll",place,true);
    return()=>{window.removeEventListener("resize",place);window.removeEventListener("scroll",place,true);};
  },[open]);
  useEffect(()=>{
    if(!open)return;
    const onDoc=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    const onKey=(e)=>{if(e.key==="Escape")setOpen(false);};
    document.addEventListener("mousedown",onDoc);document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDoc);document.removeEventListener("keydown",onKey);};
  },[open]);
  const tipStyle=pos
    ?{position:"fixed",left:pos.left,top:pos.top,right:"auto",width:pos.width,maxWidth:"none",maxHeight:"calc(100dvh - "+(pos.top+12)+"px)",overflowY:"auto"}
    :{visibility:"hidden"};
  return(<span className={"hstat"+(cls?" "+cls:"")+(open?" open":"")} ref={ref}>
    <button ref={btnRef} className="hstat-btn" aria-label={ariaLabel||undefined} aria-expanded={open} title={titleHint?titleHint+" — "+t("click for details, fixes & commands"):t("Click for details, fixes & commands")} onClick={()=>setOpen(o=>!o)}>
      {label?label+" ":""}<b style={color?{color}:null}>{value}</b>
    </button>
    {open&&<span className="tip" style={tipStyle}><button className="tip-x" title={t("Close")} aria-label={t("Close")} onClick={()=>setOpen(false)}>✕</button>
      {series&&series.data&&series.data.length>1&&<span className="spark-wrap">
        <span className="spark-head"><b>{t("Last {n} readings",{n:series.data.length})}</b><span className="spark-cur" style={series.color?{color:series.color}:null}>{value}</span></span>
        <Sparkline data={series.data} warn={series.warn} bad={series.bad}/>
      </span>}
      {tip}</span>}
  </span>);
}
// Compact inline SVG sparkline of the last N host readings for a single metric.
// `data` is an array of numbers (percent, 0-100), oldest→newest. Draws a filled
// area + line, coloured by the current (last) value's severity band.
function Sparkline({data,warn,bad,w=120,h=32}){
  const pts=(data||[]).filter(v=>v!=null&&!isNaN(v)).map(Number);
  if(pts.length<2)return null;
  const cur=pts[pts.length-1];
  const col=lvlColor(cur,warn,bad)||"var(--text-3)";
  // Fixed 0-100 scale so the y-axis is honest across CPU/RAM/disk and doesn't
  // exaggerate tiny fluctuations by auto-scaling to the data's own min/max.
  const pad=3,iw=w-pad*2,ih=h-pad*2;
  const x=i=>pad+(pts.length===1?0:(i/(pts.length-1))*iw);
  const y=v=>pad+ih-(Math.max(0,Math.min(100,v))/100)*ih;
  const line=pts.map((v,i)=>(i?"L":"M")+x(i).toFixed(1)+" "+y(v).toFixed(1)).join(" ");
  const area=line+" L"+x(pts.length-1).toFixed(1)+" "+(pad+ih)+" L"+x(0).toFixed(1)+" "+(pad+ih)+" Z";
  return(<svg className="spark" width={w} height={h} viewBox={"0 0 "+w+" "+h} preserveAspectRatio="none" aria-hidden="true">
    <path d={area} fill={col} fillOpacity="0.14"/>
    <path d={line} fill="none" stroke={col} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
    <circle cx={x(pts.length-1)} cy={y(cur)} r="2" fill={col}/>
  </svg>);
}
function usageResetLabel(iso){
  if(!iso)return"—";
  const d=new Date(iso),diff=d.getTime()-Date.now();
  const tm=d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  if(diff<0)return"now";
  const mins=Math.max(0,Math.round(diff/60000)),h=Math.floor(mins/60),m=mins%60,days=Math.floor(h/24);
  const left=days>=1?(days+"d "+(h%24)+"h"):(h>=1?(h+"h "+m+"m"):(m+"m"));
  const day=d.toLocaleDateString("en-GB",{weekday:"short"});
  return left+" ("+(diff<86400000?"today ":day+" ")+tm+")";
}
// Merge a fresh usage poll onto the previous one. A successful poll replaces it.
// A failed poll (rate-limited, CLI offline, etc.) is dropped in favour of the last
// good reading — kept intact but flagged `stale` with the new error — so the
// header chip never falls back to an empty "—". Only when there is no prior good
// reading at all do we surface the raw error object.
// ...but a last good reading only stands while the quota windows it describes are
// still open. A percentage measures one window; once that window has rolled over
// the number is about a finished period, so keeping it shows a dead value as the
// current one (a host with broken credentials displayed "session 7% / weekly 97%"
// from windows that had closed nine days earlier). Mirrors usageLastExpired() in
// cloud/agent/hub-cloud-agent.js — the same fossil can be served by the agent's
// on-disk cache, the cloud shim's localStorage, or this in-memory merge.
const USAGE_STALE_MAX_AGE_MS=5*60*60*1000; // the 5-hour session window
function usageExpired(u,now){
  if(!u||!u.ok)return true;
  const t=typeof now==="number"?now:Date.now();
  let sawReset=false;
  for(const w of [u.session,u.weekly]){
    const at=w&&w.resets_at?Date.parse(w.resets_at):NaN;
    if(Number.isNaN(at))continue;
    sawReset=true;
    if(at<=t)return true;
  }
  if(sawReset)return false;
  const seen=typeof u.seen_at==="number"?u.seen_at:0;
  return !seen||t-seen>USAGE_STALE_MAX_AGE_MS;
}
function mergeUsage(prev,next){
  if(next&&next.ok)return{...next,seen_at:Date.now()};
  if(prev&&prev.ok&&!usageExpired(prev))return{...prev,stale:true,error:(next&&next.error)||prev.error};
  return next;
}
// Plain-language text for the usage collector's error codes (see
// cloud/agent/hub-cloud-agent.js fetchClaudeUsage/fetchCodexUsage), so the
// header never shows a raw code like "no-credentials".
function usageErrorText(code,name){
  const c=String(code||"");
  if(c==="no-credentials")return t("Connect your agent and sign in to {name} on that machine to see usage.",{name});
  if(c==="revoked")return t("{name} sign-in on your agent machine has expired. Sign in again to see usage.",{name});
  if(c==="unreachable")return t("Couldn't reach {name} to read usage. It will retry automatically.",{name});
  if(/^http-\d+$/.test(c))return t("{name} didn't return usage right now. It will retry automatically.",{name});
  return t("{name} usage isn't available right now. It will retry automatically.",{name});
}
function ProviderUsage({provider,data}){
  const{lang}=useLang();
  const name=provider==="codex"?"Codex":"Claude";
  const sess=data&&data.session||{},wk=data&&data.weekly||{};
  const pct=Math.max(0,Math.min(100,Number(sess.percent)||0));
  if(!data||!data.ok){
    // No successful reading yet (first load, or an error before any good poll).
    // Never render a bare "—" — show a neutral loading ellipsis instead, since
    // mergeUsage() keeps the last good value once one exists.
    const msg=data?usageErrorText(data.error,name):t("Loading {name} usage…",{name});
    return <HostStat cls={"usage-stat "+provider} label={name} value="…" titleHint={msg} ariaLabel={name+": "+msg} tip={
      <span className="lvl">{msg}</span>
    }/>;
  }
  const wkPct=Math.max(0,Math.min(100,Number(wk.percent)||0));
  // A stale reading is still the best number we have, but it must not look like a
  // live one: drop the health colour for neutral grey and dash the track, so the
  // chip reads "last known" at a glance rather than "you are at 97% right now".
  const stale=!!data.stale,col=stale?"var(--text-3)":lvlColor(pct,70,90);
  return <HostStat cls={"usage-stat "+provider+(stale?" stale":"")} label={name}
    value={<span className="usage-value">
      <span className="usage-pie" aria-hidden="true" style={{background:"conic-gradient("+col+" "+(pct*3.6)+"deg,var(--line-2) 0)"}}/>
      <span className="usage-num">{pct}%</span><span className="usage-track" role="progressbar"
      aria-label={name+" 5-hour usage"+(stale?" (last known reading)":"")} aria-valuemin="0" aria-valuemax="100" aria-valuenow={pct}>
      <span className="usage-fill" style={{width:pct+"%",background:col}}/>
    </span></span>}
    color={col} tip={
      <><span className="usage-tiptitle">{name} usage</span>
      <UsageMeter label="Session · 5 hours" pct={pct} sub={"Resets in "+usageResetLabel(sess.resets_at)}/>
      <UsageMeter label="Weekly quota" pct={wkPct} sub={"Resets in "+usageResetLabel(wk.resets_at)}/>
      {data.plan&&<span className="lvl"><b>{t("Plan:")}</b> {data.plan}</span>}
      {data.stale&&<span className="lvl">{t("Last known reading. Live refresh failed:")} {usageErrorText(data.error,name)}</span>}</>
    }/>;
}
// A single labelled quota meter for the usage popover: percent + a health-tinted
// progress bar (green <70% / amber 70–90% / red 90%+) matching the header chip.
function UsageMeter({label,pct,sub}){
  const p=Math.max(0,Math.min(100,Number(pct)||0)),c=lvlColor(p,70,90);
  return <span className="usage-meter" style={{"--um":c}}>
    <span className="usage-meter-head"><span className="usage-meter-label">{label}</span><span className="usage-meter-pct">{p}%</span></span>
    <span className="usage-meter-bar" role="progressbar" aria-label={label} aria-valuemin="0" aria-valuemax="100" aria-valuenow={p}>
      <span className="usage-meter-fill" style={{width:p+"%"}}/></span>
    {sub&&<span className="usage-meter-sub">{sub}</span>}
  </span>;
}
function locKind(p){
  const loc=((p&&p.location)||"").toLowerCase();
  const local=/mac|local|localhost|laptop|desktop/.test(loc);
  if(local)return{icon:"💻",t:"Local — "+(p.location||"this machine")};
  return{icon:"☁️",t:"Live — "+(p.location||"remote server")};
}
// Client-clock timestamp of the status measurement. The server sends server_now,
// so the age (server_now - updated_at) is re-anchored to this browser's clock:
// a skewed client clock no longer pins the header timer at 0s or inflates it.
function anchorStatus(d){
  if(d&&d.updated_at&&d.server_now){const age=new Date(d.server_now).getTime()-new Date(d.updated_at).getTime();if(Number.isFinite(age))return{...d,_at:Date.now()-Math.max(0,age)};}
  return d;
}
function statusAt(st){return st&&(st._at||(st.updated_at?new Date(st.updated_at).getTime():0))||0;}
function stale(st){if(!st||!st.updated_at)return true;return (Date.now()-statusAt(st))>660000;}

function linesToArr(s){return (s||"").split("\n").map(x=>x.trim()).filter(Boolean);}
function arrToLines(a){return (a||[]).join("\n");}
function slugFromProjectName(name){
  return String(name||"").trim().toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/g,"").replace(/[^\p{L}\p{N}]+/gu,"-")
    .replace(/^-+|-+$/g,"").slice(0,80)||"project";
}
function saveProjects(next){
  return fetch("/api/projects",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(next)})
    .then(r=>r.json());
}
function blankProject(){return{slug:"new-project",nameHe:"New project",nameEn:"",category:"",priority:"med",isLocal:false,agentHost:"",localPort:"",location:"",oneLiner:"",description:"",goals:"",hosting:"",repo:"",repoBranch:"",dir:"",db:{type:"",host:"",studioUrl:"",user:""},urls:[],stack:[],integrations:[],notes:"",attachments:[],todos:[]};}

// ── Claude headless chat: stream newline-delimited stream-json from the hub ──
async function readNdjson(res,onEvent,opts){
  opts=opts||{};
  const timeoutMs=opts.timeoutMs||45000;
  const reader=res.body.getReader(),dec=new TextDecoder();let buf="";
  for(;;){
    let timer=null;
    const read=timeoutMs
      ?Promise.race([
        reader.read(),
        new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error("Stream timed out — no events received.")),timeoutMs);})
      ]).finally(()=>{if(timer)clearTimeout(timer);})
      :reader.read();
    let chunk;
    try{chunk=await read;}
    catch(e){try{await reader.cancel();}catch{}throw e;}
    const {value,done}=chunk;if(done)break;
    buf+=dec.decode(value,{stream:true});
    let i;while((i=buf.indexOf("\n"))>=0){const line=buf.slice(0,i);buf=buf.slice(i+1);if(line.trim()){try{onEvent(JSON.parse(line));}catch{}}}
  }
  if(buf.trim()){try{onEvent(JSON.parse(buf));}catch{}}
}
// Start a NEW turn. The server runs Claude as a detached "run" (it keeps going
// even if this connection drops), and streams its events back. `chatId` lets the
// server save the finished turn to history if we navigate away and never return.
async function runClaude({slug, prompt, sessionId, model, provider, executionMode, effort, chatId, chatSession, chatLang, target, followupHint, onEvent, signal}){
  const res=await fetch("/api/claude",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug,prompt,sessionId,model,provider,executionMode,effort,chatId,chatSession,chatLang,target:target||undefined,followupHint:followupHint===true}),signal});
  if(!res.ok){let e;try{e=(await res.json()).error;}catch{}throw new Error(e||("HTTP "+res.status));}
  await readNdjson(res,onEvent,{timeoutMs:45000});
}
// RESUME an already-running turn after a project switch / page reload: attach to
// its server-side run and stream buffered + live events. The server keeps the run
// alive whether or not a browser is connected, so the work was never interrupted.
async function reconnectClaude({runId, since, onEvent, signal}){
  const q="/api/claude/stream?runId="+encodeURIComponent(runId)+(since>0?"&since="+since:"");
  const res=await fetch(q,{signal});
  if(!res.ok)throw new Error("HTTP "+res.status);
  await readNdjson(res,onEvent,{timeoutMs:45000});
}
// ── stream resume ───────────────────────────────────────────────────────────
// A dropped socket (wifi blip, laptop sleep, proxy idle timeout, hub restart) is
// NOT a lost turn: the run keeps going server-side and every event it emitted is
// persisted with a monotonic `_seq`. So instead of surfacing "Connection lost"
// the moment the stream ends, we reconnect to /api/claude/stream?since=<lastSeq>
// with a backoff and keep filling the same bubble. The hard error is only shown
// once the attempts are exhausted, or when the server says the run really ended
// (hub done / gone / interrupted): a TERMINAL event, which is the only thing
// that stops the loop.
const RESUME_BACKOFFS=[500,1000,2000,4000,8000,10000];
const RESUME_GIVEUP_MS=120000; // stop retrying after ~2min with no progress
const RESUME_GAVE_UP="The connection dropped and could not be restored. You can retry the turn.";
// The give-up banner must say HOW LONG we kept retrying, so "connection lost"
// is never a bare unexplained verdict. `ms` is streamWithResume's retriedMs.
const resumeGaveUpText=(ms)=>RESUME_GAVE_UP+" (kept retrying for "+Math.round(((ms>0?ms:RESUME_GIVEUP_MS))/1000)+"s)";
// Human text for a hub `interrupted` reason. The server / cloud shim may tag WHY
// a run stopped: known tokens map to a clear sentence, any other server-supplied
// text passes through, and no reason at all keeps the legacy server-restart
// wording (the only cause that existed before reasons were introduced).
function interruptReasonText(reason){
  if(reason==="agent-offline")return"The Mac agent went offline mid-run";
  if(reason==="agent-never-started")return"No agent picked up this run - is the Mac agent running?";
  if(reason==="run-abandoned")return"This run went silent and never reported finishing - the agent probably restarted mid-run.";
  return String(reason||"The server restarted while this run was still generating a reply.");
}
function resumeSleep(ms,signal){
  return new Promise((resolve,reject)=>{
    if(signal&&signal.aborted){const e=new Error("aborted");e.name="AbortError";return reject(e);}
    const timer=setTimeout(done,ms);
    function done(){cleanup();resolve();}
    function onAbort(){cleanup();const e=new Error("aborted");e.name="AbortError";reject(e);}
    function cleanup(){clearTimeout(timer);if(signal)signal.removeEventListener("abort",onAbort);}
    if(signal)signal.addEventListener("abort",onAbort);
  });
}
// start(onEvent) opens the FIRST stream (a fresh POST /api/claude, or an attach).
// Resolves {terminal,lastSeq,runId,error}: terminal=false means we really gave up.
async function streamWithResume({start,runId,onEvent,signal,onStatus}){
  let lastSeq=0,curRunId=runId||null,terminal=false,attempt=0,lastError=null;
  let progressAt=Date.now();
  let waiting=false;
  const wrap=(ev)=>{
    progressAt=Date.now();
    // The first event after a reconnect proves the turn is alive again: drop the
    // "reconnecting" notice before handing the event on.
    if(waiting){waiting=false;if(onStatus)onStatus({state:"resumed"});}
    if(ev&&typeof ev._seq==="number"&&ev._seq>lastSeq)lastSeq=ev._seq;
    if(ev&&ev.type==="hub"){
      if(ev.runId)curRunId=ev.runId;
      if(ev.event==="done"||ev.event==="gone"||ev.event==="interrupted")terminal=true;
    }
    onEvent(ev);
  };
  try{await start(wrap);}
  catch(e){if(e&&e.name==="AbortError")throw e;lastError=e;}
  while(!terminal&&curRunId){
    if(signal&&signal.aborted)break;
    if(Date.now()-progressAt>=RESUME_GIVEUP_MS)break;
    const wait=RESUME_BACKOFFS[Math.min(attempt,RESUME_BACKOFFS.length-1)];
    attempt++;
    waiting=true;
    // Surface WHY the last attempt failed (network error vs stream timeout) so
    // "Reconnecting..." is never an unexplained state.
    if(onStatus)onStatus({state:"reconnecting",attempt,waitMs:wait,lastError:lastError?String((lastError&&lastError.message)||lastError):null});
    try{await resumeSleep(wait,signal);}catch(e){if(e&&e.name==="AbortError")throw e;break;}
    try{
      await reconnectClaude({runId:curRunId,since:lastSeq,onEvent:wrap,signal});
      attempt=0; // a stream that actually attached resets the backoff ladder
    }catch(e){
      if(e&&e.name==="AbortError")throw e;
      lastError=e;
    }
  }
  // retriedMs: how long the resume ladder ran without progress before this
  // return - the give-up banner reports it so the user knows we really tried.
  return {terminal,lastSeq,runId:curRunId,error:terminal?null:lastError,retriedMs:Date.now()-progressAt};
}
function toolDesc(input){
  if(!input||typeof input!=="object")return"";
  const v=input.command||input.file_path||input.path||input.pattern||input.url||input.description;
  return v?String(v):JSON.stringify(input).slice(0,90);
}
// Short live status for the in-progress turn: "thinking" before anything streams,
// or what Claude is currently doing once it starts acting (tool calls).
const TOOL_VERBS={Read:"reading files",Edit:"editing",MultiEdit:"editing",Write:"writing a file",
  Bash:"running a command",Glob:"searching files",Grep:"searching code",
  WebFetch:"reading the web",WebSearch:"searching the web",TodoWrite:"planning",
  Task:"working with a subagent",NotebookEdit:"editing a notebook"};
// Claude models exposed by both chat and the global per-use AI settings. Values
// are Claude CLI *aliases* ("opus", "sonnet", "haiku", "fable"), which the CLI
// resolves to the latest model of that family - so a new release (e.g. Opus 5 →
// Opus 5.5) is picked up with no Hub change. Labels are version-free for the same
// reason. Don't put pinned full IDs (claude-opus-5) here unless a specific
// version must be locked on purpose.
const CLAUDE_MODELS=[
  {value:"opus",label:"Opus",hint:"default"},
  {value:"sonnet",label:"Sonnet"},
  {value:"haiku",label:"Haiku"},
  {value:"fable",label:"Fable"},
];
// Pinned IDs the Hub used to store before switching to aliases. Saved prefs,
// schedules and chats may still hold them; map them to the alias so they follow
// the latest model too. (The agent applies the same map before spawning.)
const LEGACY_CLAUDE_MODEL_ALIASES={"claude-opus-5":"opus","claude-fable-5":"fable"};
function canonModel(provider,model){
  if((provider||"claude")==="claude"&&model&&LEGACY_CLAUDE_MODEL_ALIASES[model])return LEGACY_CLAUDE_MODEL_ALIASES[model];
  return model;
}
// Codex models. GPT-5.6 ships as tiers (Sol/Terra/Luna): the generation number
// (5.6) advances the model family, while Sol/Terra/Luna are durable capability
// tiers within it (flagship/balanced/fast). GPT-6 Astra (2026-09-05) is the next
// generation and a single model, not a tier set — it is opt-in here, exactly as
// in the Codex CLI, where it is NOT the default (5x Sol's output price).
// desc feeds the model picker's hover tooltip; hint is the short badge shown on
// the pill/menu item. The blank/default entry (value:"") sends no explicit
// --model, but the agent pins that to gpt-5.6-sol itself (see hub-agent.js) —
// so "default" here and Sol below always resolve to the same model.
const CODEX_MODELS=[
  {value:"gpt-6-astra",label:"Astra",hint:"frontier",desc:"GPT-6 Astra — next-generation frontier model: state of the art on software engineering, computer use, and long-horizon research, with a 1.05M-token context window. $10 / $50 per 1M tokens (in/out). Opt-in: pick it explicitly, it is not the Codex default."},
  {value:"",label:"Sol",hint:"default",desc:"GPT-5.6 Sol — flagship tier: frontier reasoning for advanced coding, security work, and long-horizon technical tasks. $5 / $30 per 1M tokens (in/out). Used by default."},
  {value:"gpt-5.6-terra",label:"Terra",hint:"balanced",desc:"GPT-5.6 Terra — balanced everyday tier: matches GPT-5.5 quality at roughly half the cost; general coding support, docs, and analysis. $2.50 / $15 per 1M tokens (in/out)."},
  {value:"gpt-5.6-luna",label:"Luna",hint:"fast & cheap",desc:"GPT-5.6 Luna — fast, affordable tier: built for high-volume, lightweight work like quick edits, classification, and repeated small requests. $1 / $6 per 1M tokens (in/out)."},
];
const AI_MODELS={
  claude:CLAUDE_MODELS,
  codex:CODEX_MODELS,
  // Gemini chat runs on the Antigravity CLI (agy), not gemini-cli, so these
  // values must be agy's own model names (agy --model rejects anything else,
  // e.g. the old "pro"/"flash"/"flash-lite" shorthand fails immediately with
  // "invalid model selection"). The blank/default entry omits --model and
  // lets agy pick its own default.
  gemini:[
    {value:"",label:"Gemini",hint:"auto"},
    {value:"Gemini 3.1 Pro (High)",label:"Gemini 3.1 Pro"},
    {value:"Gemini 3.7 Flash (High)",label:"Gemini 3.7 Flash"},
    {value:"Gemini 3.7 Flash (Low)",label:"Gemini 3.7 Flash Lite"},
  ],
};
// Per-provider default model, used when a provider is picked but no model is
// chosen yet (chat picker, AI settings, Plan sections). Seeded from these
// built-ins (Claude → the "opus" alias, i.e. latest Opus; others omit --model) but the
// user can override each in the AI settings panel; overrides live in localStorage
// under AI_DEFAULT_MODELS_KEY.
const AI_DEFAULT_MODEL_SEED={claude:"opus",codex:"",gemini:""};
const AI_DEFAULT_MODELS_KEY="hub.aiDefaultModels";
// A stored model value is only honored if it is still a known model for that
// provider; otherwise we fall back to the seed. Module-level cache keeps
// defaultModelFor() synchronous (chat picker reads it during useState init).
function sanitizeDefaultModels(value){
  const out={};
  for(const p of Object.keys(AI_DEFAULT_MODEL_SEED)){
    const v=canonModel(p,value&&value[p]);
    const known=(AI_MODELS[p]||[]).some(m=>m.value===v);
    out[p]=known?v:AI_DEFAULT_MODEL_SEED[p];
  }
  return out;
}
let aiDefaultModelsCache=null;
function loadAiDefaultModels(){
  if(aiDefaultModelsCache)return aiDefaultModelsCache;
  let parsed=null;
  try{parsed=JSON.parse(localStorage.getItem(AI_DEFAULT_MODELS_KEY));}catch{}
  aiDefaultModelsCache=sanitizeDefaultModels(parsed);
  return aiDefaultModelsCache;
}
function saveAiDefaultModels(value){
  const clean=sanitizeDefaultModels(value);
  aiDefaultModelsCache=clean;
  try{localStorage.setItem(AI_DEFAULT_MODELS_KEY,JSON.stringify(clean));}catch{}
  return clean;
}
function defaultModelFor(provider){const m=loadAiDefaultModels();return m[provider]||"";}
// The four agent execution postures offered in the chat controls. Each maps on
// the server (safeExecutionMode/aiCommand) to a concrete permission/sandbox
// policy for the underlying CLI. Order = least → most autonomous.
const EXECUTION_MODES=[
  {value:"manual",     icon:"✋", title:"Manual",             short:"Manual", desc:pn=>pn+" will ask for approval before making each edit."},
  {value:"acceptEdits",icon:"✎", title:"Edit automatically", short:"Edit",   desc:pn=>pn+" will edit your selected text or the whole file."},
  {value:"plan",       icon:"❑", title:"Plan",               short:"Plan",   desc:pn=>pn+" will explore the code and present a plan before editing."},
  {value:"auto",       icon:"⚡", title:"Auto",               short:"Auto",   desc:pn=>pn+" will approve actions that pass a safety check and pause for anything risky."},
];
const EXECUTION_MODE_VALUES=EXECUTION_MODES.map(m=>m.value);
const safeMode=(m)=>EXECUTION_MODE_VALUES.includes(m)?m:"auto";
// Model reasoning/thinking effort — the server translates these to a thinking
// budget (Claude MAX_THINKING_TOKENS) or reasoning-effort config (Codex).
const EFFORTS=[
  {value:"low",   label:"Low",    desc:"Fast, minimal deliberation — good for quick edits and simple asks."},
  {value:"medium",label:"Medium", desc:"Balanced speed and rigor — the default for most work."},
  {value:"high",  label:"High",   desc:"Deep, thorough reasoning — best for tricky bugs and design work."},
];
const EFFORT_VALUES=EFFORTS.map(e=>e.value);
const safeEffortClient=(e)=>EFFORT_VALUES.includes(e)?e:"medium";
const AI_USE_PREFS_KEY="hub.aiUseModels";
const DEFAULT_AI_USE_PREFS={
  chat:{provider:"claude",model:""},
  scan:{provider:"claude",model:""},
  fix:{provider:"claude",model:""},
  explain:{provider:"codex",model:"gpt-5.6-terra"},
  details:{provider:"claude",model:""},
  subject:{provider:"claude",model:"haiku"},
};
function normalizeAiUseChoice(value,fallback){
  // Migrate the old shape where each use stored only a Claude model string.
  if(typeof value==="string")return{provider:"claude",model:value};
  const provider=value&&["claude","codex","gemini"].includes(value.provider)?value.provider:fallback.provider;
  return{
    provider,
    model:value&&typeof value.model==="string"?value.model:fallback.model,
  };
}
function loadAiUsePrefs(){
  let out;
  try{
    const v=JSON.parse(localStorage.getItem(AI_USE_PREFS_KEY));
    if(v&&typeof v==="object")out=Object.fromEntries(Object.keys(DEFAULT_AI_USE_PREFS).map(k=>[k,normalizeAiUseChoice(v[k],DEFAULT_AI_USE_PREFS[k])]));
  }catch(e){}
  if(!out)out=JSON.parse(JSON.stringify(DEFAULT_AI_USE_PREFS));
  return out;
}
function saveAiUsePrefs(p){try{localStorage.setItem(AI_USE_PREFS_KEY,JSON.stringify(p));}catch(e){}}
// Chat prompt/instructions live in their own key (aiUsePrefs only keeps
// provider/model). Mirrors the Plan sections' editable prompt shape.
const CHAT_AI_PROMPT_KEY="hub.chatAiPrompt";
const CHAT_AI_PROMPT_DEFAULT={
  prompt:'You are helping with the project "{name}".\n\nContext:\n{context}\n\nHave a helpful, focused conversation. Answer clearly and concisely.',
  instructions:"",
};
function loadChatAiPrompt(){
  const out=JSON.parse(JSON.stringify(CHAT_AI_PROMPT_DEFAULT));
  try{const v=JSON.parse(localStorage.getItem(CHAT_AI_PROMPT_KEY))||{};if(typeof v.prompt==="string")out.prompt=v.prompt;if(typeof v.instructions==="string")out.instructions=v.instructions;}catch(e){}
  return out;
}
// Build the interpolated chat system preamble from the editable template,
// filling {name}/{context} the same way the Plan sections do. Returns "" when
// there's nothing meaningful to prepend so a first turn stays clean.
function chatSystemPreamble(p){
  const cur=loadChatAiPrompt();
  let prompt=String(cur.prompt||"")
    .replace(/\{name\}/g,String((p&&p.name)||""))
    .replace(/\{context\}/g,(planContext(p)||"").trim()||"(none provided)");
  if(cur.instructions&&cur.instructions.trim())prompt+="\n\nAdditional instructions:\n"+cur.instructions.trim();
  return prompt.trim();
}
function saveChatAiPrompt(p){try{localStorage.setItem(CHAT_AI_PROMPT_KEY,JSON.stringify(p));}catch(e){}}
// Editable instructions for the Scan/Fix AI uses (aiUsePrefs only holds
// provider/model). `system` optionally REPLACES the built-in default brief
// (empty = use the built-in default); `instructions` is extra text appended to
// the run. The built-in default text itself is fetched from the server on demand.
const AI_USE_PROMPT_KEY="hub.aiUsePrompts";
const AI_USE_PROMPT_DEFAULT={scan:{system:"",instructions:""},fix:{system:"",instructions:""}};
function loadAiUsePrompts(){
  const out=JSON.parse(JSON.stringify(AI_USE_PROMPT_DEFAULT));
  try{const v=JSON.parse(localStorage.getItem(AI_USE_PROMPT_KEY))||{};for(const k of Object.keys(out)){if(v[k]){if(typeof v[k].system==="string")out[k].system=v[k].system;if(typeof v[k].instructions==="string")out[k].instructions=v[k].instructions;}}}catch(e){}
  return out;
}
function saveAiUsePrompts(p){try{localStorage.setItem(AI_USE_PROMPT_KEY,JSON.stringify(p));}catch(e){}}
// Fetch the built-in default brief for a use key so the settings UI can show it
// and let the user load it as a starting point to customize.
async function fetchAiUseDefault(key){
  const url=key==="fix"?"/api/fix/default-system":"/api/scan/default-system";
  const r=await fetch(url);const d=await r.json().catch(()=>({}));
  if(!r.ok||!d.ok)throw new Error((d&&d.error)||("HTTP "+r.status));
  return String(d.system||"");
}
// Editable instruction brief for the Plan "Auto-fill from project files" run.
// Empty string = use the server's built-in default (fetched on demand for the
// Settings modal). A non-empty value is sent verbatim as the run's lead brief.
const PLAN_FILL_BRIEF_KEY="hub.planFillBrief";
function loadPlanFillBrief(){try{const v=localStorage.getItem(PLAN_FILL_BRIEF_KEY);return typeof v==="string"?v:"";}catch(e){return"";}}
function savePlanFillBrief(v){try{if(v&&v.trim())localStorage.setItem(PLAN_FILL_BRIEF_KEY,v);else localStorage.removeItem(PLAN_FILL_BRIEF_KEY);}catch(e){}}
async function fetchPlanFillDefaultBrief(){
  const r=await fetch("/api/projects/plan-fill/default-brief");const d=await r.json().catch(()=>({}));
  if(!r.ok||!d.ok)throw new Error((d&&d.error)||("HTTP "+r.status));
  return String(d.brief||"");
}

function claudeStatus(parts){
  const last=parts&&parts.length?parts[parts.length-1]:null;
  if(last&&last.type==="tool")return TOOL_VERBS[last.name]||("running "+last.name);
  return"thinking";
}

// Collapse the message's parts into a render list: consecutive text parts are merged
// into a single part, while runs of consecutive tool calls (the green "⚙ …" rows)
// are folded into a single {type:"tools",items:[…]} group so they can be tucked
// behind a toggle instead of crowding out long prose responses.
function groupParts(parts,live){
  const out=[];
  for(const pt of (parts||[])){
    if(pt.type==="tool"){
      const prev=out[out.length-1];
      if(prev&&prev.type==="tools")prev.items.push(pt);
      else out.push({type:"tools",items:[pt]});
    }else if(pt.type==="text"){
      const prev=out[out.length-1];
      if(prev&&prev.type==="text"){
        prev.text+=(pt.text||"");
      }else{
        out.push({type:"text",text:pt.text||""});
      }
    }else out.push(pt);
  }
  // The inline follow-up marker (<<followup: ...>>) is ghost-text input, never
  // transcript: hide a complete one always, and a half-streamed one while live.
  for(const g of out)if(g.type==="text"&&g.text.indexOf("<")>=0)g.text=stripFollowupForDisplay(g.text,live);
  return out;
}
// Resolve a Skill tool's id to the label and blurb shown in the transcript. The
// tool sends plugin-qualified ids ("superpowers:brainstorming") while the picker's
// SKILLS_CACHE stores short labels ("brainstorming"), so try the full id first and
// fall back to the short form. An unresolved skill still renders — just without a
// blurb (the cache may not have loaded, or it may be a skill the picker hides).
function skillInfo(id){
  const raw=String(id||"");
  const short=raw.includes(":")?raw.slice(raw.indexOf(":")+1):raw;
  const list=SKILLS_CACHE||[];
  const hit=list.find(s=>s.name===raw)||list.find(s=>s.label===raw)
          ||list.find(s=>s.name===short)||list.find(s=>s.label===short);
  return{label:hit?hit.label:short,description:hit?hit.description:""};
}
// One transcript row for a skill Claude invoked. Distinct from the teal tool rows:
// skills carry the same accent colour as the "/" picker, and hovering the name
// explains what the skill does.
function SkillToolRow({pt}){
  const ref=useRef(null);
  const info=skillInfo(pt.skill);
  const[pos,show,hide]=useHoverPop(ref);
  return <div className="ctool skill">
    <span>✦</span>
    <span ref={ref} className="nm skill-n" tabIndex={0}
      onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>/{info.label}</span>
    {pt.desc&&<span className="dsc">{pt.desc}</span>}
    {pos&&info.description&&ReactDOM.createPortal(
      <span className="slash-pop" style={{left:pos.left,top:pos.top}}><b>/{info.label}</b>{info.description}</span>,
      document.body)}
  </div>;
}
// A collapsed run of tool calls. Shows a compact "⚙ N steps ▾" pill by default so
// the green command history doesn't drown a long answer; click to reveal the rows.
function ToolGroup({items}){
  const[open,setOpen]=useState(true);
  const n=items.length;
  return <div className="ctools">
    <button type="button" className="ctools-tog" aria-expanded={open} onClick={()=>setOpen(o=>!o)}
      title={open?"Hide the "+n+" command"+(n===1?"":"s")+" Claude ran":"Show the "+n+" command"+(n===1?"":"s")+" Claude ran"}>
      {open?"▴":"▾"}
    </button>
    {open&&<div className="ctools-list">
      {items.map((pt,j)=>pt.skill
        ?<SkillToolRow key={j} pt={pt}/>
        :<div key={j} className="ctool"><span>⚙</span><span className="nm">{pt.name}</span>{pt.desc&&<span className="dsc">{pt.desc}</span>}</div>)}
    </div>}
  </div>;
}

// Persistent shell utilities in the left navigation rail. Theme selection is
// global and writes the same `light` class/localStorage key the first-paint
// Relative "time ago" for the notification feed (e.g. "3m", "2h", "yesterday").
function timeAgo(ts){
  const s=Math.max(0,Math.floor((Date.now()-ts)/1000));
  if(s<45)return "just now";
  if(s<90)return "1m";
  const m=Math.floor(s/60); if(m<60)return m+"m";
  const h=Math.floor(m/60); if(h<24)return h+"h";
  const d=Math.floor(h/24); if(d===1)return "yesterday";
  if(d<7)return d+"d";
  return new Date(ts).toLocaleDateString();
}
// Header bell: a top-nav item that shows recent notifications (run finished/
// failed, task done, needs-approval) and an unread badge. It polls the in-app
// feed (/api/notifications) so it works even without phone push subscribed;
// opening the panel marks everything read, and clicking a row deep-links to the
// originating chat via its hash route.
function NotificationsBell({compact,onSettings,onOpen}){
  const{lang}=useLang();
  const[items,setItems]=useState([]);
  const[unread,setUnread]=useState(0);
  const[open,setOpen]=useState(false);
  const[muted,setMuted]=useState(()=>loadNotifMuted());
  const bellRef=useRef(null),bellMenuRef=useRef(null);
  useMenuFocus(open,bellMenuRef,bellRef);
  // Stay in sync if mute is flipped elsewhere (another tab / the settings panel).
  useEffect(()=>{
    const f=e=>setMuted(!!(e.detail&&e.detail.muted));
    window.addEventListener("hub-notif-muted",f);
    return()=>window.removeEventListener("hub-notif-muted",f);
  },[]);
  function toggleMute(e){
    if(e)e.stopPropagation();
    const next=!muted;
    setMuted(next);
    saveNotifMuted(next);
  }
  async function load(){
    try{
      const d=await fetch("/api/notifications").then(r=>r.json());
      if(Array.isArray(d.items))setItems(d.items);
      setUnread(Number(d.unread)||0);
    }catch(e){}
  }
  useEffect(()=>{
    load();
    const iv=setInterval(load,20000);
    // Refetch as soon as the tab regains focus so the badge is fresh on return.
    const onVis=()=>{if(!document.hidden)load();};
    document.addEventListener("visibilitychange",onVis);
    return()=>{clearInterval(iv);document.removeEventListener("visibilitychange",onVis);};
  },[]);
  useEffect(()=>{
    if(!open)return;
    const onKey=e=>{if(e.key==="Escape")setOpen(false);};
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[open]);
  function toggle(){
    const next=!open;
    setOpen(next);
    // Opening the panel clears the unread badge (and persists lastReadAt).
    if(next&&unread>0){
      setUnread(0);
      fetch("/api/notifications/read",{method:"POST"}).catch(()=>{});
    }
  }
  function openItem(n){
    setOpen(false);
    if(!n||!n.url)return;
    // The url is like "/#/slug/chat/id" — pull out the hash portion.
    const hash=n.url.indexOf("#")>=0?n.url.slice(n.url.indexOf("#")):"";
    const route=hash?chatRouteFromHash(hash):{slug:null,chatId:null};
    // A chat notification deep-links into a conversation. Just setting location.hash
    // isn't enough: the chat panel/dock is unmounted on Home, so the hashchange
    // handler switches project but never opens the chat. Hand the route to the app
    // (openTipChat) so it selects the project, opens the dock AND jumps to the chat.
    if(route.slug&&route.chatId&&onOpen){onOpen(route.slug,route.chatId);return;}
    // Non-chat urls (or no handler): route via the hash, or full-load as a fallback.
    if(hash)location.hash=hash;else location.href=n.url;
  }
  return <div className={"nav-settings"+(compact?" nav-settings-compact":"")}>
    <button type="button" ref={bellRef} className={"nav-link nav-bell"+(compact?" nav-bell-compact":"")+(open?" on":"")}
      aria-haspopup="menu" aria-expanded={open} title={t("Notifications")}
      aria-label={unread>0&&!muted?t("Notifications")+" ("+unread+" "+t("unread")+")":t("Notifications")} onClick={toggle}>
      <Icon name="bell"/>{!compact&&t("Notifications")}
      {muted&&<span className="notif-muted-dot" aria-hidden="true">🔕</span>}
      {unread>0&&!muted&&<span className="badge-n">{unread>99?"99+":unread}</span>}
    </button>
    {open&&<>
      <div className="pop-back" onMouseDown={()=>setOpen(false)}/>
      <div ref={bellMenuRef} className="nav-menu nav-menu-down nav-notif" role="menu" aria-label={t("Notifications")}>
        <div className="nav-util-label nav-notif-head">
          <span>{t("Notifications")}</span>
          <span className="notif-head-actions">
            <button type="button" className={"notif-mute"+(muted?" on":"")}
              title={muted?t("Unmute notifications"):t("Mute notifications")}
              aria-label={muted?t("Unmute notifications"):t("Mute notifications")}
              aria-pressed={muted} onClick={toggleMute}>{muted?"🔕":"🔔"}</button>
            {onSettings&&<button type="button" className="notif-gear" title={t("Notification settings")}
              aria-label={t("Notification settings")}
              onClick={()=>{setOpen(false);onSettings();}}><Icon name="settings" size={15}/></button>}
          </span>
        </div>
        {muted&&<div className="notif-muted-banner">{t("Notifications are muted.")}</div>}
        {items.length===0
          ?<div className="notif-empty">{t("You're all caught up.")}</div>
          :items.slice(0,30).map(n=>{
            // Headline each row with the originating conversation's title (the
            // server stores it as `label`); fall back to the generic event title.
            const convo=(n.label||"").trim();
            const head=convo||n.title;
            // Sub-line: when we headline with the conversation name, use the event
            // title as the sub ("Run finished"); otherwise fall back to the raw body.
            const sub=convo?n.title:(n.body&&n.body.indexOf("{")<0?n.body:"");
            const act=!!n.needsAction;
            return <button key={n.id} type="button"
              className={"notif-item ev-"+(n.event||"")+(act?" needs-action":"")} role="menuitem"
              onClick={()=>openItem(n)}>
              <span className="notif-dot" aria-hidden="true"/>
              <span className="notif-body">
                <span className="notif-title">{head}</span>
                {sub?<span className="notif-sub">{sub}</span>:null}
                {act&&<span className="notif-action-tag">{t("Action required")}</span>}
              </span>
              <span className="notif-time">{timeAgo(n.ts)}</span>
            </button>;
          })}
      </div>
    </>}
  </div>;
}
// script reads in public/index.html. It's a single top-nav item ("Settings")
// whose popup holds every account/preference surface: GitHub, AI settings,
// Sounds, the appearance toggle, and Sign out.
function SettingsMenu({onAiSettings,onGithub,onNotifications,onApiKeys,active,variant,exp}){
  const[light,setLight]=useState(()=>{try{return !document.documentElement.classList.contains("dark");}catch(e){return true;}});
  const[email,setEmail]=useState("");
  const[open,setOpen]=useState(false);
  const[sub,setSub]=useState(false);
  const{lang,setLang}=useLang();
  const menuBtnRef=useRef(null),menuRef=useRef(null);
  useMenuFocus(open,menuRef,menuBtnRef);
  useEffect(()=>{
    let live=true;
    fetch("/api/me").then(r=>r.json()).then(d=>{if(live&&d&&d.user&&d.user.email)setEmail(d.user.email);}).catch(()=>{});
    return()=>{live=false;};
  },[]);
  useEffect(()=>{
    const el=document.documentElement;
    el.classList.toggle("dark",!light);
    try{localStorage.setItem("hub-theme",light?"light":"dark");}catch(e){}
  },[light]);
  // Stay in sync when the theme is flipped elsewhere (e.g. the command palette),
  // so this menu's Dark/Light buttons reflect the real state.
  useEffect(()=>{
    const f=e=>setLight(!!(e.detail&&e.detail.light));
    window.addEventListener("hub-theme",f);
    return()=>window.removeEventListener("hub-theme",f);
  },[]);
  // Collapse the Settings submenu whenever the whole menu closes.
  useEffect(()=>{if(!open)setSub(false);},[open]);
  // Close the overflow menu on Escape while it is open.
  useEffect(()=>{
    if(!open)return;
    const onKey=e=>{if(e.key==="Escape")setOpen(false);};
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[open]);
  // Sign out: on Heygents the JWT lives in localStorage (gate.js owns it, and
  // exposes __hubLogout); on the internal hub the session is a cookie cleared
  // by GET /logout. Either way we reload so the login gate takes over.
  function logout(){
    if(CLOUD&&typeof window.__hubLogout==="function"){window.__hubLogout();return;}
    fetch("/logout").catch(()=>{}).finally(()=>window.location.reload());
  }
  // `variant==="hamburger"` renders an icon-only chip (no "Settings" label) for
  // the mobile header, where the sidebar — and its Settings menu — is hidden.
  const ham=variant==="hamburger";
  return <div className={"nav-settings"+(ham?" nav-settings-ham":"")}>
    <button type="button" ref={menuBtnRef} className={"nav-link"+(ham?" nav-ham":"")+(open||active?" on":"")}
      aria-haspopup="menu" aria-expanded={open} aria-label={ham?t("Menu"):undefined}
      title={ham?t("Menu"):undefined} onClick={()=>setOpen(o=>!o)}><Icon name="menu"/>{ham?(exp?<span className="rlabel">{t("Menu")}</span>:null):t("Menu")}</button>
    {open&&<>
      <div className="pop-back" onMouseDown={()=>setOpen(false)}/>
      <div ref={menuRef} className="nav-menu nav-menu-down" role="menu" aria-label={t("Menu")}>
        <div className="nav-util-label">{email||"Heygents"}</div>
        <button type="button" className={"menu-item"+(sub?" on":"")} role="menuitem"
          aria-haspopup="menu" aria-expanded={sub}
          title={t("GitHub, AI settings and notifications")}
          onClick={()=>setSub(s=>!s)}><Icon name="settings"/>{t("Settings")}<span style={{marginInlineStart:"auto",opacity:.6}}>{sub?"▾":"▸"}</span></button>
        {sub&&<>
          <button type="button" className="menu-item" role="menuitem" style={{paddingInlineStart:28}}
            title={t("Connect GitHub, pick repos to create projects, or link a project to its repo")}
            onClick={()=>{setOpen(false);onGithub();}}><Icon name="github"/>{t("GitHub")}</button>
          <button type="button" className="menu-item" role="menuitem" style={{paddingInlineStart:28}}
            title={t("Choose which AI provider and model handle scans, fixes and task explanations")}
            onClick={()=>{setOpen(false);onAiSettings();}}><Icon name="brain"/>{t("AI settings")}</button>
          <button type="button" className="menu-item" role="menuitem" style={{paddingInlineStart:28}}
            title={t("Choose per-event sounds and phone push for run, approval and task notifications")}
            onClick={()=>{setOpen(false);onNotifications();}}><Icon name="volume"/>{t("Notifications")}</button>
        </>}
        <a className="menu-item" role="menuitem"
          href="/guides/" target="_blank" rel="noopener noreferrer"
          title={t("Product documentation, guides and articles about managing projects")}
          onClick={()=>setOpen(false)}><Icon name="book"/>{t("Docs")}</a>
        <a className="menu-item" role="menuitem"
          href="/pricing" target="_blank" rel="noopener noreferrer"
          title={t("Plans and pricing")}
          onClick={()=>setOpen(false)}><Icon name="tag"/>{t("Pricing")}</a>
        <div className="nav-menu-sep"/>
        <div className="nav-util-label">{t("Appearance")}</div>
        <div className="nav-appearance" role="group" aria-label={t("Appearance")}>
          <button type="button" className={!light?"on":""} onClick={()=>setLight(false)}><Icon name="moon"/>{t("Dark")}</button>
          <button type="button" className={light?"on":""} onClick={()=>setLight(true)}><Icon name="sun"/>{t("Light")}</button>
        </div>
        <div className="nav-menu-sep"/>
        <button type="button" className="menu-item danger nav-signout" role="menuitem"
          title={t("Sign out of Heygents")} onClick={()=>{setOpen(false);logout();}}><Icon name="logout"/>{t("Sign out")}</button>
      </div>
    </>}
  </div>;
}

// Small text chip that names a state (ok / warn / err / info) so severity is
// never carried by colour alone. Pairs with the .state-box tinted container.
function StateChip({kind,label}){
  return <span className={"state-chip "+(kind||"info")}>{label}</span>;
}

// HUB_CLOUD: install/update the companion agent. OAuth-only — there is no API
// key anywhere in this flow: the agent runs every provider on the machine's own
// CLI subscription login. The install command carries only the pairing token.
function CloudAgentSetup({onClose,onConnected}){
  const{lang}=useLang();
  // One token, two install lines: bash (macOS/Linux) and PowerShell (Windows).
  // The token itself is identical, so switching OS never needs a new token.
  const[cmds,setCmds]=useState(null);
  const[err,setErr]=useState("");
  const[copied,setCopied]=useState(false);
  // Once a command is on screen the user is about to run it, so we fast-poll
  // /api/agents to catch the connection the instant it lands instead of waiting
  // out App's 20s background poll. `connected` holds the new machine's host name.
  const[connected,setConnected]=useState(null);
  const baselineRef=useRef(null); // Set of hosts already online when the command was generated
  // Default the OS picker to the machine the browser runs on, which is the
  // machine the user is most likely installing on.
  const[os,setOs]=useState(()=>{try{return /win/i.test(navigator.platform||navigator.userAgent||"")?"win":"unix";}catch(e){return "unix";}});
  const cmd=cmds?(os==="win"?cmds.win:cmds.unix):"";
  // macOS vs Linux only changes the "open a terminal" hint; both use the unix line.
  const isMac=(()=>{try{return /mac/i.test(navigator.platform||navigator.userAgent||"");}catch(e){return false;}})();
  const[busy,setBusy]=useState(false);
  function copy(text){
    if(!text)return;
    try{navigator.clipboard.writeText(text).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2500);},()=>{});}catch(e){}
  }
  // A browser can't open or type into a local terminal, so the shortest path is:
  // generate + copy the command the moment the modal opens, then tell the user
  // the exact keystrokes to open a terminal and paste.
  function gen(){
    setErr("");setCopied(false);setConnected(null);baselineRef.current=null;setBusy(true);
    fetch("/api/agent/tokens",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({label:"my-machine"})})
      .then(r=>r.json()).then(j=>{
        if(j&&j.install){const c={unix:j.install,win:j.installWindows||""};setCmds(c);copy(os==="win"?c.win:c.unix);}
        else setErr((j&&j.error)||"Failed to create a pairing token.");
      })
      .catch(()=>setErr("Network error.")).finally(()=>setBusy(false));
  }
  useEffect(()=>{gen();},[]);
  // While a command is shown and no connection has been detected yet, poll every
  // 2.5s: seed the baseline hosts on the first response, then watch for a host
  // that wasn't there before. The moment one appears, show inline success, hand
  // the fresh list to App (which raises the toast + fanfare) and auto-close.
  useEffect(()=>{
    if(!cmds||connected)return;
    let alive=true;
    const poll=()=>fetch("/api/agents").then(r=>r.json()).then(d=>{
      if(!alive||!d||!d.ok)return;
      const list=Array.isArray(d.agents)?d.agents:[];
      if(!baselineRef.current){baselineRef.current=new Set(list.map(a=>a&&a.host));return;}
      const fresh=list.find(a=>a&&!baselineRef.current.has(a.host));
      if(fresh){
        setConnected(fresh.host||"");
        try{onConnected&&onConnected(list);}catch(e){}
        setTimeout(()=>{if(alive)onClose();},1600);
      }
    }).catch(()=>{});
    poll();
    const t=setInterval(poll,2500);
    return()=>{alive=false;clearInterval(t);};
  },[cmds,connected]);
  useEffect(()=>{
    function onKey(e){if(e.key==="Escape"){e.stopPropagation();onClose();}}
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[]);
  const osTab=(id,label)=><button className="btn sm" aria-pressed={os===id} onClick={()=>{setOs(id);setCopied(false);if(cmds)copy(id==="win"?cmds.win:cmds.unix);}}
    style={{fontWeight:os===id?700:400,opacity:os===id?1:.65,borderColor:os===id?"var(--accent)":undefined}}>{label}</button>;
  return ReactDOM.createPortal(
    <div onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:"fixed",inset:0,zIndex:"var(--z-modal)",background:"rgba(0,0,0,.55)",display:"grid",placeItems:"center",padding:16}}>
      <div role="dialog" aria-modal="true" aria-labelledby="agent-setup-title"
        style={{width:"min(92vw,640px)",background:"var(--surface)",border:"1px solid var(--line-2)",borderRadius:14,padding:18}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
          <h3 id="agent-setup-title" style={{fontSize:15,margin:0,fontWeight:700}}>{t("Install the Heygents agent")}</h3>
          <span style={{flex:1}}/>
          <button className="btn sm" onClick={onClose}>✕ {t("Close")}</button>
        </div>
        <p className="muted" style={{fontSize:13,margin:"0 0 12px"}}>
          Run this on the machine your projects live on. It connects out to Heygents, runs Claude/Codex/Gemini
          locally in your project folders using your existing CLI logins (no API key needed), and reports
          CPU/RAM/disk and plan usage. Nothing but that leaves your machine. Re-running the command updates the agent.
        </p>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,flexWrap:"wrap"}}>
          {osTab("unix",isMac?"🍎 macOS":"🐧 Linux / 🍎 macOS")}{osTab("win","🪟 Windows")}
          <span style={{flex:1}}/>
          <button className="btn sm" onClick={gen} disabled={busy} title={t("Generate a fresh pairing token")}>{busy?t("Generating…"):"↻ "+t("New token")}</button>
        </div>
        {!cmd&&busy&&<div className="muted" style={{fontSize:12}}>{t("Generating your install command…")}</div>}
        {cmd&&<ol className="agent-steps" style={{fontSize:13,margin:"4px 0 8px",paddingInlineStart:20,lineHeight:1.7}}>
          <li><b>{t("Open a terminal:")}</b>{" "}
            {os==="win"
              ?<span>{t("press")} <kbd>Win</kbd> + <kbd>R</kbd>, {t("type")} <code>powershell</code>, {t("press")} <kbd>Enter</kbd> <span className="muted">({t("no admin needed")})</span></span>
              :isMac
                ?<span>{t("press")} <kbd>⌘</kbd> + <kbd>Space</kbd>, {t("type")} <code>Terminal</code>, {t("press")} <kbd>Enter</kbd></span>
                :<span>{t("press")} <kbd>Ctrl</kbd> + <kbd>Alt</kbd> + <kbd>T</kbd></span>}
          </li>
          <li><b>{t("Paste and run:")}</b>{" "}
            {os==="win"?<span><kbd>Ctrl</kbd> + <kbd>V</kbd></span>:isMac?<span><kbd>⌘</kbd> + <kbd>V</kbd></span>:<span><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>V</kbd></span>}, {t("then")} <kbd>Enter</kbd>
            {copied?<span style={{color:"var(--ok)",marginInlineStart:8}}>✓ {t("already copied to your clipboard")}</span>:null}
          </li>
          <li className="muted">{t("This window closes by itself once your machine connects.")}</li>
        </ol>}
        {cmd&&<pre onClick={()=>copy(cmd)} title={t("Click to copy")} style={{cursor:"copy",whiteSpace:"pre-wrap",wordBreak:"break-all",fontSize:12,fontFamily:"ui-monospace,Menlo,monospace",
          background:"var(--bg)",border:"1px solid var(--line)",borderRadius:10,padding:12,margin:"6px 0"}}>{cmd}</pre>}
        {cmd&&<button className="btn sm primary" onClick={()=>copy(cmd)}>{copied?"Copied ✓":"📋 Copy command"}</button>}
        {cmds&&os==="win"&&<div className="muted" style={{fontSize:12,marginTop:8}}>
          Needs Node.js 18+ (https://nodejs.org). It installs a scheduled task called HubCloudAgent that starts at logon
          and keeps the agent running; re-run the same command any time to update it.
        </div>}
        {err&&<div style={{color:"var(--err)",fontSize:13,marginTop:6}}>{err}</div>}
        {cmds&&!connected&&<div className="muted" style={{fontSize:12,marginTop:10,display:"flex",alignItems:"center",gap:8}}>
          <span className="spin" style={{width:12,height:12,border:"2px solid var(--line-2)",borderTopColor:"var(--accent)",borderRadius:"50%",display:"inline-block",animation:"cn-spin 0.8s linear infinite"}}/>
          {t("Waiting for your machine to connect…")}
        </div>}
        {connected&&<div role="status" className="state-box ok" style={{marginTop:12,padding:"10px 12px",borderRadius:10,fontSize:13,display:"flex",alignItems:"center",gap:8}}>
          <StateChip kind="ok" label={t("Connected")}/>
          <span><b>{t("Agent connected!")}</b> {connected?<span><b style={{color:"var(--text-2)"}}>{connected}</b> {t("is online.")}</span>:null} {t("clone, scans, chat and automations are ready.")}</span>
        </div>}
      </div>
    </div>, document.body);
}

// HUB_CLOUD: shared "Install agent" call-to-action. Every empty/blocked state
// that needs a connected agent renders one of these, so a freshly-onboarded user
// is never left at a dead end — they all open the SAME CloudAgentSetup modal,
// which App hoists and exposes as window.__hubOpenAgentSetup.
function openAgentSetup(){try{window.__hubOpenAgentSetup&&window.__hubOpenAgentSetup();}catch(e){}}
function InstallAgentBtn({label,className,style}){
  const{lang}=useLang();
  return <button className={className||"btn sm primary"} style={style}
    title={t("Install the Heygents agent on your machine — it runs Claude/Codex/Gemini in your project folders and makes clone, scans, chat and automations work")}
    onClick={openAgentSetup}>{label||t("Install agent")}</button>;
}

// HUB_CLOUD: persistent banner under the header while the signed-in tenant has
// NO agent connected. Nothing real (clone, scan, chat, automations) works until
// an agent is running, so this is the single most important next step after
// login — kept visible on every screen until an agent polls in.
// On phones it collapses to one line (CSS hides the explainer) and can be
// dismissed there; the dismissal is remembered but only hides the banner at
// mobile widths, so a desktop visit still shows the next step.
const AGENT_BANNER_DISMISS_KEY="hub.agentBanner.mobileDismissed";
function AgentBanner(){
  const{lang}=useLang();
  const[dismissed,setDismissed]=useState(()=>{try{return localStorage.getItem(AGENT_BANNER_DISMISS_KEY)==="1";}catch(e){return false;}});
  const dismiss=()=>{setDismissed(true);try{localStorage.setItem(AGENT_BANNER_DISMISS_KEY,"1");}catch(e){}};
  return <div className={"agent-banner"+(dismissed?" is-dismissed":"")} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
    background:"var(--surface)",borderBottom:"1px solid var(--line-2)"}}>
    <div className="agent-banner-text" style={{flex:1,minWidth:0,fontSize:13,lineHeight:1.45}}>
      <b>{t("Connect your machine to finish setup.")}</b>{" "}
      <span className="agent-banner-more" style={{color:"var(--text-3)"}}>{t("Heygents runs Claude/Codex/Gemini in your own project folders through a small agent. Until it's connected, clone, scans, chat and automations can't run.")}</span>
    </div>
    <InstallAgentBtn label={t("Install the agent")}/>
    <button type="button" className="btn sm agent-banner-x" onClick={dismiss} aria-label={t("Dismiss")} title={t("Dismiss")}>×</button>
  </div>;
}

// HUB_CLOUD: the plan cap, explained. Billing is a MOCK provider standing in
// for the Israeli card processor that is not yet contracted, so whether a
// price/upgrade flow may be shown at all is a server-controlled flag
// (plan.billingEnabled, from HUB_BILLING_ENABLED — OFF by default). Off, this
// stays the pre-billing honest copy: no price, no checkout, "Notify me" only.
// On, PlanUpgradeModal (below) renders the real price + checkout redirect.
function PlanLimitModal({plan,onClose}){
  const{lang}=useLang();
  if(plan&&plan.billingEnabled)return <PlanUpgradeModal plan={plan} onClose={onClose}/>;
  const[state,setState]=useState("idle"); // idle | sending | done | error
  useEffect(()=>{
    function onKey(e){if(e.key==="Escape")onClose();}
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[]);
  function notifyMe(){
    setState("sending");
    fetch("/api/plan/interest",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({})})
      .then(r=>r.json()).then(d=>setState(d&&d.ok?"done":"error")).catch(()=>setState("error"));
  }
  const count=plan&&plan.projectCount;
  return ReactDOM.createPortal(
    <div onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:"fixed",inset:0,zIndex:"var(--z-modal)",background:"rgba(0,0,0,.55)",display:"grid",placeItems:"center",padding:16}}>
      <div role="dialog" aria-modal="true" aria-labelledby="plan-limit-title"
        style={{width:"min(92vw,460px)",background:"var(--surface)",border:"1px solid var(--line-2)",borderRadius:14,padding:18}}>
        <h3 id="plan-limit-title" style={{margin:"0 0 10px",fontSize:15}}>{t("Free includes 2 projects.")}</h3>
        <p style={{margin:"0 0 16px",color:"var(--text-3)",fontSize:13,lineHeight:1.5}}>
          {t("Premium removes the limit — it's not on sale yet. Delete a project to make room, or tell us you want Premium and we'll let you know the moment it's ready.")}
          {typeof count==="number"?` ${t("You currently have")} ${count}.`:""}
        </p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center",flexWrap:"wrap"}}>
          {state==="error"&&<span style={{fontSize:12,color:"var(--err)"}}>{t("Couldn't record that — try again.")}</span>}
          {state==="done"
            ? <span style={{fontSize:13,color:"var(--text-3)"}}>{t("Thanks — we'll be in touch.")}</span>
            : <button className="btn sm" onClick={notifyMe} disabled={state==="sending"}>
                {state==="sending"?t("Sending…"):t("Notify me")}
              </button>}
          <button className="btn sm primary" onClick={onClose}>{t("Close")}</button>
        </div>
      </div>
    </div>, document.body);
}

// HUB_CLOUD: the billing-enabled variant of the plan-limit modal — real price,
// real hosted-payment redirect. Only reachable when plan.billingEnabled is
// true (see PlanLimitModal above); kept as its own function so the honest,
// pre-billing copy above never shares a component with pricing/payment text.
function PlanUpgradeModal({plan,onClose}){
  const{lang}=useLang();
  useEffect(()=>{
    function onKey(e){if(e.key==="Escape")onClose();}
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[]);
  // The upgrade CTA opens the pricing/payment page in its own tab — no price
  // or plan details are stated in the modal itself, only that an upgrade is
  // required, and the app underneath stays put instead of navigating away.
  function upgrade(){window.open("/pricing","_blank","noopener,noreferrer");}
  return ReactDOM.createPortal(
    <div dir="ltr" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:"fixed",inset:0,zIndex:"var(--z-modal)",background:"rgba(0,0,0,.55)",display:"grid",placeItems:"center",padding:16}}>
      <div role="dialog" aria-modal="true" aria-labelledby="plan-upgrade-title"
        style={{width:"min(94vw,440px)",maxHeight:"92vh",overflowY:"auto",background:"var(--surface)",border:"1px solid var(--line-2)",borderRadius:16,padding:0}}>
        {/* Header */}
        <div style={{padding:"22px 22px 4px",display:"flex",alignItems:"center",gap:10}}>
          <span style={{display:"grid",placeItems:"center",width:36,height:36,borderRadius:10,background:"rgba(215,141,254,.18)",color:"#D78DFE"}}>
            <Icon name="crown" size={20}/>
          </span>
          <h3 id="plan-upgrade-title" style={{margin:0,fontSize:18,fontWeight:700}}>{t("Upgrade required")}</h3>
        </div>
        {/* Body */}
        <div style={{padding:"12px 22px 6px"}}>
          <p style={{margin:0,color:"var(--text-2)",fontSize:14,lineHeight:1.55}}>
            {t("Upgrading your account is required to manage a 3rd project.")}
          </p>
        </div>
        {/* Footer */}
        <div style={{padding:"18px 22px 20px",display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center"}}>
          <button className="btn sm" onClick={onClose}>{t("Cancel")}</button>
          <button className="btn sm primary" onClick={upgrade}
            style={{display:"inline-flex",alignItems:"center",gap:7}}>
            <Icon name="crown" size={15}/>
            {t("Upgrade")}
          </button>
        </div>
      </div>
    </div>, document.body);
}

// HUB_CLOUD: the return from a hosted checkout. It renders what the SERVER says,
// never what the redirect claims — the webhook is the source of truth and may
// land a beat after the browser comes back, so success re-reads /api/me.
function BillingBanner({state,onClose}){
  const{lang}=useLang();
  const ok=state==="ok";
  return <div role="status" style={{
    position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",zIndex:"var(--z-toast)",
    display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,
    boxShadow:"0 10px 34px rgba(0,0,0,.30)"}} className={"state-box "+(ok?"ok":"err")}>
    <StateChip kind={ok?"ok":"err"} label={ok?t("Upgraded"):t("Payment failed")}/>
    <span style={{fontSize:13}}>{ok?t("You're on Premium — the project limit is lifted."):t("That payment didn't go through. Nothing was charged.")}</span>
    <button className="btn sm" onClick={onClose}>{t("Dismiss")}</button>
  </div>;
}

// HUB_CLOUD: transient confirmation that a machine just polled in — the moment a
// freshly-installed agent connects. Slides in top-right, auto-dismisses after 7s,
// and can be closed manually. Complements the persistent AgentBanner (which is the
// *absence* cue) by giving the connection a clear, positive acknowledgement.
function AgentToast({host,n,onClose}){
  const{lang}=useLang();
  const[shown,setShown]=useState(false);
  useEffect(()=>{
    const a=setTimeout(()=>setShown(true),20);
    const b=setTimeout(()=>onClose(),7000);
    return()=>{clearTimeout(a);clearTimeout(b);};
  },[]);
  return <div role="status" aria-live="polite" style={{
    position:"fixed",top:16,insetInlineEnd:16,zIndex:"var(--z-toast)",maxWidth:340,
    display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",
    borderRadius:10,boxShadow:"0 10px 34px rgba(0,0,0,.30)",
    opacity:shown?1:0,transform:`translateY(${shown?0:-8}px)`,transition:"opacity .25s ease, transform .25s ease"}} className="state-box ok">
    <span className="state-ic ok" aria-hidden="true"><Icon name="check" size={14}/></span>
    <div style={{flex:1,minWidth:0,fontSize:13,lineHeight:1.45}}>
      <b style={{color:"var(--text)"}}>{n>1?n+" "+t("agents connected"):t("Agent connected")}</b>
      <div style={{color:"var(--text-3)",marginTop:2}}>
        {host?<span>{t("Your machine")} <b style={{color:"var(--text-2)"}}>{host}</b> {t("is online —")} </span>:<span>{t("Your machine is online —")} </span>}
        {t("clone, scans, chat and automations are ready.")}
      </div>
    </div>
    <button onClick={onClose} aria-label={t("Dismiss")} title={t("Dismiss")} style={{background:"none",border:"none",
      color:"var(--text-3)",cursor:"pointer",fontSize:16,lineHeight:1,padding:0,marginTop:-1}}>×</button>
  </div>;
}

// Unified notification events — each has an in-app sound (chosen from the
// library, plays while a hub tab is open) and, for the push-capable ones, an
// optional phone push (OS sound, fires when Heygents is closed). `repeat` events
// can nag until you respond; `push` events map to a server-side notifier type.
// See NotificationsSettings for the merged Sounds + phone-push panel.
const NOTIFICATION_EVENTS=[
  {key:"runFinished",  title:"Run finished",                     desc:"A chat turn / run completes",             push:true},
  {key:"needsApproval",title:"Needs approval / popup question",  desc:"A run pauses for your approval or answer",push:true,repeat:true},
  {key:"taskDone",     title:"Task done (autonomous / scheduled)",desc:"A scheduled chat or automation finishes", push:true},
  {key:"skillUsed",    title:"Skill used",                       desc:"Claude invokes a skill during a run"},
];
// Library tones plus the spoken-announcement option, offered per event.
const SOUND_OPTIONS=[...Object.keys(SOUND_LIBRARY),VOICE_SOUND];
// Choices for the "repeat every N until you respond" interval.
const REPEAT_INTERVALS=[[30,"30s"],[60,"60s"],[120,"2m"],[300,"5m"]];

const AI_USE_EVENTS=[
  {key:"chat",title:"Chat",desc:"Every conversation you start with a project."},
  {key:"scan",title:"Deep Scan",desc:"Deep Scan and re-scan of a project."},
  {key:"fix",title:"Fix from a task card",desc:"When you ask AI to act on a task card."},
  {key:"explain",title:"Task explanation",desc:"Short explanation of a task."},
  {key:"subject",title:"Chat subject",desc:"Very short 3–4 word name for each new chat."},
];
// Settings → API keys. The single place every third-party credential is defined,
// instead of hand-editing .env and restarting the hub.
//
// STORE-ONLY, deliberately: saving a key here does not change how internal AI
// runs authenticate. The server still strips ANTHROPIC_API_KEY / GEMINI_API_KEY /
// GOOGLE_API_KEY from every CLI spawn (aiSpawnEnv), so chat, Deep Scan and
// automations keep running on the free subscription login and can never silently
// start billing a metered API. These values are for explicit uses — the Cloud
// agent installer, a connection test, copy-paste elsewhere.
//
// Values never come back to the browser: /api/secrets returns only a mask, and a
// row left untouched round-trips as an empty string, which the server reads as
// "keep what you have". Testing therefore happens server-side.
function ApiKeys({onClose}){
  const{lang}=useLang();
  const[data,setData]=useState(null);
  const[err,setErr]=useState("");
  const[draft,setDraft]=useState({});      // id → newly typed value, cleared on save
  const[test,setTest]=useState({});        // id → {state:"testing"|"ok"|"bad",text}
  const[saving,setSaving]=useState("");
  const[newName,setNewName]=useState("");
  const[newValue,setNewValue]=useState("");
  const load=()=>fetch("/api/secrets").then(r=>r.json())
    .then(d=>{if(d&&d.ok){setData(d);setErr("");}else setErr("Couldn't load the key list.");})
    .catch(e=>setErr(String(e)));
  useEffect(()=>{load();},[]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  function send(entries,after){
    setSaving("saving");
    fetch("/api/secrets",{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({entries})})
      .then(r=>r.json()).then(d=>{
        if(d&&d.ok){setData(d);setSaving("ok");setTimeout(()=>setSaving(s=>s==="ok"?"":s),2200);if(after)after();}
        else setSaving("err");
      }).catch(()=>setSaving("err"));
  }
  function saveAll(){
    const entries=Object.keys(draft).filter(id=>draft[id]&&draft[id].trim()).map(id=>({id,value:draft[id].trim()}));
    if(!entries.length)return;
    send(entries,()=>setDraft({}));
  }
  function remove(id){
    send([{id,remove:true}],()=>{
      setDraft(d=>{const n={...d};delete n[id];return n;});
      setTest(prev=>{const n={...prev};delete n[id];return n;});
    });
  }
  function addCustom(){
    const name=newName.trim(),value=newValue.trim();
    if(!name||!value)return;
    send([{id:name,label:name,value}],()=>{setNewName("");setNewValue("");});
  }
  function runTest(id){
    setTest(prev=>({...prev,[id]:{state:"testing",text:"Testing…"}}));
    fetch("/api/secrets/"+encodeURIComponent(id)+"/test",{method:"POST"}).then(r=>r.json())
      .then(d=>setTest(prev=>({...prev,[id]:{state:d&&d.ok?"ok":"bad",text:(d&&d.text)||"Couldn't verify."}})))
      .catch(()=>setTest(prev=>({...prev,[id]:{state:"bad",text:"Test request failed."}})));
  }
  const dirty=Object.keys(draft).some(id=>draft[id]&&draft[id].trim());
  const row=(e)=>{
    const st=test[e.id];
    return <div className="sset-row" key={e.id} style={{alignItems:"flex-start",flexWrap:"wrap"}}>
      <span className="lbl" style={{minWidth:180}}>
        <span className="t">{e.label}</span>
        <span className="d">
          {e.env?<code style={{fontSize:11.5}}>{e.env}</code>:null}
          {e.set?<> · {t("saved")} {e.masked}</>:<> · {t("not set")}</>}
        </span>
      </span>
      <span style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end",flex:1}}>
        <input type="password" autoComplete="off" spellCheck={false}
          aria-label={e.label+" API key"}
          placeholder={e.set?t("Replace this key…"):t("Paste the key…")}
          value={draft[e.id]||""}
          onChange={ev=>{const v=ev.target.value;setDraft(d=>({...d,[e.id]:v}));}}
          style={{flex:"1 1 220px",minWidth:180,font:"inherit",fontSize:13}}/>
        {e.set&&<button type="button" className="btn xs" onClick={()=>runTest(e.id)}
          disabled={st&&st.state==="testing"} title={t("Ask the server to make one read-only authenticated call with this key")}>
          {st&&st.state==="testing"?"…":"✓ "+t("Test")}</button>}
        {e.docsUrl&&<a className="btn xs" href={e.docsUrl} target="_blank" rel="noopener noreferrer" title={t("Opens in a new tab")}>{t("Get key →")}</a>}
        {e.set&&<button type="button" className="btn xs danger" onClick={()=>remove(e.id)} title={t("Delete this key from the server")}>🗑</button>}
      </span>
      {st&&<div className={st.state==="ok"?"ok":st.state==="bad"?"err":"muted"} style={{fontSize:12.5,flexBasis:"100%",textAlign:"end"}}>{st.text}</div>}
    </div>;
  };
  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset" role="dialog" aria-modal="true" aria-labelledby="dlg-api-keys-title">
      <div className="sset-head"><h3 id="dlg-api-keys-title">{t("API keys")}</h3><button className="btn sm" onClick={onClose}>✕ {t("Close")}</button></div>
      <div className="sset-body">
        <p className="muted" style={{fontSize:12.5,lineHeight:1.5,margin:"4px 0 10px"}}>
          {t("Every third-party key Heygents knows about, in one place. Stored server-side in")}
          <code> data/secrets.json</code> {t("(owner-only, never committed) and shown here masked — the full value never comes back to the browser.")}
        </p>
        <div style={{border:"1px solid var(--line-2)",background:"var(--bg)",borderRadius:10,padding:"9px 12px",margin:"0 0 12px",fontSize:12.5,lineHeight:1.6,color:"var(--text-2)"}}>
          <b>{t("These keys are stored, not spent.")}</b> {t("Internal AI runs (chat, Deep Scan, automations) keep using the Claude and Gemini CLI subscription logins — saving a key here never starts billing a metered API. Use them for the Heygents agent installer, connection tests, or copy-paste elsewhere.")}
        </div>
        {err&&<div className="err" style={{fontSize:12.5,padding:"8px 2px"}}>{err}</div>}
        {!data&&!err&&<div className="muted" style={{fontSize:12.5}}>{t("Loading…")}</div>}
        {data&&<>
          {data.known.map(row)}
          {data.known.some(k=>k.id==="github")&&<p className="muted" style={{fontSize:11.5,margin:"2px 0 0"}}>
            {t("The GitHub token here is a standalone PAT. The live repo connection Heygents actually clones and opens PRs with is the OAuth one under")} <b>{t("Settings → GitHub")}</b>.
          </p>}
          <h4 style={{margin:"18px 0 4px",fontSize:13.5}}>{t("Custom keys")}</h4>
          <p className="muted" style={{fontSize:12.5,margin:"0 0 6px"}}>{t("Anything else you want defined once — Supabase, Telegram, a webhook secret.")}</p>
          {data.custom.length===0&&<div className="muted" style={{fontSize:12.5}}>{t("None yet.")}</div>}
          {data.custom.map(row)}
          <div className="sset-row" style={{alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <input type="text" placeholder={t("NAME (e.g. SUPABASE_SERVICE_KEY)")} value={newName}
              aria-label={t("New key name")} spellCheck={false}
              onChange={e=>setNewName(e.target.value)} style={{flex:"1 1 200px",font:"inherit",fontSize:13}}/>
            <input type="password" placeholder={t("Value")} value={newValue} autoComplete="off" spellCheck={false}
              aria-label={t("New key value")}
              onChange={e=>setNewValue(e.target.value)} style={{flex:"1 1 200px",font:"inherit",fontSize:13}}/>
            <button type="button" className="btn sm" disabled={!newName.trim()||!newValue.trim()||saving==="saving"}
              onClick={addCustom}><Icon name="plus" size={14}/> {t("Add")}</button>
          </div>
        </>}
      </div>
      <div className="sset-foot">
        <span className="sp" style={{flex:1,fontSize:11.5,color:"var(--text-4)"}}>
          {saving==="ok"?"Saved ✓":saving==="err"?t("Couldn't save — try again."):dirty?t("Unsaved changes."):t("Keys are saved on the server, owner-only.")}
        </span>
        <button className="btn sm primary" disabled={!dirty||saving==="saving"} onClick={saveAll}>{saving==="saving"?t("Saving…"):t("Save")}</button>
      </div>
    </div>
  </div>;
}

function AiSettings({onClose,onOpen,only}){
  const{lang}=useLang();
  const[prefs,setPrefs]=useState(loadAiUsePrefs);
  const set=(key,patch)=>setPrefs(prev=>{const next={...prev,[key]:{...prev[key],...patch}};saveAiUsePrefs(next);return next;});
  const[defModels,setDefModels]=useState(loadAiDefaultModels);
  const setDefModel=(provider,value)=>setDefModels(prev=>{const next=saveAiDefaultModels({...prev,[provider]:value});
    // Chat remembers the last model picked per provider in hub.<provider>Model,
    // and that stored value wins over the default (both chat init and provider
    // switch read `stored || defaultModelFor`). Clear it so the new default
    // actually takes effect instead of being shadowed by a stale pick.
    try{localStorage.removeItem("hub."+provider+"Model");}catch{}
    return next;});
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  const onlyEv=only&&AI_USE_EVENTS.find(ev=>ev.key===only);
  // Editable instructions are offered for the Scan/Fix uses only.
  const hasPrompts=only==="scan"||only==="fix";
  const[prompts,setPrompts]=useState(loadAiUsePrompts);
  const promptCur=hasPrompts?(prompts[only]||AI_USE_PROMPT_DEFAULT[only]):null;
  const setPrompt=patch=>setPrompts(prev=>{const next={...prev,[only]:{...prev[only],...patch}};saveAiUsePrompts(next);return next;});
  const[defBusy,setDefBusy]=useState(false);
  const loadBuiltIn=async()=>{if(defBusy)return;setDefBusy(true);try{const s=await fetchAiUseDefault(only);setPrompt({system:s});}catch(e){alert(t("Failed to load the default instructions.")+" "+((e&&e.message)||e));}finally{setDefBusy(false);}};
  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset aiset" role="dialog" aria-modal="true" aria-labelledby="dlg-ai-settings-title">
      <div className="sset-head">
        <h3 id="dlg-ai-settings-title">{t("AI settings")}{onlyEv?" - "+t(onlyEv.title):""}</h3>
        <button className="btn sm" onClick={onClose}>✕ {t("Close")}</button>
      </div>
      <div className="sset-body">
        <p className="aiset-intro">{t("Pick the provider and model for each place Heygents talks to AI.")}</p>
        {AI_USE_EVENTS.filter(ev=>only?ev.key===only:(ev.key!=="chat"&&ev.key!=="scan"&&ev.key!=="subject")).map(ev=>{
          const cur=prefs[ev.key];
          return (
          <section className="aiset-card" key={ev.key}>
            <header className="aiset-cardhead">
              <div className="aiset-titles">
                <span className="aiset-title">{t(ev.title)}</span>
                <span className="aiset-desc">{t(ev.desc)}</span>
              </div>
            </header>
            <div className="aiset-controls">
              <select aria-label={ev.title+" provider"} value={cur.provider}
                onChange={e=>set(ev.key,{provider:e.target.value,model:""})}>
                <option value="claude">{t("Claude")}</option>
                <option value="codex">{t("Codex")}</option>
                <option value="gemini">{t("Gemini")}</option>
              </select>
              <select aria-label={ev.title+" model"} value={cur.model}
                onChange={e=>set(ev.key,{model:e.target.value})}>
                {(AI_MODELS[cur.provider]||AI_MODELS.claude).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?" ("+m.hint+")":""}</option>)}
              </select>
            </div>
          </section>
          );
        })}
        {!only&&<section className="aiset-card" style={{marginTop:12}}>
          <header className="aiset-cardhead">
            <div className="aiset-titles">
              <span className="aiset-title">{t("Default model per provider")}</span>
              <span className="aiset-desc">{t("The model each provider uses when none is picked yet - in chat, above, and in Plan sections.")}</span>
            </div>
          </header>
          {[{p:"claude",label:"Claude"},{p:"codex",label:"Codex"},{p:"gemini",label:"Gemini"}].map(({p,label})=>(
            <div className="aiset-controls" key={p} style={{marginTop:6}}>
              <label className="muted" style={{fontSize:12,minWidth:70}}>{t(label)}</label>
              <select aria-label={label+" default model"} value={defModels[p]}
                onChange={e=>setDefModel(p,e.target.value)}>
                {(AI_MODELS[p]||[]).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?" ("+m.hint+")":""}</option>)}
              </select>
            </div>
          ))}
        </section>}
        {hasPrompts&&<section className="aiset-card" style={{marginTop:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6}}>
            <label className="muted" style={{fontSize:12}}>{t("Default instructions")}</label>
            <button type="button" className="btn sm" disabled={defBusy} onClick={loadBuiltIn}>{defBusy?"…":t("View built-in default")}</button>
          </div>
          <textarea dir="ltr" rows={8} style={{width:"100%",fontSize:12.5,lineHeight:1.5}}
            placeholder={t("Leave empty to use the built-in default. Click \"View built-in default\" to load and edit it.")}
            value={promptCur.system} onChange={e=>setPrompt({system:e.target.value})}/>
          <div style={{marginTop:12}}>
            <label className="muted" style={{fontSize:12,display:"block",marginBottom:4}}>{t("Extra instructions")}</label>
            <textarea dir="ltr" rows={4} style={{width:"100%",fontSize:12.5,lineHeight:1.5}}
              placeholder={t("Optional. Appended to the instructions above, e.g. focus areas, tone, constraints.")}
              value={promptCur.instructions} onChange={e=>setPrompt({instructions:e.target.value})}/>
          </div>
        </section>}
      </div>
      <div className="sset-foot">
        <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{t("Changes apply to the next run.")}</span>
        <button className="btn sm" onClick={()=>{const d=JSON.parse(JSON.stringify(DEFAULT_AI_USE_PREFS));saveAiUsePrefs(d);setPrefs(d);if(!only){setDefModels(saveAiDefaultModels(AI_DEFAULT_MODEL_SEED));try{["claude","codex","gemini"].forEach(p=>localStorage.removeItem("hub."+p+"Model"));}catch{}}if(hasPrompts){setPrompts(prev=>{const next={...prev,[only]:JSON.parse(JSON.stringify(AI_USE_PROMPT_DEFAULT[only]))};saveAiUsePrompts(next);return next;});}}}>{t("Reset to defaults")}</button>
      </div>
    </div>
  </div>;
}

// Small ✦ settings button for the Chat section. Scoped to the "chat" AI use
// preference, so users tune the chat provider/model right beside the chat
// history button instead of in the global AI settings modal.
function ChatAiOptions(){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  const[prefs,setPrefs]=useState(loadAiUsePrefs);
  const[prompt,setPromptState]=useState(loadChatAiPrompt);
  const[followupOn,setFollowupOn]=useState(followupEnabled);
  const cur=prefs.chat||DEFAULT_AI_USE_PREFS.chat;
  const set=patch=>setPrefs(prev=>{const next={...prev,chat:{...prev.chat,...patch}};saveAiUsePrefs(next);return next;});
  const setPrompt=patch=>setPromptState(prev=>{const next={...prev,...patch};saveChatAiPrompt(next);return next;});
  const resetAll=()=>{const d=JSON.parse(JSON.stringify(DEFAULT_AI_USE_PREFS.chat));set(d);const p=JSON.parse(JSON.stringify(CHAT_AI_PROMPT_DEFAULT));saveChatAiPrompt(p);setPromptState(p);};
  useEffect(()=>{if(!open)return;const h=e=>{if(e.key==="Escape")setOpen(false);};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[open]);
  return <>
    <button type="button" className="btn sm chat-ai-gear" title={t("AI settings")} aria-label={t("AI settings")} onClick={()=>setOpen(true)}>⚙</button>
    {open&&<div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)setOpen(false);}}>
      <div className="sset aiset" role="dialog" aria-modal="true" aria-labelledby="dlg-chat-ai-title">
        <div className="sset-head">
          <h3 id="dlg-chat-ai-title">{t("AI settings")} - {t("Chat")}</h3>
          <button className="btn sm" onClick={()=>setOpen(false)}>✕ {t("Close")}</button>
        </div>
        <div className="sset-body">
          <p className="aiset-intro">{t("Edit the prompt used to start chats, add extra instructions, and pick the model.")}</p>
          <section className="aiset-card">
            <div className="aiset-controls">
              <select aria-label={t("Provider")} value={cur.provider}
                onChange={e=>set({provider:e.target.value,model:""})}>
                <option value="claude">{t("Claude")}</option>
                <option value="codex">{t("Codex")}</option>
                <option value="gemini">{t("Gemini")}</option>
              </select>
              <select aria-label={t("Model")} value={cur.model}
                onChange={e=>set({model:e.target.value})}>
                {(AI_MODELS[cur.provider]||AI_MODELS.claude).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?" ("+m.hint+")":""}</option>)}
              </select>
            </div>
          </section>
          <div style={{marginTop:12}}>
            <label className="muted" style={{fontSize:12,display:"block",marginBottom:4}}>{t("Default prompt")}</label>
            <textarea dir="ltr" rows={7} style={{width:"100%",fontSize:13,lineHeight:1.5}}
              value={prompt.prompt} onChange={e=>setPrompt({prompt:e.target.value})}/>
          </div>
          <div style={{marginTop:12}}>
            <label className="muted" style={{fontSize:12,display:"block",marginBottom:4}}>{t("Extra instructions")}</label>
            <textarea dir="ltr" rows={4} style={{width:"100%",fontSize:13,lineHeight:1.5}}
              placeholder={t("Optional. Appended to the prompt, e.g. tone, format, language.")}
              value={prompt.instructions} onChange={e=>setPrompt({instructions:e.target.value})}/>
          </div>
          <label className="followup-pref" style={{marginTop:12,display:"flex",gap:8,alignItems:"flex-start",fontSize:13}}>
            <input type="checkbox" checked={followupOn}
              onChange={e=>{setFollowupOn(e.target.checked);setFollowupEnabled(e.target.checked);}}/>
            <span>{t("Suggest a follow-up question")}<br/>
              <span className="muted" style={{fontSize:12}}>{t("After Claude answers, a likely next question appears in the empty message box. Press Tab (or tap ↹) to use it.")}</span></span>
          </label>
        </div>
        <div className="sset-foot">
          <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{t("Saved automatically. Applies to the next run.")}</span>
          <button className="btn sm" onClick={resetAll}>{t("Reset to defaults")}</button>
        </div>
      </div>
    </div>}
  </>;
}

// Per-section AI options for the Plan tab (Goal / Roadmap / Notes). Browser-local,
// mirroring loadAiUsePrefs: each section stores its own editable prompt, extra
// instructions, provider and model. {name} / {context} are placeholders the
// generation call fills in later.
const PLAN_AI_PREFS_KEY="hub.planAiPrefs";
const PLAN_AI_DEFAULTS={
  goal:{title:"Goal",provider:"claude",model:"opus",instructions:"",
    prompt:'You are helping define the product goals for the project "{name}".\n\nContext:\n{context}\n\nWrite 3-6 concise, outcome-focused goals (measurable where possible). Return only the goals as plain text, one per line.'},
  roadmap:{title:"Roadmap",provider:"claude",model:"opus",instructions:"",
    prompt:'You are drafting a delivery roadmap for the project "{name}".\n\nContext:\n{context}\n\nWrite a short, phased roadmap (e.g. Now / Next / Later or by quarter). Return only the roadmap as plain text.'},
  notes:{title:"Notes",provider:"claude",model:"opus",instructions:"",
    prompt:'You are drafting a project note for "{name}".\n\nContext:\n{context}\n\nWrite a clear, useful note. Return only the note text.'},
};
function loadPlanAiPrefs(){
  const out=JSON.parse(JSON.stringify(PLAN_AI_DEFAULTS));
  try{const v=JSON.parse(localStorage.getItem(PLAN_AI_PREFS_KEY))||{};for(const k of Object.keys(out)){if(v[k]){Object.assign(out[k],v[k]);out[k].model=canonModel(out[k].provider,out[k].model);}}}catch(e){}
  return out;
}
function savePlanAiPrefs(p){try{localStorage.setItem(PLAN_AI_PREFS_KEY,JSON.stringify(p));}catch(e){}}
// Assembles the {context} placeholder value for a Plan-section generation: the
// project's descriptive fields plus whatever Goal/Roadmap already exist.
function planContext(p){
  if(!p)return "";
  const parts=[];
  if(p.desc)parts.push("Description: "+p.desc);
  if(p.stack)parts.push("Stack: "+p.stack);
  if(p.tags&&p.tags.length)parts.push("Tags: "+(Array.isArray(p.tags)?p.tags.join(", "):p.tags));
  if(String(p.goals||"").trim())parts.push("Current goals:\n"+p.goals);
  if(String(p.roadmap||"").trim())parts.push("Current roadmap:\n"+p.roadmap);
  return parts.join("\n\n");
}

// usePlanAi: the generate + settings-modal logic behind PlanAiOptions, split out
// so the Plan tab section headers can surface "Generate with AI" / "AI settings"
// as overflow-menu items instead of two more inline buttons. Returns
// {busy,generate,openSettings,modal}; the caller renders `modal` once.
function usePlanAi(section,{disabled,name,context,onApply}={}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  const[busy,setBusy]=useState(false);
  const[prefs,setPrefs]=useState(loadPlanAiPrefs);
  const cur=prefs[section]||PLAN_AI_DEFAULTS[section]||{};
  const generate=async()=>{
    if(busy||disabled)return;
    setBusy(true);
    try{
      let prompt=String(cur.prompt||"").replace(/\{name\}/g,String(name||"")).replace(/\{context\}/g,String(context||"").trim()||"(none provided)");
      if(cur.instructions&&cur.instructions.trim())prompt+="\n\nAdditional instructions:\n"+cur.instructions.trim();
      const r=await fetch("/api/ai/generate",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({provider:cur.provider,model:cur.model,prompt})});
      const d=await r.json().catch(()=>({}));
      if(!r.ok||!d.ok){alert((d&&d.error)||t("AI generation failed."));return;}
      const text=String(d.text||"").trim();
      if(!text){alert(t("AI returned no text."));return;}
      if(onApply)onApply(text);
    }catch(e){alert(t("AI generation failed.")+" "+((e&&e.message)||e));}
    finally{setBusy(false);}
  };
  const set=patch=>setPrefs(prev=>{const next={...prev,[section]:{...prev[section],...patch}};savePlanAiPrefs(next);return next;});
  const resetSection=()=>setPrefs(prev=>{const next={...prev,[section]:JSON.parse(JSON.stringify(PLAN_AI_DEFAULTS[section]))};savePlanAiPrefs(next);return next;});
  useEffect(()=>{if(!open)return;const h=e=>{if(e.key==="Escape")setOpen(false);};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[open]);
  const label=(PLAN_AI_DEFAULTS[section]||{}).title||section;
  const modal=open?<div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)setOpen(false);}}>
      <div className="sset aiset" role="dialog" aria-modal="true" aria-labelledby="dlg-field-ai-title">
        <div className="sset-head">
          <h3 id="dlg-field-ai-title">{t("AI settings")} - {t(label)}</h3>
          <button className="btn sm" onClick={()=>setOpen(false)}>✕ {t("Close")}</button>
        </div>
        <div className="sset-body">
          <p className="aiset-intro">{t("Edit the prompt used to generate this section, add extra instructions, and pick the model.")}</p>
          <section className="aiset-card">
            <div className="aiset-controls">
              <select aria-label={t("Provider")} value={cur.provider}
                onChange={e=>set({provider:e.target.value,model:defaultModelFor(e.target.value)})}>
                <option value="claude">{t("Claude")}</option>
                <option value="codex">{t("Codex")}</option>
                <option value="gemini">{t("Gemini")}</option>
              </select>
              <select aria-label={t("Model")} value={cur.model}
                onChange={e=>set({model:e.target.value})}>
                {(AI_MODELS[cur.provider]||AI_MODELS.claude).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?" ("+m.hint+")":""}</option>)}
              </select>
            </div>
          </section>
          <div style={{marginTop:12}}>
            <label className="muted" style={{fontSize:12,display:"block",marginBottom:4}}>{t("Default prompt")}</label>
            <textarea dir="ltr" rows={7} style={{width:"100%",fontSize:13,lineHeight:1.5}}
              value={cur.prompt} onChange={e=>set({prompt:e.target.value})}/>
          </div>
          <div style={{marginTop:12}}>
            <label className="muted" style={{fontSize:12,display:"block",marginBottom:4}}>{t("Extra instructions")}</label>
            <textarea dir="ltr" rows={4} style={{width:"100%",fontSize:13,lineHeight:1.5}}
              placeholder={t("Optional. Appended to the prompt, e.g. tone, format, language.")}
              value={cur.instructions} onChange={e=>set({instructions:e.target.value})}/>
          </div>
        </div>
        <div className="sset-foot">
          <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{t("Saved automatically. Applies to the next generation.")}</span>
          <button className="btn sm" onClick={resetSection}>{t("Reset to defaults")}</button>
        </div>
      </div>
    </div>:null;
  return {busy,generate,openSettings:()=>setOpen(true),modal};
}
// Renders the small ✨ (generate) + ⚙ (settings) button pair for one Plan
// section (still used inline by the Notes composer).
function PlanAiOptions({section,disabled,name,context,onApply}){
  const ai=usePlanAi(section,{disabled,name,context,onApply});
  return <>
    <button type="button" className="btn sm" disabled={disabled||ai.busy} title={t("Generate with AI")} aria-label={t("Generate with AI")} onClick={ai.generate}>{ai.busy?"…":"✨"}</button>
    <button type="button" className="btn sm" title={t("AI settings")} aria-label={t("AI settings")} onClick={ai.openSettings}>⚙</button>
    {ai.modal}
  </>;
}

// Check-frequency settings — how often the hub re-checks each project's health
// (Reachable / Port / PM2 / Container) and the host metrics (CPU / RAM / Disk /
// Uptime). Unlike AI settings (browser-local), these cadences are SERVER-owned:
// they persist to data/settings.json via /api/settings and drive an internal
// scheduler in hub-server that spawns the collector at the chosen intervals, so a
// change takes effect within a minute — no restart, no cron editing. Rendered as
// the ⏱ Frequency tab on the top-level Automations page (see GlobalAutomations).
const MONITOR_DEFAULTS={projectHealthMinutes:60,hostMetricsMinutes:5,hostGuardianMinutes:2};
function CheckFrequency(){
  const{lang}=useLang();
  const[health,setHealth]=useState(MONITOR_DEFAULTS.projectHealthMinutes);
  const[hostM,setHostM]=useState(MONITOR_DEFAULTS.hostMetricsMinutes);
  const[guard,setGuard]=useState(MONITOR_DEFAULTS.hostGuardianMinutes);
  const[loaded,setLoaded]=useState(false);
  const[saved,setSaved]=useState("");
  useEffect(()=>{
    let live=true;
    fetch("/api/settings").then(r=>r.json()).then(d=>{
      if(!live)return;
      const ci=(d&&d.checkIntervals)||{};
      if(ci.projectHealthMinutes)setHealth(ci.projectHealthMinutes);
      if(ci.hostMetricsMinutes)setHostM(ci.hostMetricsMinutes);
      if(ci.hostGuardianMinutes)setGuard(ci.hostGuardianMinutes);
      setLoaded(true);
    }).catch(()=>setLoaded(true));
    return()=>{live=false;};
  },[]);
  const clamp=v=>Math.min(1440,Math.max(1,Math.round(Number(v)||0)));
  function save(){
    setSaved("saving");
    const body={checkIntervals:{projectHealthMinutes:clamp(health),hostMetricsMinutes:clamp(hostM),hostGuardianMinutes:clamp(guard)}};
    fetch("/api/settings",{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)})
      .then(r=>r.json()).then(d=>{
        if(d&&d.ok&&d.checkIntervals){setHealth(d.checkIntervals.projectHealthMinutes);setHostM(d.checkIntervals.hostMetricsMinutes);if(d.checkIntervals.hostGuardianMinutes)setGuard(d.checkIntervals.hostGuardianMinutes);setSaved("ok");setTimeout(()=>setSaved(s=>s==="ok"?"":s),2200);}
        else setSaved("err");
      }).catch(()=>setSaved("err"));
  }
  const row=(title,desc,val,setVal)=>(
    <label className="sset-row">
      <span className="lbl"><span className="t">{title}</span><span className="d">{desc}</span></span>
      <span style={{display:"flex",alignItems:"center",gap:6}}>
        <input type="number" min={1} max={1440} value={val} aria-label={title+" interval in minutes"}
          onChange={e=>setVal(e.target.value)} onBlur={e=>setVal(clamp(e.target.value))}
          style={{width:72,textAlign:"end"}}/>
        <span className="muted" style={{fontSize:12.5}}>min</span>
      </span>
    </label>
  );
  return(
   <div className="sec">
    <h3 style={{margin:"0 0 2px"}}>⏱ {t("Check frequency")}</h3>
    <p className="muted" style={{fontSize:12.5,lineHeight:1.5,margin:"6px 0 10px"}}>
      {t("How often Heygents re-measures each check. Lower = fresher data, slightly more load. Range 1–1440 min.")}
    </p>
    {row(t("Project health"),t("Reachable · Port · PM2 · Container"),health,setHealth)}
    {row(t("Host metrics"),t("CPU · RAM · Disk · Uptime"),hostM,setHostM)}
    {row(t("Host guardian"),t("Resource-pressure watchdog (systemd timer)"),guard,setGuard)}
    <div style={{display:"flex",alignItems:"center",gap:10,marginTop:14}}>
      <span className="sp" style={{flex:1,fontSize:11.5,color:"var(--text-4)"}}>
        {saved==="ok"?t("Saved ✓ — applies within a minute."):saved==="err"?t("Couldn't save — try again."):!loaded?t("Loading…"):t("Applies within a minute of saving.")}
      </span>
      <button className="btn sm" onClick={()=>{setHealth(MONITOR_DEFAULTS.projectHealthMinutes);setHostM(MONITOR_DEFAULTS.hostMetricsMinutes);setGuard(MONITOR_DEFAULTS.hostGuardianMinutes);}}>{t("Reset to defaults")}</button>
      <button className="btn sm primary" disabled={saved==="saving"} onClick={save}>{saved==="saving"?t("Saving…"):t("Save")}</button>
    </div>
   </div>);
}

// Unified Notifications panel — merges the old in-app Sounds settings with the
// mobile push-notification settings. Two stores, one UI: the per-event SOUND
// choice / volume / repeat live client-side in localStorage (hub.soundPrefs,
// read live by playEventSound); the per-event PHONE PUSH opt-in lives server-side
// in settings.json's notifications.owner block (the server needs it to decide
// whether to send a Web Push). Custom library tones only play while a hub tab is
// open; a background push uses the phone's own OS sound — a platform limit, not a
// bug. Each event row therefore offers an in-app sound AND (where push-capable) a
// phone-push toggle. `needsApproval` also nags on a repeat interval until the
// prompt is answered or dismissed (see AskModal).
function NotificationsSettings({onClose}){
  const{lang}=useLang();
  const[snd,setSnd]=useState(loadSoundPrefs);          // client-side sound prefs
  const setS=(patch)=>setSnd(prev=>{const next={...prev,...patch};saveSoundPrefs(next);return next;});
  const[push,setPush]=useState({enabled:false,events:{}}); // server-side push prefs
  const[subscribed,setSubscribed]=useState(false);
  const[status,setStatus]=useState("");
  const supported=typeof navigator!=="undefined"&&"serviceWorker" in navigator&&"PushManager" in window;
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  useEffect(()=>{
    let live=true;
    fetch("/api/settings").then(r=>r.json()).then(d=>{
      if(!live)return;
      const p=d&&d.notifications&&d.notifications.owner;
      if(p){const e=p.events||{};
        // pushOnFailure supersedes the old failuresOnly filter; fall back to it so
        // an existing user's failure preference carries over on first open.
        const events={...e,pushOnFailure:e.pushOnFailure!=null?!!e.pushOnFailure:!!e.failuresOnly};
        setPush({enabled:!!p.enabled,events});}
    }).catch(()=>{});
    // Don't just trust getSubscription(): a subscription minted under a rotated
    // VAPID key still resolves here but can never receive a push, and the server
    // has already pruned its row. Validate against the live key — drop a stale
    // one (so Enable can re-subscribe cleanly) and re-register a valid one the
    // server has forgotten, which self-heals a wiped push-subscriptions.json.
    if(supported)(async()=>{
      try{
        const reg=await navigator.serviceWorker.ready;
        const sub=await reg.pushManager.getSubscription();
        if(!sub){if(live)setSubscribed(false);return;}
        const{key}=await fetch("/api/push/vapid-public-key").then(r=>r.json()).catch(()=>({}));
        if(!subKeyMatches(sub,key)){
          try{await sub.unsubscribe();}catch(e){}
          if(live){setSubscribed(false);setStatus("This device's subscription expired (server keys changed) — tap Enable to re-subscribe.");}
          return;
        }
        await fetch("/api/push/subscribe",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({subscription:sub})}).catch(()=>{});
        if(live)setSubscribed(true);
      }catch(e){if(live)setSubscribed(false);}
    })();
    return()=>{live=false;};
  },[]);
  function persistPush(next){
    setPush(next);
    fetch("/api/settings",{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({notifications:{owner:next}})}).catch(()=>{});
  }
  const setPushEvent=(k,v)=>persistPush({...push,events:{...push.events,[k]:v}});
  async function enable(){
    setStatus("");
    try{
      const perm=await Notification.requestPermission();
      if(perm!=="granted"){setStatus("Permission denied. Enable notifications for this site in your browser.");return;}
      const reg=await navigator.serviceWorker.ready;
      const{key}=await fetch("/api/push/vapid-public-key").then(r=>r.json());
      if(!key){setStatus("Server is missing VAPID keys — run scripts/gen-vapid.js.");return;}
      // Drop any subscription made under a previous VAPID key first: subscribing
      // with a different applicationServerKey while one exists throws
      // InvalidStateError, which used to make re-subscribing impossible after a
      // key rotation.
      const stale=await reg.pushManager.getSubscription();
      if(stale&&!subKeyMatches(stale,key)){try{await stale.unsubscribe();}catch(e){}}
      const sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:urlBase64ToUint8Array(key)});
      const r=await fetch("/api/push/subscribe",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({subscription:sub})}).then(x=>x.json());
      if(!r.ok){setStatus("Could not register this device.");return;}
      setSubscribed(true);
      persistPush({...push,enabled:true});
      setStatus("This device is now subscribed.");
    }catch(e){setStatus("Subscribe failed: "+String((e&&e.message)||e));}
  }
  async function disable(){
    try{
      const reg=await navigator.serviceWorker.ready;
      const sub=await reg.pushManager.getSubscription();
      if(sub){await fetch("/api/push/unsubscribe",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({endpoint:sub.endpoint})});await sub.unsubscribe();}
      setSubscribed(false);
      persistPush({...push,enabled:false});
      setStatus("This device was unsubscribed.");
    }catch(e){setStatus("Unsubscribe failed: "+String((e&&e.message)||e));}
  }
  async function sendTest(){
    const r=await fetch("/api/push/test",{method:"POST"}).then(x=>x.json()).catch(()=>({}));
    setStatus(r&&r.sent?"Test sent to "+r.sent+" device(s).":"No device received the test (subscribe first).");
  }
  const pushReady=supported&&subscribed&&push.enabled;
  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset" role="dialog" aria-modal="true" aria-labelledby="dlg-notif-title">
      <div className="sset-head">
        <h3 id="dlg-notif-title">{t("Notifications")}</h3>
        <button className="btn sm" style={{marginInlineStart:"auto"}} onClick={onClose}>✕ {t("Close")}</button>
      </div>
      <div className="sset-body">
        {/* Shared volume for every in-app sound */}
        <div className="sset-vol">
          <span className="vlab">🔉 Volume</span>
          <input type="range" min="0" max="1" step="0.05" value={snd.volume}
                 onChange={e=>setS({volume:+e.target.value})}/>
          <span className="vpct">{Math.round(snd.volume*100)}%</span>
        </div>
        {/* Per-event: in-app sound + optional phone push (+ repeat for approvals) */}
        {NOTIFICATION_EVENTS.map(ev=>{
          const on=snd[ev.key+"On"]!==false;
          const preview=()=>{const c=snd[ev.key];if(c===VOICE_SOUND)speakAnnouncement(ev.key,"Heygents",snd.volume);else playSound(c,snd.volume);};
          return <div className={"sset-row"+(on?"":" off")} key={ev.key}>
            <div className="sset-rowmain">
              <label className="sset-toggle" title={on?t("Turn this sound off"):t("Turn this sound on")}>
                <input type="checkbox" checked={on} onChange={e=>setS({[ev.key+"On"]:e.target.checked})}/>
              </label>
              <span className="lbl"><span className="t">{ev.title}</span><span className="d">{ev.desc}</span></span>
              <select value={snd[ev.key]} disabled={!on} onChange={e=>setS({[ev.key]:e.target.value})}>
                {SOUND_OPTIONS.map(k=><option key={k} value={k}>{soundLabel(k)}</option>)}
              </select>
              <button className="sset-play" title={t("Preview")} disabled={!on} onClick={preview} aria-label={t("Preview")}>▶</button>
            </div>
            {ev.push&&<label className="sset-sub" title={pushReady?t("Also send a push to your phone (OS sound) when Heygents is closed"):t("Subscribe this device below to enable phone push")}>
              <input type="checkbox" checked={!!push.events[ev.key]} disabled={!pushReady}
                     onChange={e=>setPushEvent(ev.key,e.target.checked)}/>
              <span>📱 {t("Also push to my phone")}</span>
            </label>}
            {ev.repeat&&<label className="sset-sub" title={t("Replay this sound on an interval until you answer or dismiss the prompt")}>
              <input type="checkbox" checked={snd.needsApprovalRepeat!==false} disabled={!on}
                     onChange={e=>setS({needsApprovalRepeat:e.target.checked})}/>
              <span>{t("Repeat every")}</span>
              <select value={snd.needsApprovalRepeatSecs||60} disabled={!on||snd.needsApprovalRepeat===false}
                      onChange={e=>setS({needsApprovalRepeatSecs:+e.target.value})} style={{marginInlineStart:4}}>
                {REPEAT_INTERVALS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
              <span>{t("until you respond")}</span>
            </label>}
          </div>;
        })}
        {/* Failures: always buzz in-app; optionally also push to the phone */}
        <div className="sset-row">
          <div className="sset-rowmain">
            <span className="lbl"><span className="t">{t("Failure")}</span><span className="d">{t("A run fails — always plays the fixed low buzz")}</span></span>
          </div>
          <label className="sset-sub" title={pushReady?t("Also push failures to your phone"):t("Subscribe this device below to enable phone push")}>
            <input type="checkbox" checked={!!push.events.pushOnFailure} disabled={!pushReady}
                   onChange={e=>setPushEvent("pushOnFailure",e.target.checked)}/>
            <span>📱 {t("Also push failures to my phone")}</span>
          </label>
        </div>
        {/* Device subscription for phone push */}
        <div className="sset-device" style={{marginTop:12,paddingTop:10,borderTop:"1px solid var(--line-2)"}}>
          {!supported
            ?<p className="muted" style={{fontSize:12.5,lineHeight:1.5,margin:0}}>{t("This browser can’t send phone push notifications; in-app sounds still work.")}</p>
            :<>
              <p className="muted" style={{fontSize:12.5,lineHeight:1.5,margin:"0 0 8px"}}>
                {t("Subscribe this device to also get phone push notifications when Heygents is closed.")}
              </p>
              {!subscribed
                ?<button type="button" className="btn sm primary" onClick={enable}>{t("Enable phone push on this device")}</button>
                :<button type="button" className="btn sm" onClick={disable}>{t("Disable on this device")}</button>}
              {subscribed&&<button type="button" className="btn sm" style={{marginInlineStart:8}} onClick={sendTest}>{t("Send test")}</button>}
            </>}
        </div>
      </div>
      <div className="sset-foot">
        <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{status||t("Applies immediately.")}</span>
        <button className="btn sm" onClick={()=>{const d={...DEFAULT_SOUND_PREFS};saveSoundPrefs(d);setSnd(d);}}>{t("Reset sounds")}</button>
      </div>
    </div>
  </div>;
}

// Shared repo picker list: fetches the connected account's repos once, filters by
// a search box, and lets the user select. `multi` toggles checkbox vs single-pick.
// Rows already linked to an existing project are marked and (in multi mode) skipped
// from selection. Calls onSelect(repo) per click; parent owns the selection set.
function GithubRepoList({linkedUrls,multi,selected,onToggle}){
  const{lang}=useLang();
  const[repos,setRepos]=useState(null);
  const[err,setErr]=useState("");
  const[q,setQ]=useState("");
  useEffect(()=>{
    fetch("/api/github/repos").then(r=>r.json()).then(d=>{
      if(d&&d.ok)setRepos(d.repos);else setErr((d&&d.error)||"Couldn't load repositories.");
    }).catch(e=>setErr(String(e)));
  },[]);
  if(err)return <div className="err" style={{padding:"10px 2px"}}>{err}</div>;
  if(!repos)return <div className="muted" style={{padding:"10px 2px"}}>{t("Loading repositories…")}</div>;
  const norm=u=>String(u||"").replace(/\.git$/,"").replace(/\/$/,"");
  const linked=new Set((linkedUrls||[]).map(norm));
  const ql=q.trim().toLowerCase();
  const shown=repos.filter(r=>!ql||r.fullName.toLowerCase().includes(ql)||(r.description||"").toLowerCase().includes(ql));
  return <div>
    <input className="gh-search" placeholder={"🔎 "+t("Filter repositories…")} value={q} onChange={e=>setQ(e.target.value)}/>
    <div className="gh-list">
      {shown.length===0&&<div className="muted" style={{padding:"12px"}}>{t("No matching repositories.")}</div>}
      {shown.map(r=>{
        const isLinked=linked.has(norm(r.htmlUrl));
        const sel=selected&&selected.has(r.fullName);
        return <label className={"gh-row"+(sel?" sel":"")} key={r.fullName}>
          <input type={multi?"checkbox":"radio"} name="ghrepo" checked={!!sel} disabled={multi&&isLinked}
                 onChange={()=>onToggle(r)}/>
          <span className="nm">
            <span className="t">{r.fullName}</span>
            {r.description&&<span className="d">{r.description}</span>}
          </span>
          {r.language&&<span className="lang">{r.language}</span>}
          {r.private&&<span className="tag">private</span>}
          {isLinked&&<span className="gh-linked">✓ linked</span>}
        </label>;
      })}
    </div>
  </div>;
}

// Settings → GitHub: the hub-level connection panel. Shows the connected account (or a
// Sign-in button), lets you disconnect, and — when connected — pick repos to create
// projects from. The PIN still gates the app; this is purely an integration.
function GithubPanel({projects,onClose,onImported}){
  const{lang}=useLang();
  const[st,setSt]=useState(null); // {configured,connected,login,avatar}
  const[sel,setSel]=useState(()=>new Set());
  const[busy,setBusy]=useState(false);
  const[msg,setMsg]=useState(null); // {ok,text}
  const refresh=()=>fetch("/api/github/status").then(r=>r.json()).then(setSt).catch(()=>setSt({configured:false,connected:false}));
  useEffect(()=>{refresh();},[]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  // Surface any message the OAuth callback passed back via the #github=… hash.
  useEffect(()=>{
    const m=(location.hash||"").match(/^#github=(.+)$/);
    if(m){setMsg({ok:false,text:decodeURIComponent(m[1])});try{history.replaceState(null,"","#github");}catch(e){}}
  },[]);
  const toggle=(r)=>setSel(prev=>{const n=new Set(prev);n.has(r.fullName)?n.delete(r.fullName):n.add(r.fullName);return n;});
  const linkedUrls=(projects||[]).map(p=>p.repo).filter(Boolean);
  function connect(switchAccount){
    // On Heygents the OAuth start is a gated JSON endpoint (a plain <a href>
    // navigation carries no auth token, so the server couldn't tell which tenant
    // it is). Fetch the authorize URL — its signed state encodes the tenant —
    // then send the whole page to GitHub.
    // switchAccount=true bounces through github.com/login first so a user with an
    // active GitHub session can pick a different account instead of being silently
    // reused (GitHub's authorize endpoint has no account-picker of its own).
    setBusy(true);setMsg(null);
    fetch("/api/github/login"+(switchAccount?"?switch_account=true":"")).then(r=>r.json()).then(d=>{
      if(d&&d.url){location.href=d.url;}
      else{setMsg({ok:false,text:(d&&d.error)||"Couldn't start GitHub sign-in."});setBusy(false);}
    }).catch(()=>{setMsg({ok:false,text:"Couldn't reach the server."});setBusy(false);});
  }
  function disconnect(){
    setBusy(true);
    fetch("/api/github/disconnect",{method:"POST"}).then(()=>{setSel(new Set());refresh();}).finally(()=>setBusy(false));
  }
  function importSelected(){
    // Resolve the selected fullNames back to repo objects from the list via a fetch.
    setBusy(true);setMsg(null);
    fetch("/api/github/repos").then(r=>r.json()).then(d=>{
      const chosen=(d&&d.repos||[]).filter(r=>sel.has(r.fullName));
      return fetch("/api/github/import",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({repos:chosen})});
    }).then(r=>r.json()).then(d=>{
      if(d&&d.ok){
        onImported&&onImported(d.projects);
        setSel(new Set());
        setMsg({ok:true,text:d.added.length?`Created ${d.added.length} project${d.added.length>1?"s":""}.${d.cloning?" Cloning onto your agent now — the working folder fills in when it's done.":" Install the agent to clone them onto your machine."}`:"Nothing new — those repos are already linked."});
      } else setMsg({ok:false,text:(d&&d.error)||"Import failed."});
    }).catch(e=>setMsg({ok:false,text:String(e)})).finally(()=>setBusy(false));
  }
  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset" role="dialog" aria-modal="true" aria-labelledby="dlg-github-title">
      <div className="sset-head"><h3 id="dlg-github-title">{t("GitHub")}</h3><span style={{flex:1}}/><button className="btn sm" onClick={onClose}>✕ {t("Close")}</button></div>
      <div className="sset-body">
        {!st?<div className="muted" style={{padding:"14px 0"}}>{t("Loading…")}</div>
         :!st.configured?<div style={{padding:"14px 0",fontSize:13,color:"var(--text-2)",lineHeight:1.6}}>
            {t("GitHub isn't set up on the server yet. Register an OAuth App and set")} <code>{t("HUB_GITHUB_CLIENT_ID")}</code> / <code>{t("HUB_GITHUB_CLIENT_SECRET")}</code> {t("in")} <code>.env</code>, {t("then restart Heygents. See README → GitHub.")}
          </div>
         :!st.connected?<div>
            <div style={{padding:"14px 0",fontSize:13,color:"var(--text-2)",lineHeight:1.6}}>
              {t("Connect your GitHub account to pick repositories and turn them into projects, or link an existing project to its repo.")}
            </div>
            <button className="btn primary" onClick={()=>connect(false)} disabled={busy}>{busy?"…":t("Sign in with GitHub")}</button>
            <div style={{marginTop:8}}><a href="#" onClick={e=>{e.preventDefault();if(!busy)connect(true);}} style={{fontSize:12,color:"var(--text-2)"}}>{t("Sign in with a different GitHub account")}</a></div>
          </div>
         :<div>
            <div className="gh-acct">
              {st.avatar&&<img src={st.avatar} alt=""/>}
              <span className="who"><span className="l">@{st.login}</span><span className="s">{t("Connected · pick repos to create projects")}</span></span>
              <button className="btn sm" onClick={disconnect} disabled={busy}>{t("Disconnect")}</button>
            </div>
            <GithubRepoList linkedUrls={linkedUrls} multi={true} selected={sel} onToggle={toggle}/>
          </div>}
        {msg&&<div className={msg.ok?"ok":"err"} style={{marginTop:8}}>{msg.text}</div>}
      </div>
      {st&&st.connected&&<div className="sset-foot">
        <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{sel.size?`${sel.size} ${t("selected")}`:t("Select repositories to import")}</span>
        <button className="btn primary" onClick={importSelected} disabled={busy||!sel.size}>{busy?"…":t("Create projects from selected")}</button>
      </div>}
    </div>
  </div>;
}

// HUB_CLOUD: git management for a connected project. Every operation runs on
// the tenant's own agent (the cloud server never touches a customer machine),
// so this component is a thin driver: it reads a status snapshot, fires one of
// the four run-shaped ops, follows that run's output live, and re-reads status
// when it ends.
//
//   Clone - put the repo on the machine (recovers a failed import clone, and is
//           the only way to get a working copy for a hand-linked project)
//   Sync  - commit + pull + push the SAME branch: "make my clone and GitHub agree"
//   Pull  - fetch and merge origin into the checkout (--autostash, so pending
//           edits survive)
//   Push  - commit onto a fresh hub/… branch and push it as a reviewable PR;
//           never writes to the default branch
const GIT_OPS={
  clone:{url:"/api/github/clone",label:"Clone",icon:"⤓",title:"Clone this repo onto your agent's machine"},
  sync:{url:"/api/github/sync",label:"Sync",icon:"⇅",title:"Commit pending changes, pull origin, then push the same branch back"},
  pull:{url:"/api/github/pull",label:"Pull",icon:"⬇",title:"Fetch and merge the latest commits from GitHub into this project's clone"},
  push:{url:"/api/github/push",label:"Push",icon:"⬆",title:"Commit the agent's changes to a new branch and push to GitHub as a pull request"},
};
function GitPanel({p,onRefetch}){
  const{lang}=useLang();
  const slug=p.slug;
  const[status,setStatus]=useState(null);
  const[statusErr,setStatusErr]=useState("");
  const[loading,setLoading]=useState(false);
  const[busy,setBusy]=useState("");
  const[note,setNote]=useState(null);
  const[run,setRun]=useState(null);   // {runId,op,lines,done,exit}
  const[showLog,setShowLog]=useState(false);
  const cloned=!!p.dir;

  // fetchRemote=true asks the agent to `git fetch` first, so "behind" reflects
  // GitHub right now. Costs a network round trip - only on explicit refresh and
  // after an op finishes, never on every render.
  const loadStatus=useCallback((fetchRemote)=>{
    if(!cloned)return;
    setLoading(true);setStatusErr("");
    fetch("/api/git/status?slug="+encodeURIComponent(slug)+(fetchRemote?"&fetch=1":""))
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(ok&&d&&d.ok)setStatus(d.status);
        else{setStatus(null);setStatusErr((d&&d.error)||t("Couldn't read git status."));}
      })
      .catch(()=>{setStatus(null);setStatusErr(t("Couldn't reach the server."));})
      .finally(()=>setLoading(false));
  },[slug,cloned]);

  useEffect(()=>{setStatus(null);setStatusErr("");setNote(null);setRun(null);loadStatus(false);},[slug,cloned,loadStatus]);

  // Follow a git run to completion. Git ops are short, so a 1.2s poll of the
  // run buffer is plenty and avoids holding a stream open per project card.
  useEffect(()=>{
    if(!run||!run.runId||run.done)return;
    let alive=true;
    const id=setInterval(()=>{
      fetch("/api/run/"+encodeURIComponent(run.runId)).then(r=>r.json()).then(d=>{
        if(!alive||!d||!d.run)return;
        const r2=d.run;
        setRun(prev=>prev&&prev.runId===r2.runId?{...prev,lines:r2.lines||[],done:!!r2.done,exit:r2.exit}:prev);
        if(r2.done){
          setBusy("");
          if(r2.exit===0)setNote(n=>n&&n.url?n:{ok:true,text:t("{op} finished.",{op:GIT_OPS[run.op]?GIT_OPS[run.op].label:"Git"})});
          else{setNote({ok:false,text:t("{op} failed - see the log.",{op:GIT_OPS[run.op]?GIT_OPS[run.op].label:"Git"})});setShowLog(true);}
          // A clone writes the working dir onto the project - pull the record
          // back so the panel switches out of its "not cloned yet" state.
          if(run.op==="clone"&&onRefetch)onRefetch();
          else loadStatus(true);
        }
      }).catch(()=>{});
    },1200);
    return()=>{alive=false;clearInterval(id);};
  },[run,loadStatus,onRefetch]);

  function doOp(op){
    const cfg=GIT_OPS[op];if(!cfg||busy)return;
    setBusy(op);setNote(null);setRun(null);setShowLog(false);
    fetch(cfg.url,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug})})
      .then(r=>r.json()).then(d=>{
        if(d&&d.ok){
          setRun({runId:d.runId,op,lines:[],done:false,exit:null});
          if(d.compareUrl)setNote({ok:true,url:d.compareUrl,branch:d.branch});
          else setNote({ok:true,text:t("{op} running…",{op:cfg.label})});
        }else{
          setBusy("");
          setNote({ok:false,text:(d&&d.error)||t("{op} failed.",{op:cfg.label})});
        }
      }).catch(()=>{setBusy("");setNote({ok:false,text:t("Couldn't reach the server.")});});
  }

  const s=status;
  const running=!!busy;
  return <div className="git-panel">
    <div className="infra-h" style={{margin:"0 0 8px"}}>{t("Git")}
      {cloned&&<button className="btn xs" style={{marginInlineStart:"auto"}} disabled={loading||running}
        title={t("Fetch origin and re-read this clone's status")} onClick={()=>loadStatus(true)}>{loading?"⟳ …":"⟳ "+t("Refresh")}</button>}
    </div>

    {!cloned?<>
      <div className="muted" style={{fontSize:12.5,marginBottom:8}}>
        {p.cloneState==="failed"?t("The last clone failed - check that the agent is online, then try again.")
          :p.cloneState==="pending"?t("Cloning onto your agent…")
          :t("This project isn't on your machine yet. Clone it to run tasks, browse files, and push work back.")}
      </div>
      <div className="git-actions">
        <button className="btn xs primary" disabled={running} title={t(GIT_OPS.clone.title)} onClick={()=>doOp("clone")}>
          {busy==="clone"?"…":GIT_OPS.clone.icon+" "+t("Clone")}</button>
      </div>
    </>:<>
      <div className="irow"><span className="ik">{t("Branch")}</span><span className="iv mono">
        {s?(s.detached?t("detached HEAD"):(s.branch||"-")):(statusErr?"-":"…")}
        {s&&s.upstream?<span className="muted"> → {s.upstream}</span>:s&&!s.detached?<span className="muted"> ({t("no upstream")})</span>:null}
      </span></div>
      <div className="irow"><span className="ik">{t("Vs GitHub")}</span><span className="iv">
        {!s?(statusErr?"-":"…"):(s.ahead||s.behind
          ?<>{s.ahead?<span className="git-badge ahead" title={t("commits here that GitHub doesn't have")}>↑{s.ahead}</span>:null}
            {s.behind?<span className="git-badge behind" title={t("commits on GitHub that this clone doesn't have")}>↓{s.behind}</span>:null}</>
          :<span className="ok">{t("in sync")}</span>)}
      </span></div>
      <div className="irow"><span className="ik">{t("Working tree")}</span><span className="iv"
        title={s&&s.files&&s.files.length?s.files.map(f=>f.x+f.y+" "+f.path).join("\n"):""}>
        {!s?(statusErr?"-":"…"):(s.dirty&&s.dirty.total
          ?<span className="git-badge dirty">{t({one:"1 change",other:"{n} changes"},{n:s.dirty.total})}</span>
          :<span className="ok">{t("clean")}</span>)}
      </span></div>
      {s&&s.lastCommit&&<div className="irow"><span className="ik">{t("Last commit")}</span>
        <span className="iv one-line" title={s.lastCommit.subject+" - "+s.lastCommit.author}>
          <span className="mono">{s.lastCommit.hash}</span> {s.lastCommit.subject}</span></div>}
      {statusErr&&<div className="err" style={{fontSize:11.5,marginTop:6}}>{statusErr}</div>}

      <div className="git-actions">
        <button className="btn xs primary" disabled={running} title={t(GIT_OPS.sync.title)} onClick={()=>doOp("sync")}>
          {busy==="sync"?"…":GIT_OPS.sync.icon+" "+t("Sync")}</button>
        <button className="btn xs" disabled={running} title={t(GIT_OPS.pull.title)} onClick={()=>doOp("pull")}>
          {busy==="pull"?"…":GIT_OPS.pull.icon+" "+t("Pull")}</button>
        <button className="btn xs" disabled={running} title={t(GIT_OPS.push.title)} onClick={()=>doOp("push")}>
          {busy==="push"?"…":GIT_OPS.push.icon+" "+t("Push")}</button>
        <button className="btn xs" disabled={running} title={t("Clone again onto the agent (an existing folder is adopted, never overwritten)")} onClick={()=>doOp("clone")}>
          {busy==="clone"?"…":GIT_OPS.clone.icon+" "+t("Re-clone")}</button>
      </div>
    </>}

    {note&&<div className="git-note">
      {note.ok
        ?(note.url
          ?<a className="ok" href={note.url} target="_blank">{t("open PR")} ({note.branch}) ↗</a>
          :<span className="ok">{note.text}</span>)
        :<span className="err">{note.text}</span>}
      {run&&(run.lines&&run.lines.length||run.done)?
        <button className="btn xs" onClick={()=>setShowLog(v=>!v)}>{showLog?t("Hide log"):t("Show log")}</button>:null}
    </div>}
    {showLog&&run&&<pre className="git-log">{(run.lines||[]).join("\n")||t("waiting for the agent…")}</pre>}
  </div>;
}

// Single-repo picker used from the project Editor to link an existing project to a
// repo (fills Repo URL + branch). If GitHub isn't connected, points to Settings → GitHub.
function GithubRepoDialog({onClose,onPick}){
  const{lang}=useLang();
  const[st,setSt]=useState(null);
  const[pick,setPick]=useState(null); // the selected repo obj
  const[busy,setBusy]=useState(false);
  const[msg,setMsg]=useState(null);
  useEffect(()=>{fetch("/api/github/status").then(r=>r.json()).then(setSt).catch(()=>setSt({connected:false}));},[]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  // Not connected? Start OAuth right here instead of sending the user off to
  // Settings → GitHub. Same gated-JSON login flow the GithubPanel uses.
  function connect(switchAccount){
    setBusy(true);setMsg(null);
    fetch("/api/github/login"+(switchAccount?"?switch_account=true":"")).then(r=>r.json()).then(d=>{
      if(d&&d.url){location.href=d.url;}
      else{setMsg((d&&d.error)||"Couldn't start GitHub sign-in.");setBusy(false);}
    }).catch(()=>{setMsg("Couldn't reach the server.");setBusy(false);});
  }
  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset" role="dialog" aria-modal="true" aria-labelledby="dlg-link-repo-title">
      <div className="sset-head"><h3 id="dlg-link-repo-title">{t("Link a GitHub repo")}</h3><span style={{flex:1}}/><button className="btn sm" onClick={onClose}>✕ {t("Close")}</button></div>
      <div className="sset-body">
        {!st?<div className="muted" style={{padding:"14px 0"}}>{t("Loading…")}</div>
         :!st.connected?(st.configured===false?<div style={{padding:"14px 0",fontSize:13,color:"var(--text-2)",lineHeight:1.6}}>
            {t("GitHub isn't set up on the server yet. Register an OAuth App and set")} <code>{t("HUB_GITHUB_CLIENT_ID")}</code> / <code>{t("HUB_GITHUB_CLIENT_SECRET")}</code> {t("in")} <code>.env</code>, {t("then restart Heygents. See README → GitHub.")}
          </div>:<div style={{padding:"14px 0",fontSize:13,color:"var(--text-2)",lineHeight:1.6}}>
            {t("Connect your GitHub account to pick a repository, or link this project to its repo.")}
            <div style={{marginTop:12}}><button className="btn primary" onClick={()=>connect(false)} disabled={busy}>{busy?"…":t("Sign in with GitHub")}</button></div>
            <div style={{marginTop:8}}><a href="#" onClick={e=>{e.preventDefault();if(!busy)connect(true);}} style={{fontSize:12,color:"var(--text-2)"}}>{t("Sign in with a different GitHub account")}</a></div>
            {msg&&<div style={{marginTop:8,fontSize:12,color:"var(--danger)"}}>{msg}</div>}
          </div>)
         :<GithubRepoList multi={false} selected={pick?new Set([pick.fullName]):new Set()} onToggle={r=>setPick(r)}/>}
      </div>
      {st&&st.connected&&<div className="sset-foot">
        <span className="sp" style={{fontSize:11.5,color:"var(--text-4)"}}>{pick?pick.fullName:t("Pick a repository")}</span>
        <button className="btn primary" disabled={!pick} onClick={()=>{onPick(pick);onClose();}}>{t("Use this repo")}</button>
      </div>}
    </div>
  </div>;
}

// Right-click on a sidebar project row: a small fixed-position menu that carries
// the same edit/hide/delete tools each row used to expose through a cluttered
// icon row. Fixed positioning (not absolute) keeps it out from under the sidebar
// list's own overflow:hidden/auto ancestors, so it renders wherever the click
// landed instead of being clipped to the rail.
function ProjectContextMenu({project,x,y,onClose,onEdit,onToggleHidden,onDelete}){
  const{lang}=useLang();
  const ref=useRef(null);
  const[pos,setPos]=useState({left:x,top:y,ready:false});
  useEffect(()=>{
    const el=ref.current;if(!el)return;
    const r=el.getBoundingClientRect();const pad=8;
    setPos({
      left:Math.max(pad,Math.min(x,window.innerWidth-r.width-pad)),
      top:Math.max(pad,Math.min(y,window.innerHeight-r.height-pad)),
      ready:true,
    });
  },[x,y]);
  useEffect(()=>{
    const onKey=e=>{if(e.key==="Escape")onClose();};
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[onClose]);
  useMenuFocus(true,ref,null);
  // Render at document level.  The sidebar is scrollable and sits inside several
  // layout/stacking contexts; keeping a fixed menu inside it made its actions
  // intermittently sit behind the workspace, so a visible "Edit project" could
  // fail to receive the click.
  return ReactDOM.createPortal(<>
    <div className="pop-back" onMouseDown={onClose} onContextMenu={e=>{e.preventDefault();onClose();}}/>
    <div ref={ref} className="ctx-menu" role="menu" aria-label={t("Project actions —")+" "+(project.nameHe||project.slug)}
      style={{left:pos.left,top:pos.top,visibility:pos.ready?"visible":"hidden"}}>
      <button type="button" className="menu-item" role="menuitem" onClick={onEdit}><Icon name="edit"/>{t("Edit project")}</button>
      <button type="button" className="menu-item" role="menuitem" onClick={onToggleHidden}>
        <Icon name={project.hidden?"eye":"eyeOff"}/>{project.hidden?t("Unhide project"):t("Hide project")}</button>
      <div className="nav-menu-sep"/>
      <button type="button" className="menu-item danger" role="menuitem" onClick={onDelete}><Icon name="trash"/>{t("Delete project")}</button>
    </div>
  </>,document.body);
}

// True on phones/tablets (coarse pointer or touch). Used to skip autoFocus so
// tapping the project switcher doesn't yank up the on-screen keyboard.
const isTouchDevice=()=>{try{return window.matchMedia("(pointer:coarse)").matches||"ontouchstart"in window;}catch(e){return false;}};

// Header project switcher: always shows the active project (name + health dot) and
// opens a searchable dropdown to jump to any project — so you can see and change
// what you're working on from any screen, without leaving the chat.
// Per-project conversation status indicators, shown on the right of a project
// row: running (orange), completed (green), failed (red). Only non-zero statuses
// render, so a project sits between one and three chips wide — and a project
// with no conversations shows nothing at all.
const CHAT_STATUSES=[
  {k:"running",cls:"run", label:"running"},
  {k:"done",   cls:"done",label:"completed"},
  {k:"failed", cls:"fail",label:"failed"},
];
function chatStatusCounts(chatCounts,slug){
  const c=chatCounts||{};
  return{running:((c.running||{})[slug])||0,done:((c.unseen||{})[slug])||0,
         failed:((c.failed||{})[slug])||0};
}
function ChatStatusDots({counts,className}){
  const shown=CHAT_STATUSES.filter(s=>counts[s.k]>0);
  if(!shown.length)return null;
  return<span className={"cstat"+(className?" "+className:"")}>
    {shown.map(s=><span key={s.k} className={"cstat-n "+s.cls}
      title={counts[s.k]+" "+s.label+" conversation"+(counts[s.k]===1?"":"s")}>{counts[s.k]}</span>)}
  </span>;
}

function ProjectSwitcher({projects,sel,st,openCounts,totalCounts,chatCounts,onPick,onCtx,onReorder,up}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  const[q,setQ]=useState("");
  const[showHidden,setShowHidden]=useState(false);
  const[dragSlug,setDragSlug]=useState(null); // project being dragged
  const[overSlug,setOverSlug]=useState(null); // drop target currently hovered
  const ref=useRef(null);
  useEffect(()=>{
    if(!open)return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    const k=e=>{if(e.key==="Escape")setOpen(false);};
    document.addEventListener("mousedown",h);document.addEventListener("keydown",k);
    return()=>{document.removeEventListener("mousedown",h);document.removeEventListener("keydown",k);};
  },[open]);
  const cur=projects.find(x=>x.slug===sel);
  const qq=q.trim().toLowerCase();
  const hiddenCount=projects.filter(x=>x.hidden).length;
  const list=projects.filter(x=>showHidden||!x.hidden).filter(x=>!qq||(x.nameHe||"").toLowerCase().includes(qq)||(x.slug||"").toLowerCase().includes(qq));
  const curH=cur?health(cur.slug,st):null;
  return(
   <div className={"pswitch"+(up?" pswitch-up":"")} ref={ref}>
     <button className="pswitch-btn" title={t("Switch project")} aria-expanded={open} onClick={()=>{setQ("");setOpen(o=>!o);}}>
       {cur&&<span className="ps-dot" style={{background:curH.c}} title={"Health: "+curH.t}/>}
       <span className="ps-nm" dir={dirOf(cur?cur.nameHe:t("Select a project"))}>{cur?cur.nameHe:t("Select a project")}</span>
       {cur&&<ChatStatusDots counts={chatStatusCounts(chatCounts,cur.slug)}/>}
       <span className="ps-cv">{up?"▴":"▾"}</span>
     </button>
     {open&&<div className="pswitch-menu">
       <input autoFocus={!isTouchDevice()} className="pswitch-search" placeholder={t("Search projects…")} value={q}
         onChange={e=>setQ(e.target.value)}
         onKeyDown={e=>{if(e.key==="Enter"&&list[0]){onPick(list[0].slug);setOpen(false);setQ("");}}}/>
       {hiddenCount>0&&<div className="pswitch-filters">
         <button className={"ftab"+(showHidden?" on":"")} title={t("Hidden")} onClick={()=>setShowHidden(v=>!v)}>
           <Icon name="eyeOff" className="ftab-ic" size={14}/>{t("Hidden")}<span className="fcount">{hiddenCount}</span></button>
       </div>}
       <div className="pswitch-list">
         {list.length===0&&<div className="pswitch-empty">{t("No matching projects.")}</div>}
         {list.map(x=>{const hh=health(x.slug,st);const cc=chatStatusCounts(chatCounts,x.slug);
           // Drag-reorder is only offered on the unfiltered list (a filtered/searched
           // view has no meaningful "position" to drop into) and never on touch.
           const canDrag=!!onReorder&&!qq&&!isTouchDevice();
           return(
           <button key={x.slug} className={"pswitch-item"+(x.slug===sel?" active":"")+(x.hidden?" pi-hidden":"")+(dragSlug===x.slug?" pi-dragging":"")+(overSlug===x.slug&&dragSlug&&dragSlug!==x.slug?" pi-drop":"")}
             draggable={canDrag}
             onDragStart={canDrag?e=>{setDragSlug(x.slug);e.dataTransfer.effectAllowed="move";try{e.dataTransfer.setData("text/plain",x.slug);}catch(_){}}:undefined}
             onDragOver={canDrag?e=>{e.preventDefault();e.dataTransfer.dropEffect="move";if(overSlug!==x.slug)setOverSlug(x.slug);}:undefined}
             onDragEnd={canDrag?()=>{setDragSlug(null);setOverSlug(null);}:undefined}
             onDrop={canDrag?e=>{e.preventDefault();if(dragSlug&&dragSlug!==x.slug)onReorder(dragSlug,x.slug);setDragSlug(null);setOverSlug(null);}:undefined}
             onClick={()=>{onPick(x.slug);setOpen(false);setQ("");}}
             onContextMenu={e=>{if(!onCtx)return;e.preventDefault();e.stopPropagation();onCtx(x.slug,e.clientX,e.clientY);}}>
             {canDrag&&<span className="pi-grip" aria-hidden="true" title={t("Drag to reorder")}>⋮⋮</span>}
             <span className="pi-dot" style={{background:hh.c}} title={"Health: "+hh.t}/>
             <span className="pi-nm" dir={dirOf(x.nameHe)}>{x.nameHe}</span>
             {x.hidden&&<Icon name="eyeOff" className="pi-hidden-ic" size={12}/>}
             <ChatStatusDots counts={cc}/>
             {x.slug===sel&&<span className="pi-cur">✓</span>}
           </button>);})}
       </div>
     </div>}
   </div>);
}

// Row state for a chat-history row. The live overlays beat the stored flags:
// `doneIds` ("this run just ended") wins over everything, and `liveIds` ("this
// tab is running it right now") turns a row orange before any refetch lands.
// A chat's row state from the server snapshot plus the two same-browser overlays.
// `doneIds` entries mean "a run of this chat ENDED here": "done"/"failed" also
// carry the not-seen-yet colour, "seen" is the same fact after the chat has been
// looked at. Any of them outranks the fetched `running` flag — the server derives
// that from its live-run map, which keeps claiming a chat is running until the
// agent reports the exit (and forever if it never does), so a chat that finished
// in front of us must never be dragged back to orange by a later poll.
function rchatState(s,liveIds,doneIds){
  const fresh=(doneIds&&doneIds[s.id])||"";
  if((s.running||!!(liveIds&&liveIds[s.id]))&&!fresh)return"running";
  if(fresh==="failed"||(!fresh&&s.failedUnseen))return"failed";
  if(fresh==="done"||(!fresh&&s.doneUnseen))return"done";
  return"";
}
// Same overlay for Home's Last Chats, applied every time the 15s poll REPLACES
// the list — patching the rows once when the run ends is not enough, the next
// snapshot would bring the stale "running" straight back.
function overlayRecentChats(chats,bySlug){
  if(!Array.isArray(chats))return[];
  return chats.map(x=>{
    const kind=x&&x.slug&&((bySlug[x.slug]||{})[x.id]);
    if(!kind)return x;
    return{...x,running:false,doneUnseen:kind==="done",failedUnseen:kind==="failed"};
  });
}
// Remember when each row was first seen running, and forget a run the moment it
// ends. `started` is mutated in place — it lives in a ref across renders.
function stampRunStarts(rows,started,now){
  const live={};
  for(const r of rows)if(r.state==="running"){live[r.s.id]=true;if(!started[r.s.id])started[r.s.id]=now;}
  for(const id of Object.keys(started))if(!live[id])delete started[id];
  return started;
}
// Order the rail's rows: running chats lead, most recently started first — a
// chat that goes back to work carries a stale updatedAt until the run writes
// again, so recency alone would leave it buried. The rest stay newest-first.
function orderChatRows(rows,started){
  return rows.slice().sort((a,b)=>{
    const ar=a.state==="running"?1:0,br=b.state==="running"?1:0;
    if(ar!==br)return br-ar;
    if(ar)return((started&&started[b.s.id])||b.s.updatedAt||0)-((started&&started[a.s.id])||a.s.updatedAt||0);
    return(b.s.updatedAt||0)-(a.s.updatedAt||0);
  });
}
// Coalescing window for the rail's refetches: the signals a run start produces
// (counts SSE, the local live-run overlay) land within milliseconds of each
// other, so they collapse into one request instead of a burst.
const RCHAT_MIN_GAP=1200;
// Slow catch-up poll for what changes with no status transition at all — a new
// title, a newer timestamp on a chat that was already running.
const RCHAT_POLL=8000;

// Desktop sidebar chat history for the currently selected project. Fetches the
// project's saved chats (light — metadata only) and lists running ones first,
// then newest-first; clicking a row jumps the dock to that conversation.
function SidebarChats({slug,onOpen,sig,liveIds,doneIds}){
  const{lang}=useLang();
  const[sessions,setSessions]=useState([]);
  const[loaded,setLoaded]=useState(false);
  const[ctx,setCtx]=useState(null); // {id,title,x,y} right-click menu on a chat row
  const gen=useRef(0);              // fetch generation — a stale reply is dropped
  const lastAt=useRef(0);           // when the last fetch was issued (throttle clock)
  const timer=useRef(null);         // pending coalesced refetch
  const lastSlug=useRef(null);      // so a project switch doesn't double-fetch
  const runStart=useRef({});        // chatId → when the row was first seen running
  function delChat(id){
    setSessions(prev=>prev.filter(s=>s.id!==id));
    fetch("/api/claude/history",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug,sessions:[],deletedIds:[id]})}).catch(()=>{});
  }
  // Single fetch path for every trigger. Order is decided at render time (below),
  // not here, so a status flip re-orders the list without waiting for a response.
  const load=useCallback(()=>{
    if(!slug)return;
    const my=++gen.current;lastAt.current=Date.now();
    fetch("/api/claude/history?slug="+encodeURIComponent(slug)+"&light=1")
      .then(r=>r.json()).then(d=>{
        if(my!==gen.current)return;
        setSessions(((d&&d.sessions)||[]).filter(s=>s&&s.id));
      }).catch(()=>{if(my===gen.current)setSessions([]);})
      .finally(()=>{if(my===gen.current)setLoaded(true);});
  },[slug]);
  const schedule=useCallback(()=>{
    if(timer.current)return;
    const wait=Math.max(0,RCHAT_MIN_GAP-(Date.now()-lastAt.current));
    timer.current=setTimeout(()=>{timer.current=null;load();},wait);
  },[load]);
  useEffect(()=>{
    if(!slug){setSessions([]);setLoaded(true);return;}
    setSessions([]);setLoaded(false);load();
    return()=>{gen.current++;if(timer.current){clearTimeout(timer.current);timer.current=null;}};
  },[slug,load]);
  // Any status signal refreshes the rail within ~1s: `sig` carries the per-project
  // counts (SSE-driven locally), and the live/done id sets flip the instant a run
  // starts or ends in this browser — no waiting on the slow catch-up poll.
  const liveKey=liveIds?Object.keys(liveIds).sort().join(","):"";
  const doneKey=doneIds?Object.keys(doneIds).sort().join(","):"";
  useEffect(()=>{
    if(lastSlug.current!==slug){lastSlug.current=slug;return;} // the effect above just fetched
    schedule();
  },[slug,sig,liveKey,doneKey,schedule]);
  // Catch-up poll, paused while the tab is hidden, plus an immediate refresh on
  // return — coming back to the window never shows a minutes-old list.
  useEffect(()=>{
    if(!slug)return;
    const i=setInterval(()=>{if(!document.hidden)schedule();},RCHAT_POLL);
    const vis=()=>{if(!document.hidden)schedule();};
    document.addEventListener("visibilitychange",vis);
    return()=>{clearInterval(i);document.removeEventListener("visibilitychange",vis);};
  },[slug,schedule]);
  // The fetched `running` flag is a snapshot, so a fetch that raced a finish can
  // leave a permanently stale `running:true` behind. The overlays normally hide
  // it, but `doneIds` is retired the moment the chat is seen, and the row would
  // then fall back to that stale flag and turn orange again on a chat that is
  // sitting on "Ready". A run that ended here is over: clear the flag for good.
  useEffect(()=>{
    const ended=doneIds?Object.keys(doneIds):[];
    if(!ended.length)return;
    setSessions(prev=>{
      let hit=false;
      const next=prev.map(s=>{if(s&&s.running&&ended.indexOf(s.id)>=0){hit=true;return{...s,running:false};}return s;});
      return hit?next:prev;
    });
  },[doneIds]);
  if(!slug)return<div className="rail-chats-inner"><div className="rail-chats-empty rlabel">{t("Select a project to see its chats.")}</div></div>;
  // Ordering is derived here, not at fetch time, so a chat that goes back to
  // running jumps to the top on the very render its dot turns orange — the live
  // overlay moves it, the refetch only confirms it.
  const rows=sessions.map(s=>({s,state:rchatState(s,liveIds,doneIds)}));
  const ordered=orderChatRows(rows,stampRunStarts(rows,runStart.current,Date.now()));
  return(<div className="rail-chats-inner">
    <div className="rail-chats-head rlabel">{t("Chat history")}</div>
    {loaded&&sessions.length===0&&<div className="rail-chats-empty rlabel">{t("No saved chats yet.")}</div>}
    {ordered.map(({s,state})=>{const prov=["codex","gemini"].includes(s.provider)?s.provider:"claude";return(
      <button key={s.id} className={"rchat "+prov+(state?" st-"+state:"")} title={state?t(state==="done"?"new":state):s.title||t("Untitled chat")} onClick={()=>onOpen(slug,s.id)} onContextMenu={e=>{e.preventDefault();setCtx({id:s.id,title:s.title,x:e.clientX,y:e.clientY});}}>
        <span className={"rchat-dot"+(state?" st-"+state:" none")} aria-hidden="true"/>
        <span className={"ci-bar "+prov}/>
        <span className="rchat-t rlabel" dir={dirOf(s.title||t("Untitled chat"))}>{s.title||t("Untitled chat")}</span>
        <span className="rchat-w rlabel">{relTime(s.updatedAt)}</span>
      </button>);})}
    {ctx&&<ChatRowContextMenu title={ctx.title} x={ctx.x} y={ctx.y} onClose={()=>setCtx(null)} onDelete={()=>{const id=ctx.id;setCtx(null);delChat(id);}}/>}
  </div>);
}

function ChatRowContextMenu({title,x,y,onClose,onDelete}){
  const{lang}=useLang();
  const ref=useRef(null);
  const[pos,setPos]=useState({left:x,top:y,ready:false});
  useEffect(()=>{
    const el=ref.current;if(!el)return;
    const r=el.getBoundingClientRect();const pad=8;
    setPos({left:Math.max(pad,Math.min(x,window.innerWidth-r.width-pad)),top:Math.max(pad,Math.min(y,window.innerHeight-r.height-pad)),ready:true});
  },[x,y]);
  useEffect(()=>{const onKey=e=>{if(e.key==="Escape")onClose();};window.addEventListener("keydown",onKey);return()=>window.removeEventListener("keydown",onKey);},[onClose]);
  useMenuFocus(true,ref,null);
  return ReactDOM.createPortal(<>
    <div className="pop-back" onMouseDown={onClose} onContextMenu={e=>{e.preventDefault();onClose();}}/>
    <div ref={ref} className="ctx-menu" role="menu" aria-label={t("Chat actions")} style={{left:pos.left,top:pos.top,visibility:pos.ready?"visible":"hidden"}}>
      <button type="button" className="menu-item danger" role="menuitem" onClick={onDelete}><Icon name="trash"/>{t("Delete chat")}</button>
    </div>
  </>,document.body);
}

// Prodify-style Home dashboard: a greeting, quick-action pills, and cards that
// summarise the hub's *real* data — open tasks per project and live chats.
// No mock widgets (goals/reminders/calendar).
const MARKC=["#53BCF6","#D78DFE","#FD6765","#F6B24B","#38C492","#4BD0A0"];
function markColor(s){let h=0;for(let i=0;i<(s||"").length;i++)h=(h*31+s.charCodeAt(i))>>>0;return MARKC[h%MARKC.length];}
function projInitials(x){const p=(x.slug||"").replace(/[^a-z0-9]/gi," ").trim().split(/\s+/);return((p[0]||"?")[0]+((p[1]&&p[1][0])||(p[0]||"")[1]||"")).toUpperCase();}
// Display name for a project: Hebrew and English names deduped (many projects
// carry the same string in both fields), falling back to the slug.
function projDisplayNames(x){return x?[...new Set([x.nameHe,x.nameEn].filter(Boolean))]:[];}
function projLabel(x){const n=projDisplayNames(x);return n.length?n.join(" / "):((x&&x.slug)||"");}
function HomeDashboard({projects,openCounts,recentChats,st,onOpen,onOpenChat,onAdd,onAskAI,currentSlug,onNewChat,onAutomations,visible,filter,setFilter,hiddenCount,onCtx,projectsLoaded=true,projectsError=false,onRetryProjects,recentChatsLoaded=true}){
  const{lang}=useLang();
  const shown=projects.filter(x=>!x.hidden);
  const totalOpen=shown.reduce((a,x)=>a+(openCounts[x.slug]||0),0);
  // projects that have open tasks, most first — the "My Tasks" summary
  const withTasks=shown.filter(x=>(openCounts[x.slug]||0)>0)
    .sort((a,b)=>(openCounts[b.slug]||0)-(openCounts[a.slug]||0)).slice(0,7);
  const chats=recentChats||[];
  const runningChats=chats.filter(x=>x.running).length;
  const firstRunning=chats.find(x=>x.running);
  const tasksRef=useRef(null),projectsRef=useRef(null),chatsRef=useRef(null);
  const scrollToRef=r=>{try{r.current&&r.current.scrollIntoView({behavior:"smooth",block:"start"});}catch(e){}};
  const hour=(()=>{try{return new Date().getHours();}catch(e){return 12;}})();
  const greet=hour<5?t("Good night"):hour<12?t("Good morning"):hour<18?t("Good afternoon"):t("Good evening");
  const today=(()=>{try{return new Date().toLocaleDateString(getLang()==="he"?"he-IL":"en-GB",{weekday:"long",month:"long",day:"numeric"});}catch(e){return"";}})();
  // "Need attention" is a task KPI: use the exact same visible-project and
  // actionable-status counts as My Tasks. Health is presented separately on
  // project cards and must not create a task alert with no matching task row.
  // (The old "Need attention" tile showed exactly totalOpen, a duplicate of
  // "Open tasks", so it was dropped.)
  // Ask AI: with several projects, never silently reuse the last-opened one -
  // show which project the chat will open in and let the user change it.
  const[askChoice,setAskChoice]=useState("");
  const askSlug=(askChoice&&shown.some(x=>x.slug===askChoice))?askChoice
    :shown.length===1?shown[0].slug
    :(shown.some(x=>x.slug===currentSlug)?currentSlug:"");
  const gridProjects=(visible&&typeof visible.length==="number")?visible:shown;
  return <div className="home"><div className="home-inner">
    <div className="home-greet">
      <div className="date">{today}</div>
      <h1>{greet}</h1>
      {/* "delete this": subtitle removed — greeting collapses to date + heading */}
      <div className="sub" style={{ display: "none" }} aria-hidden="true" />
    </div>
    <div className="home-actions">
      {shown.length>1&&<label className="home-ask-proj">
        <span>{t("Project")}</span>
        <select value={askSlug} onChange={e=>setAskChoice(e.target.value)} title={t("The project the AI chat opens in")}>
          {!askSlug&&<option value="" disabled>{t("Choose a project")}</option>}
          {shown.map(x=>{const nm=projLabel(x);return <option key={x.slug} value={x.slug} dir={HEB_RE.test(nm)?"rtl":undefined}>{nm}</option>;})}
        </select>
      </label>}
      <button className="actionpill primary" disabled={shown.length>1&&!askSlug} onClick={()=>onAskAI&&onAskAI(askSlug||null)}
        title={shown.length>1&&!askSlug?t("Choose a project first"):undefined}><Icon name="chat" size={16}/>{t("Ask AI")}</button>
    </div>
    <div className="home-kpis">
      <button type="button" className="kpi" title={t("Jump to your projects")} onClick={()=>scrollToRef(projectsRef)}><div className="kpi-top"><span className="kpi-ic"><Icon name="box" size={18}/></span></div><div className="kpi-val">{shown.length}</div><div className="kpi-lbl">{t("Projects")}</div></button>
      <button type="button" className="kpi" title={t("Jump to open tasks")} onClick={()=>scrollToRef(tasksRef)}><div className="kpi-top"><span className="kpi-ic"><Icon name="edit" size={18}/></span></div><div className="kpi-val">{totalOpen}</div><div className="kpi-lbl">{t("Open tasks")}</div></button>
      <button type="button" className="kpi" title={firstRunning?t("Open the running agent chat"):t("Jump to your chats")} onClick={()=>firstRunning?onOpenChat(firstRunning.slug,firstRunning.id):scrollToRef(chatsRef)}><div className="kpi-top"><span className="kpi-ic"><Icon name="bot" size={18}/></span></div><div className="kpi-val">{runningChats}</div><div className="kpi-lbl">{t("Agents running")}</div></button>
    </div>
    <div className="home-grid">
        <div className="hcard" ref={chatsRef}>
          <div className="hcard-head last-chats-head">
            <h2>{t("Last Chats")}</h2>
            <div className="home-chat-actions">
              {runningChats?<span className="cnt">{runningChats+" "+t("running")}</span>:null}
              <button type="button" className="home-chat-btn claude" onClick={()=>onNewChat&&onNewChat("claude")} title={t("Start a new Claude chat")}>
                <ProviderLogo provider="claude" size={13}/><span>{t("Claude")}</span>
              </button>
              <button type="button" className="home-chat-btn codex" onClick={()=>onNewChat&&onNewChat("codex")} title={t("Start a new Codex chat")}>
                <ProviderLogo provider="codex" size={13}/><span>{t("Codex")}</span>
              </button>
            </div>
          </div>
          {!recentChatsLoaded
            ?<div className="last-chats-feat"><div className="lc-side" aria-hidden="true">{[0,1,2].map(i=><div key={i} className="lc-row skel-row"><span className="skel skel-dot"/><span className="skel skel-line"/></div>)}</div></div>
            :chats.length===0
            ?<div className="hcard-empty">{t("No saved chats yet. Start a project chat and it will appear here.")}</div>
            :(<div className="last-chats-feat">
                <div className="lc-side">{chats.map(x=>{const prov=x.provider||"claude";return(
                  <button key={x.slug+":"+x.id} className={"lc-row provider-"+prov} onClick={()=>onOpenChat(x.slug,x.id)}>
                    <ProviderLogo provider={prov} size={14}/>
                    <span className="lc-rmain">
                      <span className="lc-rname" dir={dirOf(x.title||t("New chat"))}>{x.title||t("New chat")}</span>
                      <span className="lc-rsub" dir={dirOf(x.projectName||x.slug)}>{x.projectName||x.slug}</span>
                    </span>
                    {x.running?<span className="lc-rtime live">{t("running")}</span>:<span className={"lc-rtime"+((x.doneUnseen||x.failedUnseen)?" unseen":"")+(x.failedUnseen?" fail":"")}>{(x.doneUnseen||x.failedUnseen)?t("new · "):""}{relTime(x.updatedAt)}</span>}
                  </button>);})}</div>
              </div>)}
        </div>
      <div className="hcard" ref={tasksRef}>
        <div className="hcard-head"><h2>{t("My Tasks")}</h2><span className="cnt">{totalOpen} {t("open")}</span></div>
        <div style={{fontSize:"12px",color:"var(--text-4)",lineHeight:"1.5",marginBottom:"12px"}}>{t("Actionable items from Deep Scans. Click a project to view and manage its open tasks.")}</div>
        {!projectsLoaded
          ?<div aria-hidden="true">{[0,1,2].map(i=><div key={i} className="hrow skel-row"><span className="skel skel-dot"/><span className="skel skel-line"/></div>)}</div>
          :withTasks.length===0
          ?<div className="hcard-empty">{t("No open tasks — every project is caught up. Run a Deep Scan to surface more.")}</div>
          :withTasks.map(x=>{const h=health(x.slug,st);return(
            <button key={x.slug} className="hrow" onClick={()=>onOpen(x.slug)}>
              <span className="dot" style={{background:h.c}}/>
              <span className="nm" dir={dirOf(x.nameHe)}>{x.nameHe}</span>
              <span className="pill-n">{openCounts[x.slug]} {t("open")}</span>
            </button>);})}
      </div>
    </div>
    <div className="home-projects" ref={projectsRef}>
      <div className="hp-head">
        <h2>{t("Projects")}</h2>
        <div className="hp-filters">
          <button className={"ftab"+(filter!=="hidden"?" on":"")} onClick={()=>setFilter&&setFilter("all")}>{t("All")}{shown.length?<span className="fcount">{shown.length}</span>:null}</button>
          {hiddenCount>0&&<button className={"ftab"+(filter==="hidden"?" on":"")} onClick={()=>setFilter&&setFilter("hidden")}><Icon name="eyeOff" className="ftab-ic" size={14}/>{t("Hidden")}<span className="fcount">{hiddenCount}</span></button>}
          <button className="see-all" onClick={onAdd}>＋ {t("Add")}</button>
        </div>
      </div>
      <div className="hp-grid">
        {!projectsLoaded&&[0,1,2,3].map(i=><div key={"sk"+i} className="pcard skel-card" aria-hidden="true"><div className="pcard-top"><span className="skel skel-mark"/><span className="skel skel-line"/></div><div className="skel skel-line sm"/></div>)}
        {projectsLoaded&&projectsError&&<div className="hcard-error" style={{gridColumn:"1/-1"}}><span>{t("Couldn't load your projects. Check your connection.")}</span>{onRetryProjects&&<button className="btn sm" onClick={onRetryProjects}>{t("Retry")}</button>}</div>}
        {projectsLoaded&&!projectsError&&gridProjects.length===0&&<div className="hcard-empty" style={{gridColumn:"1/-1"}}>{t("No projects in this filter.")}</div>}
        {projectsLoaded&&!projectsError&&gridProjects.map(x=>{const h=health(x.slug,st);const oc=openCounts[x.slug]||0;return(
          <button key={x.slug} className="pcard" onClick={()=>onOpen(x.slug)}
            onContextMenu={e=>{e.preventDefault();onCtx&&onCtx(x.slug,e.clientX,e.clientY);}}>
            <div className="pcard-top">
              <span className="pcard-name" dir={dirOf(x.nameHe||x.slug)}>{x.nameHe||x.slug}</span>
              <span className="pcard-mark" style={{background:markColor(x.slug)}}>{projInitials(x)}</span>
            </div>
            <div className="pcard-foot">
              <span className="pcard-meta">{oc?oc+" "+(oc===1?t("open task"):t("open tasks")):t("No open tasks")}</span>
              <span className="pcard-badge" style={{color:h.c}}><span className="pcard-dot" style={{background:h.c}}/>{h.t}</span>
            </div>
          </button>);})}
      </div>
    </div>
  </div></div>;
}

// Server-restart watchdog. hub-server occasionally restarts under the page — most
// often when a task edits the hub itself and finishes with a restart (see
// scripts/hub-restart.sh). When that happens the live streams and polls go dead
// and the UI silently rots until a manual refresh. This pings the open
// /api/health probe every few seconds and recovers on its own: it shows a
// "reconnecting" banner while the server is unreachable, and reloads the page
// once when the server comes back — or when uptimeSecs resets, which means a new
// process started under us even if no single request happened to fail.
function ServerReconnect(){
  const[down,setDown]=useState(false);
  const[ready,setReady]=useState(false); // server is back / restarted — waiting for the user to approve the refresh
  const ref=useRef({fails:0,lastUp:null,wasDown:false});
  useEffect(()=>{
    let alive=true;
    // Turn one poll into a {ok,up} sample, then let reconnectTick own the state
    // machine: a single failed ping is a blip (not an outage), and recovery is
    // surfaced exactly once so the "refresh" banner does not re-fire every tick.
    async function ping(){
      let sample;
      try{
        const r=await fetch("/api/health",{cache:"no-store"});
        if(!r.ok)throw new Error("health "+r.status);
        const d=await r.json().catch(()=>null);
        sample={ok:true,up:d&&typeof d.uptimeSecs==="number"?d.uptimeSecs:null};
      }catch(e){
        sample={ok:false,up:null};
      }
      if(!alive)return;
      const res=reconnectTick(ref.current,sample);
      ref.current=res.state;
      setDown(res.down);
      // `ready` is an edge — latch it in React state until the user dismisses it.
      if(res.ready)setReady(true);
    }
    const t=setInterval(ping,4000);
    return()=>{alive=false;clearInterval(t);};
  },[]);
  if(ready)return <div dir="ltr" style={{position:"fixed",top:0,left:0,right:0,zIndex:"var(--z-toast)",
    display:"flex",gap:12,alignItems:"center",justifyContent:"center",flexWrap:"wrap",
    padding:"8px 14px",textAlign:"center",fontSize:13,fontWeight:600,
    background:"var(--btn-primary,#269069)",color:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,.25)"}}>
    <span>✓ The server is back — refresh the page to load the latest version.</span>
    <button onClick={()=>location.reload()} style={{padding:"4px 12px",fontSize:13,fontWeight:700,
      borderRadius:8,border:"none",cursor:"pointer",background:"#fff",color:"var(--btn-primary,#269069)"}}>Refresh now</button>
    <button onClick={()=>setReady(false)} style={{padding:"4px 12px",fontSize:13,fontWeight:600,
      borderRadius:6,border:"1px solid rgba(255,255,255,.6)",cursor:"pointer",background:"transparent",color:"#fff"}}>Not now</button>
  </div>;
  if(!down)return null;
  return <div dir="ltr" style={{position:"fixed",top:0,left:0,right:0,zIndex:"var(--z-toast)",
    padding:"8px 14px",textAlign:"center",fontSize:13,fontWeight:600,
    background:"var(--warn)",color:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,.25)"}}>
    ⟳ Lost connection to the server — reconnecting… a refresh button will appear when the server is back.
  </div>;
}

// Restart-approval banner. A task that edited the hub POSTed a restart request;
// the server holds it until the hub is idle (auto-fires after a grace window) or
// a human approves. We poll /api/restart/pending and surface a yes/no bar. On
// "Restart now" (or auto-fire) the server restarts and ServerReconnect reloads.
function RestartPrompt(){
  const[st,setSt]=useState(null); // {pending,busy,secondsLeft,requestedAt}
  const[acting,setActing]=useState(false);
  const[actErr,setActErr]=useState("");
  useEffect(()=>{
    let alive=true;
    async function poll(){
      try{
        const r=await fetch("/api/restart/pending",{cache:"no-store"});
        if(!r.ok)return; // 401 before login etc. — just skip
        const d=await r.json().catch(()=>null);
        if(alive&&d)setSt(d);
      }catch(e){/* server down → ServerReconnect handles it */}
    }
    poll();
    const t=setInterval(poll,2000);
    return()=>{alive=false;clearInterval(t);};
  },[]);
  async function act(path){
    setActing(true);setActErr("");
    try{
      const r=await fetch(path,{method:"POST"});
      if(!r.ok)throw new Error("HTTP "+r.status);
      if(path.endsWith("/cancel"))setSt({pending:false});
    }catch(e){
      // Keep the bar up so the action can be retried — never pretend it worked.
      setActErr("We couldn't complete the action. Please try again.");
    }
    setActing(false);
  }
  if(!st||!st.pending)return null;
  const busy=!!st.busy;
  const secs=typeof st.secondsLeft==="number"?st.secondsLeft:null;
  // Centered card. The full-screen wrapper is click-through (pointerEvents:none)
  // so the dialog is prominent in the middle of the screen without blocking the
  // rest of the page; only the card itself captures clicks.
  return <div dir="ltr" style={{position:"fixed",inset:0,zIndex:"var(--z-toast)",display:"flex",
    alignItems:"center",justifyContent:"center",pointerEvents:"none",padding:16}}>
    <div style={{pointerEvents:"auto",maxWidth:420,width:"100%",boxSizing:"border-box",
      padding:"20px 22px",borderRadius:14,textAlign:"center",
      display:"flex",flexDirection:"column",gap:16,
      fontSize:15,fontWeight:600,color:"#fff",lineHeight:1.5,
      background:busy?"var(--warn)":"var(--accent)",boxShadow:"0 16px 48px rgba(0,0,0,.4)"}}>
      <span>
        {busy
          ? "⟳ A dashboard update is ready — the restart is waiting for an active AI task to finish."
          : (secs!=null
              ? "⟳ A dashboard update is ready — restarting in "+secs+"s…"
              : "⟳ A dashboard update is ready — restarting once the server is free…")}
      </span>
      <span style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
        <button disabled={acting} onClick={()=>act("/api/restart/approve")}
          style={{padding:"7px 16px",borderRadius:8,border:"1px solid rgba(255,255,255,.6)",
            background:"rgba(255,255,255,.18)",color:"#fff",fontWeight:600,fontSize:14,cursor:"pointer"}}>
          {busy?"Restart now (will stop the task)":"Restart now"}
        </button>
        <button disabled={acting} onClick={()=>act("/api/restart/cancel")}
          style={{padding:"7px 16px",borderRadius:8,border:"1px solid rgba(255,255,255,.35)",
            background:"transparent",color:"#fff",fontWeight:600,fontSize:14,cursor:"pointer"}}>
          {busy?"Cancel":"Not now"}
        </button>
      </span>
      {actErr&&<span style={{fontSize:13,fontWeight:600,color:"#fff",opacity:.95}}>{actErr}</span>}
    </div>
  </div>;
}

// Score a command against the query: 2 = substring hit, 1 = subsequence (fuzzy),
// -1 = no match. An empty query keeps everything in its natural (grouped) order.
function cmdScore(q,cmd){
  if(!q)return 0;
  const hay=(cmd.label+" "+cmd.section+" "+(cmd.keywords||"")).toLowerCase();
  const needle=q.toLowerCase();
  const at=hay.indexOf(needle);
  if(at>=0)return at===0?3:2; // prefer prefix matches
  let i=0;for(let c=0;c<hay.length&&i<needle.length;c++){if(hay[c]===needle[i])i++;}
  return i===needle.length?1:-1;
}

// Global command palette. Every top-level action is reachable by keyboard here
// (⌘K / Ctrl-K), giving power users an accelerator layer and first-timers a
// discoverable index of what the app can do — no memorised shortcuts required.
function CommandPalette({open,onClose,commands}){
  const{lang}=useLang();
  const[q,setQ]=useState("");
  const[active,setActive]=useState(0);
  const inputRef=useRef(null);
  const listRef=useRef(null);
  const restoreRef=useRef(null);

  // On open: remember what had focus, reset the query, and focus the input after
  // paint. On close: return focus to whatever the user was on before (so keyboard
  // users are never dropped at the top of the document).
  useEffect(()=>{
    if(open){
      restoreRef.current=document.activeElement;
      setQ("");setActive(0);
      const tm=setTimeout(()=>{try{inputRef.current&&inputRef.current.focus();}catch(e){}},0);
      return()=>clearTimeout(tm);
    }
    const prev=restoreRef.current;restoreRef.current=null;
    if(prev&&prev.focus)try{prev.focus();}catch(e){}
  },[open]);

  const query=q.trim();
  const items=commands.filter(c=>!c.hidden)
    .map(c=>({c,s:cmdScore(query,c)}))
    .filter(x=>x.s>=0)
    .sort((a,b)=>query?(b.s-a.s):0) // stable when no query → keep grouped order
    .map(x=>x.c);

  // Keep the active index inside the (possibly shrunken) result set, and scroll
  // the highlighted row into view as the user arrows through a long list.
  useEffect(()=>{setActive(a=>items.length?Math.max(0,Math.min(a,items.length-1)):0);},[items.length]);
  useEffect(()=>{
    if(!open||!listRef.current)return;
    const el=listRef.current.querySelector('[data-idx="'+active+'"]');
    if(el&&el.scrollIntoView)el.scrollIntoView({block:"nearest"});
  },[active,open]);

  if(!open)return null;

  const run=(cmd)=>{if(!cmd||cmd.disabled)return;onClose();try{cmd.run();}catch(e){console.error(e);}};
  const onKey=(e)=>{
    if(e.key==="ArrowDown"){e.preventDefault();setActive(a=>items.length?(a+1)%items.length:0);}
    else if(e.key==="ArrowUp"){e.preventDefault();setActive(a=>items.length?(a-1+items.length)%items.length:0);}
    else if(e.key==="Home"){e.preventDefault();setActive(0);}
    else if(e.key==="End"){e.preventDefault();setActive(items.length-1);}
    else if(e.key==="Enter"){e.preventDefault();run(items[active]);}
    else if(e.key==="Escape"){e.preventDefault();e.stopPropagation();onClose();}
  };
  const grouped=!query; // show section headers only in the unfiltered list

  return ReactDOM.createPortal(
    <div className="cmdk-back" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label={t("Command palette")}
        onKeyDown={e=>{if(e.key==="Escape"){e.preventDefault();e.stopPropagation();onClose();}}}>
        <div className="cmdk-search">
          <Icon name="search" className="cmdk-search-ic" size={16}/>
          <input ref={inputRef} className="cmdk-input" type="text" value={q}
            placeholder={t("Search commands, projects, settings…")}
            onChange={e=>{setQ(e.target.value);setActive(0);}} onKeyDown={onKey}
            role="combobox" aria-label={t("Search commands")} aria-expanded="true" aria-controls="cmdk-list"
            aria-activedescendant={items[active]?"cmdk-opt-"+active:undefined}
            spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off"/>
          <kbd className="cmdk-esc">Esc</kbd>
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox" ref={listRef} aria-label={t("Commands")}>
          {items.length===0
            ? <div className="cmdk-empty">{t("No commands match")} <b>“{query}”</b>.</div>
            : items.map((c,i)=>{
                const head=grouped&&(i===0||items[i-1].section!==c.section);
                return(
                  <React.Fragment key={c.id}>
                    {head&&<div className="cmdk-sec" role="presentation">{c.section}</div>}
                    <button type="button" data-idx={i} id={"cmdk-opt-"+i} tabIndex={-1}
                      role="option" aria-selected={i===active}
                      className={"cmdk-opt"+(i===active?" on":"")}
                      onMouseMove={()=>setActive(i)} onClick={()=>run(c)}>
                      <Icon name={c.icon} className="cmdk-opt-ic" size={18}/>
                      <span className="cmdk-opt-main">
                        <span className="cmdk-opt-label">{c.label}</span>
                        {c.sub&&<span className="cmdk-opt-sub">{c.sub}</span>}
                      </span>
                      {c.hint&&<kbd className="cmdk-opt-hint">{c.hint}</kbd>}
                    </button>
                  </React.Fragment>
                );
              })}
        </div>
        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> run</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Global focus management for modal dialogs. Rather than plumb a ref through
// every modal, we (1) watch Tab at the capture phase and keep focus inside the
// topmost on-screen dialog, and (2) watch the DOM for dialogs opening/closing:
// on open we remember the element that had focus and move focus into the dialog
// (first form field, else first focusable, else the sheet itself); on close we
// hand focus back to that element so keyboard users are never dropped at the top
// of the document. Escape-to-close is handled per dialog.
// Non-modal popovers also use role=dialog (model controls, to-do pop), so only
// aria-modal dialogs are trapped.
const MODAL_SEL='.modal,.qp-overlay,.cmdk,.fbrow,[role=dialog][aria-modal=true]';
// tabindex=-1 controls (e.g. roving command-palette options) are not Tab stops:
// counting them as the trap's "last" element let Tab leave the dialog to <body>.
const FOCUSABLE_SEL='a[href]:not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]):not([tabindex="-1"]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])';
function useModalFocusTrap(){
  useEffect(()=>{
    const visible=el=>el.offsetParent!==null||el.getClientRects().length>0;
    // Outermost open dialogs only (a .modal scrim wrapping a role=dialog sheet
    // counts once), in document order - the last one is the topmost.
    const openDialogs=()=>{
      const all=Array.from(document.querySelectorAll(MODAL_SEL));
      return all.filter(el=>!all.some(o=>o!==el&&o.contains(el)));
    };
    const topModal=()=>{const ms=openDialogs();return ms.length?ms[ms.length-1]:null;};
    const focusables=m=>Array.from(m.querySelectorAll(FOCUSABLE_SEL)).filter(visible);
    const onKey=e=>{
      if(e.key!=="Tab")return;
      const m=topModal();if(!m)return;
      const f=focusables(m);
      if(!f.length){e.preventDefault();return;}
      const first=f[0],last=f[f.length-1],act=document.activeElement;
      if(!m.contains(act)){e.preventDefault();first.focus();return;}
      if(e.shiftKey&&act===first){e.preventDefault();last.focus();}
      else if(!e.shiftKey&&act===last){e.preventDefault();first.focus();}
    };
    // Open/close tracking. `tracked` maps each open dialog to the element that
    // had focus just before it appeared.
    const tracked=new Map();
    let raf=0;
    const sync=()=>{
      raf=0;
      const now=openDialogs();
      for(const[el,restore]of Array.from(tracked)){
        if(now.includes(el)&&el.isConnected)continue;
        tracked.delete(el);
        const act=document.activeElement;
        const lost=!act||act===document.body||!act.isConnected||el.contains(act);
        if(lost&&restore&&restore.isConnected&&restore.focus){try{restore.focus();}catch(e){}}
      }
      for(const el of now){
        if(tracked.has(el))continue;
        const prev=document.activeElement;
        tracked.set(el,prev&&!el.contains(prev)?prev:null);
        // Let the dialog's own autoFocus (e.g. the command palette input) win.
        setTimeout(()=>{
          if(!el.isConnected||el.contains(document.activeElement))return;
          const f=focusables(el);
          const field=f.find(x=>/^(INPUT|TEXTAREA|SELECT)$/.test(x.tagName));
          const target=field||f[0];
          try{
            if(target)target.focus();
            else{
              const sheet=el.querySelector("[role=dialog]")||el;
              if(!sheet.hasAttribute("tabindex"))sheet.setAttribute("tabindex","-1");
              sheet.focus();
            }
          }catch(e){}
        },0);
      }
    };
    const schedule=()=>{if(!raf)raf=requestAnimationFrame(sync);};
    const mo=new MutationObserver(schedule);
    mo.observe(document.body,{childList:true,subtree:true});
    sync();
    document.addEventListener("keydown",onKey,true);
    return()=>{document.removeEventListener("keydown",onKey,true);mo.disconnect();if(raf)cancelAnimationFrame(raf);};
  },[]);
}

// Keyboard behaviour for popup menus (role="menu"): on open, focus the first
// menuitem; ArrowUp/ArrowDown/Home/End move focus between items; when the menu
// closes (Escape, outside click or picking an item) focus goes back to the
// trigger if it would otherwise be stranded on <body>. `triggerRef` is optional:
// context menus have no trigger, so the element focused at open time is used.
const IS_MAC=(()=>{try{return /Mac|iPhone|iPad|iPod/i.test(navigator.platform||navigator.userAgent||"");}catch(e){return false;}})();
function useMenuFocus(open,menuRef,triggerRef){
  useEffect(()=>{
    if(!open)return;
    const menu=menuRef&&menuRef.current;
    const trigger=(triggerRef&&triggerRef.current)||document.activeElement;
    const items=()=>menu?Array.from(menu.querySelectorAll('[role="menuitem"]:not([disabled])'))
      .filter(el=>el.offsetParent!==null||el.getClientRects().length>0):[];
    const raf=requestAnimationFrame(()=>{
      if(!menu||menu.contains(document.activeElement))return;
      const f=items()[0];if(f)try{f.focus();}catch(e){}
    });
    const onKey=e=>{
      if(!["ArrowDown","ArrowUp","Home","End"].includes(e.key))return;
      const list=items();if(!list.length)return;
      const i=list.indexOf(document.activeElement);
      const n=e.key==="Home"?0:e.key==="End"?list.length-1
        :e.key==="ArrowDown"?(i+1)%list.length:(i<=0?list.length-1:i-1);
      e.preventDefault();list[n].focus();
    };
    if(menu)menu.addEventListener("keydown",onKey);
    return()=>{
      cancelAnimationFrame(raf);
      if(menu)menu.removeEventListener("keydown",onKey);
      setTimeout(()=>{
        const a=document.activeElement;
        const stranded=!a||a===document.body||!a.isConnected||(menu&&menu.contains(a));
        if(stranded&&trigger&&trigger.isConnected&&trigger.focus)try{trigger.focus();}catch(e){}
      },0);
    };
  },[open]);
}

function App(){
  const{lang,setLang}=useLang(); // subscribe so the shell (sidebar/header/mobile nav) re-renders on language change
  useModalFocusTrap();
  const[projects,setProjects]=useState([]);
  const[status,setStatus]=useState(null);
  // Client-side ring buffer of the last 10 host metric readings (CPU/RAM/disk),
  // keyed on the collector's updated_at so repeated polls of the same measurement
  // never double-count. Persisted per host so the graph survives a page reload.
  const[hostHist,setHostHist]=useState([]);
  const[agentSetup,setAgentSetup]=useState(false); // HUB_CLOUD: install-agent modal, hoisted so any CTA can open it
  const[agentsOnline,setAgentsOnline]=useState(null); // HUB_CLOUD: connected agents (null=unknown, []=none) for the "no agent" CTAs
  const[agentToast,setAgentToast]=useState(null); // HUB_CLOUD: {host,n} celebratory toast when a machine first polls in
  const[planInfo,setPlanInfo]=useState(null); // HUB_CLOUD: {name,maxProjects,projectCount,canAddProject} from /api/me — the server is the only authority on the cap
  const[isAdmin,setIsAdmin]=useState(false); // from /api/me — gates admin-only UI (Project State → Drive sync)
  const[deployDiff,setDeployDiff]=useState(null); // HUB_CLOUD admin: {available,shortSha,filesChanged,insertions,deletions,commitsSince} from /api/deploy/diff
  const[planLimit,setPlanLimit]=useState(false); // HUB_CLOUD: the plan-limit modal is open
  const[billingNotice,setBillingNotice]=useState(null); // HUB_CLOUD: "ok" | "failed" from a checkout return
  const prevAgentsRef=useRef(null); // last-seen agent list, to detect a fresh connection (null=first poll, no toast)
  const[usage,setUsage]=useState({claude:null,codex:null}); // provider subscription rate limits
  const[openCounts,setOpenCounts]=useState({}); // {slug:number} open (non-solved) tasks per project, for the sidebar
  const[totalCounts,setTotalCounts]=useState({}); // {slug:number} total tasks (all statuses) per project — the badge number
  const[chatCounts,setChatCounts]=useState({running:{},unseen:{},failed:{}}); // per-slug unread terminal states plus unchanged live-run counts
  const[runningChatIds,setRunningChatIds]=useState({}); // slug -> {chatId:true} of chats running in THIS browser right now; drives the left-rail live dot without waiting for the (racy) server light-fetch flag
  const[localChatDone,setLocalChatDone]=useState({}); // slug -> {chatId:"done"|"failed"|"seen"} for runs that ENDED here — same idea, so the rail turns green immediately instead of after the debounced save + refetch
  const localChatDoneRef=useRef({});localChatDoneRef.current=localChatDone; // read inside the recent-chats poll, which is a stable useCallback
  const localChatRuns=useRef(new Map()); // chat key -> {slug,expected,at}, for instant same-browser project badges
  const[recentChats,setRecentChats]=useState([]); // cross-project recent chats for the Home dashboard
  const[recentChatsLoaded,setRecentChatsLoaded]=useState(false); // gate the "No saved chats yet" empty state until the first fetch settles
  const[projectsLoaded,setProjectsLoaded]=useState(false); // gate the "No projects" empty state until /api/projects settles
  const[projectsError,setProjectsError]=useState(false); // network failure on the projects fetch → show retry, never a false "empty account"
  // restore the last-viewed project across reloads (validated against the list once it loads).
  // The URL hash (#/slug) is the source of truth so every project has its own shareable link;
  // localStorage is only a fallback for a first visit with no hash.
  const[sel,setSel]=useState(()=>{try{return chatRouteFromHash().slug||localStorage.getItem("hub-sel")||null;}catch(e){return null;}});
  const[filter,setFilter]=useState(()=>{try{
    const saved=localStorage.getItem("hub-filter");
    return ["all","dev","live","hidden"].includes(saved)?saved:"all";
  }catch(e){return"all";}});
  const[sideOpen,setSideOpen]=useState(()=>{try{return localStorage.getItem("hub-side")!=="0";}catch(e){return true;}}); // project list collapsed/expanded
  const[dragSlug,setDragSlug]=useState(null); // slug being dragged in the sidebar
  const[dragOver,setDragOver]=useState(null); // slug currently hovered as a drop target
  const[arrange,setArrange]=useState(false); // sidebar "Arrange" mode: bulk select → hide / remove
  const[arrSel,setArrSel]=useState(()=>new Set()); // slugs ticked while arranging
  const[term,setTerm]=useState(null); // {slug,mode,cmd,nonce} when the full-screen terminal modal is open
  const[dockOpen,setDockOpen]=useState(true); // right-side Claude dock visible
  const[dockMaxed,setDockMaxed]=useState(false); // Claude dock opened wide over the middle
  const[dockTab,setDockTab]=useState("chat"); // "chat" | "term"
  const[mView,setMView]=useState("home"); // active view (drives phone single-panel + desktop nav highlight): "home" | "chat" | "projects" | "detail"
  const[page,setPage]=useState("home"); // left-nav top-level page: "home" (dashboard) | "work" (project workspace: detail + dock)
  const[narrow,setNarrow]=useState(()=>{try{return window.innerWidth<=760;}catch(e){return false;}}); // phone/tablet width → tab-bar layout (matches the ≤760px CSS shell)
  const[showAuto,setShowAuto]=useState(false); // top-level Automations page open
  const[showAiSettings,setShowAiSettings]=useState(false); // per-use AI model settings modal
  const[showApiKeys,setShowApiKeys]=useState(false); // Settings → API keys vault
  const[showNotifications,setShowNotifications]=useState(false); // unified notifications (sounds + phone push) modal open
  const[showGithub,setShowGithub]=useState(()=>{try{return(location.hash||"").indexOf("#github")===0;}catch(e){return false;}}); // GitHub connection panel (auto-opens after OAuth callback redirect)
  const[ask,setAsk]=useState(null); // {prompt,nonce,tipId,slug} — a prompt pushed into the dock chat
  const askSeq=useRef(0);
  const[openChat,setOpenChat]=useState(null); // {slug,chatId,nonce} — jump the dock chat to a specific past conversation
  const openChatSeq=useRef(0);
  const[openRun,setOpenRun]=useState(null); // {slug,taskId,title,detail,nonce} — open an autonomous AI run as a continuable chat
  const openRunSeq=useRef(0);
  const[newChatReq,setNewChatReq]=useState(null); // {slug,provider,nonce} — open a fresh blank chat for a provider
  const newChatSeq=useRef(0);
  const[detailTabReq,setDetailTabReq]=useState(null); // {tab,nonce} — force the open project's Detail onto a tab (e.g. mobile "Tasks")
  const detailTabSeq=useRef(0);
  const[nudge,setNudge]=useState(null); // {slug,tipId} — "Claude finished — mark solved?" nudge on a tip card
  const[started,setStarted]=useState(null); // {slug,tipId,chatId,nonce} — a tip whose Claude run just launched → promote to "in progress" + link to its chat
  const startSeq=useRef(0);
  const[runCmd,setRunCmd]=useState(null); // {cmd,mode,nonce} — text typed into the dock terminal
  const runCmdSeq=useRef(0);
  const[editing,setEditing]=useState(false);
  const[isNew,setIsNew]=useState(false);
  const[ctxMenu,setCtxMenu]=useState(null); // {slug,x,y} — sidebar row right-click menu
  // Expanded rail: widen the icon rail to show text labels + project names. Sticky per-browser.
  const[railExp,setRailExp]=useState(()=>{try{return localStorage.getItem("hub.railExp")==="1";}catch(e){return false;}});
  useEffect(()=>{try{localStorage.setItem("hub.railExp",railExp?"1":"0");}catch(e){}},[railExp]);
  // Expanded sidebar is freely drag-resizable; width persists per browser.
  const RAIL_MIN=180,RAIL_MAX=460;
  const[railW,setRailW]=useState(()=>{try{const v=parseInt(localStorage.getItem("hub.railW"),10);return v>=RAIL_MIN&&v<=RAIL_MAX?v:236;}catch(e){return 236;}});
  useEffect(()=>{try{localStorage.setItem("hub.railW",String(railW));}catch(e){}},[railW]);
  const[railDrag,setRailDrag]=useState(false);
  function startRailResize(e){
    if(!railExp)return;
    e.preventDefault();
    const startX=e.clientX,startW=railW,rtl=document.documentElement.dir==="rtl";
    setRailDrag(true);
    const move=ev=>{
      const d=(ev.clientX-startX)*(rtl?-1:1);
      setRailW(Math.max(RAIL_MIN,Math.min(RAIL_MAX,startW+d)));
    };
    const up=()=>{setRailDrag(false);window.removeEventListener("pointermove",move);window.removeEventListener("pointerup",up);};
    window.addEventListener("pointermove",move);
    window.addEventListener("pointerup",up);
  }
  const[editSlug,setEditSlug]=useState(null); // identity of project being edited (slug may change in the form)
  const[draft,setDraft]=useState(null);
  const[saving,setSaving]=useState(false);
  const[saveErr,setSaveErr]=useState("");
  const[savedTick,setSavedTick]=useState(0);

  // Refs mirror the latest state so the debounced auto-saver never reads stale
  // closures (the timeout fires a render or two after the edit that scheduled it).
  const projectsRef=useRef(projects); projectsRef.current=projects;
  const draftRef=useRef(draft); draftRef.current=draft;
  const editSlugRef=useRef(editSlug); editSlugRef.current=editSlug;
  const isNewRef=useRef(isNew); isNewRef.current=isNew;
  const skipAuto=useRef(false); // set when opening the editor, to skip the initial draft-set

  // HUB_CLOUD: expose a global opener so any CTA (banner, chat, empty states) can
  // open the single hoisted install modal without prop-drilling, and poll which
  // agents are connected so the "no agent" call-to-actions appear/disappear live.
  // Fold a fresh /api/agents result into state and, when a machine that wasn't
  // there on the previous poll appears, raise the "Agent connected" toast + cue.
  // The first poll (prevAgentsRef===null) only seeds the baseline — an agent that
  // was already online at page load is not a new connection, so it stays quiet.
  const reconcileAgents=useCallback((list)=>{
    const next=Array.isArray(list)?list:[];
    const prev=prevAgentsRef.current;
    if(prev){
      const seen=new Set(prev.map(a=>a&&a.host));
      const fresh=next.filter(a=>a&&!seen.has(a.host));
      if(fresh.length){
        setAgentToast({host:fresh[0].host||"",n:fresh.length});
        try{playSound("fanfare",loadSoundPrefs().volume);}catch(e){}
      }
    }
    prevAgentsRef.current=next;
    setAgentsOnline(next);
  },[]);
  useEffect(()=>{
    if(!CLOUD)return;
    window.__hubOpenAgentSetup=()=>setAgentSetup(true);
    let alive=true;
    const load=()=>fetch("/api/agents").then(r=>r.json())
      .then(d=>{if(alive&&d&&d.ok)reconcileAgents(d.agents||[]);}).catch(()=>{});
    load();
    const t=setInterval(load,20000);
    return()=>{alive=false;clearInterval(t);try{delete window.__hubOpenAgentSetup;}catch(e){}};
  },[reconcileAgents]);
  const noAgent=CLOUD&&Array.isArray(agentsOnline)&&agentsOnline.length===0;

  // Guard against a non-array body (e.g. a 401 "{error}" when the session cookie
  // expired): feeding that into setProjects makes `projects.find(...)` throw in
  // render and blanks the whole app. The login gate handles the 401 UX; here we
  // just keep `projects` an array no matter what came back.
  // r.ok?json:[] keeps a 401 (login gate handles that UX) from throwing; only a
  // real network failure trips projectsError so we can offer Retry instead of a
  // misleading "no projects" empty state. projectsLoaded gates the empty state.
  const loadProjects=useCallback(()=>{
    setProjectsError(false);
    return fetch("/api/projects").then(r=>r.ok?r.json():[])
      .then(d=>{const list=Array.isArray(d)?d:[];setProjects(list);setSel(s=>(s&&list.some(x=>x.slug===s))?s:(list[0]&&list[0].slug));})
      .catch(()=>setProjectsError(true))
      .finally(()=>setProjectsLoaded(true));
  },[]);
  useEffect(()=>{loadProjects();},[loadProjects]);
  // The hash is the source of truth for which project is open. Mirror sel into it
  // (so a refresh / bookmark / copied link returns to the same project) and also
  // keep localStorage as a fallback for a first visit that has no hash yet.
  useEffect(()=>{try{if(sel){localStorage.setItem("hub-sel",sel);if(slugFromHash()!==sel)location.hash=chatHash(sel,null);}}catch(e){}},[sel]);
  // Follow browser back/forward and manual hash edits.
  useEffect(()=>{
    const f=()=>{
      const route=chatRouteFromHash();
      if(route.slug)setSel(route.slug);
      if(route.slug&&route.chatId)setOpenChat({slug:route.slug,chatId:route.chatId,nonce:++openChatSeq.current});
    };
    f();
    window.addEventListener("hashchange",f);
    return()=>window.removeEventListener("hashchange",f);
  },[]);
  // Arm the audio unlock once on mount so the first click/keypress anywhere in
  // the hub makes later notification cues audible under the autoplay policy.
  useEffect(()=>{unlockAudio();},[]);
  // Register the push-notification service worker and route notification taps
  // (or foreground-suppressed pushes) to the chat they point at, if it names one.
  useEffect(()=>{
    if(!("serviceWorker" in navigator))return;
    navigator.serviceWorker.register("/sw.js").catch(()=>{});
    const onMsg=e=>{
      const m=e.data||{};
      // A hub-push arrives when the SW suppressed the OS notification because a
      // hub window is focused — replay the matching in-app sound here, since the
      // service worker has no AudioContext to play it itself. (hub-open is a
      // notification tap: the user already acted, so no cue.)
      if(m.type==="hub-push"&&m.data&&m.data.event){
        // same dedup key as the run-stream cue (chat id), so a focused tab that
        // already played this finish locally doesn't play the push echo too.
        fireCue(m.data.chatId||m.data.tag||m.data.slug,m.data.event,m.data.ok!==false,m.data.label||"");
      }
      // ONLY a real notification tap (hub-open) navigates. A hub-push is an
      // unsolicited "your run finished" arriving while you're actively using the
      // hub — navigating on it yanked the user out of whatever chat they were
      // reading and into the finished one. The blue doneUnseen dot already
      // surfaces it; let them choose when to switch.
      if(m.type==="hub-open"&&m.data&&m.data.slug&&m.data.chatId){
        // Switch to the notification's project too — jumpToRequestedChat only
        // opens the chat once its ClaudePanel is the mounted one (sel===slug), so
        // setting openChat alone does nothing when the tap arrives on Home or a
        // different project (the usual case on a backgrounded mobile PWA). Mirror
        // the hashchange handler: set sel AND openChat, and sync the hash so a
        // later refresh/back stays on the chat.
        setSel(m.data.slug);
        setOpenChat({slug:m.data.slug,chatId:m.data.chatId,nonce:++openChatSeq.current});
        try{const h=chatHash(m.data.slug,m.data.chatId);if(location.hash!==h)location.hash=h;}catch(_){}
      }
    };
    navigator.serviceWorker.addEventListener("message",onMsg);
    return()=>navigator.serviceWorker.removeEventListener("message",onMsg);
  },[]);
  useEffect(()=>{try{localStorage.setItem("hub-filter",filter);}catch(e){}},[filter]);
  useEffect(()=>{try{localStorage.setItem("hub-side",sideOpen?"1":"0");}catch(e){}},[sideOpen]);
  useEffect(()=>{const f=()=>setNarrow(window.innerWidth<=760);window.addEventListener("resize",f);return()=>window.removeEventListener("resize",f);},[]);
  // Named refreshers so the header's "Check now" button can force an immediate
  // re-poll of the same data the intervals fetch on their own schedule.
  const refreshStatus=useCallback(()=>fetch("/api/status").then(r=>r.json()).then(d=>setStatus(anchorStatus(d))).catch(()=>{}),[]);
  const refreshUsage=useCallback(()=>Promise.all([
    fetch("/api/claude/usage").then(r=>r.json()),
    fetch("/api/codex/usage").then(r=>r.json()),
  ]).then(([claude,codex])=>setUsage(prev=>({
    // Never blank the indicator on a transient error (rate limit / offline CLI):
    // a failed poll keeps the last successful reading and just marks it stale, so
    // the header chip always shows a real percentage instead of "—".
    claude:mergeUsage(prev&&prev.claude,claude),
    codex:mergeUsage(prev&&prev.codex,codex),
  }))).catch(()=>{}),[]);
  const refreshCounts=useCallback(()=>fetch("/api/tips/counts").then(r=>r.json()).then(d=>{if(d&&d.counts)setOpenCounts(d.counts);if(d&&d.totals)setTotalCounts(d.totals);}).catch(()=>{}),[]);
  useEffect(()=>{refreshStatus();const i=setInterval(refreshStatus,30000);return()=>clearInterval(i);},[refreshStatus]);
  // Provider usage %: the server caches both APIs for 60s, matching this poll.
  useEffect(()=>{refreshUsage();const i=setInterval(refreshUsage,60000);return()=>clearInterval(i);},[refreshUsage]);
  // open-task counts for the sidebar badges; refresh periodically so a Deep Scan
  // or a task marked completed elsewhere updates the number without a reload.
  useEffect(()=>{refreshCounts();const i=setInterval(refreshCounts,30000);return()=>clearInterval(i);},[refreshCounts]);
  // Chat activity badges arrive immediately over one lightweight SSE connection.
  // A slow JSON poll remains as a safety net for missed events/unsupported SSE.
  const applyChatCounts=useCallback(d=>{
    if(!d||!d.ok)return;
    // A stream snapshot can race the POST that launches a chat. Keep locally
    // launched runs overlaid until their own client stream finishes, so an older
    // zero-count snapshot cannot briefly erase the immediate optimistic badge.
    const running={...(d.running||{})};
    const localMin={},now=Date.now();
    for(const[key,entry]of localChatRuns.current){
      const server=running[entry.slug]||0;
      // Retire the overlay once the backend has acknowledged its expected count.
      // Also expire it after 10s if a legacy/unrestarted backend never emits the
      // live state (the normal 15s Cloud poll remains the final reconciliation).
      if(server>=entry.expected){entry.acknowledged=true;continue;}
      if(server===0&&now-entry.at>10000){localChatRuns.current.delete(key);continue;}
      localMin[entry.slug]=Math.max(localMin[entry.slug]||0,entry.expected);
    }
    for(const slug of Object.keys(localMin))running[slug]=Math.max(running[slug]||0,localMin[slug]);
    setChatCounts({running,unseen:d.unseen||{},failed:d.failed||{}});
  },[]);
  const refreshChatCounts=useCallback(()=>fetch("/api/chats/counts").then(r=>r.json()).then(applyChatCounts).catch(()=>{}),[applyChatCounts]);
  const refreshRecentChats=useCallback(()=>fetch("/api/chats/recent?limit=30").then(r=>r.json()).then(d=>{if(d&&d.ok)setRecentChats(overlayRecentChats(d.chats,localChatDoneRef.current));}).catch(()=>{}).finally(()=>setRecentChatsLoaded(true)),[]);
  // Viewing a finished chat clears its unread flag, but persistence and the
  // resulting SSE event are debounced. Drop the badge optimistically the instant
  // it is seen, then let the live snapshot reconcile it.
  const markChatSeen=useCallback((slug,chatId,kind)=>{if(!slug)return;setChatCounts(c=>{const key=kind==="failed"?"failed":"unseen",next={...(c[key]||{})};if(next[slug]>1)next[slug]-=1;else delete next[slug];return{...c,[key]:next};});setRecentChats(list=>Array.isArray(list)?list.map(x=>x&&x.slug===slug&&x.id===chatId?{...x,doneUnseen:false,failedUnseen:false}:x):list);
    // Seen: drop the green, but KEEP the "this run ended here" fact (as "seen") —
    // deleting the entry outright let the server's stale `running:true` resurface
    // on the next poll and flipped an already-finished chat back to orange. Only a
    // NEW run clears it (markLocalChatRunning).
    setLocalChatDone(m=>{const ids=m[slug];if(!ids||!ids[chatId]||ids[chatId]==="seen")return m;return{...m,[slug]:{...ids,[chatId]:"seen"}};});
    // Persist the "seen" clear server-side right away, independent of the debounced
    // full-history save (which omits still-`partial` stubs). Without this the next
    // recent-chats/counts poll re-reports the stale new/failed dot after viewing.
    if(chatId)fetch("/api/claude/history/seen",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug,id:chatId})}).catch(()=>{});},[]);
  // A run that just ended, before the persisted flag makes it back from the store.
  // The chat lists show "finished, not seen yet" (green) from the instant the run
  // reports done. The entry outlives being seen (it degrades to "seen", which still
  // suppresses the server's `running`) and is only dropped when a NEW run starts on
  // that chat — see markLocalChatRunning.
  const markLocalChatDone=useCallback((slug,chatId,kind)=>{
    if(!slug||!chatId)return;
    setLocalChatDone(m=>((m[slug]||{})[chatId]===kind?m:{...m,[slug]:{...(m[slug]||{}),[chatId]:kind}}));
    // Clear `running` too, or Home's Last Chats keeps its orange "running" label
    // on a finished chat until the next 15s recent-chats poll overwrites it.
    setRecentChats(list=>Array.isArray(list)?list.map(x=>x&&x.slug===slug&&x.id===chatId?{...x,running:false,doneUnseen:kind==="done",failedUnseen:kind==="failed"}:x):list);
  },[]);
  const markLocalChatRunning=useCallback((slug,chatId,on)=>{
    if(!slug||!chatId)return;
    const key=slug+"\0"+chatId,has=localChatRuns.current.has(key);
    if(on&&has)return;
    if(!on&&!has)return;
    // Working again (a queued follow-up turn chains straight off the finished one)
    // — the "just finished" overlay has to go, or the row would sit on green while
    // the chat is actually back to work.
    if(on)setLocalChatDone(m=>{const ids=m[slug];if(!ids||!ids[chatId])return m;const next={...ids};delete next[chatId];const out={...m};if(Object.keys(next).length)out[slug]=next;else delete out[slug];return out;});
    setChatCounts(c=>{
      const running={...(c.running||{})},next=Math.max(0,(running[slug]||0)+(on?1:-1));
      if(on)localChatRuns.current.set(key,{slug,expected:next,at:Date.now()});else localChatRuns.current.delete(key);
      if(next)running[slug]=next;else delete running[slug];
      return{...c,running};
    });
    setRunningChatIds(m=>{
      const ids={...(m[slug]||{})};
      if(on)ids[chatId]=true;else delete ids[chatId];
      const next={...m};if(Object.keys(ids).length)next[slug]=ids;else delete next[slug];
      return next;
    });
  },[]);
  useEffect(()=>{
    refreshChatCounts();
    // Cloud uses an Authorization-bearing fetch-stream adapter because native
    // EventSource cannot attach its JWT. Both live streams keep a slow poll as
    // reconciliation; only unsupported Cloud browsers retain the 15s fallback.
    const cloudStream=CLOUD&&typeof window.__hubOpenChatCountsStream==="function";
    const i=setInterval(refreshChatCounts,CLOUD?15000:60000);
    if(cloudStream){
      const close=window.__hubOpenChatCountsStream(applyChatCounts);
      return()=>{clearInterval(i);if(close)close();};
    }
    if(CLOUD||typeof EventSource==="undefined")return()=>clearInterval(i);
    const stream=new EventSource("/api/chats/counts/stream");
    const onCounts=e=>{try{applyChatCounts(JSON.parse(e.data));}catch(_){}};
    stream.addEventListener("counts",onCounts);
    return()=>{clearInterval(i);stream.removeEventListener("counts",onCounts);stream.close();};
  },[refreshChatCounts,applyChatCounts]);
  useEffect(()=>{refreshRecentChats();const i=setInterval(refreshRecentChats,15000);return()=>clearInterval(i);},[refreshRecentChats]);
  const[checking,setChecking]=useState(false);
  // 1s ticker so the header freshness label counts up live ("Updated 12s ago")
  // between the periodic status polls.
  const[,setNowTick]=useState(0);
  useEffect(()=>{const i=setInterval(()=>setNowTick(n=>n+1),1000);return()=>clearInterval(i);},[]);
  // Force a *live* re-measure of host vitals (CPU/RAM/disk/uptime) and per-project
  // website reachability, plus a re-poll of provider usage and task counts. This
  // POSTs the fast /api/status/refresh collector — it is NOT the LLM Deep Scan
  // (/api/tips/scan) and launches no agent. The spinner lingers briefly so a fast
  // round-trip still reads as a deliberate action.
  const liveRefreshStatus=useCallback(()=>fetch("/api/status/refresh",{method:"POST"}).then(r=>r.json()).then(d=>setStatus(anchorStatus(d))).catch(()=>{}),[]);
  const checkNow=useCallback(()=>{setChecking(true);Promise.all([liveRefreshStatus(),refreshUsage(),refreshCounts()]).finally(()=>setTimeout(()=>setChecking(false),450));},[liveRefreshStatus,refreshUsage,refreshCounts]);
  // Scoped, single-project live health check for the project view's "Scan now"
  // button. POSTs the targeted collector, which probes ONLY this slug (URL
  // reachability, port listening, PM2/Docker status), then merges just that one
  // row back into status — the rest of the snapshot is left untouched. This is a
  // live check, NOT the LLM Deep Scan (/api/tips/scan).
  const scanProjectHealth=useCallback((slug)=>
    fetch("/api/projects/"+encodeURIComponent(slug)+"/scan-health",{method:"POST"})
      .then(r=>r.json())
      .then(d=>{
        if(d&&d.ok&&d.project){
          setStatus(prev=>{
            const base=prev||{projects:{},host:{}};
            return{...base,projects:{...(base.projects||{}),[slug]:d.project},projects_checked_at:d.projects_checked_at||base.projects_checked_at};
          });
        }
        return d;
      })
      .catch(()=>{}),[]);

  const p=projects.find(x=>x.slug===sel);
  // Project-switch skeleton: `p` (list data) is available instantly, but each
  // Detail remount re-fires per-slug fetches (readme/claude preview, tips,
  // automations, health) that paint a beat later. Flash a skeleton for a short
  // fixed window on every real slug change so the swap reads as a smooth load
  // instead of a half-empty flicker. prevSelRef skips the very first paint.
  const[switching,setSwitching]=useState(false);
  const prevSelRef=useRef(sel);
  const switchTimerRef=useRef(null);
  useEffect(()=>{
    if(prevSelRef.current===sel)return;
    prevSelRef.current=sel;
    if(!sel)return;
    setSwitching(true);
    clearTimeout(switchTimerRef.current);
    switchTimerRef.current=setTimeout(()=>setSwitching(false),450);
    return()=>clearTimeout(switchTimerRef.current);
  },[sel]);
  // total "new" completed chats across projects (finished in the background,
  // not yet opened) — drives the mobile Chat badge.
  const chatDone=Object.values((chatCounts&&chatCounts.unseen)||{}).reduce((a,n)=>a+(n||0),0);
  const host=status&&status.host||{};
  const st=status; const old=stale(st);
  // Record each distinct host measurement into the last-10 ring buffer. Load the
  // persisted history on first sight of a host, then append when updated_at (or,
  // lacking one, any of the three metrics) advances.
  const hostName=host&&host.name||"";
  const hostAt=(st&&st.updated_at)||"";
  useEffect(()=>{
    if(!hostName||host.cpu_pct==null)return;
    const key="hub:hostHist:"+hostName;
    setHostHist(prev=>{
      let arr=prev;
      // first paint for this host: hydrate from localStorage
      if(!arr.length||arr._host!==hostName){
        try{const raw=JSON.parse(localStorage.getItem(key)||"[]");arr=Array.isArray(raw)?raw:[];}catch(e){arr=[];}
        arr=arr.slice();
      }
      const last=arr[arr.length-1];
      const stamp=hostAt||String(Math.round(host.cpu_pct)+"/"+Math.round(host.mem_pct)+"/"+Math.round(host.disk_pct));
      if(last&&last.at===stamp)return prev; // same measurement, no new point
      const next=arr.concat([{at:stamp,cpu:host.cpu_pct,mem:host.mem_pct,disk:host.disk_pct}]).slice(-10);
      next._host=hostName;
      try{localStorage.setItem(key,JSON.stringify(next.map(({at,cpu,mem,disk})=>({at,cpu,mem,disk}))));}catch(e){}
      return next;
    });
  },[hostName,hostAt,host.cpu_pct,host.mem_pct,host.disk_pct]);
  // counts feed the filter tabs (status counts are over visible/non-hidden projects)
  const shown=projects.filter(x=>!x.hidden);
  const hiddenCount=projects.length-shown.length;
  // the project list after the active filter tab
  const visible=filter==="hidden"?projects.filter(x=>x.hidden):shown;

  // proj lets callers outside the open workspace (e.g. the sidebar's right-click
  // menu) edit a project that isn't the currently selected one.
  function startEdit(proj){
    // React passes the click event to a handler used directly as onClick. Only
    // accept an actual project here, so both `onClick={startEdit}` and callers
    // that deliberately supply another project are safe.
    const pr=proj&&proj.slug?proj:p;
    if(!pr)return;
    setSel(pr.slug);setEditing(false);setDraft(null);setEditSlug(null);setIsNew(false);setSaveErr("");
    setShowAuto(false);setPage("work");setDockMaxed(false);setMView("detail");
    setDetailTabReq({tab:"info",nonce:++detailTabSeq.current});
  }
  // HUB_CLOUD: read the tenant's plan. Re-read after any project add/delete so
  // the count stays honest without the client ever computing the cap itself.
  function refreshPlan(){
    if(!CLOUD)return Promise.resolve();
    return fetch("/api/me").then(r=>r.ok?r.json():null).then(d=>{if(d&&d.plan)setPlanInfo(d.plan);if(d)setIsAdmin(!!d.admin);}).catch(()=>{});
  }
  useEffect(()=>{refreshPlan();},[]);
  // HUB_CLOUD admin: "changes since last deploy" widget. Fetched once admin
  // status is known true; no polling since deploys are infrequent.
  const fetchDeployDiff=useCallback(()=>{
    fetch("/api/deploy/diff").then(r=>r.ok?r.json():null).then(d=>setDeployDiff(d&&d.available?d:null)).catch(()=>{});
  },[]);
  useEffect(()=>{if(isAdmin)fetchDeployDiff();},[isAdmin,fetchDeployDiff]);
  // HUB_CLOUD: returning from the hosted checkout. Read the query flag, refetch
  // the plan (the webhook is authoritative — the redirect is only a hint), and
  // scrub the flag so a reload doesn't re-announce it.
  useEffect(()=>{
    if(!CLOUD)return;
    const q=new URLSearchParams(window.location.search).get("billing");
    if(q!=="ok"&&q!=="failed")return;
    setBillingNotice(q);
    refreshPlan();
    const u=new URL(window.location.href);
    u.searchParams.delete("billing");
    window.history.replaceState({},"",u.pathname+u.search+u.hash);
  },[]);

  function startAdd(){
    if(CLOUD&&planInfo&&!planInfo.canAddProject){setPlanLimit(true);return;}
    skipAuto.current=true;setShowAuto(false);setSavedTick(0);const b=blankProject();setDraft(b);setEditSlug(null);setIsNew(true);setSaveErr("");setEditing(true);setPage("work");setDockMaxed(false);setMView("detail");}
  // Open the dock's Claude chat for the selected project and push a prompt into a
  // fresh chat session there (so it never appends to an ongoing conversation).
  function askClaudeInDock(slug,prompt,tipId,tipTitle,choice,target){
    if(slug&&slug!==sel)setSel(slug);
    setShowAuto(false);setPage("work");setDockOpen(true);setDockTab("chat");setMView("chat");
    // tipId (set only by a tip card's "Ask Claude") lets the dock nudge that card
    // to "mark solved?" once this run finishes; host-health asks pass none.
    // tipTitle names the fresh chat after the tip, instead of the long prompt blob.
    // choice (the task card's per-button provider/model pick) overrides the global
    // "fix" AI preference; without one we fall back to that saved preference.
    const fixChoice=choice||(tipId?loadAiUsePrefs().fix:null);
    // Prepend the stored Fix-AI instructions (⚙ next to the fix tab): a custom
    // brief and/or extra instructions the user set as their fix default.
    const fixPrompt=loadAiUsePrompts().fix||{};
    const fixPre=[String(fixPrompt.system||"").trim(),String(fixPrompt.instructions||"").trim()].filter(Boolean).join("\n\n");
    if(fixPre)prompt=fixPre+"\n\n"+prompt;
    setAsk({prompt,nonce:++askSeq.current,tipId,slug,title:tipTitle,target,
      model:fixChoice?fixChoice.model:undefined,provider:fixChoice?fixChoice.provider:undefined});
  }
  function openDock(tab){setShowAuto(false);setPage("work");setDockOpen(true);setDockTab(tab||"chat");setMView("chat");}
  function startProviderChat(provider){
    const slug=(p&&p.slug)||(projects[0]&&projects[0].slug);
    if(slug&&slug!==sel)setSel(slug);
    if(slug&&location.hash!==chatHash(slug,null))location.hash=chatHash(slug,null);
    setShowAuto(false);setPage("work");setDockOpen(true);setDockTab("chat");setDockMaxed(true);setMView("chat");
    setNewChatReq({slug,provider:["claude","codex"].includes(provider)?provider:"claude",nonce:++newChatSeq.current});
  }
  // Left-nav navigation. goHome shows the dashboard; goWork enters the project
  // workspace (detail + dock). Both keep the phone single-panel view in sync.
  function goHome(){setPage("home");setMView("home");setShowAuto(false);setEditing(false);setDockMaxed(false);}
  function goWork(view){setPage("work");setDockMaxed(false);if(view)setMView(view);}
  // Mobile "Tasks": open the current (or first) project's Detail on its Tasks tab.
  function goTasks(){
    const slug=(p&&p.slug)||(projects[0]&&projects[0].slug);
    if(!slug)return; // no projects yet — nothing to show
    if(slug!==sel)setSel(slug);
    setShowAuto(false);setEditing(false);setPage("work");setMView("detail");
    setDetailTabReq({tab:"tasks",nonce:++detailTabSeq.current});
  }

  // A task card links to the chat where Claude worked on it. Select that project,
  // open the dock, and tell the panel which past conversation to switch to.
  function openTipChat(slug,chatId){
    if(!chatId)return;
    const targetSlug=slug||sel;
    if(!targetSlug)return;
    if(targetSlug!==sel)setSel(targetSlug);
    // Route first so the requested ID is durable even while the project changes
    // and its history is still loading.
    if(location.hash!==chatHash(targetSlug,chatId))location.hash=chatHash(targetSlug,chatId);
    setShowAuto(false);setPage("work");setDockOpen(true);setDockTab("chat");setMView("chat");
    setOpenChat({slug:targetSlug,chatId,nonce:++openChatSeq.current});
  }

  // "AI run" on a task card: instead of a read-only transcript modal, open the
  // autonomous run as a real, continuable chat in the dock (seeded from the saved
  // transcript and resumable via its recorded session id) so the user can keep
  // chatting from where the automation left off.
  function openTaskRunChat(slug,task){
    const targetSlug=slug||sel;
    if(!targetSlug||!task)return;
    if(targetSlug!==sel)setSel(targetSlug);
    setShowAuto(false);setPage("work");setDockOpen(true);setDockTab("chat");setMView("chat");
    setOpenRun({slug:targetSlug,taskId:task.id,title:task.title,detail:task.detail||"",nonce:++openRunSeq.current});
  }

  // Type text into the dock terminal. Defaults to shell, but the chat can target
  // other terminal tools (Gemini) when handing off a completed Claude turn.
  // Stable identity (it is passed down to memoised chat components as onRunCmd).
  const runInTerminal=useCallback(function runInTerminal(c,mode,target){
    const nonce=++runCmdSeq.current;
    if(target==="modal"){
      setTerm({slug:p&&p.slug,mode:mode||"shell",cmd:c,nonce});
      return;
    }
    setPage("work");setDockOpen(true);setDockTab("term");setMView("chat");setRunCmd({cmd:c,mode:mode||"shell",nonce});
  },[p]);
  // Ask Claude about a host-level fix (the RAM/CPU/disk indicators). The chat is
  // pinned to target "server" so it always runs on the server agent, never on a
  // laptop agent, even when the selected project lives on the Mac.
  function askClaudeHost(prompt){askClaudeInDock((p&&p.slug)||(projects[0]&&projects[0].slug),prompt,undefined,undefined,undefined,"server");}
  // Render helpers for the health popovers: a clickable command + an Ask-Claude button.
  const cmd=(c)=> <code className="cmd" title={t("Type into the terminal")} onClick={()=>runInTerminal(c)}>{c}</code>;
  const askBtn=(q,label)=> <button className="ask" onClick={()=>askClaudeHost(q)}>🤖 {label||t("Ask Claude to fix this")}</button>;

  // Persist the current draft in place — keeps the editor open (used by auto-save).
  function autoSave(d){
    if(!d)return;
    if(!d.slug||!d.slug.trim()){setSaveErr("Slug is required.");return;}
    const projects=projectsRef.current,editSlug=editSlugRef.current,isNew=isNewRef.current;
    let next;
    if(isNew){
      if(projects.some(x=>x.slug===d.slug)){setSaveErr("Slug already exists — choose a unique slug.");return;}
      next=[...projects,d];
    }else{
      if(d.slug!==editSlug && projects.some(x=>x.slug===d.slug)){setSaveErr("Slug already exists — choose a unique slug.");return;}
      next=projects.map(x=>x.slug===editSlug?d:x);
    }
    setSaving(true);setSaveErr("");
    return saveProjects(next).then(r=>{
      setSaving(false);
      // Track the saved identity so the next auto-save updates (never re-appends),
      // even if the slug itself was edited.
      if(r&&r.ok){
        setProjects(next);setSel(d.slug);setEditSlug(d.slug);setSavedTick(t=>t+1);refreshPlan();
        // Creation is the only standalone form left. As soon as the new record
        // exists, continue on its ordinary Info tab where every field is editable.
        if(isNew){
          setIsNew(false);setEditing(false);setDraft(null);
          setDetailTabReq({tab:"info",nonce:++detailTabSeq.current});
        }
      }
      // HUB_CLOUD backstop: a save can still hit the cap (stale planInfo, a
      // race, an import) — the shim reports it as {ok:false,code:"plan_limit"}.
      else if(r&&r.code==="plan_limit"){setPlanLimit(true);setSaveErr(r.error||"Project limit reached.");refreshPlan();}
      else setSaveErr("Save failed: "+((r&&r.error)||"unknown error"));
    }).catch(e=>{setSaving(false);setSaveErr("Save failed: "+String(e));});
  }

  // Auto-save: write the draft ~700ms after the last keystroke — no Save button.
  // Opening the editor sets skipAuto so merely opening it never triggers a write.
  useEffect(()=>{
    if(!editing)return;
    if(skipAuto.current){skipAuto.current=false;return;}
    const t=setTimeout(()=>autoSave(draftRef.current),700);
    return()=>clearTimeout(t);
  },[draft,editing]);

  // immediate, in-place update (used by the notes/files panel) — no full edit cycle
  function patchProject(slug,patch){
    const next=projects.map(x=>x.slug===slug?{...x,...patch}:x);
    setProjects(next);
    return saveProjects(next);
  }

  // Save one chat message's text as a project note (the hover "+" on assistant
  // messages). Prepends to p.attachments, same shape the Plan > Notes panel writes.
  function addMsgToNotes(slug,text){
    const body=String(text||"").trim();
    if(!slug||!body)return false;
    const proj=projects.find(x=>x.slug===slug);
    if(!proj)return false;
    patchProject(slug,{attachments:[{id:rid("n"),kind:"note",text:body,createdAt:Date.now()},...(proj.attachments||[])]});
    return true;
  }

  // Drag-reorder by slug so it works regardless of the active filter: pull the
  // dragged project out of the full array and drop it at the target's position.
  function reorderProjects(fromSlug,toSlug){
    if(!fromSlug||fromSlug===toSlug)return;
    const next=[...projects];
    const from=next.findIndex(x=>x.slug===fromSlug),to=next.findIndex(x=>x.slug===toSlug);
    if(from<0||to<0)return;
    const[m]=next.splice(from,1);
    next.splice(to,0,m);
    setProjects(next);saveProjects(next);
  }
  // Hide / unhide a project from the default list (persisted on the project itself).
  function toggleHidden(slug){
    const cur=projects.find(x=>x.slug===slug);if(!cur)return;
    patchProject(slug,{hidden:!cur.hidden});
  }

  // Shared delete path for both the Editor's "Delete" button and the sidebar's
  // right-click menu — the latter can target a project that isn't open for edit.
  function deleteProject(slug){
    if(!window.confirm(t("Delete this project from Heygents? The project cannot be restored; its chat history is kept.")))return;
    const next=projects.filter(x=>x.slug!==slug);
    setSaving(true);setSaveErr("");
    saveProjects(next).then(r=>{
      setSaving(false);
      if(r&&r.ok){
        setProjects(next);
        if(editSlug===slug){setEditing(false);setDraft(null);}
        if(sel===slug)setSel(next[0]&&next[0].slug);
      }else{
        const msg=t("Delete failed:")+" "+((r&&r.error)||t("unknown error"));
        setSaveErr(msg);
        if(editSlug!==slug)window.alert(msg); // no Editor open to show saveErr inline
      }
    }).catch(e=>{
      setSaving(false);
      const msg=t("Delete failed:")+" "+String(e);
      setSaveErr(msg);
      if(editSlug!==slug)window.alert(msg);
    });
  }
  // "Arrange" mode: an explicit edit state on the project rail so you can freely
  // clear projects off the board — tick several, then Hide (reversible) or Remove
  // (one confirm; the project row and its scan tips go, chat history stays — see
  // store.deleteProject). Leaving Arrange always drops the selection so a stale
  // tick can't act on the next thing you click.
  function toggleArrange(){setArrange(a=>{if(a)setArrSel(new Set());setArrWhy(false);return!a;});}
  function toggleArrSel(slug){setArrSel(prev=>{const n=new Set(prev);n.has(slug)?n.delete(slug):n.add(slug);return n;});}
  function selectAllArr(slugs){setArrSel(prev=>prev.size>=slugs.length?new Set():new Set(slugs));}
  // Hide/unhide every ticked project at once — non-destructive, the data stays.
  function hideSelected(hidden){
    if(!arrSel.size)return;
    const next=projects.map(x=>arrSel.has(x.slug)?{...x,hidden}:x);
    setProjects(next);saveProjects(next);setArrSel(new Set());
  }
  // Bulk delete: one confirm that names what's going, then a single save.
  function removeSelected(){
    const slugs=[...arrSel];if(!slugs.length)return;
    const names=slugs.map(s=>{const pr=projects.find(x=>x.slug===s);return pr?(pr.nameHe||pr.slug):s;});
    const preview=names.slice(0,8).join(", ")+(names.length>8?", +"+(names.length-8)+" more":"");
    if(!window.confirm(t("Remove")+" "+slugs.length+" "+(slugs.length>1?t("projects"):t("project"))+" "+t("from Heygents? The projects cannot be restored; their chat history is kept.")+"\n\n"+preview))return;
    const gone=new Set(slugs);
    const next=projects.filter(x=>!gone.has(x.slug));
    setSaving(true);setSaveErr("");
    saveProjects(next).then(r=>{
      setSaving(false);
      if(r&&r.ok){
        setProjects(next);setArrSel(new Set());setArrange(false);refreshPlan();
        if(gone.has(sel))setSel(next[0]&&next[0].slug);
      }else{const msg=t("Delete failed:")+" "+((r&&r.error)||t("unknown error"));setSaveErr(msg);window.alert(msg);}
    }).catch(e=>{setSaving(false);const msg=t("Delete failed:")+" "+String(e);setSaveErr(msg);window.alert(msg);});
  }

  // click a project in the left rail → open its workspace
  function pickProject(slug){
    setSel(slug);
    if(location.hash!==chatHash(slug,null))location.hash=chatHash(slug,null);
    setEditing(false);setShowAuto(false);
    // Swiping/tapping between projects defaults to the Chat view: switching just
    // loads the newly selected project's chat context (the Dock reloads it from
    // the new `p`). Opening the project info page is done via the small rproj-open
    // button, which calls goWork("detail") instead.
    setPage("work");setDockOpen(true);setDockTab("chat");setMView("chat");
  }

  // Global command palette (⌘K / Ctrl-K). One accelerator opens an index of every
  // top-level action, so nothing is buried behind mouse-only menus.
  const[cmdkOpen,setCmdkOpen]=useState(false);
  useEffect(()=>{
    const onKey=e=>{
      if((e.metaKey||e.ctrlKey)&&!e.altKey&&(e.key==="k"||e.key==="K")){
        e.preventDefault();setCmdkOpen(o=>!o);
      }
    };
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[]);
  // Flip light/dark from the palette. The class + localStorage are the source of
  // truth (the first-paint script reads them); the event keeps SettingsMenu's
  // Dark/Light toggle in sync without prop-drilling.
  function toggleTheme(){
    try{
      const el=document.documentElement;
      const nowLight=el.classList.contains("dark");
      el.classList.toggle("dark",!nowLight);
      localStorage.setItem("hub-theme",nowLight?"light":"dark");
      window.dispatchEvent(new CustomEvent("hub-theme",{detail:{light:nowLight}}));
    }catch(e){}
  }
  const commands=(()=>{
    const list=[];
    const add=(id,label,section,run,opts)=>list.push(Object.assign({id,label,section,run},opts||{}));
    add("nav-home",t("Go to Dashboard"),t("Navigate"),goHome,{icon:"home",keywords:"home overview start"});
    add("nav-chat",t("Open Chat"),t("Navigate"),()=>{setDockMaxed(true);openDock("chat");},{icon:"chat",keywords:"assistant claude ai dock ask chat"});
    add("nav-term",t("Open terminal"),t("Navigate"),()=>openDock("term"),{icon:"terminal",keywords:"shell console command run"});
    add("new-claude",t("New Claude chat"),t("Create"),()=>startProviderChat("claude"),{icon:"bot",keywords:"conversation ask anthropic"});
    add("new-codex",t("New Codex chat"),t("Create"),()=>startProviderChat("codex"),{icon:"bot",keywords:"conversation ask openai gpt"});
    add("new-project",t("New project…"),t("Create"),startAdd,{icon:"plus",keywords:"add create repo"});
    if(p)add("edit-project",t("Edit current project"),t("Create"),()=>startEdit(p),{icon:"edit",sub:p.name||p.slug,keywords:"settings configure "+p.slug});
    add("view-dock",t("Toggle right panel"),t("View"),()=>setDockOpen(o=>!o),{icon:"panelRight",keywords:"dock chat hide show collapse"});
    add("view-side",t("Toggle project sidebar"),t("View"),()=>setSideOpen(o=>!o),{icon:"sidebar",keywords:"projects list collapse expand"});
    add("view-theme",t("Toggle light / dark theme"),t("View"),toggleTheme,{icon:"moon",keywords:"dark mode appearance colour color"});
    add("sys-refresh",t("Refresh all data"),t("System"),checkNow,{icon:"refresh",keywords:"reload check now usage status metrics"});
    add("sys-ai",t("AI settings"),t("System"),()=>setShowAiSettings(true),{icon:"brain",keywords:"model provider effort reasoning ai"});
    add("sys-keys",t("API keys"),t("System"),()=>setShowApiKeys(true),{icon:"key",keywords:"api key secret token anthropic claude gemini openai codex github credentials"});
    add("sys-sound",t("Notifications"),t("System"),()=>setShowNotifications(true),{icon:"volume",keywords:"audio sound notifications mute push phone approval"});
    add("sys-github",t("GitHub connection"),t("System"),()=>setShowGithub(true),{icon:"github",keywords:"git repo oauth connect"});
    if(CLOUD)add("sys-agent",t("Install agent"),t("System"),()=>setAgentSetup(true),{icon:"download",keywords:"cloud agent install update setup connect machine"});
    add("view-rail",railExp?t("Collapse sidebar"):t("Expand sidebar"),t("View"),()=>setRailExp(v=>!v),{icon:railExp?"chevL":"chevR",keywords:"rail narrow wide collapse expand"});
    add("view-maximize",t("Toggle full-width chat"),t("View"),()=>setDockMaxed(v=>!v),{icon:"panelRight",keywords:"maximize fullscreen dock wide chat"});
    if(hiddenCount>0)add("view-hidden",filter==="hidden"?t("Show all projects"):t("Show hidden projects"),t("View"),()=>setFilter(filter==="hidden"?"all":"hidden"),{icon:"eyeOff",keywords:"hidden archived filter projects"});
    // One row per project so any project is one keystroke away, even hidden ones.
    projects.forEach(x=>add("proj-"+x.slug,x.name||x.slug,t("Projects"),()=>pickProject(x.slug),
      {icon:"box",sub:x.slug!==(x.name||x.slug)?x.slug:null,keywords:"project open switch "+x.slug}));
    return list;
  })();

  // The active project tints the app header so the whole top bar reflects "where
  // you are" — a per-project accent (same slug → same colour) only while a
  // project workspace is open; Home keeps the neutral header.
  const headProj=page==="work"?projects.find(x=>x.slug===sel):null;
  const headAc=headProj?projAccent(headProj.slug):null;

  return(
   <div className="wrap">
    {/* The app routes on location.hash, so the skip link must not change the
        hash: move focus to the main region programmatically instead. */}
    <a className="skip-link" href="#main" onClick={e=>{e.preventDefault();const m=document.getElementById("main");if(m){try{m.focus();m.scrollIntoView({block:"start"});}catch(err){}}}}>{t("Skip to content")}</a>
    <div className={"appshell mv-"+mView+(dockOpen&&dockMaxed&&page==="work"?" dock-maxed":"")}>
     <aside className={"appnav apprail"+(railExp?" exp":"")+(railDrag?" resizing":"")} style={railExp?{width:railW+"px"}:null}>
       <button className="rbrand" onClick={goHome} title={t("Heygents - Home")} aria-label={t("Home")}><span className="rbrand-i"><BrandMark size={36} alt=""/></span><span className="rlabel rbrand-t">{t("Heygents")}</span></button>
       <div className="rail-pswitch">
         <ProjectSwitcher projects={projects} sel={sel} st={st} openCounts={openCounts} totalCounts={totalCounts} chatCounts={chatCounts} onPick={pickProject} onCtx={(slug,x,y)=>setCtxMenu({slug,x,y})} onReorder={reorderProjects}/>
       </div>
       <div className="rail-chats">
         <SidebarChats slug={sel} onOpen={openTipChat} liveIds={(sel&&runningChatIds[sel])||null} doneIds={(sel&&localChatDone[sel])||null}
           sig={sel&&chatCounts?JSON.stringify([(chatCounts.running||{})[sel]||0,(chatCounts.unseen||{})[sel]||0,(chatCounts.failed||{})[sel]||0]):""}/>
       </div>
       <div className="rail-foot">
         <button className="rit nav-cmdk" onClick={()=>setCmdkOpen(true)} aria-keyshortcuts="Meta+K Control+K"
           title={t("Search commands")+" ("+(IS_MAC?"⌘K":"Ctrl+K")+")"} aria-label={t("Search commands")}>
           <Icon name="search"/><span className="rlabel">{t("Search")}</span><kbd className="nav-kbd rlabel" aria-hidden="true">{IS_MAC?"⌘K":"Ctrl+K"}</kbd></button>
         <button className="rit" onClick={()=>{setDockMaxed(true);openDock("chat");}} title={t("Chat")} aria-label={t("Chat")}><Icon name="chat"/><span className="rlabel">{t("Chat")}</span></button>
         {CLOUD&&<button className="rit" onClick={()=>setAgentSetup(true)} title={t("Install or update the Heygents agent")} aria-label={t("Install agent")}><Icon name="download"/><span className="rlabel">{t("Install agent")}</span></button>}
         <button className="rit rit-add" onClick={startAdd} title={t("Add project")} aria-label={t("Add project")}><Icon name="plus"/><span className="rlabel">{t("Add project")}</span></button>
         <button className="rit rail-toggle" onClick={()=>setRailExp(v=>!v)} aria-expanded={railExp}
           title={railExp?t("Collapse sidebar"):t("Expand sidebar")} aria-label={railExp?t("Collapse sidebar"):t("Expand sidebar")}>
           <Icon name={railExp?"chevL":"chevR"}/></button>
         <SettingsMenu variant="hamburger" exp={railExp} active={showGithub||showAiSettings||showNotifications||showApiKeys}
           onGithub={()=>setShowGithub(true)}
           onAiSettings={()=>setShowAiSettings(true)}
           onApiKeys={()=>setShowApiKeys(true)}
           onNotifications={()=>setShowNotifications(true)}/>
       </div>
       {railExp&&<div className={"rail-rsz"+(railDrag?" active":"")} onPointerDown={startRailResize} title={t("Drag to resize")}/>}
     </aside>
     {ctxMenu&&(()=>{
       const cp=projects.find(pr=>pr.slug===ctxMenu.slug);
       if(!cp)return null;
       return <ProjectContextMenu project={cp} x={ctxMenu.x} y={ctxMenu.y}
         onClose={()=>setCtxMenu(null)}
         onEdit={()=>{setCtxMenu(null);startEdit(cp);}}
         onToggleHidden={()=>{setCtxMenu(null);toggleHidden(cp.slug);}}
         onDelete={()=>{setCtxMenu(null);deleteProject(cp.slug);}}/>;
     })()}
     <div className="appmain">
    <div className="apphead" style={headAc?{background:"var(--surface)",borderBottom:"1px solid var(--line)",paddingTop:13,paddingBottom:13}:null}>
      <div className="ah-start">
        {!narrow&&headProj&&<div className="ah-proj" dir={dirOf(projLabel(headProj))} title={projLabel(headProj)}>
          <span className="proj-id-dot" style={{background:markColor(headProj.slug)}} aria-hidden="true"/>
          {headProj.localPort&&<a className="btn sm ah-openport" href={"http://localhost:"+headProj.localPort} target="_blank" rel="noopener noreferrer"
            title={t("Open http://localhost:{port} in a new tab",{port:headProj.localPort})} onClick={e=>e.stopPropagation()}>↗ :{headProj.localPort}</a>}
        </div>}
      </div>
      <div className="ah-usage">
        <ProviderUsage provider="claude" data={usage.claude}/>
        <ProviderUsage provider="codex" data={usage.codex}/>
      </div>
      <div className="ah-end">
        {!narrow&&<NotificationsBell compact onSettings={()=>setShowNotifications(true)} onOpen={openTipChat}/>}
        {/* Mobile: the hamburger menu + notification bell now live in the bottom
            .mnav bar (see below), not the header. */}
        <div className="ah-lead">
        <div className="metrics">
        <div className="cn-col">
        <button className={"checknow"+(checking?" busy":"")} onClick={checkNow} disabled={checking}
          aria-label={checking?t("Scanning…"):t("Scan now")}
          title={t("Scan now — refresh CPU/RAM/disk, Claude/Codex usage and task counts immediately")}>
          <span className="cn-ic" aria-hidden="true">⟳</span><span className="cn-lbl">{checking?t("Scanning…"):t("Scan")}</span>
        </button>
        <HostStat cls="ah-fresh" color={old?"var(--err)":"var(--text-3)"}
          value={st&&st.updated_at?(t("Updated {rel} ago",{rel:relTimeSec(statusAt(st))})+(old?" "+t("(stale)"):"")):t("Waiting for data…")}
          tip={
            <><b>{t("Data freshness")}</b> — when the monitoring agent last reported. Host stats (CPU/RAM/disk) refresh every 5 minutes; project health refreshes hourly.
            <span className="lvl">🔴 <b>{t("(stale)")}</b> means no update for 11+ minutes — the collector has stopped and the numbers above may be wrong.</span>
            <span className="lvl"><b>{t("Fix:")}</b> the collector isn't running. Check {cmd("pm2 status")} (or the agent's systemd unit) and restart it; confirm the server itself is reachable.</span>
            {askBtn("The Heygents monitoring data is stale — the status collector agent isn't reporting. Check whether agent.py is running, look at its logs, and restart it.")}</>
          }/>
        </div>
        {host.name&&<HostStat titleHint={t("CPU load")} value={Math.round(host.cpu_pct)+"%"} color={lvlColor(host.cpu_pct,70,90)}
          series={{data:hostHist.map(x=>x.cpu),warn:70,bad:90,color:lvlColor(host.cpu_pct,70,90)}} tip={
          <><b>{t("CPU load")}</b> — processor use across all cores, right now.
          <span className="lvl">✅ under 70% · ⚠️ 70–90% busy · 🔴 90%+ pegged</span>
          <span className="lvl"><b>{t("Fix:")}</b> find the hog with {cmd("htop")} or {cmd("pm2 monit")}, then {cmd("pm2 restart <app>")}. If it stays high, the app needs optimising or the box needs more cores.</span>
          {askBtn("The server CPU is running hot. Run htop / pm2 monit to find the process using the most CPU, then help me bring it down safely.")}</>
        }/>}
        {host.name&&<HostStat titleHint={t("Memory used")} value={Math.round(host.mem_pct)+"%"} color={lvlColor(host.mem_pct,80,92)}
          series={{data:hostHist.map(x=>x.mem),warn:80,bad:92,color:lvlColor(host.mem_pct,80,92)}} tip={
          <><b>{t("Memory used")}</b> — share of RAM in use.
          <span className="lvl">✅ under 80% · ⚠️ 80–92% tight · 🔴 92%+ risks swapping &amp; OOM kills</span>
          <span className="lvl"><b>{t("Fix:")}</b> check {cmd("free -h")} and {cmd("ps aux --sort=-%mem | head")}, then restart the heaviest app — {cmd("pm2 restart <app>")} or {cmd("docker restart <name>")}. Chronic pressure → add RAM or a swapfile.</span>
          {askBtn("The server is low on memory. Check free -h and the top memory-consuming processes, then help me free up RAM safely.")}</>
        }/>}
        {host.name&&<HostStat titleHint={t("Disk used")} value={Math.round(host.disk_pct)+"%"} color={lvlColor(host.disk_pct,80,90)}
          series={{data:hostHist.map(x=>x.disk),warn:80,bad:90,color:lvlColor(host.disk_pct,80,90)}} tip={
          <><b>{t("Disk used")}</b> — root filesystem (<code>/</code>) usage.
          <span className="lvl">✅ under 80% · ⚠️ 80–90% getting full · 🔴 90%+ writes &amp; DBs start failing</span>
          <span className="lvl"><b>{t("Fix:")}</b> reclaim space — {cmd("pm2 flush")}, {cmd("docker system prune -af")}, {cmd("journalctl --vacuum-size=200M")}. Find the big stuff with {cmd("du -sh /* | sort -h")}. Still full → grow the volume.</span>
          {askBtn("The server disk is almost full. Run du to find what's using the most space, then help me reclaim disk safely.")}</>
        }/>}
        {host.name&&<HostStat cls="ah-up" titleHint={t("Uptime")} value={fmt(host.uptime_secs)} tip={
          <><b>{t("Uptime")}</b> — time since the server last booted. Longer is better — it means no crashes or reboots.
          <span className="lvl"><b>{t("Watch out:")}</b> if this suddenly drops to minutes, the machine rebooted or crashed. Check {cmd("last reboot")} and {cmd("dmesg -T | tail")}, then confirm every app came back up with {cmd("pm2 status")}.</span>
          {askBtn("The server uptime just reset, so it rebooted. Check 'last reboot' and dmesg for the cause, and verify all apps and containers came back up.")}</>
        }/>}
        {isAdmin&&deployDiff&&<button type="button" className="ah-deploydiff" onClick={fetchDeployDiff}
          title={t("Changes since the last deploy ({sha}) - click to refresh",{sha:deployDiff.shortSha})}>
          <span style={{color:"var(--ok)"}}>+{deployDiff.insertions}</span>{" "}
          <span style={{color:"var(--err)"}}>-{deployDiff.deletions}</span>{" "}
          <span className="ah-deploydiff-lbl">{t("since deploy ({n} commits)",{n:deployDiff.commitsSince})}</span>
        </button>}
        </div>
        </div>
      </div>
    </div>
    {noAgent&&<AgentBanner/>}
    <div className="appbody" id="main" role="main" tabIndex={-1}>
      {page==="home"
        ?<HomeDashboard projects={projects} openCounts={openCounts} st={st}
            recentChats={recentChats} projectsLoaded={projectsLoaded} projectsError={projectsError} onRetryProjects={loadProjects} recentChatsLoaded={recentChatsLoaded}
            onOpen={pickProject} onOpenChat={openTipChat} onAdd={startAdd} currentSlug={sel} onAskAI={(slug)=>{if(slug&&slug!==sel)pickProject(slug);setDockMaxed(true);openDock("chat");}} onNewChat={startProviderChat}
            visible={visible} filter={filter} setFilter={setFilter} hiddenCount={hiddenCount}
            onCtx={(slug,x,y)=>setCtxMenu({slug,x,y})}/>
        :<>
      <div className="main">
        {/* Standalone menu screen: no project identity header and no project tab
            strip — it is not scoped to a project. Per-project automations live on
            the project's own Automations tab (see <Automations>). */}
        {editing&&draft
          ?<Editor draft={draft} setDraft={setDraft} isNew={isNew} saving={saving} saveErr={saveErr} savedTick={savedTick}/>
          :(p?(switching?<DetailSkeleton/>:<Detail key={p.slug} p={p} st={st} admin={isAdmin} projects={projects} noAgent={noAgent} onScanHealth={scanProjectHealth} onPatch={patchProject} onDelete={()=>deleteProject(p.slug)} onAskClaude={(prompt,tipId,tipTitle,choice)=>askClaudeInDock(p.slug,prompt,tipId,tipTitle,choice)}
              openCount={openCounts[p.slug]||0}
              onOpenCount={(slug,count)=>setOpenCounts(prev=>((prev&&prev[slug])===count?prev:{...prev,[slug]:count}))}
              nudgeTipId={nudge&&p&&nudge.slug===p.slug?nudge.tipId:null} onClearNudge={()=>setNudge(null)} onOpenTipChat={openTipChat} onOpenRunChat={openTaskRunChat}
              tabReq={detailTabReq}
              onRefetch={()=>fetch("/api/projects").then(r=>r.json()).then(setProjects).catch(()=>{})}
              startRun={started&&p&&started.slug===p.slug?started:null}/>):<div className="muted">{t("Select a project.")}</div>)}
      </div>
      {!showAuto&&<Dock p={p} open={dockOpen} projects={projects} onSwitchProject={pickProject} setOpen={setDockOpen} maxed={dockMaxed} setMaxed={setDockMaxed} tab={dockTab} setTab={setDockTab}
            usage={usage} ask={ask} openChat={openChat} openRun={openRun} newChatReq={newChatReq} runCmd={runCmd} onTipDone={(slug,tipId)=>setNudge({slug,tipId})}
            onTipStart={(slug,tipId,chatId)=>setStarted({slug,tipId,chatId,nonce:++startSeq.current})} onExpandTerminal={(mode)=>setTerm({slug:p&&p.slug,mode})}
            onRunCmd={runInTerminal} onChatSeen={markChatSeen} onChatRunning={markLocalChatRunning} onChatDone={markLocalChatDone} onAddNote={addMsgToNotes}
            localChatDone={localChatDone} visible={!narrow||mView==="chat"}
            onFocusChat={()=>{setPage("work");setDockOpen(true);setMView("chat");}}/>}
        </>}
    </div>
     </div>
    </div>
    {narrow&&<div className="mprojbar">
      <ProjectSwitcher projects={projects} sel={sel} st={st} openCounts={openCounts} totalCounts={totalCounts} chatCounts={chatCounts} onCtx={(slug,x,y)=>setCtxMenu({slug,x,y})} up
        onPick={(slug)=>pickProject(slug)}/>
    </div>}
    <nav className="mnav">
      <button className={page==="home"?"on":""} onClick={goHome}>
        <svg className="mic" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 10.6 12 3l9 7.6"/><path d="M5.2 9.4V19a1.4 1.4 0 0 0 1.4 1.4h3.4v-5.2h4v5.2h3.4A1.4 1.4 0 0 0 18.8 19V9.4"/></svg><span className="mlabel">{t("Home")}</span>
      </button>
      <button className={mView==="detail"&&!showAuto?"on":""} onClick={goTasks}>
        <svg className="mic" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4.6h6a1 1 0 0 1 1 1v.8h1.4A1.6 1.6 0 0 1 19 8v10.4a1.6 1.6 0 0 1-1.6 1.6H6.6A1.6 1.6 0 0 1 5 18.4V8a1.6 1.6 0 0 1 1.6-1.6H8v-.8a1 1 0 0 1 1-1z"/><path d="M9.2 12h5.6M9.2 15.4h3.8"/></svg><span className="mlabel">{t("Tasks")}</span>{(()=>{const ts=(p&&p.slug)||(projects[0]&&projects[0].slug);const n=ts?openCounts[ts]:0;return n>0?<span className="mcount" title={n+" open task"+(n>1?"s":"")+" in this project"}>{n}</span>:null;})()}
      </button>
      <button className={mView==="chat"&&!showAuto?"on":""} onClick={()=>{setShowAuto(false);setPage("work");setMView("chat");setDockOpen(true);}}>
        <svg className="mic" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 11.6a7.6 7.6 0 0 1-10.9 6.9L4 20l1.6-5.2A7.6 7.6 0 1 1 20.5 11.6z"/></svg><span className="mlabel">{t("Chat")}</span>{chatDone>0?<span className="mcount done" title={chatDone+" finished chat"+(chatDone>1?"s":"")+" you haven't opened yet"}>{chatDone}</span>:null}
      </button>
      <div className="mnav-notif"><NotificationsBell compact onSettings={()=>setShowNotifications(true)} onOpen={openTipChat}/></div>
      <div className="mnav-menu"><SettingsMenu variant="hamburger" active={showGithub||showAiSettings||showNotifications||showApiKeys}
        onGithub={()=>setShowGithub(true)}
        onAiSettings={()=>setShowAiSettings(true)}
        onApiKeys={()=>setShowApiKeys(true)}
        onNotifications={()=>setShowNotifications(true)}/></div>
    </nav>
    {term&&<TerminalModal projects={projects} initialSlug={term.slug} initialMode={term.mode}
      initialCmd={term.cmd?{cmd:term.cmd,nonce:term.nonce}:null} onClose={()=>setTerm(null)}/>}
    {showAiSettings&&<AiSettings onClose={()=>setShowAiSettings(false)} onOpen={(r)=>{
      if(!r||!r.slug||!projects.some(x=>x.slug===r.slug))return;
      setShowAiSettings(false);
      setSel(r.slug);setPage("work");setDockOpen(true);
      if(r.kind==="chat"){setDockTab("chat");setMView("chat");}
      else setMView("detail");
    }}/>}
    {showNotifications&&<NotificationsSettings onClose={()=>setShowNotifications(false)}/>}
    {showApiKeys&&<ApiKeys onClose={()=>setShowApiKeys(false)}/>}
    {showGithub&&<GithubPanel projects={projects} onClose={()=>setShowGithub(false)} onImported={(next)=>setProjects(next)}/>}
    {CLOUD&&agentSetup&&<CloudAgentSetup onConnected={(list)=>reconcileAgents(list)} onClose={()=>{setAgentSetup(false);fetch("/api/agents").then(r=>r.json()).then(d=>{if(d&&d.ok)reconcileAgents(d.agents||[]);}).catch(()=>{});}}/>}
    {CLOUD&&agentToast&&<AgentToast host={agentToast.host} n={agentToast.n} onClose={()=>setAgentToast(null)}/>}
    {CLOUD&&planLimit&&<PlanLimitModal plan={planInfo} onClose={()=>setPlanLimit(false)}/>}
    {CLOUD&&billingNotice&&planInfo&&planInfo.billingEnabled&&<BillingBanner state={billingNotice} onClose={()=>setBillingNotice(null)}/>}
    <CommandPalette open={cmdkOpen} onClose={()=>setCmdkOpen(false)} commands={commands}/>
    <ServerReconnect/>
    <RestartPrompt/>
   </div>);
}

// Edit a single text field in place (one-liner…) without opening the
// full editor. View mode shows the value + a hover ✎; click to swap in an input
// (Enter / blur saves) or textarea (⌘/Ctrl+Enter saves). Esc cancels.
function InlineText({value,placeholder,onSave,multiline,className,style}){
  const{lang}=useLang();
  const[editing,setEditing]=useState(false);
  const[val,setVal]=useState(value||"");
  useEffect(()=>{if(!editing)setVal(value||"");},[value,editing]);
  if(editing){
    const save=()=>{setEditing(false);if((val||"").trim()!==(value||"").trim())onSave((val||"").trim());};
    if(multiline)return(<div>
      <textarea dir="ltr" value={val} autoFocus onChange={e=>setVal(e.target.value)}
        onKeyDown={e=>{if((e.metaKey||e.ctrlKey)&&e.key==="Enter")save();else if(e.key==="Escape")setEditing(false);}}/>
      <div style={{marginTop:6,display:"flex",gap:8}}>
        <button className="btn sm primary" onClick={save}>{t("Save")}</button>
        <button className="btn sm" onClick={()=>setEditing(false)}>{t("Cancel")}</button>
      </div></div>);
    return <input dir="ltr" value={val} autoFocus onChange={e=>setVal(e.target.value)} onBlur={save}
      onKeyDown={e=>{if(e.key==="Enter")save();else if(e.key==="Escape")setEditing(false);}}/>;
  }
  const Tag=multiline?"div":"span";
  return <Tag className={"inline-wrap"+(className?" "+className:"")} style={style} dir="ltr">
    {value?value:<span className="muted">{placeholder}</span>}
    <button className="inline-ed" title={t("Edit")} onClick={()=>setEditing(true)}>✎</button>
  </Tag>;
}

// A stable accent colour for a project's header, derived from its slug so each
// project reads distinctly at a glance. The tint uses alpha over the theme's
// --bg, so it stays legible in both the light and dark themes.
function projAccent(slug){
  let h=0;const s=slug||"";
  for(let i=0;i<s.length;i++)h=(h*31+s.charCodeAt(i))>>>0;
  const hue=h%360;
  return{
    solid:`hsl(${hue} 68% 55%)`,
    tint:`hsla(${hue},72%,52%,.16)`,
    tint0:`hsla(${hue},72%,52%,0)`,
  };
}

// Build a targeted "Fix with AI" prompt from a project's live health snapshot, so
// the seeded chat knows exactly which infrastructure symptom to investigate.
function buildFixPrompt(p,h){
  const issues=[];
  if(h){
    if(h.reachable===false&&servingState(h)!==false)issues.push(`the site is not reachable (HTTP ${h.http_status||"no response"})`);
    if(servingState(h)===false)issues.push(`its URL answers HTTP ${h.http_status} — the host is up but is not serving this project (wrong vhost/route, or the domain is not pointed at it yet)`);
    if(h.port_listening===false)issues.push("its port is not listening");
    if(h.pm2_status&&!NOT_APPL(h.pm2_status)&&h.pm2_status!=="online")issues.push(`its PM2 process is "${h.pm2_status}"${h.restarts!=null?` with ${h.restarts} restarts`:""}`);
  }
  const problem=issues.length?issues.join("; "):"a health/infrastructure issue is suspected";
  const name=p.nameEn||p.nameHe||p.slug;
  return `The project "${name}" (slug: ${p.slug}${p.dir?`, path: ${p.dir}`:""}) has an infrastructure/health problem: ${problem}. `+
    `Investigate the root cause — check the PM2 process status and logs, whether the port is listening, and whether the site responds — then fix it and verify the service is healthy again.`;
}

// Slugs whose "new project details" run has already auto-fired this session.
// Module-scoped so it survives Detail's per-slug remount (key={p.slug}).
const AUTO_FIRED_DETAILS=new Set();
// Placeholder shown for the brief window while a project switch is settling.
// Mirrors Detail's shape (identity header, tab strip, content block) with
// shimmer blocks so the swap reads as a load, not an empty flash.
function DetailSkeleton(){
  return(
    <div className="detail detail-skel" aria-hidden="true">
      <div className="dhead">
        <span className="skel skel-avatar"/>
        <div className="skel-lines">
          <span className="skel skel-line" style={{width:"42%"}}/>
          <span className="skel skel-line sm" style={{width:"24%"}}/>
        </div>
      </div>
      <div className="skel-tabs">
        <span className="skel skel-tab"/><span className="skel skel-tab"/><span className="skel skel-tab"/><span className="skel skel-tab"/>
      </div>
      <div className="skel-body">
        <span className="skel skel-block" style={{height:"96px"}}/>
        <span className="skel skel-line" style={{width:"88%"}}/>
        <span className="skel skel-line" style={{width:"72%"}}/>
        <span className="skel skel-line" style={{width:"80%"}}/>
        <span className="skel skel-block" style={{height:"140px"}}/>
      </div>
    </div>
  );
}

function Detail({p,st,admin,onPatch,onDelete,onAskClaude,nudgeTipId,onClearNudge,onOpenTipChat,onOpenRunChat,onOpenCount,openCount,startRun,projects,noAgent,onScanHealth,onRefetch,tabReq}){
  const{lang}=useLang();
  // Tab-strip badges: open tasks (kept live by AiTips → onOpenCount) and the
  // number of automation rules that actually run for this project. The
  // automation count is fetched here so the badge is right before the tab is
  // ever opened; <Automations> keeps its own copy for the list itself.
  const[autoCount,setAutoCount]=useState(null);
  useEffect(()=>{
    let dead=false;
    function load(){
      // Automations badge = active rules + pending scheduled prompts (each a
      // running automation that will fire on its schedule).
      Promise.all([
        fetch("/api/automations?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).catch(()=>null),
        fetch("/api/scheduled-chats?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).catch(()=>null),
      ]).then(([d,s])=>{
        if(dead)return;
        // mirror the Automations tab: inactive cross-project rules aren't shown here
        const rules=(d&&d.ok&&Array.isArray(d.automations))
          ?d.automations.filter(a=>!((a.target==="*"||a.target===AUTO_TARGET_MULTI)&&!a.enabled)).length:0;
        const sched=(s&&s.ok&&Array.isArray(s.scheduledChats))
          ?s.scheduledChats.filter(x=>x.status==="pending").length:0;
        setAutoCount(rules+sched);
      }).catch(()=>{});
    }
    setAutoCount(null);load();
    const i=setInterval(load,30000);
    return()=>{dead=true;clearInterval(i);};
  },[p.slug]);
  const h=st&&st.projects&&st.projects[p.slug];
  const hh=health(p.slug,st);
  const[healthScanning,setHealthScanning]=useState(false);
  // No scanning and no results until the project has a real website link
  // (typed by the user or discovered by the AI details run from the code).
  const siteConnected=hasSiteUrl(p);
  const scanHealthNow=()=>{if(healthScanning||!siteConnected)return;setHealthScanning(true);Promise.resolve(onScanHealth&&onScanHealth(p.slug)).finally(()=>setTimeout(()=>setHealthScanning(false),400));};
  // Main project tabs: Info · Plan · Tasks · Automations. The identity header
  // (title, status/health badges) stays pinned above the strip; only the section
  // below the tabs swaps. Detail is remounted per slug (key=slug), so the tab
  // resets to Info on every project switch — the expected default.
  const[tab,setTab]=useState("info");
  // Existing-project settings live directly on the Info tab. Keep a local draft
  // so typing is immediate, then persist the complete editable field set after a
  // short pause. There is deliberately no separate edit-page state anymore.
  const[infoDraft,setInfoDraft]=useState(()=>JSON.parse(JSON.stringify(p)));
  const[infoSaving,setInfoSaving]=useState(false);
  const[infoSaved,setInfoSaved]=useState(false);
  const[infoErr,setInfoErr]=useState("");
  const infoSkip=useRef(true);
  const infoSaveSeq=useRef(0);
  const infoDraftRef=useRef(infoDraft);
  const infoDirtyRef=useRef(false);
  const onPatchRef=useRef(onPatch);
  infoDraftRef.current=infoDraft;
  onPatchRef.current=onPatch;
  function updateInfoDraft(next){
    infoDirtyRef.current=true;
    setInfoDraft(next);
  }
  // A quick project switch must not discard the final keystroke still waiting in
  // the debounce window. Flush the latest draft when this Detail unmounts.
  useEffect(()=>()=>{if(infoDirtyRef.current)onPatchRef.current(p.slug,editableProjectPatch(infoDraftRef.current));},[]);
  useEffect(()=>{
    if(infoSkip.current){infoSkip.current=false;return;}
    const seq=++infoSaveSeq.current;
    setInfoSaved(false);setInfoErr("");
    const timer=setTimeout(()=>{
      setInfoSaving(true);
      Promise.resolve(onPatch(p.slug,editableProjectPatch(infoDraft)))
        .then(r=>{
          if(seq!==infoSaveSeq.current)return;
          if(r&&r.ok===false)throw new Error(r.error||"unknown error");
          infoDirtyRef.current=false;
          setInfoSaved(true);
        })
        .catch(e=>{if(seq===infoSaveSeq.current)setInfoErr(t("Save failed:")+" "+String(e&&e.message||e));})
        .finally(()=>{if(seq===infoSaveSeq.current)setInfoSaving(false);});
    },700);
    return()=>clearTimeout(timer);
  },[infoDraft]);
  // The Goals tab edits its one field inline (its own tab) rather than opening the
  // whole-project Info edit form — goalsDraft holds the in-progress text.
  const[editingGoals,setEditingGoals]=useState(false);
  const[goalsDraft,setGoalsDraft]=useState("");
  const[goalsSaving,setGoalsSaving]=useState(false);
  const[goalsErr,setGoalsErr]=useState("");
  function startEditGoals(){setGoalsDraft(String(p.goals||""));setGoalsErr("");setEditingGoals(true);}
  function saveGoals(){
    if(goalsSaving)return;
    setGoalsSaving(true);setGoalsErr("");
    Promise.resolve(onPatch(p.slug,{goals:goalsDraft}))
      .then(r=>{
        if(r&&r.ok===false)throw new Error(r.error||"unknown error");
        return fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).catch(()=>{}).then(()=>setEditingGoals(false));
      })
      .catch(e=>setGoalsErr(t("Save failed:")+" "+String(e&&e.message||e))) // keep the editor open so the edit isn't lost
      .finally(()=>setGoalsSaving(false));
  }
  // Roadmap edits the same way as Goals — its own inline editor, sitting beside
  // it in the Plan tab. Saving also best-effort re-publishes PROJECT_STATE.md.
  const[editingRoadmap,setEditingRoadmap]=useState(false);
  const[roadmapDraft,setRoadmapDraft]=useState("");
  const[roadmapSaving,setRoadmapSaving]=useState(false);
  const[roadmapErr,setRoadmapErr]=useState("");
  function startEditRoadmap(){setRoadmapDraft(String(p.roadmap||""));setRoadmapErr("");setEditingRoadmap(true);}
  function saveRoadmap(){
    if(roadmapSaving)return;
    setRoadmapSaving(true);setRoadmapErr("");
    Promise.resolve(onPatch(p.slug,{roadmap:roadmapDraft}))
      .then(r=>{
        if(r&&r.ok===false)throw new Error(r.error||"unknown error");
        return fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).catch(()=>{}).then(()=>setEditingRoadmap(false));
      })
      .catch(e=>setRoadmapErr(t("Save failed:")+" "+String(e&&e.message||e))) // keep the editor open so the edit isn't lost
      .finally(()=>setRoadmapSaving(false));
  }
  // Readme path + preview. The path is just a project field (patched on blur);
  // the View / Close view toggle fetches on open and hides the preview on close.
  const[readmePathDraft,setReadmePathDraft]=useState(String(p.readmePath||"README.md"));
  const[readmeView,setReadmeView]=useState(null); // {content,error} | null
  function saveReadmePath(){
    Promise.resolve(onPatch(p.slug,{readmePath:readmePathDraft}))
      .then(()=>fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).catch(()=>{}));
  }
  function viewReadme(){
    if(readmeView){setReadmeView(null);return;}
    fetch(`/api/projects/${p.slug}/readme`).then(async r=>{
      const d=await r.json();if(!r.ok)throw new Error(d.error||("HTTP "+r.status));return d;
    })
      .then(d=>setReadmeView({content:d.content,error:d.error}))
      .catch(e=>setReadmeView({content:"",error:(e&&e.message)||"Could not load the readme."}));
  }
  const[claudePathDraft,setClaudePathDraft]=useState(String(p.claudePath||"CLAUDE.md"));
  const[claudeView,setClaudeView]=useState(null);
  function saveClaudePath(){
    Promise.resolve(onPatch(p.slug,{claudePath:claudePathDraft}))
      .then(()=>fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).catch(()=>{}));
  }
  function viewClaude(){
    if(claudeView){setClaudeView(null);return;}
    fetch(`/api/projects/${p.slug}/claude`).then(async r=>{
      const d=await r.json();if(!r.ok)throw new Error(d.error||("HTTP "+r.status));return d;
    })
      .then(d=>setClaudeView({content:d.content,error:d.error}))
      .catch(e=>setClaudeView({content:"",error:(e&&e.message)||"Could not load CLAUDE.md."}));
  }
  // Project State (Google Drive) — publishes PROJECT_STATE.md on demand; the
  // automation agent reads that file. Success shows the timestamp + destination
  // returned by the server (no extra load-on-mount endpoint — see Task 4 Step 6).
  const[stateSync,setStateSync]=useState(null); // {syncedAt,dest} | null
  const[syncing,setSyncing]=useState(false);
  function syncNow(){
    if(syncing)return;
    setSyncing(true);
    fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).then(r=>r.json())
      .then(d=>{if(d&&d.ok)setStateSync({syncedAt:d.syncedAt,dest:d.dest});
                else setStateSync({error:(d&&d.error)||"Drive sync failed."});})
      .catch(()=>setStateSync({error:"Drive sync failed."}))
      .finally(()=>setSyncing(false));
  }
  // A task nudge (notification / "go look at this task") must land on the Tasks
  // tab, or the highlighted tip would be hidden behind another tab.
  useEffect(()=>{if(nudgeTipId)setTab("tasks");},[nudgeTipId]);
  // Mobile bottom-nav "Tasks" (and any external tab request) drives the tab here.
  useEffect(()=>{if(tabReq&&tabReq.tab)setTab(tabReq.tab);},[tabReq&&tabReq.nonce]);
  // "New project details": run the AI over the project's repo + live status and
  // fill in its infra fields (URLs, hosting, stack, DB…) server-side. The run is
  // detached (survives disconnects); on the final {event:"details"} we refetch the
  // project list so the merged fields render. detailsBusy guards double-runs.
  const[detailsBusy,setDetailsBusy]=useState(false);
  const[detailsMsg,setDetailsMsg]=useState("");
  const[detailsErr,setDetailsErr]=useState("");
  const detailsAcRef=useRef(null); // aborts the details ndjson stream if this Detail unmounts (project switch) mid-run
  function runDetails(){
    if(detailsBusy)return;
    const ac=new AbortController();detailsAcRef.current=ac;
    setDetailsErr("");setDetailsMsg("generating details…");setDetailsBusy(true);
    let sawErr=false; // a local flag survives the async chain (a state read here would be stale)
    const body={slug:p.slug,...loadAiUsePrefs().details};
    fetch("/api/projects/details",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body),signal:ac.signal})
      .then(res=>{if(!res.ok)return res.json().then(d=>{throw new Error((d&&d.error)||("HTTP "+res.status));});return res;})
      .then(res=>readNdjson(res,ev=>{
        if(!ev||ev.type!=="hub")return;
        if(ev.event==="error"){sawErr=true;setDetailsErr(ev.error||"Details run failed.");}
        if(ev.event==="details"){setDetailsMsg("done — refreshing…");if(onRefetch)onRefetch();}
      }))
      .catch(e=>{if(ac.signal.aborted)return;if(!sawErr)setDetailsErr("Details run failed: "+String(e&&e.message||e));})
      .finally(()=>{if(ac.signal.aborted)return;setDetailsBusy(false);setDetailsMsg("");});
  }
  useEffect(()=>()=>{if(detailsAcRef.current)detailsAcRef.current.abort();},[]);
  // Auto-run ONCE when a project first has a repo or working folder but has never
  // been filled (no detailsFilledAt, stamped server-side on success). Detail is
  // remounted (key=slug) on every project switch, so the "already auto-fired" guard
  // must live at module scope (AUTO_FIRED_DETAILS) to survive remounts — a per-mount
  // ref would let navigating away and back re-fire the run. Once a slug auto-fires it
  // won't auto-fire again this session (even on failure); use the button to retry.
  useEffect(()=>{
    const connected=!!(String(p.repo||"").trim()||String(p.dir||"").trim());
    if(!connected||p.detailsFilledAt)return;
    if(AUTO_FIRED_DETAILS.has(p.slug))return;
    AUTO_FIRED_DETAILS.add(p.slug);
    runDetails();
  // eslint-disable-next-line
  },[p.slug,p.repo,p.dir,p.detailsFilledAt]);
  // "Auto-fill plan from files": run the AI over the project's own docs (README,
  // docs/**, ROADMAP…) and fill Goal/Roadmap server-side. Detached run; on the
  // final {event:"plan"} we refetch so the merged fields render. planBusy guards.
  const[planBusy,setPlanBusy]=useState(false);
  const[planMsg,setPlanMsg]=useState("");
  const[planErr,setPlanErr]=useState("");
  const planAcRef=useRef(null); // aborts the plan-fill ndjson stream if this Detail unmounts mid-run
  function runPlanFill(){
    if(planBusy)return;
    const hasPlan=String(p.goals||"").trim()||String(p.roadmap||"").trim();
    if(hasPlan&&!window.confirm(t("This project's plan is already filled. Re-write Goals and Roadmap based on the current project files?")))return;
    const ac=new AbortController();planAcRef.current=ac;
    setPlanErr("");setPlanMsg(t("reading project files…"));setPlanBusy(true);
    let sawErr=false;
    const brief=loadPlanFillBrief();
    const body={slug:p.slug,...loadAiUsePrefs().details,...(brief&&brief.trim()?{brief}:{})};
    fetch("/api/projects/plan-fill",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body),signal:ac.signal})
      .then(res=>{if(!res.ok)return res.json().then(d=>{throw new Error((d&&d.error)||("HTTP "+res.status));});return res;})
      .then(res=>readNdjson(res,ev=>{
        if(!ev||ev.type!=="hub")return;
        if(ev.event==="error"){sawErr=true;setPlanErr(ev.error||"Plan fill failed.");}
        if(ev.event==="plan"){setPlanMsg(t("done — refreshing…"));if(onRefetch)onRefetch();}
      }))
      .catch(e=>{if(ac.signal.aborted)return;if(!sawErr)setPlanErr("Plan fill failed: "+String(e&&e.message||e));})
      .finally(()=>{if(ac.signal.aborted)return;setPlanBusy(false);setPlanMsg("");});
  }
  useEffect(()=>()=>{if(planAcRef.current)planAcRef.current.abort();},[]);
  // PRD: the single upstream source of intent (goals/roadmap/todos derive from it
  // via the plan fill). Inline Edit/Save/Cancel like Roadmap; "Generate PRD" runs
  // the agent over the repo (detached run, terminal {event:"prd"} -> refetch).
  const[editingPrd,setEditingPrd]=useState(false);
  const[prdDraft,setPrdDraft]=useState("");
  const[prdSaving,setPrdSaving]=useState(false);
  const[prdErr,setPrdErr]=useState("");
  function startEditPrd(){setPrdDraft(String(p.prd||""));setPrdErr("");setEditingPrd(true);}
  function savePrd(){
    if(prdSaving)return;
    setPrdSaving(true);setPrdErr("");
    Promise.resolve(onPatch(p.slug,{prd:prdDraft}))
      .then(r=>{
        if(r&&r.ok===false)throw new Error(r.error||"unknown error");
        return fetch(`/api/projects/${p.slug}/sync-state`,{method:"POST"}).catch(()=>{}).then(()=>setEditingPrd(false));
      })
      .catch(e=>setPrdErr(t("Save failed:")+" "+String(e&&e.message||e))) // keep the editor open so the edit isn't lost
      .finally(()=>setPrdSaving(false));
  }
  const[prdBusy,setPrdBusy]=useState(false);
  const[prdMsg,setPrdMsg]=useState("");
  const[prdRunErr,setPrdRunErr]=useState("");
  const prdAcRef=useRef(null); // aborts the prd-fill ndjson stream if this Detail unmounts mid-run
  function runPrdFill(){
    if(prdBusy)return;
    if(String(p.prd||"").trim()&&!window.confirm(t("This project already has a PRD. Regenerate it from the current project files and overwrite the stored PRD?")))return;
    const ac=new AbortController();prdAcRef.current=ac;
    setPrdRunErr("");setPrdMsg(t("reading project files…"));setPrdBusy(true);
    let sawErr=false;
    const body={slug:p.slug,...loadAiUsePrefs().details};
    fetch("/api/projects/prd-fill",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body),signal:ac.signal})
      .then(res=>{if(!res.ok)return res.json().then(d=>{throw new Error((d&&d.error)||("HTTP "+res.status));});return res;})
      .then(res=>readNdjson(res,ev=>{
        if(!ev||ev.type!=="hub")return;
        if(ev.event==="error"){sawErr=true;setPrdRunErr(ev.error||"PRD generation failed.");}
        if(ev.event==="prd"){setPrdMsg(t("done - refreshing…"));if(onRefetch)onRefetch();}
      }))
      .catch(e=>{if(ac.signal.aborted)return;if(!sawErr)setPrdRunErr("PRD generation failed: "+String(e&&e.message||e));})
      .finally(()=>{if(ac.signal.aborted)return;setPrdBusy(false);setPrdMsg("");});
  }
  useEffect(()=>()=>{if(prdAcRef.current)prdAcRef.current.abort();},[]);
  // "Sync from file": read an existing PRD.md (repo root or docs/) through the
  // agent and store it as p.prd. Read-only: nothing is written back to the repo.
  const[prdSyncing,setPrdSyncing]=useState(false);
  const[prdSyncErr,setPrdSyncErr]=useState("");
  function syncPrdFromFile(){
    if(prdSyncing||prdBusy)return;
    setPrdSyncErr("");setPrdSyncing(true);
    const call=preview=>fetch("/api/projects/prd-sync",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,preview})})
      .then(r=>r.json().catch(()=>({})).then(j=>{if(!r.ok||!j.ok)throw new Error(j.error||("HTTP "+r.status));return j;}));
    const cur=String(p.prd||"").trim();
    // A different Hub PRD exists: preview the file first and confirm before overwriting it.
    (cur?call(true).then(j=>String(j.content||"").trim()===cur||window.confirm(t("Replace the Hub PRD with the file from the repo?"))):Promise.resolve(true))
      .then(ok=>ok?call(false).then(()=>{if(onRefetch)onRefetch();}):null)
      .catch(e=>setPrdSyncErr(t("Sync failed:")+" "+String(e&&e.message||e)))
      .finally(()=>setPrdSyncing(false));
  }
  const prdStatus=Array.isArray(p.prdStatus)?p.prdStatus.filter(x=>x&&String(x.text||"").trim()):[];
  const prdDoneCount=prdStatus.filter(x=>x.status==="done").length;
  // Unconditional hook calls (never inside the editing/empty ternaries below) —
  // goals/roadmap are STABLE user content, safe to lazily machine-translate.
  const goalsText=useTranslated(p.goals);
  const roadmapText=useTranslated(p.roadmap);
  // Plan tab collapse state (per project per section, see usePlanCollapse).
  // A long PRD starts collapsed; Readme / CLAUDE.md / Project State are
  // reference panels and start collapsed; everything else starts open.
  const prdText=String(p.prd||"").trim();
  const prdLong=prdText.length>1200||prdText.split("\n").length>30;
  const plan=usePlanCollapse(p.slug,{prd:!prdLong,readme:false,claude:false,state:false});
  const planIds=["prd",...(prdStatus.length?["reqs"]:[]),"goal","roadmap","todos","readme","claude",...(admin?["state"]:[]),"notes"];
  const planAnyOpen=planIds.some(plan.isOpen);
  const[prdFull,setPrdFull]=useState(false);
  const goalAi=usePlanAi("goal",{name:p.name,context:planContext(p),onApply:txt=>{setGoalsDraft(txt);setEditingGoals(true);plan.setOpen("goal",true);}});
  const roadmapAi=usePlanAi("roadmap",{name:p.name,context:planContext(p),onApply:txt=>{setRoadmapDraft(txt);setEditingRoadmap(true);plan.setOpen("roadmap",true);}});
  const prdFilledTs=Date.parse(p.prdFilledAt||"")||0;
  const prdSyncedTs=Date.parse(p.prdSyncedAt||"")||0;
  const prdMeta=!prdText?t("Empty")
    :(p.prdSource&&prdSyncedTs>=prdFilledTs)?t("Synced from {file}",{file:p.prdSource})+(p.prdSyncedAt?" - "+agoLabel(Date.parse(p.prdSyncedAt)):"")
    :prdFilledTs?t("Generated {when}",{when:agoLabel(prdFilledTs)})
    :t("Stored in the Hub");
  const goalsLine=firstLine(goalsText);
  const roadmapLine=firstLine(roadmapText);
  return(
   <div>
    <div className="detail-head">
    <div className="proj-sticky" style={{display:"flex",alignItems:"flex-start",gap:12,paddingTop:16,paddingBottom:16}}>
      <div className="proj-head-main" style={{flex:1,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
        <h2 style={{margin:0,fontSize:32,fontWeight:800,letterSpacing:"-.6px",lineHeight:1.1}} dir={dirOf(p.nameHe)}>{p.nameHe} {p.nameEn&&p.nameEn!==p.nameHe&&(p.urls&&p.urls[0]&&p.urls[0].url
          ?<a className="project-head-link" href={p.urls[0].url} target="_blank" rel="noopener noreferrer" dir="ltr">/ {p.nameEn}</a>
          :<span className="project-head-link muted">/ {p.nameEn}</span>)}</h2>
        {primaryUrl(p)&&<a className="project-head-link" href={primaryUrl(p)} target="_blank" rel="noopener noreferrer" dir="ltr" title={primaryUrl(p)}>↗ {primaryUrl(p).replace(/^https?:\/\/(www\.)?/,"").replace(/\/$/,"")}</a>}
        <div className="proj-meta" style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{display:"flex",gap:8,alignItems:"center",flexWrap:"nowrap"}}>
            <span className="badge"><span className="dot" title={"Health: "+hh.t} style={{background:hh.c}}/> {t("Live health:")} <b style={{color:hh.c}}>{hh.t}</b>{primaryUrl(p)&&<> · <a href={primaryUrl(p)} target="_blank" rel="noopener noreferrer" dir="ltr" style={{color:"inherit",textDecoration:"underline"}}>{t("visit")} ↗</a></>}</span>
          </span>
        </div>
      </div>
    </div>
    {(detailsErr||detailsMsg)&&<div style={{margin:"-6px 0 8px"}}>{detailsErr
      ?(/No agent is connected/i.test(detailsErr)
        ?<span className="muted" style={{color:"var(--danger)",fontSize:12}}>{detailsErr.replace(/\s*Install the Heygents agent on your machine\.?/i,"").trim()}{" "}<a href="#" onClick={e=>{e.preventDefault();openAgentSetup();}} style={{color:"var(--accent)",cursor:"pointer",fontWeight:600}}>{t("Install Heygents")}</a></span>
        :<span className="muted" style={{color:"var(--danger)",fontSize:12}}>{detailsErr}</span>)
      :<span className="muted" style={{fontSize:12}}>{detailsMsg}</span>}</div>}

    <div className="ptabs" role="tablist">
      {[["info",t("Info")],["plan",t("Plan")],["tasks",t("Tasks")],["automations",t("Automations")]].map(([id,label])=>{
        const n=id==="tasks"?(openCount||0):id==="automations"?(autoCount||0):0;
        const nTitle=id==="tasks"?n+" open task"+(n===1?"":"s"):n+" automation"+(n===1?"":"s");
        return(
        <button key={id} type="button" role="tab" aria-selected={tab===id}
          className={"ptab"+(tab===id?" on":"")} onClick={()=>setTab(id)}>{label}
          {n>0&&<span className="ptab-n" title={nTitle}>{n}</span>}</button>);})}
    </div>
    </div>

    {tab==="info"&&<div>
    <div className="sec">
     <div className="live"><div className="infra">
      <div className="infra-col">
        <div className="infra-h"><span className="dot" style={{background:siteConnected?hh.c:"var(--warn)"}}/>{t("Live status")} {siteConnected&&<button className="btn xs" style={{marginInlineStart:"auto"}} disabled={healthScanning} title={t("Live health check for THIS project only — probes its URL reachability, port, and PM2/Docker status now (not an LLM Deep Scan)")} onClick={scanHealthNow}>{healthScanning?"⟳ Scanning…":"⟳ Scan now"}</button>}</div>
       {!siteConnected?(<div className="muted" style={{fontSize:13}}>
        {t("No website connected yet — add a website link (or let the agent detect one from the project files) to run live status checks.")}
       </div>):!h?(<div className="muted" style={{fontSize:13}}>
        {p.isLocal?(noAgent?"Not monitored yet — install the agent to probe this project on your machine.":"Not monitored yet — click ⟳ Scan now to probe it through your agent."):"Not monitored from this server (local / external)."}
        {noAgent&&p.isLocal&&<div style={{marginTop:8}}><InstallAgentBtn label="Install the agent to monitor this"/></div>}
       </div>):(<>
        {/* Reachable = the origin answered. A 404/410 answer is NOT this project
            being served, so it renders amber with the reason, never a green ✓. */}
        <div className="irow"><span className="ik">{t("Reachable")}</span>{(()=>{
          if(h.reachable==null)return<span className="iv">—</span>;
          if(h.reachable===false)return<span className="iv" style={{color:"var(--err)"}}>{"✗ "+(h.http_status||"unreachable")}</span>;
          const srv=servingState(h);
          if(srv===false)return<span className="iv" style={{color:"var(--warn)"}} title={t("The host answered, but not with this project — check the vhost/route or the DNS record.")}>{"⚠ "+h.http_status+" "+t("not serving")}</span>;
          if(srv==null)return<span className="iv" style={{color:"var(--warn)"}} title={t("The host answered, but the response does not confirm this project is being served.")}>{"⚠ "+(h.http_status||"")+" "+t("unverified")}</span>;
          return<span className="iv" style={{color:"var(--ok)"}}>{"✓ "+(h.http_status||"")}</span>;
        })()}</div>
        <div className="irow"><span className="ik">{t("Port")}</span><span className="iv" style={h.port_listening==null?null:{color:h.port_listening?"var(--ok)":"var(--err)"}}>{h.port_listening==null?"—":(h.port_listening?"listening ✓":"not listening ✗")}</span></div>
        {/* "n/a" / "not found" mean this project simply isn't managed by PM2 or
            Docker — that is not a failure, so render it neutral, never red. */}
        {h.pm2_status!=null&&<div className="irow"><span className="ik">PM2</span><span className="iv" style={NOT_APPL(h.pm2_status)?null:{color:h.pm2_status==="online"?"var(--ok)":"var(--err)"}}>{NOT_APPL(h.pm2_status)?"—":h.pm2_status}</span></div>}
        {h.container_status!=null&&<div className="irow"><span className="ik">Docker</span><span className="iv" style={NOT_APPL(h.container_status)?null:{color:h.container_status==="running"?"var(--ok)":"var(--err)"}}>{NOT_APPL(h.container_status)?"—":h.container_status}</span></div>}
        <div className="irow"><span className="ik">{t("Checked")}</span><span className="iv mono">{(()=>{const t2=st&&(st.projects_checked_at||st.updated_at);return t2?new Date(t2).toLocaleString("en-GB").replace(", "," "):"—";})()}</span></div>
        {hh.c!=="var(--ok)"&&<div style={{marginTop:10}}>
         <button type="button" className="btn xs" title={t("Open a chat pre-loaded with a prompt to investigate and fix this project's infrastructure / health")}
          onClick={()=>onAskClaude&&onAskClaude(buildFixPrompt(p,h),undefined,"Fix infrastructure issue")}>🤖 Fix with AI</button>
        </div>}
       </>)}
      </div>
      <div className="infra-col">
       <div className="irow"><span className="ik">{t("Repo")}</span><span className="iv" title={p.repo||""}>{p.repo?<a href={p.repo} target="_blank" className="mono">{p.repo.replace(/^https?:\/\/(www\.)?github\.com\//,"").replace(/^https?:\/\//,"").replace(/\.git$/,"")}{p.repoBranch?` (${p.repoBranch})`:""}</a>:"—"}
        </span></div>
       {p.db&&p.db.studioUrl&&<div className="irow"><span className="ik">{t("Studio")}</span><a className="studio-link" href={p.db.studioUrl} target="_blank">{t("Open Studio ↗")}</a></div>}
       {/* Git management (cloud only): every op runs on the tenant's agent, so
           it needs a linked repo — the panel itself handles "not cloned yet". */}
       {CLOUD&&p.repo&&<GitPanel p={p} onRefetch={onRefetch}/>}
      </div>
     </div></div>
    </div>

    <div className="sec project-fields">
      <ProjectFields draft={infoDraft} setDraft={updateInfoDraft}/>
      <div className="toolbar project-fields-status">
        <span className="muted">
          {infoSaving?t("Saving…"):infoErr?"":infoSaved?t("Saved ✓"):t("Auto-saves as you type")}
        </span>
      </div>
      {infoErr&&<div className="err">{infoErr}</div>}
    </div>

    <div className="sec danger-zone">
      <h3>{t("Danger zone")}</h3>
      <button type="button" className="btn sm danger" onClick={onDelete}>{t("Delete project")}</button>
    </div>

    </div>}

    {tab==="plan"&&<div className="plan-tab">
    <div className="plan-toolbar">
      <button type="button" className="btn sm primary" disabled={planBusy} title={t("Read this project's README and docs, then auto-fill Goal, Roadmap and To-Do list")} onClick={runPlanFill}>{planBusy?"⟳ "+t("Working…"):"✨ "+t("Auto-fill from project files")}</button>
      <PlanFillSettings/>
      {(planErr||planMsg)&&(planErr
        ? <span className="muted plan-toolbar-msg" style={{color:"var(--danger)"}} dir="ltr">{planErr}</span>
        : <span className="muted plan-toolbar-msg" dir="ltr">{planMsg}</span>)}
      <button type="button" className="btn sm plan-collapse-all" aria-pressed={!planAnyOpen}
        onClick={()=>plan.setAll(planIds,!planAnyOpen)}>{planAnyOpen?t("Collapse all"):t("Expand all")}</button>
    </div>

    <PlanSection id="prd" title={t("PRD")} meta={prdMeta} open={plan.isOpen("prd")} onToggle={()=>plan.toggle("prd")} empty={!prdText}
      menuLabel={t("More PRD actions")}
      actions={!editingPrd&&<button type="button" className={"btn sm"+(String(p.prd||"").trim()?"":" primary")} disabled={prdSyncing||prdBusy}
                title={t("Read PRD.md from the repo root or docs/ and store it in the Hub (read-only, the file is not changed)")}
                onClick={syncPrdFromFile}>{prdSyncing?"⟳ "+t("Syncing…"):"⇣ "+t("Sync from file")}</button>}
      menu={editingPrd?null:[
        {label:prdBusy?"⟳ "+t("Working…"):(prdText?"↻ "+t("Regenerate"):"✨ "+t("Generate PRD")),disabled:prdBusy||prdSyncing,
         title:t("Read this project's files and draft a PRD (writes PRD.md only if the repo has none)"),onClick:runPrdFill},
        {label:"✎ "+t("Edit PRD"),onClick:()=>{startEditPrd();plan.setOpen("prd",true);}},
      ]}
      status={(prdSyncErr||prdRunErr||prdMsg)&&<>
        {prdSyncErr&&<span className="muted" style={{color:"var(--danger)"}} dir="ltr">{prdSyncErr}</span>}
        {(prdRunErr||prdMsg)&&(prdRunErr
          ? <span className="muted" style={{color:"var(--danger)"}} dir="ltr">{prdRunErr}</span>
          : <span className="muted" dir="ltr">{prdMsg}</span>)}
      </>}>
      {editingPrd
        ? <>
            <textarea dir={dirOf(prdDraft)} autoFocus rows={16} className="mono" style={{width:"100%",fontSize:13,lineHeight:1.6}}
              value={prdDraft} onChange={e=>setPrdDraft(e.target.value)}
              placeholder={t("## Problem\n…\n\n## Requirements\n1. …")}/>
            <div className="muted" style={{fontSize:12,marginTop:6}}>{p.prdSource
              ? t("Saved in the Hub only. {file} in the repo is not changed.",{file:p.prdSource})
              : t("Saved in the Hub only. No file in the repo is changed.")}</div>
            {prdErr&&<div className="err" style={{marginTop:8}}>{prdErr}</div>}
            <div className="psec-edit-acts">
              <button type="button" className="btn sm primary" disabled={prdSaving} onClick={savePrd}>{prdSaving?t("Saving…"):t("Save")}</button>
              <button type="button" className="btn sm" disabled={prdSaving} onClick={()=>{setEditingPrd(false);setPrdErr("");}}>{t("Cancel")}</button>
            </div>
          </>
        : prdText
          ? <>
              <div className={"psec-clamp"+(prdLong&&!prdFull?" clamped":"")}>
                <Md className="readme-preview prd-view" dir={dirOf(p.prd)} text={p.prd}/>
              </div>
              {prdLong&&<button type="button" className="btn xs psec-more" aria-expanded={prdFull} onClick={()=>setPrdFull(v=>!v)}>{prdFull?t("Show less"):t("Show more")}</button>}
            </>
          : <div className="muted psec-empty">{t("No PRD yet.")} {t("Sync PRD.md from the repo, or generate one from the project files.")}</div>}
    </PlanSection>

    {prdStatus.length>0&&<PlanSection id="reqs" title={t("Requirements status")} open={plan.isOpen("reqs")} onToggle={()=>plan.toggle("reqs")}
      meta={t("{done}/{total} done",{done:prdDoneCount,total:prdStatus.length})} progress={prdDoneCount/prdStatus.length}>
      <ul className="prd-req-list">
        {prdStatus.map((r,i)=>{
          const st=r.status==="done"||r.status==="in-progress"?r.status:"planned";
          const tone=st==="done"?"tone-ok":st==="in-progress"?"tone-warn":"tone-muted";
          return <li key={i} className="prd-req">
            <span className={"chip prd-req-chip "+tone}>{t(st==="done"?"done":st==="in-progress"?"in progress":"planned")}</span>
            <span className="prd-req-text" dir={dirOf(r.text)}>{r.text}</span>
          </li>;
        })}
      </ul>
    </PlanSection>}

    <PlanSection id="goal" title={t("Goal")} open={plan.isOpen("goal")} onToggle={()=>plan.toggle("goal")}
      meta={goalsLine||t("Empty")} metaDir={goalsLine?dirOf(goalsLine):undefined} empty={!String(p.goals||"").trim()}
      menuLabel={t("More Goal actions")}
      actions={!editingGoals&&<button type="button" className="btn sm" onClick={()=>{startEditGoals();plan.setOpen("goal",true);}}>✎ {t("Edit")}</button>}
      menu={editingGoals?null:[
        {label:goalAi.busy?"⟳ "+t("Generating…"):"✨ "+t("Generate with AI"),disabled:goalAi.busy,onClick:goalAi.generate},
        {label:"⚙ "+t("AI settings"),onClick:goalAi.openSettings},
      ]}>
      {editingGoals
        ? <>
            <textarea dir="ltr" autoFocus rows={8} style={{width:"100%",fontSize:14,lineHeight:1.6}}
              value={goalsDraft} onChange={e=>setGoalsDraft(e.target.value)}
              placeholder={t("e.g. Launch a paid tier by Q4. Support team accounts. Reach 1k weekly active users.")}/>
            <div className="muted" style={{fontSize:12,marginTop:4}}>{t("These feed the Deep Scan's Development & Upgrade/Feature ideas.")}</div>
            {goalsErr&&<div className="err" style={{marginTop:8}}>{goalsErr}</div>}
            <div className="psec-edit-acts">
              <button type="button" className="btn sm primary" disabled={goalsSaving} onClick={saveGoals}>{goalsSaving?t("Saving…"):t("Save")}</button>
              <button type="button" className="btn sm" disabled={goalsSaving} onClick={()=>{setEditingGoals(false);setGoalsErr("");}}>{t("Cancel")}</button>
            </div>
          </>
        : String(p.goals||"").trim()
          ? <div className="psec-text" dir={dirOf(goalsText)}>{goalsText}</div>
          : <div className="muted psec-empty">{t("No goals defined yet. These feed the Deep Scan's Development & Upgrade/Feature ideas.")}</div>}
    </PlanSection>
    {goalAi.modal}

    <PlanSection id="roadmap" title={t("Roadmap")} open={plan.isOpen("roadmap")} onToggle={()=>plan.toggle("roadmap")}
      meta={roadmapLine||t("Empty")} metaDir={roadmapLine?dirOf(roadmapLine):undefined} empty={!String(p.roadmap||"").trim()}
      menuLabel={t("More Roadmap actions")}
      actions={!editingRoadmap&&<button type="button" className="btn sm" onClick={()=>{startEditRoadmap();plan.setOpen("roadmap",true);}}>✎ {t("Edit")}</button>}
      menu={editingRoadmap?null:[
        {label:roadmapAi.busy?"⟳ "+t("Generating…"):"✨ "+t("Generate with AI"),disabled:roadmapAi.busy,onClick:roadmapAi.generate},
        {label:"⚙ "+t("AI settings"),onClick:roadmapAi.openSettings},
      ]}>
      {editingRoadmap
        ? <>
            <textarea dir="ltr" autoFocus rows={8} style={{width:"100%",fontSize:14,lineHeight:1.6}}
              value={roadmapDraft} onChange={e=>setRoadmapDraft(e.target.value)}
              placeholder={t("e.g. Q3: multi-tenant auth. Q4: billing + usage metering. Next: mobile app.")}/>
            {roadmapErr&&<div className="err" style={{marginTop:8}}>{roadmapErr}</div>}
            <div className="psec-edit-acts">
              <button type="button" className="btn sm primary" disabled={roadmapSaving} onClick={saveRoadmap}>{roadmapSaving?t("Saving…"):t("Save")}</button>
              <button type="button" className="btn sm" disabled={roadmapSaving} onClick={()=>{setEditingRoadmap(false);setRoadmapErr("");}}>{t("Cancel")}</button>
            </div>
          </>
        : String(p.roadmap||"").trim()
          ? <div className="psec-text" dir={dirOf(roadmapText)}>{roadmapText}</div>
          : <div className="muted psec-empty">{t("No roadmap yet.")}</div>}
    </PlanSection>
    {roadmapAi.modal}

    <PlanTodos p={p} onPatch={onPatch} onAskClaude={onAskClaude} open={plan.isOpen("todos")} onToggle={()=>plan.toggle("todos")}/>

    <PlanSection id="readme" title={t("Readme")} open={plan.isOpen("readme")} onToggle={()=>plan.toggle("readme")}
      meta={readmePathDraft||"README.md"} metaDir="ltr">
      <div className="psec-row">
        <input dir="ltr" className="mono" style={{flex:1,fontSize:13}} value={readmePathDraft}
          onChange={e=>setReadmePathDraft(e.target.value)} onBlur={saveReadmePath} placeholder={t("README.md")}/>
        <button type="button" className="btn sm" onClick={viewReadme}>{t(readmeView?"Close view":"View")}</button>
        {readmeView&&!readmeView.error&&<CopyBtn cls="btn sm" text={readmeView.content}
          label={"📋 "+t("Copy")} doneLabel={"✓ "+t("Copied")} title={t("Copy README to clipboard")}/>}
      </div>
      {readmeView&&<div style={{marginTop:12}}>{readmeView.error
        ? <div className="muted" style={{fontSize:13}}>{readmeView.error}</div>
        : <Md className="readme-preview" dir={dirOf(readmeView.content)} text={readmeView.content}/>}</div>}
    </PlanSection>

    <PlanSection id="claude" title="CLAUDE.md" open={plan.isOpen("claude")} onToggle={()=>plan.toggle("claude")}
      meta={claudePathDraft||"CLAUDE.md"} metaDir="ltr">
      <div className="psec-row">
        <input dir="ltr" className="mono" style={{flex:1,fontSize:13}} value={claudePathDraft}
          onChange={e=>setClaudePathDraft(e.target.value)} onBlur={saveClaudePath} placeholder="CLAUDE.md"/>
        <button type="button" className="btn sm" onClick={viewClaude}>{t(claudeView?"Close view":"View")}</button>
        {claudeView&&!claudeView.error&&<CopyBtn cls="btn sm" text={claudeView.content}
          label={"📋 "+t("Copy")} doneLabel={"✓ "+t("Copied")} title={t("Copy CLAUDE.md to clipboard")}/>}
      </div>
      {claudeView&&<div style={{marginTop:12}}>{claudeView.error
        ? <div className="muted" style={{fontSize:13}}>{claudeView.error}</div>
        : <Md className="readme-preview" dir={dirOf(claudeView.content)} text={claudeView.content}/>}</div>}
    </PlanSection>

    {admin&&<PlanSection id="state" title={t("Project State (Google Drive)")} open={plan.isOpen("state")} onToggle={()=>plan.toggle("state")}
      meta={stateSync&&!stateSync.error?t("Synced {when}",{when:agoLabel(Date.parse(stateSync.syncedAt))}):"PROJECT_STATE.md"}
      actions={<button type="button" className="btn sm" disabled={syncing} onClick={syncNow}>{syncing?t("Syncing…"):t("Sync to Drive")}</button>}
      status={stateSync&&stateSync.error&&<span className="muted">{stateSync.error}</span>}>
      <div className="muted" style={{fontSize:12}}>{t("Published as PROJECT_STATE.md, the file the automation agent reads.")}</div>
      {stateSync&&!stateSync.error&&<div className="muted" style={{fontSize:12,marginTop:8}}>{t("Synced {when} to {dest}",{when:new Date(stateSync.syncedAt).toLocaleString(),dest:stateSync.dest})}</div>}
    </PlanSection>}

    <NotesFiles p={p} onPatch={onPatch} open={plan.isOpen("notes")} onToggle={()=>plan.toggle("notes")}/>
    </div>}

    {tab==="tasks"&&<div>
    <AiTips p={p} noAgent={noAgent} onAskClaude={onAskClaude} nudgeTipId={nudgeTipId} onClearNudge={onClearNudge} onOpenTipChat={onOpenTipChat} onOpenRunChat={onOpenRunChat} onOpenCount={onOpenCount} startRun={startRun}/>
    </div>}

    {tab==="automations"&&<div>
    <Automations p={p} st={st} projects={projects} openCount={openCount}/>
    </div>}

   </div>);
}

// A small inline stat that shows an explanatory tooltip on hover. The popover is
// rendered into a document-level portal with position:fixed, so it floats above
// the chat log instead of being clipped by the dock's overflow:hidden.
function Stat({children,tip,color}){
  const[pos,setPos]=useState(null);
  const ref=useRef(null);
  const show=()=>{const r=ref.current.getBoundingClientRect();setPos({left:r.left,bottom:window.innerHeight-r.top+6});};
  return(<span ref={ref} className="cstat" style={color?{color}:null} onMouseEnter={show} onMouseLeave={()=>setPos(null)}>
    {children}
    {pos&&ReactDOM.createPortal(
      <span className="cstat-pop" style={{left:Math.max(8,Math.min(pos.left,window.innerWidth-248)),bottom:pos.bottom}}>{tip}</span>,
      document.body)}
  </span>);
}

// Positions a hover popover as a document-level portal (position:fixed) so it is
// never clipped by an ancestor's overflow: beside the anchor, flipping to the
// other side and clamping to the viewport when there isn't room. Shared by the
// "/" picker rows and the transcript's skill chips.
function useHoverPop(ref,W=260){
  const[pos,setPos]=useState(null);
  const show=()=>{
    if(!ref.current)return;
    const r=ref.current.getBoundingClientRect();
    let left=r.right+8; if(left+W>window.innerWidth-8) left=Math.max(8,r.left-W-8);
    setPos({left,top:Math.max(8,Math.min(r.top,window.innerHeight-140))});
  };
  return[pos,show,()=>setPos(null)];
}

// One row in the "/" skill picker. Hovering it surfaces the skill's full
// explanation in a document-level portal (position:fixed) so the text is never
// clipped by the menu's overflow, and it appears beside the row (flipping to the
// other side / clamping to the viewport when there isn't room).
function SlashRow({s,active,onEnter,onPick}){
  const ref=useRef(null);
  const[pos,showPop,hidePop]=useHoverPop(ref);
  const show=()=>{onEnter();showPop();};
  return(
    <button ref={ref} type="button" className={"slash-it"+(active?" on":"")}
      onMouseDown={e=>e.preventDefault()} onMouseEnter={show} onMouseLeave={hidePop}
      onFocus={show} onBlur={hidePop} onClick={()=>onPick(s)}>
      <span className="slash-n">/{s.label}</span>
      {s.description&&<span className="slash-d">{s.description}</span>}
      {pos&&s.description&&ReactDOM.createPortal(
        <span className="slash-pop" style={{left:pos.left,top:pos.top}}><b>/{s.label}</b>{s.description}</span>,
        document.body)}
    </button>);
}

// Collapse a long working-dir path to its last few segments ("…/hub/src/app").
// The full path always stays in the element's title attribute.
const shortPath=(p,keep=3)=>{
  if(!p)return p;
  const parts=String(p).split("/").filter(Boolean);
  if(parts.length<=keep)return p;
  return "…/"+parts.slice(-keep).join("/");
};
const fmtBytes=(n)=>{if(n==null)return"";const u=["B","KB","MB","GB"];let i=0,v=n;while(v>=1024&&i<u.length-1){v/=1024;i++;}return (i?v.toFixed(1):Math.round(v))+" "+u[i];};
const fmtWhen=(ts)=>{try{return new Date(ts).toLocaleString("en-GB");}catch(e){return"";}};
// short HH:MM stamp for the per-message hover timestamp (full date/time lives in its title)
const fmtTime=(ts)=>{try{return new Date(ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});}catch(e){return"";}};
const rid=(pfx)=>pfx+Date.now().toString(36)+Math.random().toString(36).slice(2,7);
// compact token counts (1234 → "1.2k") for the chat token/context readouts
const fmtTokens=(n)=>{n=Number(n)||0;if(n<1000)return String(n);const k=n/1000;return (k>=100?Math.round(k):k.toFixed(1).replace(/\.0$/,""))+"k";};
// effective context window for the indicator (Opus/Sonnet/Haiku are all 200k)
const CTX_WINDOW=200000;
// "Lately": a chat whose last activity is within this window stays on the open tab
// strip. Older chats drop off the strip by default and live in 🕘 History (reopen
// from there to pin them back). Running/active/just-finished chats always show.
const RECENT_TAB_MS=12*60*60*1000;
const CHAT_STUCK_MS=65000;
// The tab strip is per-project curation the user cares about: which chats they
// closed off the strip (closedTabs) and which older chats they pinned back
// (openedTabs). Persist both per slug so switching projects and back — or a full
// reload — restores the exact arrangement instead of re-floating every recent chat.
function loadTabPrefs(key,slug){try{const v=JSON.parse(localStorage.getItem("hub."+key+"."+slug));return Array.isArray(v)?v:[];}catch{return[];}}
function saveTabPrefs(key,slug,ids){try{localStorage.setItem("hub."+key+"."+slug,JSON.stringify(ids));}catch{}}
// total + output tokens of a turn from a stream-json usage object
const turnTokens=(u)=>u?{in:(u.input_tokens||0)+(u.cache_read_input_tokens||0)+(u.cache_creation_input_tokens||0),out:u.output_tokens||0}:null;
// RUNTIME-COMMANDS-START
// Terminal-parity runtime commands. These are handled entirely client-side (see
// sendPrompt): typing one appends an inline note and dispatches NO agent job
// (except /compact, which sends one summarize turn). They are the terminal's
// client-side commands, which a headless `claude -p`/SDK run never receives, so
// they cannot work by being sent as a prompt - we implement them here instead.
const RUNTIME_COMMANDS=[
  {name:"context", desc:"Show context-window usage for this conversation.",       runtime:true},
  {name:"cost",    desc:"Show the running cost of this chat session.",            runtime:true},
  {name:"usage",   desc:"Show your Claude subscription usage.",                   runtime:true},
  {name:"clear",   desc:"Start a fresh conversation (new session) in this chat.", runtime:true},
  {name:"compact", desc:"Summarize the conversation to free up context.",        runtime:true},
  {name:"model",   desc:"Show or switch the model tier (/model <tier>).",        runtime:true},
  {name:"resume",  desc:"Reopen a previous chat session.",                        runtime:true},
];
const RUNTIME_NAMES=new Set(RUNTIME_COMMANDS.map(c=>c.name));
// A runtime command is the whole input: "/name" optionally followed by args.
// Returns {cmd,args} or null (null = not a runtime command; send to the model).
function parseSlash(text){
  const m=/^\/([a-z][a-z0-9-]*)(?:\s+([\s\S]*))?$/.exec(String(text==null?"":text).trim());
  if(!m||!RUNTIME_NAMES.has(m[1]))return null;
  return {cmd:m[1], args:(m[2]||"").trim()};
}
// Context-window fill for the conversation, from the last assistant request size
// stored on session.ctx (see the assistant handler in runTurn).
function contextReport(session){
  const used=(session&&session.ctx)||0;
  const win=(typeof CTX_WINDOW!=="undefined")?CTX_WINDOW:200000;
  const pct=Math.min(100,Math.round(used/win*100));
  return {used, window:win, pct, freePct:Math.max(0,100-pct)};
}
// Running cost of the session: sum of each finished turn's reported cost.
function sessionCost(session){
  const msgs=(session&&session.messages)||[];
  let sum=0;
  for(const m of msgs){if(m&&m.meta&&typeof m.meta.cost==="number")sum+=m.meta.cost;}
  return sum;
}
// RUNTIME-COMMANDS-END
// a fresh, empty AI chat session (persisted once it gets its first message)
// `lang` is set ONCE, from the first user message (see runTurn()), and frozen.
// A chat keeps answering in the language it started in even if the UI is
// toggled later — switching mid-thread would read as the assistant losing
// the plot. Sessions created before this feature have lang:null and get no
// instruction at all, preserving their original behaviour exactly.
function newSession(provider){return{id:rid("s"),title:"New chat",provider:provider||"claude",model:"",lang:null,createdAt:Date.now(),updatedAt:Date.now(),sessionId:null,messages:[]};}
// A chat is "created from a task" when it carries the fromTask flag, or (for
// chats made before that flag existed) when its id uses the airun_<taskId> form.
function isFromTaskChat(s){return!!(s&&(s.fromTask||(typeof s.id==="string"&&s.id.indexOf("airun_")===0)));}
// Manual drag order for the tab strip: {ids:[...], stamp}. Persisted per slug
// like the other tab prefs, so an arrangement survives reloads/project switches.
function loadTabOrder(slug){try{const v=JSON.parse(localStorage.getItem("hub.tabOrder."+slug));return v&&Array.isArray(v.ids)?{ids:v.ids,stamp:v.stamp||0}:null;}catch{return null;}}
function saveTabOrder(slug,order){try{if(order&&order.ids&&order.ids.length)localStorage.setItem("hub.tabOrder."+slug,JSON.stringify(order));else localStorage.removeItem("hub.tabOrder."+slug);}catch{}}
// newest updatedAt (or createdAt) across a set of chats — the strip's activity clock
function latestActivity(tabs){let m=0;for(const s of tabs){const t=(s&&(s.updatedAt||s.createdAt))||0;if(t>m)m=t;}return m;}
// Order the tab strip. Default is newest-first (left), so a new chat or one that
// just replied floats to the left. A manual drag order overrides that, but ONLY
// until the next bit of activity: it's stamped with the activity clock at drag
// time, and any newer activity makes it stale and re-sorts by recency. Chats not
// in a still-fresh order (reopened from History) lead, newest-first.
function orderTabs(tabs,order){
  const byRecency=(a,b)=>((b.updatedAt||b.createdAt||0)-(a.updatedAt||a.createdAt||0));
  const list=tabs.slice();
  if(!order||!order.ids||!order.ids.length)return list.sort(byRecency);
  if(latestActivity(tabs)>(order.stamp||0))return list.sort(byRecency); // activity always wins
  const rank=new Map();order.ids.forEach((id,i)=>rank.set(id,i));
  return list.sort((a,b)=>{
    const ai=rank.has(a.id)?rank.get(a.id):-1,bi=rank.has(b.id)?rank.get(b.id):-1;
    if(ai<0&&bi<0)return byRecency(a,b);
    if(ai<0)return -1;
    if(bi<0)return 1;
    return ai-bi;
  });
}
// move `fromId` to the near/far side of `toId`, returning a new id list
function moveTab(ids,fromId,toId,side){
  if(!fromId||!toId||fromId===toId)return ids.slice();
  const rest=ids.filter(id=>id!==fromId);
  const at=rest.indexOf(toId);
  if(at<0)return ids.slice();
  rest.splice(side==="after"?at+1:at,0,fromId);
  return rest;
}

// Reveal a selected conversation tab without using scrollIntoView(), which can
// also move the page or an ancestor panel. Geometry gives us the clipped edge
// in physical pixels, so the same delta works for both LTR and RTL strips.
function scrollTabFullyIntoView(strip,tab,behavior){
  if(!strip||!tab||!strip.getBoundingClientRect||!tab.getBoundingClientRect)return;
  const sr=strip.getBoundingClientRect(),tr=tab.getBoundingClientRect();
  const delta=tr.left<sr.left?tr.left-sr.left:tr.right>sr.right?tr.right-sr.right:0;
  if(!delta)return;
  if(strip.scrollBy)strip.scrollBy({left:delta,behavior:behavior||"smooth"});
  else strip.scrollLeft+=delta;
}
// compact "time ago" for the history list
function relTime(ts){if(!ts)return"";const s=Math.floor((Date.now()-ts)/1000);if(s<60)return"now";const m=Math.floor(s/60);if(m<60)return m+"m";const h=Math.floor(m/60);if(h<24)return h+"h";return Math.floor(h/24)+"d";}
// Freshness formatter with second granularity — used by the header "Updated Xs ago"
// chip so it shows seconds since the last scan rather than rounding up to a minute.
function relTimeSec(ts){if(!ts)return"";let s=Math.floor((Date.now()-ts)/1000);if(s<0)s=0;if(s<60)return s+"s";const m=Math.floor(s/60);if(m<60)return m+"m";const h=Math.floor(m/60);if(h<24)return h+"h";return Math.floor(h/24)+"d";}
// group history chats into recency buckets for the history dropdown
function dayStart(ts){const d=new Date(ts);d.setHours(0,0,0,0);return d.getTime();}
function groupByRecency(items){
  const today=dayStart(Date.now()),day=86400000;
  const mins=[["Today",today],["Yesterday",today-day],["Previous 7 days",today-7*day],["Older",-Infinity]];
  const out=mins.map(([label,min])=>({label,min,items:[]}));
  for(const s of items){const t=s.updatedAt||0;(out.find(b=>t>=b.min)||out[out.length-1]).items.push(s);}
  return out.filter(g=>g.items.length);
}
// Build the searchable text for one saved conversation. Besides its title, include
// the visible conversation body: user/assistant text, attachments and the compact
// tool rows that can appear between assistant text parts.
function chatSearchText(session){
  const text=[session&&session.title];
  for(const message of (session&&session.messages)||[]){
    text.push(message.text);
    for(const file of message.files||[])text.push(file.name);
    for(const part of message.parts||[]){
      text.push(part.text,part.name,part.desc,part.skill);
      for(const item of part.items||[])text.push(item.text,item.name,item.desc,item.skill);
    }
  }
  return text.filter(value=>value!=null).map(String).join("\n").toLowerCase();
}
// One-tap follow-ups shown under a finished response.
const DEFAULT_QUICK_PROMPTS=[
  {label:"go",prompt:"go"},
  {label:"run the app",prompt:"run the app"},
  {label:"deploy/restart",prompt:"deploy/restart"},
  {label:"i don't see any change",prompt:"i don't see any change"},
];
const QUICK_PROMPTS_KEY="hub.quickPrompts";
// Follow-up suggestion: after a Claude turn finishes, one likely next question
// shows as ghost text in the empty composer; Tab (or the ↹ button) accepts it.
// Browser-local, ON unless explicitly turned off in the chat's AI settings.
const FOLLOWUP_PREF_KEY="hub.chatFollowup";
function followupEnabled(){try{return localStorage.getItem(FOLLOWUP_PREF_KEY)!=="0";}catch(e){return true;}}
function setFollowupEnabled(on){
  try{localStorage.setItem(FOLLOWUP_PREF_KEY,on?"1":"0");}catch(e){}
  try{window.dispatchEvent(new Event("hub:followup-pref"));}catch(e){}
}
// Quick prompts are user-editable chips shown under the last chat message. The
// saved list (label + prompt text) lives in localStorage; a missing/invalid
// store falls back to the built-in defaults above. Built-in entries may carry a
// prompt() function or hint; user entries are always plain strings.
function loadQuickPrompts(){
  try{
    const v=JSON.parse(localStorage.getItem(QUICK_PROMPTS_KEY));
    if(Array.isArray(v))return v.filter(q=>q&&typeof q.label==="string").map(q=>({label:q.label,prompt:typeof q.prompt==="string"?q.prompt:q.label}));
  }catch(e){}
  return DEFAULT_QUICK_PROMPTS.map(q=>({...q}));
}
function saveQuickPrompts(list){try{localStorage.setItem(QUICK_PROMPTS_KEY,JSON.stringify(list));}catch(e){}}

// Popover editor for the quick-prompt chips: add / edit / delete rows, then
// Save writes the whole list back through onSave. Built-in function/hint fields
// are dropped once a row is edited — user rows are plain {label,prompt}.
function QuickPromptsEditor({list,onSave,onClose}){
  useLang(); // re-render on language change so labels stay translated
  const[rows,setRows]=useState(()=>list.map(q=>({label:q.label||"",prompt:typeof q.prompt==="string"?q.prompt:(q.label||"")})));
  const setRow=(i,patch)=>setRows(rs=>rs.map((r,k)=>k===i?{...r,...patch}:r));
  const addRow=()=>setRows(rs=>[...rs,{label:"",prompt:""}]);
  const delRow=i=>setRows(rs=>rs.filter((_,k)=>k!==i));
  const save=()=>{
    const clean=rows.map(r=>({label:(r.label||"").trim(),prompt:(r.prompt||"").trim()||( r.label||"").trim()})).filter(r=>r.label);
    onSave(clean);onClose();
  };
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};document.addEventListener("keydown",h);return()=>document.removeEventListener("keydown",h);},[onClose]);
  return <div className="qp-overlay" onMouseDown={onClose}>
    <div className="qp-modal" role="dialog" aria-modal="true" aria-label={t("Quick prompts")} onMouseDown={e=>e.stopPropagation()}>
      <div className="qp-head"><b>{t("Quick prompts")}</b><button className="btn xs" onClick={onClose} aria-label={t("Close")}>✕</button></div>
      <div className="qp-hint muted">{t("Buttons shown under the last message. Label is the chip text; prompt is what gets sent.")}</div>
      <div className="qp-rows">
        {rows.map((r,i)=><div className="qp-row" key={i}>
          <input className="qp-label" value={r.label} placeholder={t("Label")} onChange={e=>setRow(i,{label:e.target.value})}/>
          <input className="qp-prompt" dir={dirOf(r.prompt)} value={r.prompt} placeholder={t("Prompt sent to the agent")} onChange={e=>setRow(i,{prompt:e.target.value})}/>
          <button className="qp-del" title={t("Delete")} aria-label={t("Delete")} onClick={()=>delRow(i)}>🗑</button>
        </div>)}
        {rows.length===0&&<div className="muted qp-empty">{t("No quick prompts yet.")}</div>}
      </div>
      <div className="qp-foot">
        <button className="btn sm" onClick={addRow}>＋ {t("Add prompt")}</button>
        <div className="qp-foot-right">
          <button className="btn sm" onClick={()=>setRows(DEFAULT_QUICK_PROMPTS.map(q=>({label:q.label,prompt:typeof q.prompt==="string"?q.prompt:q.label})))}>{t("Reset to defaults")}</button>
          <button className="btn sm primary" onClick={save}>{t("Save")}</button>
        </div>
      </div>
    </div>
  </div>;
}

// Notes are STABLE user content — safe to lazily machine-translate. A tiny
// component (rather than calling useTranslated inline in the .map callback)
// keeps the hook call at a component's top level, one per note, in line with
// the rules of hooks.
function NoteText({text}){
  const out=useTranslated(text);
  // Notes are markdown so pasted images (![](url)) and formatting render inline.
  return <Md className="txt" dir={dirOf(out)} text={out}/>;
}
// Task title/detail are STABLE user content — same lazy-translate treatment.
// Small components (rather than calling useTranslated inline in the tasks
// .map callback) keep the hook call at a component's top level, one per task.
function TipTitle({title}){
  const out=useTranslated(title);
  return <b style={{fontSize:14}} dir={dirOf(out)}>{out}</b>;
}
function TipDetail({detail}){
  const out=useTranslated(detail);
  return <div style={{fontSize:13,color:"var(--text-2)",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}} dir={dirOf(out)} title={out}>{out}</div>;
}
/* Plan tab layout (2026-10-02): every block of the Plan tab (PRD, Requirements
   status, Goal, Roadmap, To-Do, Readme, CLAUDE.md, Project State, Notes) is a
   collapsible PlanSection card. The header carries a chevron toggle, the title,
   a one-line muted meta summary (so a collapsed card still says something),
   at most one visible action and an overflow "..." PlanMenu for the rest.
   Collapse state is per project per section in localStorage under
   hub.planCollapse.<slug> as {sectionId: open}. */
const PLAN_COLLAPSE_PREFIX="hub.planCollapse.";
function loadPlanCollapse(slug){
  try{
    const v=JSON.parse(localStorage.getItem(PLAN_COLLAPSE_PREFIX+slug)||"null");
    return v&&typeof v==="object"&&!Array.isArray(v)?v:{};
  }catch(e){return {};}
}
function savePlanCollapse(slug,map){
  try{localStorage.setItem(PLAN_COLLAPSE_PREFIX+slug,JSON.stringify(map));}catch(e){}
}
// `defaults` maps section id -> open (true when absent) for sections the viewer
// has never toggled. Returns {isOpen,toggle,setOpen,setAll}.
function usePlanCollapse(slug,defaults){
  const[state,setState]=useState(()=>({slug,map:loadPlanCollapse(slug)}));
  const map=state.slug===slug?state.map:loadPlanCollapse(slug);
  const isOpen=id=>typeof map[id]==="boolean"?map[id]:!(defaults&&defaults[id]===false);
  const write=next=>{setState({slug,map:next});savePlanCollapse(slug,next);};
  const toggle=id=>write({...map,[id]:!isOpen(id)});
  const setOpen=(id,open)=>{if(isOpen(id)!==open)write({...map,[id]:!!open});};
  const setAll=(ids,open)=>{const next={...map};ids.forEach(id=>{next[id]=!!open;});write(next);};
  return {isOpen,toggle,setOpen,setAll};
}
// First non-empty line of a block of text, for a collapsed section's preview.
function firstLine(s){
  const l=String(s||"").split("\n").map(x=>x.replace(/^[\s#>*\-\d.)]+/,"").trim()).find(Boolean);
  return l||"";
}
// "2h" -> "2h ago"; "just now" / "yesterday" / a date stay as they are.
function agoLabel(ts){
  if(!ts||isNaN(ts))return "";
  const v=timeAgo(ts);
  return /^\d+[mhd]$/.test(v)?t("{when} ago",{when:v}):t(v);
}
// Small overflow "..." menu: a role="menu" popover anchored to its trigger at the
// logical end. Closes on outside pointer-down, Escape, or picking an item; arrow
// keys move focus between items (useMenuFocus). Items: {label,onClick,disabled,title}.
function PlanMenu({label,items}){
  const[open,setOpen]=useState(false);
  const btnRef=useRef(null);
  const menuRef=useRef(null);
  useMenuFocus(open,menuRef,btnRef);
  useEffect(()=>{
    if(!open)return;
    const down=e=>{
      if(menuRef.current&&menuRef.current.contains(e.target))return;
      if(btnRef.current&&btnRef.current.contains(e.target))return;
      setOpen(false);
    };
    const key=e=>{if(e.key==="Escape"){e.stopPropagation();setOpen(false);}};
    document.addEventListener("mousedown",down);
    document.addEventListener("touchstart",down);
    document.addEventListener("keydown",key);
    return()=>{
      document.removeEventListener("mousedown",down);
      document.removeEventListener("touchstart",down);
      document.removeEventListener("keydown",key);
    };
  },[open]);
  const list=(items||[]).filter(Boolean);
  if(!list.length)return null;
  return <span className="pmenu">
    <button ref={btnRef} type="button" className="btn sm pmenu-btn" aria-haspopup="menu" aria-expanded={open}
      aria-label={label} title={label} onClick={()=>setOpen(o=>!o)}>
      <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/></svg>
    </button>
    {open&&<div ref={menuRef} className="pmenu-pop" role="menu" aria-label={label}>
      {list.map((it,i)=><button key={i} type="button" role="menuitem" className="menu-item" disabled={!!it.disabled} title={it.title}
        onClick={()=>{setOpen(false);if(it.onClick)it.onClick();}}>{it.label}</button>)}
    </div>}
  </span>;
}
// One collapsible Plan card. The title button owns the toggle (aria-expanded +
// aria-controls -> the body region); the body stays in the DOM as an empty,
// hidden region while collapsed so heavy content (markdown) is never rendered.
// `status` renders between header and body regardless of collapse, so run
// errors stay visible on a collapsed card.
function PlanSection({id,title,meta,metaDir,progress,open,onToggle,actions,menu,menuLabel,status,empty,children}){
  const bodyId="plan-sec-"+id;
  const hasMenu=Array.isArray(menu)&&menu.filter(Boolean).length>0;
  return <section className={"psec"+(open?" open":"")+(empty?" empty":"")} data-sec={id}>
    <div className="psec-head">
      <h3 className="psec-h" id={bodyId+"-h"}>
        <button type="button" className="psec-toggle" aria-expanded={!!open} aria-controls={bodyId} onClick={onToggle}>
          <span className="psec-chev" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M6 3.5 10.5 8 6 12.5"/></svg></span>
          <span className="psec-title">{title}</span>
          {meta?<span className="psec-meta" dir={metaDir}>{meta}</span>:null}
          {progress!=null&&<span className="psec-bar" aria-hidden="true"><span style={{inlineSize:Math.max(0,Math.min(100,Math.round(progress*100)))+"%"}}/></span>}
        </button>
      </h3>
      {(actions||hasMenu)&&<div className="psec-actions">
        {actions}
        {hasMenu&&<PlanMenu label={menuLabel||t("More actions")} items={menu}/>}
      </div>}
    </div>
    {status?<div className="psec-status">{status}</div>:null}
    <div id={bodyId} className="psec-body" role="region" aria-labelledby={bodyId+"-h"} hidden={!open}>{open?children:null}</div>
  </section>;
}

/* Plan tab > To-Do: a plain user-authored checklist, stored on
   the project as `todos` and persisted through the same onPatch path as goals /
   roadmap / attachments. All list maths lives in src/todos.js (pure, tested). */
// Gear button beside "Auto-fill from project files": opens a modal to view and
// edit the instruction brief the model uses when generating the plan (Goal,
// Roadmap and To-Do list). Empty = use the server's built-in default.
function PlanFillSettings(){
  const[open,setOpen]=useState(false);
  const[brief,setBrief]=useState(loadPlanFillBrief);
  const[deflt,setDeflt]=useState("");
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  useEffect(()=>{if(!open)return;setErr("");
    fetchPlanFillDefaultBrief().then(setDeflt).catch(e=>setErr(String(e&&e.message||e)));
  },[open]);
  function save(){savePlanFillBrief(brief);setOpen(false);}
  function loadDefault(){
    if(deflt){setBrief(deflt);return;}
    setBusy(true);setErr("");
    fetchPlanFillDefaultBrief().then(d=>{setDeflt(d);setBrief(d);}).catch(e=>setErr(String(e&&e.message||e))).finally(()=>setBusy(false));
  }
  function reset(){savePlanFillBrief("");setBrief("");setOpen(false);}
  return <>
    <button type="button" className="btn sm" title={t("Edit the instructions the model uses to auto-fill this plan")} aria-label={t("Settings")}
      onClick={()=>{setBrief(loadPlanFillBrief());setOpen(true);}}>⚙</button>
    {open&&<div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)setOpen(false);}}>
      <div className="sset aiset" role="dialog" aria-modal="true" aria-labelledby="dlg-autofill-title">
        <div className="sset-head">
          <h3 id="dlg-autofill-title">{t("Auto-fill instructions")}</h3>
          <button className="btn sm" onClick={()=>setOpen(false)}>✕ {t("Close")}</button>
        </div>
        <div className="sset-body">
          <p className="aiset-intro">{t("These instructions tell the model what to generate when it reads the project files - the Goal, the Roadmap and the To-Do list. Leave empty to use the built-in default.")}</p>
          <section className="aiset-card">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6}}>
              <label className="muted" style={{fontSize:12}}>{t("Default prompt instructions")}</label>
              <button type="button" className="btn sm" disabled={busy} onClick={loadDefault}>{busy?"…":t("Load built-in default")}</button>
            </div>
            <textarea dir="ltr" rows={16} style={{width:"100%",fontSize:12.5,lineHeight:1.5}}
              placeholder={t("Leave empty to use the built-in default. Click \"Load built-in default\" to edit it.")}
              value={brief} onChange={e=>setBrief(e.target.value)}/>
            {err&&<div className="err" style={{marginTop:8}}>{err}</div>}
          </section>
        </div>
        <div className="sset-foot">
          <button className="btn sm" onClick={reset}>{t("Reset to default")}</button>
          <span className="sp"/>
          <button className="btn sm primary" onClick={save}>{t("Save")}</button>
        </div>
      </div>
    </div>}
  </>;
}

function PlanTodos({p,onPatch,onAskClaude,open=true,onToggle}){
  const list=sortTodos(p.todos);
  const{done,total}=todoCounts(p.todos);
  const openN=total-done;
  const[text,setText]=useState("");
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");

  function persist(next){
    setBusy(true);setErr("");
    return Promise.resolve(onPatch(p.slug,{todos:next}))
      .then(r=>{setBusy(false);if(r&&r.ok===false)setErr("Save failed: "+(r.error||"unknown error"));return r;})
      .catch(e=>{setBusy(false);setErr("Save failed: "+String(e));});
  }
  function add(){
    const body=text.trim();if(!body)return;
    persist(addTodo(p.todos,body)).then(()=>setText(""));
  }

  return <PlanSection id="todos" title={t("To-Do")} open={open} onToggle={onToggle} empty={!total}
    meta={total?t("{open} open - {done} done",{open:openN,done}):t("Empty")}
    progress={total?done/total:null}>
    <div style={{display:"flex",gap:8,alignItems:"center"}}>
      <input dir={dirOf(text)} style={{flex:1,fontSize:14}} value={text}
        onChange={e=>setText(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();add();}}}
        placeholder={t("Add a to-do…")}/>
      <button type="button" className="btn sm primary" disabled={busy||!text.trim()} onClick={add}>{t("Add")}</button>
    </div>
    {err&&<div className="err" style={{marginTop:8}}>{err}</div>}
    {list.length
      ? <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:6}}>
          {/* The row must never squeeze the text to one character per line: body sets
              overflow-wrap:anywhere globally, which drops a flex item's min-content
              width to a single glyph, so a `flex:1` (basis 0) text span collapses as
              soon as the checkbox + buttons need room. Give the text `1 1 auto` with
              normal word breaking, keep the controls unshrinkable, and let the row
              wrap instead of the letters. */}
          {list.map(td=><div key={td.id} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
            <input type="checkbox" checked={td.done} disabled={busy}
              style={{flex:"0 0 auto",width:16,height:16,margin:"3px 0 0",padding:0}}
              title={t("Mark done")}
              onChange={()=>persist(toggleTodo(p.todos,td.id))}/>
            <span dir={dirOf(td.text)} style={{flex:"1 1 auto",minWidth:0,fontSize:14,lineHeight:1.5,whiteSpace:"pre-wrap",
              overflowWrap:"break-word",wordBreak:"normal",
              textDecoration:td.done?"line-through":"none",opacity:td.done?0.55:1}}>{td.text}</span>
            {onAskClaude&&<button type="button" className="btn xs" style={{flex:"0 0 auto"}} title={t("Prompt this task")} aria-label={t("Prompt this task")}
              onClick={()=>onAskClaude(td.text,undefined,"To-Do task")}>▶</button>}
            <button type="button" className="btn xs" style={{flex:"0 0 auto"}} title={t("Delete")} aria-label={t("Delete")} disabled={busy}
              onClick={()=>persist(removeTodo(p.todos,td.id))}>×</button>
          </div>)}
        </div>
      : <div className="muted" style={{fontSize:13,marginTop:12}}>{t("No to-dos yet.")}</div>}
  </PlanSection>;
}

function NotesFiles({p,onPatch,open=true,onToggle}){
  const{lang}=useLang();
  const list=p.attachments||[];
  const noteN=list.filter(x=>x&&x.kind==="note").length;
  const fileN=list.length-noteN;
  const notesMeta=!list.length?t("Empty"):[
    noteN?t({one:"{n} note",other:"{n} notes"},{n:noteN}):"",
    fileN?t({one:"{n} file",other:"{n} files"},{n:fileN}):""].filter(Boolean).join(" - ");
  const[text,setText]=useState("");
  const[editingId,setEditingId]=useState(null);
  const[editText,setEditText]=useState("");
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  const[drag,setDrag]=useState(false);
  const[driveOpen,setDriveOpen]=useState(false);
  const[cleanTarget,setCleanTarget]=useState(null);
  const fileRef=React.useRef(null);
  const addRef=React.useRef(null);
  const editRef=React.useRef(null);

  function addFileEntry(f){persist([{id:rid("f"),kind:"file",name:f.name,url:f.url,size:f.size,mime:f.mime,createdAt:Date.now()},...list]);}
  function addFolderEntry(fo){persist([{id:rid("d"),kind:"link",name:fo.name,url:fo.url,source:"gdrive-folder",createdAt:Date.now()},...list]);setDriveOpen(false);}

  function persist(next){
    setBusy(true);setErr("");
    return Promise.resolve(onPatch(p.slug,{attachments:next}))
      .then(r=>{setBusy(false);if(r&&r.ok===false)setErr("Save failed: "+(r.error||"unknown error"));return r;})
      .catch(e=>{setBusy(false);setErr("Save failed: "+String(e));return{ok:false,error:String(e)};});
  }

  function addNote(){
    const body=text.trim();if(!body)return;
    persist([{id:rid("n"),kind:"note",text:body,createdAt:Date.now()},...list]).then(()=>setText(""));
  }

  function startEdit(it){
    setEditingId(it.id);setEditText(it.text||"");setErr("");
  }
  function cancelEdit(){setEditingId(null);setEditText("");}
  function saveEdit(){
    const body=editText.trim();if(!body)return;
    persist(list.map(it=>it.id===editingId?{...it,text:body,updatedAt:Date.now()}:it))
      .then(r=>{if(!r||r.ok!==false)cancelEdit();});
  }

  function uploadBlob(blob,name){
    setBusy(true);setErr("");
    const r=new FileReader();
    r.onerror=()=>{setBusy(false);setErr("Could not read file.");};
    r.onload=()=>{
      fetch("/api/upload",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,filename:name||blob.name||"file",dataUrl:r.result})})
        .then(x=>x.json())
        .then(d=>{
          if(d&&d.ok){persist([{id:rid("f"),kind:"file",name:d.file.name,url:d.file.url,size:d.file.size,mime:d.file.mime,createdAt:Date.now()},...list]);}
          else{setBusy(false);setErr("Upload failed: "+((d&&d.error)||"unknown error"));}
        }).catch(e=>{setBusy(false);setErr("Upload failed: "+String(e));});
    };
    r.readAsDataURL(blob);
  }

  function onFiles(fl){Array.from(fl||[]).forEach(f=>uploadBlob(f,f.name));}

  function pasteClip(){
    setErr("");
    const nav=navigator.clipboard;
    if(!nav){setErr("Clipboard unavailable — paste into the box with ⌘/Ctrl+V.");return;}
    const grabText=()=>nav.readText().then(t=>{if(t&&t.trim())setText(prev=>prev?prev+"\n"+t:t);else setErr("Clipboard is empty.");}).catch(()=>setErr("Couldn't read clipboard — allow permission, or paste manually."));
    if(nav.read){
      nav.read().then(items=>{
        for(const it of items){const img=it.types.find(t=>t.startsWith("image/"));if(img){it.getType(img).then(b=>uploadBlob(b,"pasted-image."+((img.split("/")[1])||"png")));return;}}
        grabText();
      }).catch(grabText);
    }else grabText();
  }

  // Insert a snippet at the caret of a textarea (falls back to append).
  function insertAtCursor(ref,setter,snippet){
    const el=ref&&ref.current;
    if(!el){setter(prev=>prev?prev+snippet:snippet.replace(/^\n/,""));return;}
    const s=el.selectionStart==null?el.value.length:el.selectionStart;
    const e=el.selectionEnd==null?s:el.selectionEnd;
    setter(prev=>{const v=prev==null?"":prev;return v.slice(0,s)+snippet+v.slice(e);});
    requestAnimationFrame(()=>{try{el.focus();const pos=s+snippet.length;el.setSelectionRange(pos,pos);}catch{}});
  }
  // Upload an image blob and drop a markdown ![](url) reference at the caret so
  // it renders inline inside the note body.
  function uploadImageInline(blob,name,ref,setter){
    setBusy(true);setErr("");
    const r=new FileReader();
    r.onerror=()=>{setBusy(false);setErr("Could not read image.");};
    r.onload=()=>{
      fetch("/api/upload",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,filename:name||"pasted-image.png",dataUrl:r.result})})
        .then(x=>x.json())
        .then(d=>{setBusy(false);
          if(d&&d.ok)insertAtCursor(ref,setter,"\n!["+(d.file.name||"image")+"]("+d.file.url+")\n");
          else setErr("Upload failed: "+((d&&d.error)||"unknown error"));
        }).catch(e=>{setBusy(false);setErr("Upload failed: "+String(e));});
    };
    r.readAsDataURL(blob);
  }
  // Paste handler for the note textareas: images embed inline (markdown), other
  // files attach as before.
  function imagePaste(ref,setter){
    return (e)=>{
      const items=e.clipboardData&&e.clipboardData.items;if(!items)return;
      for(const it of items){
        if(it.kind!=="file")continue;
        const f=it.getAsFile();if(!f)continue;
        e.preventDefault();
        if(f.type&&f.type.startsWith("image/"))uploadImageInline(f,f.name||("pasted-image."+((f.type.split("/")[1])||"png")),ref,setter);
        else uploadBlob(f,f.name||"pasted-file");
        return;
      }
    };
  }

  function del(id){
    const it=list.find(x=>x.id===id);
    if(!window.confirm(t("Remove this")+" "+(it&&it.kind==="file"?t("file"):t("note"))+"?"))return;
    if(it&&it.kind==="file"&&it.url)fetch("/api/upload/delete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({url:it.url})}).catch(()=>{});
    persist(list.filter(x=>x.id!==id));
  }

  return(
   <PlanSection id="notes" title={t("Notes")} meta={notesMeta} open={open} onToggle={onToggle} empty={!list.length}>
    <div className={"dropzone"+(drag?" drag":"")}
      onDragOver={e=>{e.preventDefault();setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={e=>{e.preventDefault();setDrag(false);if(e.dataTransfer&&e.dataTransfer.files&&e.dataTransfer.files.length)onFiles(e.dataTransfer.files);}}>
      <textarea ref={addRef} dir="ltr" placeholder={t("Write a note, or paste an image / drop a file here…")} value={text}
        onChange={e=>setText(e.target.value)} onPaste={imagePaste(addRef,setText)}
        onKeyDown={e=>{if((e.metaKey||e.ctrlKey)&&e.key==="Enter")addNote();}}/>
      <div className="acts">
        <button className="btn sm primary" onClick={addNote} disabled={busy||!text.trim()}>{t("Add note")}</button>
        <PlanAiOptions section="notes" disabled={busy} name={p.name} context={planContext(p)} onApply={txt=>setText(txt)}/>
        {!CLOUD&&<button className="btn sm" onClick={pasteClip} disabled={busy}>📋 Paste clipboard</button>}
        <button className="btn sm" onClick={()=>fileRef.current&&fileRef.current.click()} disabled={busy}>📎 {t("Upload file")}</button>
        {CLOUD&&<button className="btn sm" onClick={()=>setDriveOpen(true)} disabled={busy}>☁️ {t("Google Drive")}</button>}
        <input ref={fileRef} type="file" multiple style={{display:"none"}} onChange={e=>{onFiles(e.target.files);e.target.value="";}}/>
        <div className="sp"/>
        {busy&&<span className="muted" style={{fontSize:12}}>{t("Working…")}</span>}
      </div>
    </div>
    {err&&<div className="err" style={{marginTop:8}}>{err}</div>}

    <div style={{marginTop:12}}>
      {list.length===0&&<div className="muted" style={{fontSize:13}}>{t("No notes or files yet.")}</div>}
      {list.map(it=>(
        <div className="note" key={it.id}>
          <div className="meta">
            <span>{it.kind==="link"?"☁️ Drive":it.kind==="file"?"📎 File":"📝 Note"}</span>
            <span>· {fmtWhen(it.createdAt)}</span>
            <span className="sp"/>
            {it.kind==="note"&&editingId!==it.id&&
              <button className="ed" title={t("Edit")} onClick={()=>startEdit(it)}>✎ {t("Edit")}</button>}
            <button className="del" title={t("Remove")} aria-label={t("Remove")} onClick={()=>del(it.id)}>✕</button>
          </div>
          {it.kind==="note"
            ?editingId===it.id
              ?<div>
                 <textarea dir="ltr" value={editText} autoFocus disabled={busy}
                   onChange={e=>setEditText(e.target.value)}
                   onKeyDown={e=>{if((e.metaKey||e.ctrlKey)&&e.key==="Enter")saveEdit();if(e.key==="Escape")cancelEdit();}}
                   style={{width:"100%",minHeight:100,resize:"vertical"}}/>
                 <div className="acts" style={{marginTop:8}}>
                   <button className="btn sm primary" disabled={busy||!editText.trim()} onClick={saveEdit}>{t("Save")}</button>
                   <button className="btn sm" disabled={busy} onClick={cancelEdit}>{t("Cancel")}</button>
                 </div>
               </div>
              :<NoteText text={it.text}/>
            :it.kind==="link"
            ?<div className="fileline"><a href={it.url} target="_blank" rel="noreferrer" dir="ltr">📁 {it.name}</a></div>
            :<div>
               <div className="fileline"><a href={it.url} target="_blank" download dir="ltr">{it.name}</a><span className="muted" style={{fontSize:12}}>{fmtBytes(it.size)}</span>
                 {it.mime&&it.mime.startsWith("image/")&&<button className="btn xs" style={{marginInlineStart:8}} onClick={()=>setCleanTarget(it)}>🧹 {t("Clean image")}</button>}</div>
               {it.mime&&it.mime.startsWith("image/")&&<a href={it.url} target="_blank"><img className="thumb" src={it.url} alt={it.name}/></a>}
             </div>}
        </div>
      ))}
    </div>
    {driveOpen&&<CloudDrivePicker slug={p.slug} onClose={()=>setDriveOpen(false)}
      onPickFile={f=>{addFileEntry(f);setDriveOpen(false);}} onPickFolder={addFolderEntry}/>}
    {cleanTarget&&<ImageCleaner slug={p.slug} item={cleanTarget} onClose={()=>setCleanTarget(null)}
      onCleaned={f=>{addFileEntry(f);setCleanTarget(null);}}/>}
   </PlanSection>);
}

// Brush over a logo / region on an image attachment and erase it via the
// server's OpenCV inpaint endpoint; the cleaned image is saved as a new file.
function ImageCleaner({slug,item,onClose,onCleaned}){
  const canvasRef=React.useRef(null);
  const maskRef=React.useRef(document.createElement("canvas"));
  const imgRef=React.useRef(null);
  const scrollRef=React.useRef(null);
  const[ready,setReady]=useState(false);
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  const[brush,setBrush]=useState(28);
  const[zoom,setZoom]=useState(1);
  const[cursor,setCursor]=useState(null); // live brush-preview {x,y} in rendered px, null when off-canvas
  const[canUndo,setCanUndo]=useState(false);
  const drawing=React.useRef(false);
  const history=React.useRef([]); // mask snapshots (ImageData) for undo
  const dims=React.useRef({w:0,h:0,scale:1});

  React.useEffect(()=>{
    // Same-origin uploads: no crossOrigin (it would fail the load on the static
    // uploads dir, and same-origin images never taint the canvas anyway).
    const img=new Image();
    img.onload=()=>{
      imgRef.current=img;
      const maxW=Math.min(720,window.innerWidth-80);
      const scale=Math.min(1,maxW/img.naturalWidth);
      const w=Math.round(img.naturalWidth*scale),h=Math.round(img.naturalHeight*scale);
      dims.current={w,h,scale};
      const c=canvasRef.current;if(!c)return;
      c.width=w;c.height=h;
      const m=maskRef.current;m.width=img.naturalWidth;m.height=img.naturalHeight;
      const mctx=m.getContext("2d");mctx.fillStyle="#000";mctx.fillRect(0,0,m.width,m.height);
      redraw();setReady(true);
    };
    img.onerror=()=>setErr("Could not load the image.");
    img.src=item.url;
  // eslint-disable-next-line
  },[item.url]);

  // Re-center the (possibly zoomed) canvas inside its scroll box.
  function center(){
    const s=scrollRef.current;if(!s)return;
    s.scrollLeft=(s.scrollWidth-s.clientWidth)/2;
    s.scrollTop=(s.scrollHeight-s.clientHeight)/2;
  }
  // Keep the canvas centred whenever the zoom level changes.
  React.useEffect(()=>{center();},[zoom]);

  function redraw(){
    const c=canvasRef.current,img=imgRef.current;if(!c||!img)return;
    const{w,h}=dims.current;const ctx=c.getContext("2d");
    ctx.clearRect(0,0,w,h);ctx.drawImage(img,0,0,w,h);
    ctx.save();ctx.globalAlpha=0.45;ctx.fillStyle=(()=>{try{return getComputedStyle(document.documentElement).getPropertyValue("--err").trim()||"#EF4E4C";}catch(e){return "#EF4E4C";}})();
    ctx.drawImage(maskRef.current,0,0,w,h);ctx.restore();
  }
  // Rendered-canvas coords, robust to any CSS zoom (derive scale from the rect).
  function pos(e){
    const c=canvasRef.current,r=c.getBoundingClientRect();
    const tp=e.touches&&e.touches[0];
    const rx=(tp?tp.clientX:e.clientX)-r.left,ry=(tp?tp.clientY:e.clientY)-r.top;
    // map rendered px -> unscaled canvas px so paint() math stays independent of zoom
    return{x:rx*(dims.current.w/r.width),y:ry*(dims.current.h/r.height)};
  }
  function snapshot(){
    const m=maskRef.current,mctx=m.getContext("2d");
    history.current.push(mctx.getImageData(0,0,m.width,m.height));
    if(history.current.length>20)history.current.shift();
    setCanUndo(true);
  }
  function undo(){
    if(!history.current.length)return;
    const prev=history.current.pop();
    maskRef.current.getContext("2d").putImageData(prev,0,0);
    setCanUndo(history.current.length>0);redraw();
  }
  function startStroke(e){snapshot();drawing.current=true;paint(e);}
  function paint(e){
    if(!drawing.current)return;e.preventDefault();
    const{x,y}=pos(e);const{scale}=dims.current;
    const mctx=maskRef.current.getContext("2d");
    mctx.fillStyle="#fff";mctx.beginPath();
    mctx.arc(x/scale,y/scale,brush/scale/2,0,Math.PI*2);mctx.fill();
    redraw();
  }
  function clearMask(){
    snapshot();
    const m=maskRef.current,mctx=m.getContext("2d");
    mctx.fillStyle="#000";mctx.fillRect(0,0,m.width,m.height);redraw();
  }
  function apply(){
    setBusy(true);setErr("");
    const img=imgRef.current;
    const src=document.createElement("canvas");src.width=img.naturalWidth;src.height=img.naturalHeight;
    src.getContext("2d").drawImage(img,0,0);
    let dataUrl,maskUrl;
    try{dataUrl=src.toDataURL("image/png");maskUrl=maskRef.current.toDataURL("image/png");}
    catch(e){setBusy(false);setErr("Browser blocked reading the image pixels (tainted canvas).");return;}
    fetch("/api/image/clean",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({slug,dataUrl,maskDataUrl:maskUrl,name:item.name})})
      .then(x=>x.json())
      .then(d=>{setBusy(false);if(d&&d.ok)onCleaned(d.file);else setErr("Clean failed: "+((d&&d.error)||"unknown error"));})
      .catch(e=>{setBusy(false);setErr("Clean failed: "+String(e));});
  }

  return <div className="modal" style={{alignItems:"center",justifyContent:"center"}} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="sset" role="dialog" aria-modal="true" aria-labelledby="dlg-clean-image-title" style={{maxWidth:"min(820px,94vw)"}}>
      <div className="sset-head"><h3 id="dlg-clean-image-title">{t("Clean image")}</h3><button className="btn sm" onClick={onClose}>✕ {t("Close")}</button></div>
      <div className="sset-body">
      <div className="muted" style={{fontSize:13,margin:"0 0 10px"}}>{t("Brush over the logo or area to erase, then Clean.")}</div>
      <div ref={scrollRef} style={{overflow:"auto",textAlign:"center",background:"var(--bg)",borderRadius:8,padding:8,maxHeight:"62vh"}}>
        {!ready&&!err&&<div className="muted" style={{padding:20}}>{t("Loading…")}</div>}
        <div style={{position:"relative",display:ready?"inline-block":"none",lineHeight:0}}>
          <canvas ref={canvasRef} style={{width:(dims.current.w*zoom)+"px",height:"auto",maxWidth:"none",cursor:"crosshair",touchAction:"none",display:"block"}}
            onMouseDown={startStroke}
            onMouseMove={e=>{setCursor(pos(e));paint(e);}}
            onMouseUp={()=>drawing.current=false} onMouseLeave={()=>{drawing.current=false;setCursor(null);}}
            onTouchStart={startStroke} onTouchMove={paint} onTouchEnd={()=>drawing.current=false}/>
          {cursor&&<div style={{position:"absolute",pointerEvents:"none",border:"1px solid var(--err)",borderRadius:"50%",
            width:brush*zoom+"px",height:brush*zoom+"px",
            left:(cursor.x*zoom-brush*zoom/2)+"px",
            top:(cursor.y*zoom-brush*zoom/2)+"px",
            boxShadow:"0 0 0 1px rgba(255,255,255,.6)"}}/>}
        </div>
      </div>
      <div className="acts" style={{marginTop:10,alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <label className="muted" style={{fontSize:12}}>{t("Brush")}
          <input type="range" min="8" max="80" value={brush} onChange={e=>setBrush(Number(e.target.value))} style={{marginInlineStart:6,verticalAlign:"middle"}}/>
        </label>
        <div className="muted" style={{fontSize:12,display:"inline-flex",alignItems:"center",gap:4}}>{t("Zoom")}
          <button className="btn xs" onClick={()=>setZoom(z=>Math.max(0.25,Math.round((z-0.25)*100)/100))} disabled={busy||!ready} title={t("Zoom out")}>−</button>
          <span style={{minWidth:38,textAlign:"center",display:"inline-block"}}>{Math.round(zoom*100)}%</span>
          <button className="btn xs" onClick={()=>setZoom(z=>Math.min(4,Math.round((z+0.25)*100)/100))} disabled={busy||!ready} title={t("Zoom in")}>+</button>
        </div>
        <button className="btn sm" onClick={()=>{setZoom(1);setTimeout(center,0);}} disabled={busy||!ready} title={t("Fit and center the image")}>⊕ {t("Center")}</button>
        <button className="btn sm" onClick={undo} disabled={busy||!canUndo} title={t("Undo last stroke")}>↩ {t("Undo")}</button>
        <button className="btn sm" onClick={clearMask} disabled={busy}>{t("Reset mask")}</button>
        <div className="sp"/>
        <button className="btn sm" onClick={onClose} disabled={busy}>{t("Cancel")}</button>
        <button className="btn sm primary" onClick={apply} disabled={busy||!ready}>{busy?t("Cleaning…"):("🧹 "+t("Clean"))}</button>
      </div>
      {err&&<div className="err" style={{marginTop:8}}>{err}</div>}
      </div>
    </div>
  </div>;
}

const TIP_COLORS={high:"var(--err)",med:"var(--warn)",low:"var(--text-4)"};
const TIP_LABELS={high:"High",med:"Medium",low:"Low"};
// Per-category chip styling for tasks. Keys mirror server.js TIP_CATEGORIES.
const TIP_CATEGORY_META={
  // literal hex required here (not var(--…)) — colors feed the rgba() hex-parse
  // helper below, not a CSS property directly. Security uses --err red (rather
  // than the literal warn bucket its old #fb923c hex maps to) so it stays
  // visually distinct from Performance's amber — the six categories need six
  // distinguishable swatches, and "security" reads naturally as danger-red.
  "Security":      {emoji:"🛡️",color:"#EF4E4C"},
  "Performance":   {emoji:"⚡",color:"#E8912F"},
  "Development":   {emoji:"🚧",color:"#38bdf8"},
  "Upgrade/Feature":{emoji:"✨",color:"#D78DFE"},
  "SEO/Marketing": {emoji:"📈",color:"#2FAE80"},
  "UI/UX Design":  {emoji:"🎨",color:"#FD6765"},
  "Maintenance":   {emoji:"🔧",color:"#53BCF6"},
};
function CategoryChip({category,onClick,active}){
  if(!category)return null;
  const m=TIP_CATEGORY_META[category]||{emoji:"🏷️",color:"#8A9099"};
  // Hex→rgba helper so one base color yields both the tint and the border.
  const rgba=(hex,a)=>{const n=parseInt(hex.slice(1),16);return"rgba("+((n>>16)&255)+","+((n>>8)&255)+","+(n&255)+","+a+")";};
  return <span className="chip" role={onClick?"button":null}
    title={onClick?(active?"Clear filter — show all tasks":"Filter by "+category):"Category: "+category}
    onClick={onClick||undefined}
    style={{background:rgba(m.color,active?.3:.15),color:m.color,borderColor:rgba(m.color,active?.7:.4),
      cursor:onClick?"pointer":undefined}}>{category}</span>;
}

// Build a self-contained prompt (project context + one AI tip) to hand to Claude.
function buildClaudePrompt(p,t){
  const name=[p.nameHe,p.nameEn&&("("+p.nameEn+")")].filter(Boolean).join(" ")||p.slug||"this project";
  const ctx=[
    "Project: "+name,
    p.oneLiner&&("One-liner: "+p.oneLiner),
    p.stack&&p.stack.length&&("Stack: "+p.stack.join(", ")),
    p.integrations&&p.integrations.length&&("Integrations: "+p.integrations.join(", ")),
    p.hosting&&("Hosting: "+p.hosting),
    p.db&&p.db.type&&("Database: "+p.db.type),
  ].filter(Boolean).join("\n");
  const issue=[
    t.title&&("Title: "+t.title),
    t.detail&&("Description: "+t.detail),
  ].filter(Boolean).join("\n");
  return [
    "I'm working on the project below. Fix the issue described under \"Issue to address\".",
    "","## Project context",ctx,
    "","## Issue to address",issue,
    "","## When you're done, end your reply with exactly these two lines:",
    "Whats done: <one short sentence describing what you did>",
    "Hero action: <the single action I need to take, or \"None\" besides deploy/restart etc>",
  ].join("\n");
}

// format a tip's generation time; "" when there's nothing to show
function tipTime(iso){if(!iso)return"";try{return new Date(iso).toLocaleString("en-GB");}catch{return"";}}
// Deep Scan runs are detached server-side; this remembers the live runId per slug
// so a scan's progress can be re-attached after the user leaves and returns to the
// project tab (the run keeps going + persists tips either way). Keyed by slug.
const activeScans=new Map();
// The "What does this mean?" button on each task card. Tasks are usually written
// in English (they come from the AI audit); this fetches a short plain-English
// explanation from
// /api/tips/explain (cached server-side on first click) and shows it in a small
// popover so an end user gets the gist without reading the English detail.
function WhatIsThis({slug,tip}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  const[he,setHe]=useState(tip.he||"");
  const[loading,setLoading]=useState(false);
  const[err,setErr]=useState("");
  const btnRef=useRef(null);
  const[pos,setPos]=useState(null);
  // The popover lives in .main (overflow-y:auto), so an absolutely-positioned
  // box gets clipped by the scroll container and lands out of sight. Portal it to
  // <body> with fixed positioning, anchored to the button's right edge, and flip
  // above the button when there isn't room below in the viewport.
  function place(){
    const b=btnRef.current;if(!b)return;
    const r=b.getBoundingClientRect();
    const W=270,margin=8,below=window.innerHeight-r.bottom;
    const flipUp=below<220&&r.top>below;
    setPos({
      top:flipUp?undefined:r.bottom+6,
      bottom:flipUp?(window.innerHeight-r.top+6):undefined,
      right:Math.max(margin,window.innerWidth-r.right),
      maxWidth:Math.min(W,window.innerWidth-margin*2),
    });
  }
  useEffect(()=>{
    if(!open)return;
    place();
    const h=()=>place();
    window.addEventListener("resize",h);window.addEventListener("scroll",h,true);
    return()=>{window.removeEventListener("resize",h);window.removeEventListener("scroll",h,true);};
  },[open]);
  function toggle(){
    const next=!open;setOpen(next);
    if(next&&!he&&!loading){
      setLoading(true);setErr("");
      fetch("/api/tips/explain",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({slug,id:tip.id,...loadAiUsePrefs().explain})})
        .then(r=>r.json())
        .then(d=>{
          if(!d||!d.ok)throw new Error((d&&d.error)||"fail");
          // Internal hub answers synchronously with the text; the cloud returns a
          // runId for an agent-generated explanation we then poll to completion.
          if(d.he){setHe(d.he);tip.he=d.he;return;}
          if(d.runId)return pollExplain(d.runId);
          throw new Error("Couldn't explain this task.");
        })
        .catch((e)=>setErr(String((e&&e.message)||"")||"Error loading the explanation."))
        .finally(()=>setLoading(false));
    }
  }
  // Cloud only: wait for the paired agent's explain run to finish, then read the
  // explanation the server stored on it. Rejects on failure or if the agent takes
  // too long; the .finally above clears loading.
  function pollExplain(runId){
    return new Promise((resolve,reject)=>{
      let tries=0;
      const tick=()=>{
        fetch("/api/run/"+encodeURIComponent(runId)).then(r=>r.json()).then(d=>{
          const run=d&&d.run;
          if(run&&run.done){
            if(run.explain&&run.explain.ok&&run.explain.he){setHe(run.explain.he);tip.he=run.explain.he;resolve();}
            else reject(new Error((run.explain&&run.explain.error)||"Couldn't explain this task."));
            return;
          }
          if(++tries>40)return reject(new Error("The explanation is taking too long — please try again."));
          setTimeout(tick,1500);
        }).catch(()=>reject(new Error("Error loading the explanation.")));
      };
      tick();
    });
  }
  return <span className="whatis">
    <button ref={btnRef} type="button" className="btn xs" onClick={toggle} title={t("Short explanation of the task")}>{t("What does this mean?")}</button>
    {open&&ReactDOM.createPortal(<React.Fragment>
      {/* clicking the back (anywhere outside the popover) closes it — same as the modal/iframe overlays */}
      <div className="pop-back" onMouseDown={()=>setOpen(false)}/>
      <span className="whatis-pop" style={pos?{position:"fixed",top:pos.top,bottom:pos.bottom,right:pos.right,insetInlineEnd:"auto",maxWidth:pos.maxWidth}:{visibility:"hidden"}}>
        <button className="x" title={t("Close")} aria-label={t("Close")} onClick={(e)=>{e.stopPropagation();setOpen(false);}}>✕</button>
        {loading?<span className="muted">{t("Loading explanation…")}</span>
          :err?<span style={{color:"var(--err)"}}>{err}</span>
          :<span>{he}</span>}
      </span>
    </React.Fragment>,document.body)}
  </span>;
}
// Short label for the current task-run pick, shown on the "Ask …" button.
// Any provider with more than one selectable model (AI_MODELS[provider])
// collapses to just the provider name on its default model, else names the
// model (e.g. "Sonnet 5", "Sol"); a provider with no real choices (just the
// account-default entry) always shows its provider name.
function askChoiceLabel(choice){
  if(!choice)return"Claude";
  const models=AI_MODELS[choice.provider];
  if(models&&models.length>1)return choice.model?((models.find(m=>m.value===choice.model)||{}).label||providerName(choice.provider)):providerName(choice.provider);
  return providerName(choice.provider);
}
// Split "Ask …" button for a task card: the main face runs the pick; the caret
// opens a small menu of providers (Claude / Codex / Gemini). Any provider with
// more than one selectable model expands a second menu of every model on
// hover. The pick is remembered by the parent so the last choice stays shown
// on the face across cards and reloads.
const ASK_PROVIDERS=[{key:"claude",name:"Claude"},{key:"codex",name:"Codex"},{key:"gemini",name:"Gemini"}];
function AskAgentButton({label,disabled,onAsk,choice,setChoice}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);           // provider menu open?
  const[modelsOpen,setModelsOpen]=useState(null); // key of the provider whose model submenu is open, if any
  const ref=useRef(null);
  const caretRef=useRef(null),popRef=useRef(null);
  useMenuFocus(open,popRef,caretRef);
  // close on click-outside / Esc, mirroring the header menu
  useEffect(()=>{
    if(!open)return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target)){setOpen(false);setModelsOpen(null);}};
    const esc=e=>{if(e.key==="Escape"){setOpen(false);setModelsOpen(null);}};
    document.addEventListener("mousedown",h);document.addEventListener("keydown",esc);
    return()=>{document.removeEventListener("mousedown",h);document.removeEventListener("keydown",esc);};
  },[open]);
  function pick(provider,model){setChoice({provider,model:model||""});setOpen(false);setModelsOpen(null);}
  return <div className="askbtn" ref={ref}>
    <button className="btn xs askbtn-main" disabled={disabled} onClick={onAsk}
      title={t("Run this task with the selected AI, in this project's folder, in the side panel")}>{label}</button>
    <button ref={caretRef} className={"btn xs askbtn-caret"+(open?" open":"")} aria-haspopup="menu" aria-expanded={open}
      aria-label={t("Choose which AI runs this task")}
      title={t("Choose which AI runs this task")} onClick={()=>{setModelsOpen(null);setOpen(v=>!v);}}>▾</button>
    {open&&<div ref={popRef} className="askbtn-pop" role="menu">
      {ASK_PROVIDERS.map(({key,name})=>{
        const models=AI_MODELS[key]||[];
        const active=choice.provider===key;
        if(models.length<=1)return(
          <button key={key} className={"menu-item"+(active?" on":"")} role="menuitem" onClick={()=>pick(key,"")}>
            <ProviderLogo provider={key}/><span style={{flex:1}}>{name}</span></button>
        );
        const subOpen=modelsOpen===key;
        return(
          <div key={key} className="askbtn-group" onMouseEnter={()=>setModelsOpen(key)} onMouseLeave={()=>setModelsOpen(null)}>
            <button className={"menu-item"+(active?" on":"")} role="menuitem" aria-expanded={subOpen}
              onClick={()=>pick(key,active?choice.model:"")} title={name+" — hover to pick a model"}>
              <ProviderLogo provider={key}/><span style={{flex:1}}>{name}</span>
              <span className={"askbtn-arr"+(subOpen?" open":"")} aria-hidden="true">▸</span>
            </button>
            {subOpen&&<div className="askbtn-sub">
              {models.map(m=>(
                <button key={m.value||"default"} className={"menu-item"+(active&&choice.model===m.value?" on":"")} role="menuitem"
                  onClick={()=>pick(key,m.value)} title={m.desc||""}>
                  <span className="ic">{active&&choice.model===m.value?"●":"○"}</span>
                  <span style={{flex:1}}>{m.label}</span>
                  {m.hint&&<span className="askbtn-hint">{m.hint}</span>}
                </button>
              ))}
            </div>}
          </div>
        );
      })}
    </div>}
  </div>;
}
function AiTips({p,noAgent,onAskClaude,nudgeTipId,onClearNudge,onOpenTipChat,onOpenRunChat,onOpenCount,startRun}){
  const{lang}=useLang();
  const[tips,setTips]=useState(null);
  const[at,setAt]=useState(null);
  const[ready,setReady]=useState(false);
  const[err,setErr]=useState("");
  const[note,setNote]=useState(""); // transient summary after a Deep Scan (completed/added counts)
  const[sent,setSent]=useState(null); // id of the tip just sent to the dock chat
  // Which AI runs a task card's "Ask …" button — {provider,model}, shared by every
  // card here and remembered across reloads so the last pick stays on the button.
  const[askChoice,setAskChoice]=useState(()=>{
    try{const v=JSON.parse(localStorage.getItem("hub.taskAskChoice"));
      if(v&&["claude","codex","gemini"].includes(v.provider))return{provider:v.provider,model:typeof v.model==="string"?v.model:""};
    }catch(e){}
    return{provider:"claude",model:""};
  });
  useEffect(()=>{try{localStorage.setItem("hub.taskAskChoice",JSON.stringify(askChoice));}catch(e){}},[askChoice]);
  const[scanning,setScanning]=useState(false); // a Deep Scan stream is attached
  const[scanMsg,setScanMsg]=useState(""); // live "what Claude is doing" status during a scan
  const[catFilter,setCatFilter]=useState("all"); // active task tab: "all" | a TIP_CATEGORIES value
  const[statusFilter,setStatusFilter]=useState("active"); // completion filter: "all" | "active" | "completed" — default to active so completed/postponed tasks stay out of the way
  const[scanOutcome,setScanOutcome]=useState(null); // persisted {status,at,reason} of the last scan — surfaces an interrupted/errored run across reloads
  const[scanHistory,setScanHistory]=useState([]); // persisted list of past scan outcomes (newest first): {status,at,reason}
  const[histOpen,setHistOpen]=useState(false); // scan-history panel expanded?
  const[scanType,setScanType]=useState(null); // which of the 7 scan types has its composer open (a TIP_CATEGORY_META key), or null
  const[scanMenuOpen,setScanMenuOpen]=useState(false); // the "🔍 Scan ▾" launcher menu (the 7 type buttons) open?
  // The manual Deep Scan composer reuses AutomationForm (the scheduled-scan form) so
  // its layout/styling stay identical. scanDraft mimics a scoped scan rule for this
  // project (target locked to p.slug, hidden from the selectors); the form's fields
  // map onto the manual scan: cats→scope, instructions→note, model, systemPrompt→brief.
  const[scanDraft,setScanDraft]=useState(()=>blankAuto("scan",p.slug));
  const scanRef=useRef(null); // {ac, slug} of the in-flight scan stream, so slug changes can detach it
  // build a ready-made prompt about one tip and run it in this project's dock chat.
  // The promotion to "in progress" is NOT done here: a tip should only count as
  // started once the run actually launches, which the dock signals back via
  // onTipStart → startRun (the prompt may just land in the input box if busy).
  function askClaude(t){
    onAskClaude&&onAskClaude(buildClaudePrompt(p,t),t.id,t.title,askChoice);
    setSent(t.id);setTimeout(()=>setSent(s=>s===t.id?null:s),1800);
  }
  // move one tip through its lifecycle (open → in progress → solved): optimistic
  // local update by id, then persist by id; if the save fails, roll the card back
  // and surface the error instead of silently diverging from the server.
  function setStatus(t,status,completedBy){
    const prevStatus=t.status||"open",prevBy=t.completedBy;
    // mirror the server's completedBy bookkeeping locally so badges update instantly
    const nextBy=status==="solved"?(completedBy||"human"):(status==="awaiting_approval"?t.completedBy:undefined);
    setTips(prev=>(prev||[]).map(x=>x.id===t.id?{...x,status,completedBy:nextBy}:x));
    fetch("/api/tips/status",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({slug:p.slug,id:t.id,status,completedBy})})
      .then(r=>r.json()).then(d=>{if(!d||!d.ok)throw 0;})
      .catch(()=>{setTips(prev=>(prev||[]).map(x=>x.id===t.id?{...x,status:prevStatus,completedBy:prevBy}:x));
        setErr("Couldn't save status — reverted.");setTimeout(()=>setErr(""),4000);});
  }
  // Keep the sidebar/project-list "open tasks" badge in sync the instant a task's
  // status changes here — completing, approving, reopening, or a Deep Scan all flow
  // through setTips, so reporting the active count on every tips change covers them
  // without waiting for the 30s /api/tips/counts poll. Mirrors the server's count:
  // active = status not "solved" and not "ignored".
  useEffect(()=>{
    if(!Array.isArray(tips))return;
    const active=tips.filter(t=>{const s=t.status||"open";return s!=="solved"&&s!=="ignored";}).length;
    onOpenCount&&onOpenCount(p.slug,active);
  },[tips,p.slug]);
  // Tasks can't be removed — a finished one is marked completed (status "solved")
  // and stays on the list. The status control below is the only way to "close" a task.
  // The dock fires onTipStart only when a tip's run actually launches, handing back
  // the chat's id. Promote that tip to "in progress" (only from "open" — never
  // reopen a solved one) and record its chat id so the card can link back to it.
  useEffect(()=>{
    if(!startRun||!startRun.tipId)return;
    const{tipId,chatId}=startRun;
    setTips(prev=>{
      const cur=(prev||[]).find(x=>x.id===tipId);
      if(!cur)return prev;
      const promote=(cur.status||"open")==="open";
      if(promote)fetch("/api/tips/status",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({slug:p.slug,id:tipId,status:"in_progress"})}).catch(()=>{});
      if(chatId)fetch("/api/tips/chat",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({slug:p.slug,id:tipId,chatId})}).catch(()=>{});
      return prev.map(x=>x.id===tipId?{...x,...(promote?{status:"in_progress"}:{}),...(chatId?{chatId}:{})}:x);
    });
  },[startRun]);
  // load any previously-generated tips for this project (persisted server-side)
  useEffect(()=>{
    let live=true;
    setTips(null);setAt(null);setErr("");setReady(false);setCatFilter("all");setStatusFilter("active");setScanType(null);setScanMenuOpen(false);setScanDraft(blankAuto("scan",p.slug));setScanOutcome(null);setScanHistory([]);setHistOpen(false);
    fetch("/api/tips?slug="+encodeURIComponent(p.slug))
      .then(r=>r.json())
      .then(d=>{if(!live)return;if(d&&d.ok&&d.tips){setTips(d.tips);setAt(d.generatedAt||null);}if(d&&d.ok){setScanOutcome(d.lastScan||null);setScanHistory(Array.isArray(d.scanHistory)?d.scanHistory:[]);}setReady(true);})
      .catch(()=>{if(live)setReady(true);});
    return()=>{live=false;};
  },[p.slug]);
  // Pull the persisted scan-history list after a scan lands, so the panel reflects
  // the just-recorded outcome. Guarded on slug so a late response can't write into
  // another project's view.
  function refreshScanHistory(slug){
    fetch("/api/tips?slug="+encodeURIComponent(slug)).then(r=>r.json())
      .then(d=>{if(slug===p.slug&&d&&d.ok&&Array.isArray(d.scanHistory))setScanHistory(d.scanHistory);}).catch(()=>{});}
  // Handle one ndjson event from a Deep Scan stream (fresh run or reconnect).
  // `slug` is captured at attach time so a stream can never write into a different
  // project's view. Terminal events (tips/done/gone/tips_error) clear activeScans;
  // an abort (project switch) must NOT, so the run stays re-attachable on return.
  function onScanEvent(slug){return(ev)=>{
    if(ev.type==="assistant"&&ev.message&&Array.isArray(ev.message.content)){
      for(const b of ev.message.content){
        if(b.type==="tool_use")setScanMsg(TOOL_VERBS[b.name]||("running "+b.name)); // short verb only — no command detail
        else if(b.type==="text"&&b.text)setScanMsg("analyzing findings…");
      }
      return;
    }
    if(ev.type!=="hub")return;
    if(ev.event==="start"&&ev.runId)activeScans.set(slug,ev.runId);
    else if(ev.event==="tips"&&Array.isArray(ev.tips)){activeScans.delete(slug);
      setTips(ev.tips);setAt(ev.generatedAt||null);setScanOutcome({status:"ok",at:ev.generatedAt||null});refreshScanHistory(slug);
      const parts=[];if(ev.completed)parts.push(ev.completed+" marked completed");if(ev.added)parts.push(ev.added+" new");
      setNote(parts.length?parts.join(" · "):"all tasks still relevant");setTimeout(()=>setNote(""),6000);}
    else if(ev.event==="tips_error"){activeScans.delete(slug);setErr(ev.error||"Deep scan returned no parsable result.");setScanOutcome({status:"error",reason:ev.error||"",at:null});refreshScanHistory(slug);}
    else if(ev.event==="error")setErr(ev.error||"Deep scan error.");
    else if(ev.event==="done")activeScans.delete(slug);
    else if(ev.event==="gone"){activeScans.delete(slug); // run expired while away → pull whatever it persisted
      fetch("/api/tips?slug="+encodeURIComponent(slug)).then(r=>r.json()).then(d=>{if(d&&d.ok&&d.tips){setTips(d.tips);setAt(d.generatedAt||null);}}).catch(()=>{});}
  };}
  // Read a scan stream to completion. `makeRes(signal)` resolves to the Response.
  // Only the stream that is still current for its slug clears the spinner, so a
  // detached (aborted) stream from a previous project doesn't reset a new scan.
  function runScanStream(slug,makeRes){
    const ac=new AbortController();scanRef.current={ac,slug};
    setScanning(true);
    makeRes(ac.signal)
      .then(res=>readNdjson(res,onScanEvent(slug)))
      .catch(e=>{if(!ac.signal.aborted)setErr("Deep scan failed: "+String(e&&e.message||e));})
      .finally(()=>{if(scanRef.current&&scanRef.current.ac===ac){setScanning(false);setScanMsg("");scanRef.current=null;}});
  }
  // Start a Deep Scan. `cats` (optional) scopes it to those categories — each
  // contributes its own focused prompt; several build a longer combined one. An
  // empty list runs the full deep scan across all areas. `userNote` is free-text
  // forwarded to the model as extra instructions. `opts` carries the per-scan
  // provider/model override and custom audit brief chosen in the composer.
  function deepScan(cats,userNote,opts){
    if(scanning)return;
    opts=opts||{};
    setErr("");setNote("");setScanOutcome(null);setScanMsg("starting deep scan…");setScanType(null);setScanMenuOpen(false);
    const body={slug:p.slug};
    const scanPref=loadAiUsePrefs().scan;
    body.provider=scanPref.provider;body.model=scanPref.model;
    if(opts.provider)body.provider=opts.provider; // per-scan provider override wins over the AI-settings default
    if(opts.model)body.model=opts.model; // per-scan model override wins over the AI-settings default
    // Stored Scan-AI settings (⚙ next to the scan tab): a custom brief that
    // replaces the built-in one, plus extra instructions appended to the note.
    const scanPrompt=loadAiUsePrompts().scan||{};
    if(opts.system&&opts.system.trim())body.system=opts.system; // per-scan override wins
    else if(scanPrompt.system&&scanPrompt.system.trim())body.system=scanPrompt.system;
    if(cats&&cats.length)body.cats=cats;
    let note=(userNote||"").trim();
    if(scanPrompt.instructions&&scanPrompt.instructions.trim())note=(note?note+"\n\n":"")+scanPrompt.instructions.trim();
    if(note)body.note=note;
    runScanStream(p.slug,(signal)=>fetch("/api/tips/scan",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body),signal})
      .then(res=>{if(!res.ok)return res.json().then(d=>{throw new Error((d&&d.error)||("HTTP "+res.status));});return res;}));
  }
  // (The "Live status" panel's ⟳ Refresh button now dispatches hub:refresh-status,
  // handled at the App level as a fast live health re-measure — no Deep Scan here.)
  // Re-attach to a scan still running for this project (left & returned). On slug
  // change the cleanup detaches the current stream — the server keeps the run alive.
  useEffect(()=>{
    const rid=activeScans.get(p.slug);
    if(rid){setScanMsg("re-attaching to deep scan…");
      runScanStream(p.slug,(signal)=>fetch("/api/claude/stream?runId="+encodeURIComponent(rid),{signal})
        .then(res=>{if(!res.ok)throw new Error("HTTP "+res.status);return res;}));}
    return()=>{if(scanRef.current)scanRef.current.ac.abort();};
  },[p.slug]);
  return(
   <div className="sec">
    <div className="aitips-head" style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
      <h3 className="aitips-h" style={{margin:0}}></h3>
      {at&&<span className="muted" style={{fontSize:12,letterSpacing:0}}>last run {tipTime(at)}</span>}
      {note&&<span className="muted" style={{fontSize:12,letterSpacing:0,color:"var(--accent,#38C492)"}}>· {note}</span>}
      <div style={{flex:1}}/>
    </div>
    {scanType&&!scanning&&(
      // Reuse the scheduled-scan form for the manual composer, locked to a single scan
      // type. `manualScan` locks it to this project; `lockedCat` fixes the area to the
      // chosen type and hides the area picker. Create fires deepScan for that one type.
      <AutomationForm draft={scanDraft} setDraft={setScanDraft} projects={[]}
        targetOptions={[[p.slug,"📁 This project"]]} manualScan={true} lockedCat={scanType}
        onSave={()=>deepScan([scanType],scanDraft.instructions,{provider:scanDraft.provider,model:scanDraft.model,system:scanDraft.systemPrompt})}
        onCancel={()=>setScanType(null)}/>
    )}
    {err&&<div className="err">{err}</div>}
    {scanning&&<div className="muted" style={{fontSize:13}}>🔍 Deep scan running in {p.nameEn||p.nameHe||"this project"} <span style={{opacity:.7}}>(safe to leave; it keeps going)</span></div>}
    {!scanning&&scanOutcome&&scanOutcome.status==="interrupted"&&(
      <div role="status" className="state-box warn" style={{fontSize:12.5,padding:"7px 10px",borderRadius:8,display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
        <StateChip kind="warn" label={t("Interrupted")}/>
        <span style={{flex:1,minWidth:0}}>{t("The last deep scan was interrupted by a server restart")}{scanOutcome.at?" ("+tipTime(scanOutcome.at)+")":""}. {t("Its results were lost.")}</span>
        {!noAgent&&<button type="button" className="btn xs" onClick={()=>deepScan([])}>{t("Run the scan again")}</button>}
      </div>)}
    {ready&&!tips&&!scanning&&!err&&(noAgent
      ?<div style={{fontSize:13,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}><span className="muted">{t("Connect your machine so Claude can scan this project's real code and return grounded tasks.")}</span><InstallAgentBtn label="Install the agent"/></div>
      :<div className="muted" style={{fontSize:13}}>{t("Run a Deep Scan to inspect this project's real code, dependencies, live site and database and get grounded tasks to fix and improve it.")}</div>)}
    {tips&&tips.length===0&&(noAgent
      ?<div style={{fontSize:13,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}><span className="muted">{t("No agent is connected, so scans can't run.")}</span><InstallAgentBtn label="Install the agent"/></div>
      :<div className="muted" style={{fontSize:13}}>{t("No suggestions returned.")}</div>)}
    {tips&&tips.length>0&&(()=>{const hasOld=tips.some(x=>x.generatedAt&&x.generatedAt!==at); // any pre-rescan tip still present
     // Auto-sort: tasks awaiting your approval first, then in-progress, then by urgency
     // (high→med→low), solved then ignored/postponed sink to the bottom.
     const sRank={awaiting_approval:0,in_progress:1,open:2,solved:3,ignored:4},pRank={high:0,med:1,low:2};
     const sorted=tips.map((t,i)=>[t,i]).sort((a,b)=>
       (sRank[a[0].status||"open"]-sRank[b[0].status||"open"])
       ||((pRank[a[0].priority]??1)-(pRank[b[0].priority]??1))
       ||(a[1]-b[1])).map(x=>x[0]);
     // Category tabs: "All" + one tab per category with active (not-yet-solved)
     // tasks (in the fixed TIP_CATEGORIES order), each with a live count. Counts and
     // presence ignore completed AND ignored/postponed tasks — a category whose tasks
     // are all done or parked drops off.
     const counts=sorted.reduce((m,t)=>{if(t.status==="solved"||t.status==="ignored")return m;const k=t.category||"Uncategorized";m[k]=(m[k]||0)+1;return m;},{});
     const cats=Object.keys(TIP_CATEGORY_META).filter(c=>counts[c]); // active present, in canonical order
     if(counts["Uncategorized"])cats.push("Uncategorized");
     // Guard: if a re-scan dropped the selected category, fall back to "All".
     const active=catFilter!=="all"&&!counts[catFilter]?"all":catFilter;
     const inCat=active==="all"?sorted:sorted.filter(t=>(t.category||"Uncategorized")===active);
     // Completion filter (independent of category): "all" | "active" | "completed" | "ignored".
     // "active" excludes both completed and ignored/postponed tasks.
     const doneCount=inCat.filter(t=>t.status==="solved").length;
     const ignoredCount=inCat.filter(t=>t.status==="ignored").length;
     const shown=statusFilter==="completed"?inCat.filter(t=>t.status==="solved")
       :statusFilter==="ignored"?inCat.filter(t=>t.status==="ignored")
       :statusFilter==="active"?inCat.filter(t=>t.status!=="solved"&&t.status!=="ignored"):inCat;
     const Tab=({id,label,color,count})=>{const on=active===id;
       return <button className={"tasktab"+(on?" on":"")} onClick={()=>setCatFilter(id)}
         style={on&&color?{borderColor:color,color:color,background:"color-mix(in srgb,var(--text-4) 8%,transparent)"}:null}>
         {label} <span className="tasktab-n">{count}</span></button>;};
     return <div>
      <div className="tasktabs">
        <div className="tf-group tf-cats">
          <Tab id="all" label={t("All categories")} count={sorted.length}/>
          {cats.map(c=>{const m=TIP_CATEGORY_META[c]||{color:"#8A9099"};
            return <Tab key={c} id={c} label={c} color={m.color} count={counts[c]}/>;})}
        </div>
        <label className="tf-group tf-status tf-status-select">
          <span className="tf-status-label">{t("Status:")}</span>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} title={t("Filter tasks by status")}>
            <option value="all">{t("Any status")} ({inCat.length})</option>
            <option value="active">{t("Active")} ({inCat.length-doneCount-ignoredCount})</option>
            <option value="completed">{t("Completed")} ({doneCount})</option>
            {(ignoredCount>0||statusFilter==="ignored")&&<option value="ignored">{t("Ignore/Postpone")} ({ignoredCount})</option>}
          </select>
        </label>
      </div>
      <div className="tipgrid">{shown.map((tip)=>{const c=TIP_COLORS[tip.priority]||"var(--text-3)";
      const tt=tip.generatedAt||at; // per-tip time, falling back to the batch time for older tips
      const isNew=tip.generatedAt&&at&&tip.generatedAt===at&&hasOld; // freshly added on the last scan
      return(
      <div key={tip.id} className={"live task-row prio-"+(TIP_COLORS[tip.priority]?tip.priority:"low")} style={{"--prio":c,opacity:(tip.status==="solved"||tip.status==="ignored")?0.55:1,transition:"opacity .2s",display:"flex",alignItems:"flex-start",gap:12}}>
        <div style={{flex:1,minWidth:0}}>
          {nudgeTipId===tip.id&&tip.status!=="solved"&&tip.status!=="awaiting_approval"&&
            <div className="tip-nudge">
              <span>✅ Claude finished — mark this task completed?</span>
              <span style={{flex:1}}/>
              <button className="btn xs" onClick={()=>{setStatus(tip,"solved");onClearNudge&&onClearNudge();}}>✓ Mark completed</button>
              <button className="tip-nudge-x" title={t("Dismiss")} aria-label={t("Dismiss")} onClick={()=>onClearNudge&&onClearNudge()}>✕</button>
            </div>}
          {tip.status==="awaiting_approval"&&
            <div className="tip-nudge tip-nudge-approval" style={{background:"color-mix(in srgb,var(--warn) 12%,transparent)",borderColor:"color-mix(in srgb,var(--warn) 50%,transparent)"}}>
              <span style={{flex:1,minWidth:0}}>🤖 An automation finished this task — approve the completion?</span>
              <button className="btn xs" style={{flexShrink:0,whiteSpace:"nowrap"}} onClick={()=>setStatus(tip,"solved","human")}>✓ Approve</button>
              <button className="btn xs" style={{flexShrink:0,whiteSpace:"nowrap"}} onClick={()=>setStatus(tip,"open")}>↩ Reject</button>
            </div>}
          <div className="tip-head">
            <span className="tip-headline">
              <span className={"prio-chip prio-"+(TIP_COLORS[tip.priority]?tip.priority:"low")} title={t("Urgency")}>{t(TIP_LABELS[tip.priority]||"Low")}</span>
              <TipTitle title={tip.title}/>
            </span>
            <span className="tip-chips">
              <CategoryChip category={tip.category} active={active===tip.category}
                onClick={()=>setCatFilter(active===tip.category?"all":tip.category)}/>
              {isNew&&<span className="chip tone-ok">{t("New")}</span>}
              {tip.status==="awaiting_approval"&&<span className="chip tone-warn">{t("Awaiting approval")}</span>}
              {tip.status==="solved"&&tip.completedBy==="auto"&&<span className="chip tone-info">{t("Auto-completed")}</span>}
              {tip.status==="solved"&&tip.completedBy!=="auto"&&<span className="chip tone-ok">{t("Completed")}</span>}
              {tip.status==="ignored"&&<span className="chip tone-muted">{t("Ignore/Postpone")}</span>}
            </span>
          </div>
          {tt&&<div className="muted" style={{fontSize:11,letterSpacing:0,marginBottom:tip.detail?6:4}} title={t("When this tip was generated")}>{tipTime(tt)}</div>}
          {tip.detail&&<TipDetail detail={tip.detail}/>}
        </div>
        <div className="task-actions" style={{display:"flex",flexDirection:"column",alignItems:"stretch",gap:6,flexShrink:0,width:156}}>
          <AskAgentButton choice={askChoice} setChoice={setAskChoice} onAsk={()=>askClaude(tip)}
            label={sent===tip.id?"✓ Sent →":(tip.chatId?"Ask "+askChoiceLabel(askChoice)+" again":"Ask "+askChoiceLabel(askChoice))}/>
          {tip.chatId&&<button className="btn xs" onClick={()=>onOpenTipChat&&onOpenTipChat(p.slug,tip.chatId)}
            title={t("Open the Claude chat where this task was sent")}>
            💬 {t("Open chat")} →</button>}
          {!tip.chatId&&(tip.autoRun||tip.completedBy==="auto")&&<button className="btn xs" onClick={()=>onOpenRunChat&&onOpenRunChat(p.slug,tip)}
            title={t("Open the AI run where this task was worked on")}>
            💬 {t("Open chat")} →</button>}
          <select className="tip-status" value={tip.status||"open"} onChange={e=>setStatus(tip,e.target.value,e.target.value==="solved"?"human":undefined)}
            title={t("Track your progress on this task, or set it aside to ignore/postpone")}
            style={(tip.status==="in_progress"||tip.status==="awaiting_approval")?{background:"color-mix(in srgb,var(--warn) 12%,transparent)",borderColor:"color-mix(in srgb,var(--warn) 50%,transparent)",color:"var(--warn-fg,var(--warn))",fontWeight:600}
              :tip.status==="ignored"?{background:"color-mix(in srgb,var(--text-4) 12%,transparent)",borderColor:"color-mix(in srgb,var(--text-4) 50%,transparent)",color:"var(--text-3)",fontWeight:600}:null}>
            <option value="open">○ Open</option>
            <option value="in_progress">◐ {t("In progress")}</option>
            {tip.status==="awaiting_approval"&&<option value="awaiting_approval">{t("Awaiting approval")}</option>}
            <option value="solved">✓ Completed</option>
            <option value="ignored">{t("Ignore / Postpone")}</option>
          </select>
          <WhatIsThis slug={p.slug} tip={tip}/>
        </div>
      </div>);})}</div>
      {shown.length===0&&<div className="muted" style={{fontSize:13}}>{statusFilter==="completed"?"No completed tasks here yet.":statusFilter==="ignored"?"No ignored or postponed tasks here.":statusFilter==="active"?"No active tasks — all done here.":"No tasks in this category."}</div>}
     </div>;})()}
   </div>);
}

// ── Automations: scheduled, hands-off Claude runs over a project's tasks ──────
// Each rule watches one category at/above an urgency, on an interval; when it
// fires it hands the highest-priority matching OPEN task to Claude in the
// project's dir and auto-marks it Completed on a clean finish. (Backend:
// /api/automations*.) This panel lists, creates, edits, toggles, and "Run now"s
// the rules and shows each rule's last outcome.
const AUTO_FREQ=[["15","Every 15 min"],["30","Every 30 min"],["60","Hourly"],["180","Every 3 h"],["360","Every 6 h"],["720","Every 12 h"],["1440","Daily"],["10080","Weekly"]];
const AUTO_PRIO=[["high","High only"],["med","Medium & up"],["low","Any urgency"]];
// The tables above are evaluated once at module load, before any language is
// known — kept in English and resolved at the use site via these two
// translators, which inline a literal t(string) call per value so the
// i18n-sweep ratchet's static scan (which only sees literal t(string) calls,
// not t(someVariable)) can see every string actually used.
function trFreqLabel(v){
  switch(String(v)){
    case"15":return t("Every 15 min");
    case"30":return t("Every 30 min");
    case"60":return t("Hourly");
    case"180":return t("Every 3 h");
    case"360":return t("Every 6 h");
    case"720":return t("Every 12 h");
    case"1440":return t("Daily");
    case"10080":return t("Weekly");
    default:return t("Every {n} min",{n:v});
  }
}
function trPrioLabel(v){
  switch(v){
    case"high":return t("High only");
    case"med":return t("Medium & up");
    case"low":return t("Any urgency");
    default:return v;
  }
}
// Recurrence options for scheduled prompts (0 = one-time). Must match
// SCHED_FREQS in server.js.
const SCHED_FREQ=[["0","One time"],["60","Hourly"],["180","Every 3 h"],["360","Every 6 h"],["720","Every 12 h"],["1440","Daily"],["10080","Weekly"]];
function trSchedFreq(v){
  switch(String(v)){
    case"0":return t("One time");
    case"60":return t("Hourly");
    case"180":return t("Every 3 h");
    case"360":return t("Every 6 h");
    case"720":return t("Every 12 h");
    case"1440":return t("Daily");
    case"10080":return t("Weekly");
    default:return t("One time");
  }
}
// Standalone create + list UI for scheduled prompts, shown under the
// Automations panel's 💬 tab. Wires the (previously stub) tab to
// /api/scheduled-chats so scheduled prompts actually appear, and lets the user
// type the prompt here plus pick a recurrence (daily/weekly/…).
function ScheduledPromptsPanel({p,rows,reload}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  const[editId,setEditId]=useState(null); // id being edited, or null for a new prompt
  const[prompt,setPrompt]=useState("");
  const[title,setTitle]=useState("");
  const[runAt,setRunAt]=useState("");
  const[freq,setFreq]=useState("0");
  const[provider,setProvider]=useState("claude");
  const[model,setModel]=useState(()=>defaultModelFor("claude"));
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  const toLocalInput=d=>new Date(d.getTime()-d.getTimezoneOffset()*60000).toISOString().slice(0,16);
  const schedProvider=["codex","gemini"].includes(provider)?provider:"claude";
  function start(){
    const d=new Date(Date.now()+60*60*1000);d.setSeconds(0,0);
    setRunAt(toLocalInput(d));
    setEditId(null);setPrompt("");setTitle("");setFreq("0");setProvider("claude");setModel(defaultModelFor("claude"));setErr("");setOpen(true);
  }
  function edit(x){
    setEditId(x.id);setPrompt(x.prompt||"");setTitle(x.title||"");
    setRunAt(toLocalInput(new Date(x.runAt)));setFreq(String(x.frequency||0));
    {const np=["codex","gemini"].includes(x.provider)?x.provider:"claude";setProvider(np);setModel(canonModel(np,x.model)||defaultModelFor(np));}setErr("");setOpen(true);
  }
  function save(){
    if(!prompt.trim()||!runAt){setErr(t("A prompt and time are required."));return;}
    setBusy(true);setErr("");
    const url=editId?"/api/scheduled-chats/update":"/api/scheduled-chats";
    const body=editId
      ?{id:editId,prompt:prompt.trim(),title:(title.trim()||prompt.trim().split("\n")[0]).slice(0,120),runAt:new Date(runAt).toISOString(),frequency:+freq,provider,model}
      :{slug:p.slug,prompt:prompt.trim(),title:(title.trim()||prompt.trim().split("\n")[0]).slice(0,120),runAt:new Date(runAt).toISOString(),frequency:+freq,provider,model};
    fetch(url,{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)})
      .then(r=>r.json()).then(j=>{setBusy(false);if(!j||!j.ok){setErr((j&&j.error)||t("Could not schedule prompt."));return;}setOpen(false);setEditId(null);reload();})
      .catch(e=>{setBusy(false);setErr(t("Could not schedule prompt: {e}",{e:String(e)}));});
  }
  function del(id){
    fetch("/api/scheduled-chats/delete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id})})
      .then(r=>r.json()).then(()=>reload()).catch(()=>{});
  }
  const fmt=iso=>{try{return new Date(iso).toLocaleString(undefined,{dateStyle:"medium",timeStyle:"short"});}catch{return iso;}};
  return(
   <div style={{padding:"6px 0"}}>
    {!open&&<div style={{margin:"4px 0 8px"}}><button className="btn sm primary" onClick={start}>+ {t("Schedule prompt")}</button></div>}
    {open&&<div className="sched-form" style={{border:"1px solid var(--line)",borderRadius:8,padding:12,margin:"4px 0 10px",display:"flex",flexDirection:"column",gap:8}}>
      <input className="in" placeholder={t("Title (optional)")} value={title} onChange={e=>setTitle(e.target.value)} maxLength={120}/>
      <textarea className="in" style={{minHeight:80,resize:"vertical"}} placeholder={t("Prompt to run…")} value={prompt} onChange={e=>setPrompt(e.target.value)} dir="ltr"/>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <label className="muted" style={{fontSize:12}}>{t("First run")} <input className="in" type="datetime-local" value={runAt} onChange={e=>setRunAt(e.target.value)}/></label>
        <label className="muted" style={{fontSize:12}}>{t("Repeat")} <select className="in" value={freq} onChange={e=>setFreq(e.target.value)}>{SCHED_FREQ.map(([v])=><option key={v} value={v}>{trSchedFreq(v)}</option>)}</select></label>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <label className="muted" style={{fontSize:12}}>{t("Provider")} <select className="in" value={schedProvider} onChange={e=>{setProvider(e.target.value);setModel(defaultModelFor(e.target.value));}}>
          <option value="claude">{t("Claude")}</option>
          <option value="codex">{t("Codex")}</option>
          <option value="gemini">{t("Gemini")}</option>
        </select></label>
        <label className="muted" style={{fontSize:12}}>{t("Model")} <select className="in" value={model||""} onChange={e=>setModel(e.target.value)}>
          {(AI_MODELS[schedProvider]||AI_MODELS.claude).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?(" ("+m.hint+")"):""}</option>)}
        </select></label>
      </div>
      {err&&<div className="muted" style={{fontSize:12,color:"var(--err)"}}>{err}</div>}
      <div style={{display:"flex",gap:8}}>
        <button className="btn sm primary" disabled={busy} onClick={save}>{busy?t("Saving…"):(editId?t("Save"):t("Schedule"))}</button>
        <button className="btn sm" onClick={()=>{setOpen(false);setEditId(null);}}>{t("Cancel")}</button>
      </div>
    </div>}
    {rows===null?<div className="muted" style={{fontSize:13}}>{t("Loading…")}</div>
     :rows.length===0?<div className="muted" style={{fontSize:13,padding:"8px 0"}}>{t("No scheduled prompts yet.")}</div>
     :<div className="auto-list">{rows.map(x=>(
        <div key={x.id} className="auto-row" style={{display:"block",border:"1px solid var(--line)",borderRadius:8,padding:"8px 10px",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <b style={{flex:1,minWidth:0}} dir="ltr">{x.title||t("Scheduled prompt")}</b>
            <span className="pill" style={{fontSize:11}}>{trSchedFreq(x.frequency||0)}</span>
            <span className="muted" style={{fontSize:11}}>{x.status}</span>
            {x.status==="pending"&&<button className="btn xs" onClick={()=>edit(x)}>{t("Edit")}</button>}
            <button className="btn xs" onClick={()=>del(x.id)}>{x.status==="pending"?t("Cancel"):t("Delete")}</button>
          </div>
          <div className="muted" style={{fontSize:12,marginTop:2}} dir="ltr">{(x.prompt||"").slice(0,160)}</div>
          <div className="sched-meta" style={{display:"flex",flexWrap:"wrap",gap:"4px 12px",fontSize:11,marginTop:4}}>
            <span className="muted">🕒 {(x.frequency?t("Next run"):t("Runs at"))+": "+fmt(x.runAt)}</span>
            <span className="muted">🔁 {t("Frequency")}: {trSchedFreq(x.frequency||0)}</span>
            {x.lastRunAt&&<span className="muted">✅ {t("Last run")}: {fmt(x.lastRunAt)}</span>}
            {(x.provider||x.model)&&<span className="muted">🤖 {[x.provider?providerName(x.provider):"",aiModelLabel(x.provider,x.model)].filter(Boolean).join(" · ")}</span>}
          </div>
        </div>
      ))}</div>}
   </div>
  );
}
const AUTO_CATS=["any",...Object.keys(TIP_CATEGORY_META)];
const AUTO_TARGET_MULTI="@selected"; // matches AUTO_TARGET_MULTI in server.js
function freqLabel(m){const f=AUTO_FREQ.find(x=>+x[0]===+m);return f?f[1]:("Every "+m+" min");}
// A descriptive name generated from a rule's own configuration — there is no manual
// name field. Scan: "Hourly Deep Scan" (full) or "Hourly Scan (Security, Performance)"
// (scoped). Fixer: "Hourly Fixer (Security)" or "Daily Fixer (Any)".
function autoName(a){
  const freq=freqLabel(a.everyMinutes);
  if(a.kind==="scan"){
    const cats=(a.cats||[]).filter(Boolean);
    return cats.length?(freq+" Scan ("+cats.join(", ")+")"):(freq+" Deep Scan");
  }
  const cat=(!a.category||a.category==="any")?"Any":a.category;
  return freq+" Fixer ("+cat+")";
}
function projName(slug,projects){const p=(projects||[]).find(x=>x.slug===slug);return p?(p.nameHe||p.slug):slug;}
function targetLabel(t,projects,targets){
  if(t==="*"||!t)return"🌐 All projects";
  if(t===AUTO_TARGET_MULTI){const names=(targets||[]).map(s=>projName(s,projects));return names.length<=3?("📍 "+names.join(", ")):("📍 "+names.length+" projects");}
  return projName(t,projects);
}
function aiModelLabel(provider,model){
  if(!model)return"";
  model=canonModel(provider,model);
  const m=((AI_MODELS[provider]||AI_MODELS.claude).find(x=>x.value===model)||{});
  return m.label||model;
}
function autoResultLine(r){
  if(!r)return"Not run yet.";
  const when=r.at?(" · "+relTime(new Date(r.at).getTime())+" ago"):"";
  if(r.status==="running")return"⏳ Working"+(r.total>1?(" across "+r.total+" projects"):(r.title?(" on “"+r.title+"”"):""))+when;
  if(r.status==="fanout"){
    const c=r.counts||{};const bits=[];
    if(c.completed)bits.push("✓ "+c.completed);
    if(c.awaiting_approval)bits.push("🟡 "+c.awaiting_approval);
    if(c.scanned)bits.push("🔍 "+c.scanned);
    if(c.failed)bits.push("✗ "+c.failed);
    if(c.error)bits.push("⚠ "+c.error);
    if(c.idle)bits.push("— "+c.idle);
    return"🌐 "+(r.total||0)+" projects · "+(bits.join("  ")||"done")+when;
  }
  if(r.status==="scanned")return"🔍 Scan complete — tasks refreshed"+when;
  if(r.status==="awaiting_approval")return"🟡 Done — awaiting your approval on “"+(r.title||"task")+"”"+when;
  if(r.status==="completed")return"✓ Completed “"+(r.title||"task")+"”"+when;
  if(r.status==="failed")return"✗ Claude exited with an error"+(r.title?(" on “"+r.title+"”"):"")+when;
  if(r.status==="idle")return"— No matching open tasks"+when;
  if(r.status==="interrupted")return"⚠ Interrupted (server restart)"+when;
  if(r.status==="error")return"✗ "+(r.error||"Error")+when;
  return(r.status||"")+when;
}
// Flatten a fixer rule's run history into the individual tasks it actually
// completed (or parked for approval) — across both single-project and fan-out runs.
function fixerFixed(history){
  const out=[];
  for(const r of history||[]){
    if(!r)continue;
    if(r.status==="fanout"){
      for(const pr of r.projects||[])if(pr&&(pr.status==="completed"||pr.status==="awaiting_approval"))out.push({title:pr.title,status:pr.status,slug:pr.slug,at:r.at});
    }else if(r.status==="completed"||r.status==="awaiting_approval"){
      out.push({title:r.title,status:r.status,slug:r.slug,at:r.at});
    }
  }
  return out;
}

// A fresh draft for a new rule of the given kind, targeting `target` (slug or "*").
function blankAuto(kind,target){
  return kind==="scan"
    ?{kind:"scan",target:target||"*",cats:[],provider:"claude",model:"",instructions:"",systemPrompt:"",everyMinutes:60,enabled:true}
    :{kind:"fixer",target:target||"*",category:"any",minPriority:"med",everyMinutes:60,goal:"",provider:"claude",model:"",requireApproval:false,enabled:true};
}
const apiSaveAuto=(d)=>fetch("/api/automations",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(d)}).then(r=>r.json());
const apiToggleAuto=(id,enabled)=>fetch("/api/automations/toggle",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id,enabled})}).then(r=>r.json());
const apiDeleteAuto=(id)=>fetch("/api/automations/delete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id})}).then(r=>r.json());
const apiRunAuto=(id)=>fetch("/api/automations/run",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id})}).then(r=>r.json());

// Shared create/edit form for both automation kinds (used by the per-project panel
// and the global Automations page). Fixer fields vs scan fields swap by kind.
// `manualScan` reuses this form as the per-project manual Deep Scan composer: it is
// always a scan, locked to the current project, so the kind tabs, target picker,
// schedule and enable toggle are hidden and Create fires the scan immediately.
function AutomationForm({draft,setDraft,projects,targetOptions,busy,err,onSave,onCancel,allowMulti,manualScan,lockedCat}){
  const{lang}=useLang();
  const d=draft;const isScan=manualScan?true:d.kind==="scan";
  const autoProvider=["codex","gemini"].includes(d.provider)?d.provider:"claude";
  const autoAgentName=providerName(autoProvider);
  const scanCount=(d.cats||[]).length; // for the manual-scan Create button label
  const toggleCat=(c)=>setDraft({...d,cats:(d.cats||[]).includes(c)?d.cats.filter(x=>x!==c):[...(d.cats||[]),c]});
  // Multi-project target picker (global page only). "All projects" is exclusive with the
  // per-project chips; picking projects builds a set that collapses to the simplest shape:
  // 0 → all ("*"), 1 → that single slug, 2+ → a multi rule ("@selected" + `targets`).
  const setTargetAll=()=>setDraft({...d,target:"*",targets:[]});
  const toggleTargetProject=(slug)=>{
    const cur=new Set(d.target==="*"?[]:(d.target===AUTO_TARGET_MULTI?(d.targets||[]):(d.target?[d.target]:[])));
    if(cur.has(slug))cur.delete(slug);else cur.add(slug);
    const arr=[...cur];
    if(arr.length===0)setDraft({...d,target:"*",targets:[]});
    else if(arr.length===1)setDraft({...d,target:arr[0],targets:[]});
    else setDraft({...d,target:AUTO_TARGET_MULTI,targets:arr});
  };
  const isTargeted=(slug)=>d.target===slug||(d.target===AUTO_TARGET_MULTI&&(d.targets||[]).includes(slug));
  // Custom audit brief (scan system prompt) editor state. sysDef caches the built-in
  // default fetched from the server (it varies with the selected areas) so we can show
  // it as a preview/placeholder and let the user load it as a starting point.
  const[sysOpen,setSysOpen]=useState(false);
  const[sysDef,setSysDef]=useState(null);
  const[sysBusy,setSysBusy]=useState(false);
  function fetchSysDefault(){
    setSysBusy(true);
    const q=(d.cats&&d.cats.length)?("?cats="+encodeURIComponent(d.cats.join(","))):"";
    return fetch("/api/scan/default-system"+q).then(r=>r.json())
      .then(j=>{setSysBusy(false);if(j&&j.ok){setSysDef(j);return j;}return null;})
      .catch(()=>{setSysBusy(false);return null;});
  }
  return(
   <div className="auto-form">
    {err&&<div className="muted" style={{fontSize:12,color:"var(--err)",marginBottom:6}}>{err}</div>}
    {/* Kind is fixed once you choose "New scan" vs "New fix": show only the chosen
        kind's card (no switch to the opposite kind). */}
    {!manualScan&&<div className="auto-kindpick">
      {isScan
        ?<div className="auto-kbtn on">🔍 Scan<span>generates tasks</span></div>
        :<div className="auto-kbtn on">🤖 Fixer<span>completes tasks</span></div>}
    </div>}
    <div className="auto-grid">
     {!manualScan&&<label className="auto-fld"><span>{isScan?t("Scan frequency"):t("Run frequency")}</span>
       <select value={String(d.everyMinutes)} onChange={e=>setDraft({...d,everyMinutes:+e.target.value})}>
         {AUTO_FREQ.map(([v,l])=><option key={v} value={v}>{trFreqLabel(v)}</option>)}
       </select></label>}
     {!isScan&&<label className="auto-fld"><span>{t("Category to watch")}</span>
       <select value={d.category} onChange={e=>setDraft({...d,category:e.target.value})}>
         {AUTO_CATS.map(c=><option key={c} value={c}>{c==="any"?t("Any category"):c}</option>)}
       </select></label>}
     {!isScan&&<label className="auto-fld"><span>{t("Minimum urgency")}</span>
       <select value={d.minPriority} onChange={e=>setDraft({...d,minPriority:e.target.value})}>
         {AUTO_PRIO.map(([v,l])=><option key={v} value={v}>{trPrioLabel(v)}</option>)}
       </select></label>}
     {!isScan&&<label className="auto-fld"><span>{t("When {name} finishes",{name:autoAgentName})}</span>
       <select value={d.requireApproval?"approve":"auto"} onChange={e=>setDraft({...d,requireApproval:e.target.value==="approve"})}>
         <option value="auto">{t("Auto-mark completed")}</option>
         <option value="approve">{t("Wait for my approval")}</option>
       </select></label>}
     <label className="auto-fld"><span>{t("Provider")}</span>
       <select value={autoProvider} onChange={e=>setDraft({...d,provider:e.target.value,model:""})}>
         <option value="claude">{t("Claude")}</option>
         <option value="codex">{t("Codex")}</option>
         <option value="gemini">{t("Gemini")}</option>
       </select></label>
     <label className="auto-fld"><span>{t("Model (optional)")}</span>
       <select value={d.model||""} onChange={e=>setDraft({...d,model:e.target.value})}>
         {(AI_MODELS[autoProvider]||AI_MODELS.claude).map(m=><option key={m.value} value={m.value}>{m.label}{m.hint?(" ("+m.hint+")"):""}</option>)}
       </select></label>
    </div>
    {isScan&&lockedCat&&<div className="auto-fld" style={{marginTop:8}}><span>{t("Scan type")}</span>
      <div className="auto-cats"><span className="catpick on" style={{cursor:"default"}}>{lockedCat}</span></div>
    </div>}
    {isScan&&!lockedCat&&<div className="auto-fld" style={{marginTop:8}}><span>{t("Areas to scan")} <span className="muted" style={{fontWeight:400}}>— {t("leave all off for a full deep scan across every area")}</span></span>
      <div className="auto-cats">
        {Object.keys(TIP_CATEGORY_META).map(c=>(
          <button key={c} className={"catpick"+((d.cats||[]).includes(c)?" on":"")} onClick={()=>toggleCat(c)}>{c}</button>
        ))}
      </div>
    </div>}
    <label className="auto-fld" style={{marginTop:8}}><span>{isScan?t("Extra scan instructions (optional)"):t("Extra instructions for {name} (optional)",{name:autoAgentName})}</span>
      {isScan
        ?<textarea dir="ltr" rows={2} value={d.instructions} placeholder={t("Anything to emphasise, e.g. focus on the new checkout flow. Leave blank for the standard deep audit.")} onChange={e=>setDraft({...d,instructions:e.target.value})}/>
        :<textarea dir="ltr" rows={3} value={d.goal} placeholder={t("Anything to emphasise for each matching task, e.g. keep changes minimal, run the build to verify. Leave blank to just resolve the task. (Claude may edit files but won't push or deploy.)")} onChange={e=>setDraft({...d,goal:e.target.value})}/>}
    </label>
    {isScan&&<div className="auto-fld" style={{marginTop:8}}>
      <span style={{display:"flex",alignItems:"center",gap:8}}>
        Audit brief <span className="muted" style={{fontWeight:400,flex:1}}>— the standing system prompt Claude follows during the scan</span>
        <button type="button" className="btn xs" onClick={()=>{const n=!sysOpen;setSysOpen(n);if(n&&!sysDef)fetchSysDefault();}}>{sysOpen?"Hide":(d.systemPrompt?"✎ Edit brief":"View / customize")}</button>
      </span>
      {!sysOpen&&<div className="muted" style={{fontSize:12,marginTop:4}}>{d.systemPrompt?("✎ "+t("Using a custom brief for this scan.")):null}</div>}
      {sysOpen&&<div style={{marginTop:6}}>
        <div style={{display:"flex",gap:8,marginBottom:6,flexWrap:"wrap"}}>
          <button type="button" className="btn xs" disabled={sysBusy} onClick={()=>fetchSysDefault().then(j=>{if(j)setDraft({...d,systemPrompt:j.system});})}>{sysBusy?t("Loading…"):t("Load built-in default")}</button>
          {d.systemPrompt&&<button type="button" className="btn xs" onClick={()=>setDraft({...d,systemPrompt:""})}>{t("Clear override (use default)")}</button>}
          <span className="muted" style={{fontSize:11,alignSelf:"center"}}>Leave blank to use the default. Edits are per this scan; a custom brief replaces the built-in areas.</span>
        </div>
        <textarea rows={12} value={d.systemPrompt} placeholder={(sysDef&&sysDef.system)||"Leave blank to use the built-in default brief, or click “Load built-in default” to view and edit it here."} onChange={e=>setDraft({...d,systemPrompt:e.target.value})} style={{fontFamily:"ui-monospace,Menlo,monospace",fontSize:12,lineHeight:1.5}}/>
        {sysDef&&<details style={{marginTop:6}}><summary className="muted" style={{fontSize:12,cursor:"pointer"}}>Always appended automatically (JSON output contract — you don't need to include this)</summary><pre style={{whiteSpace:"pre-wrap",fontSize:11,marginTop:6,color:"var(--text-3)",background:"var(--surface-soft)",padding:8,borderRadius:8}}>{sysDef.outputFormat}</pre></details>}
      </div>}
    </div>}
    <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8}}>
      <div style={{flex:1}}/>
      <button className="btn sm" onClick={onCancel}>{t("Cancel")}</button>
      <button className="btn sm primary" disabled={busy} onClick={onSave}>{manualScan?("🔍 "+(lockedCat?t("Run {cat} scan",{cat:lockedCat}):(scanCount?t("Scan {n} categories",{n:scanCount}):t("Full deep scan")))):(busy?t("Saving…"):(d.id?t("Save"):t("Create")))}</button>
    </div>
    <div className="muted" style={{fontSize:11,marginTop:8}}>
      {manualScan
        ?<>{t("Runs a one-off read-only Deep Scan in this project's directory right now and merges the grounded findings into its task list. Nothing is modified — it only generates and refreshes tasks.")}{scanCount?<> {t("Scoped to the {n} selected area(s).",{n:scanCount})}</>:<> {t("Covers all areas in one balanced pass.")}</>}</>
        :isScan
          ?null
          :<>{t("Runs {agent} autonomously in each target project's directory with permissions skipped. It may read, edit, and run commands, but is instructed not to git push, deploy, or restart services. One task per project per run.",{agent:autoAgentName})} {t("On a clean finish the task is either auto-marked completed or, if you chose “Wait for my approval”, parked as 🟡 Awaiting approval.")}</>}
      {!manualScan&&!isScan&&(d.target==="*"
        ?<> {t("Targets all non-hidden, non-paused projects, one at a time.")}</>
        :d.target===AUTO_TARGET_MULTI
          ?<> {t("Targets {n} selected projects, one at a time.",{n:(d.targets||[]).length})}</>
          :null)}
    </div>
   </div>);
}

// One rule row, shared by both panels. `showTarget` adds a target chip;
// `locked` (used for globals shown inside a per-project panel) hides edit/delete
// and disables the enable toggle — those globals are managed on the global page.
function AutomationRow({a,projects,onToggle,onEdit,onDelete,onRun,showTarget,locked,global}){
  const{lang}=useLang();
  const isScan=a.kind==="scan";
  const[showHist,setShowHist]=useState(false);
  // History powers "previous scans" (scan rules) / "tasks already fixed" (fixer rules).
  // The current lastResult is the head of history, so skip it here to avoid echoing the
  // "last run" line that's already shown just above.
  const hist=(a.history||[]).filter(Boolean).slice(a.running?0:1);
  const fixed=isScan?null:fixerFixed(hist);
  const histCount=isScan?hist.length:(fixed?fixed.length:0);
  return(
   <div className={"auto-row "+(isScan?"k-scan":"k-fixer")+(a.enabled?"":" off")+(global?" global":"")}>
    {global&&<span className="auto-global-tag" title={t("Cross-project automation — managed on the top-level Automations page")}>🌐 {t("Global")}</span>}
    <button className={"auto-toggle"+(a.enabled?" on":"")} disabled={locked} title={locked?t("Managed on the Automations page"):(a.enabled?t("Enabled — click to pause"):t("Paused — click to enable"))} onClick={()=>{if(!locked)onToggle(a);}}><span className="knob"/></button>
    <div className="auto-main">
      <div className="auto-title">
        <span className={"auto-kind "+(isScan?"scan":"fixer")}>{isScan?("🔍 "+t("Scan")):("🤖 "+t("Fixer"))}</span>
        <span dir={dirOf(autoName(a))}>{autoName(a)}</span>{a.running&&<span className="auto-run">⏳ {t("running")}</span>}
      </div>
      <div className="auto-meta">
        {showTarget&&<span className="chip tgt" title={a.target===AUTO_TARGET_MULTI?(a.targets||[]).map(s=>projName(s,projects)).join(", "):undefined}>{targetLabel(a.target,projects,a.targets)}</span>}
        {isScan
          ?<span className="chip">{(a.cats&&a.cats.length)?a.cats.join(", "):t("All areas")}</span>
          :<><CategoryChip category={a.category==="any"?null:a.category}/>{a.category==="any"&&<span className="chip">{t("Any category")}</span>}<span className="chip">{trPrioLabel(a.minPriority)}</span></>}
        <span className="chip"><ProviderLogo provider={a.provider||"claude"}/> {a.model?aiModelLabel(a.provider||"claude",a.model):providerName(a.provider||"claude")}</span>
        <span className="chip">{trFreqLabel(a.everyMinutes)}</span>
        {!isScan&&<span className="chip" title={a.requireApproval?t("Finished tasks wait for your approval"):t("Finished tasks are marked completed automatically")}>{a.requireApproval?("✋ "+t("Needs approval")):("🤖 "+t("Auto-complete"))}</span>}
        {isScan&&a.systemPrompt&&<span className="chip" title={t("This scan uses a custom audit brief instead of the built-in default")}>✎ {t("Custom brief")}</span>}
      </div>
      {(a.goal||a.instructions)&&<div className="auto-goal" title={a.goal||a.instructions}>{a.goal||a.instructions}</div>}
      <div className="auto-last">{autoResultLine(a.lastResult)}{a.enabled&&!a.running&&a.nextRunAt?<span className="muted"> · next {relUntil(a.nextRunAt)==="now"?"due now":("in "+relUntil(a.nextRunAt))}</span>:null}</div>
      {histCount>0&&<div className="auto-hist">
        <button className="auto-hist-toggle" onClick={()=>setShowHist(s=>!s)} title={isScan?t("Show this automation's earlier scans"):t("Show the tasks this automation has already fixed")}>
          <span className="tw">{showHist?"▾":"▸"}</span>{isScan?(t("Previous scans")+" · "+histCount):(t("Tasks already fixed")+" · "+histCount)}
        </button>
        {showHist&&(isScan
          ?<ul className="auto-hist-list">{hist.map((r,i)=><li key={i}>{autoResultLine(r)}</li>)}</ul>
          :<ul className="auto-hist-list">{fixed.map((f,i)=><li key={i}>✓ <span dir={dirOf(f.title||t("task"))}>{f.title||t("task")}</span>{f.status==="awaiting_approval"&&<span className="muted"> — {t("awaiting approval")}</span>}{showTarget&&f.slug&&<span className="muted"> · {projName(f.slug,projects)}</span>}{f.at&&<span className="muted"> · {relTime(new Date(f.at).getTime())} ago</span>}</li>)}</ul>)}
      </div>}
    </div>
    <div className="auto-actions">
      <button className="btn sm" disabled={a.running} onClick={()=>onRun(a)} title={t("Run this automation immediately")}>▶ {t("Run now")}</button>
      {!locked&&<button className="btn sm" onClick={()=>onEdit(a)}>✎ {t("Edit")}</button>}
      {!locked&&<button className="btn sm" onClick={()=>onDelete(a)} title={t("Delete automation")}>🗑</button>}
    </div>
   </div>);
}
// Per-project panel (in a project's detail): shows this project's own rules plus
// any 🌐 all-projects rules (locked here — edited on the global page).
// ── Site Health: the third automation sub-category ───────────────────────────
// Unlike Scan and Fix, Site Health is NOT a stored automation record. The hub
// already re-measures every project (Reachable · Port · PM2 · Container) on a
// server-owned timer: monitorTick spawns agent.py every
// checkIntervals.projectHealthMinutes minutes and writes data/status.json. This
// tab surfaces that — the live per-project verdict plus the single knob driving
// it. Saving sends ONLY projectHealthMinutes: PUT /api/settings falls back to the
// stored value for any interval omitted, so the host-metrics and host-guardian
// cadences survive a save here untouched.
function SiteHealth({st,projects}){
  const{lang}=useLang();
  const[mins,setMins]=useState(MONITOR_DEFAULTS.projectHealthMinutes);
  const[loaded,setLoaded]=useState(false);
  const[saved,setSaved]=useState("");
  useEffect(()=>{
    let live=true;
    fetch("/api/settings").then(r=>r.json()).then(d=>{
      if(!live)return;
      const ci=(d&&d.checkIntervals)||{};
      if(ci.projectHealthMinutes)setMins(ci.projectHealthMinutes);
      setLoaded(true);
    }).catch(()=>{if(live)setLoaded(true);});
    return()=>{live=false;};
  },[]);
  const clamp=v=>Math.min(1440,Math.max(1,Math.round(Number(v)||0)));
  function save(){
    setSaved("saving");
    fetch("/api/settings",{method:"PUT",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({checkIntervals:{projectHealthMinutes:clamp(mins)}})})
      .then(r=>r.json()).then(d=>{
        if(d&&d.checkIntervals&&d.checkIntervals.projectHealthMinutes){
          setMins(d.checkIntervals.projectHealthMinutes);
          setSaved("ok");setTimeout(()=>setSaved(s=>s==="ok"?"":s),2200);
        }else setSaved("err");
      }).catch(()=>setSaved("err"));
  }
  // Only the signals the collector actually filled in — "n/a" means the project
  // has no pm2/docker side, not that it's unhealthy, so it stays off the row.
  function detail(slug){
    const h=st&&st.projects&&st.projects[slug];
    if(!h)return"";
    const bits=[];
    if(h.http_status!=null)bits.push("HTTP "+h.http_status);
    if(h.pm2_status&&h.pm2_status!=="n/a")bits.push("pm2 "+h.pm2_status);
    if(h.container_status&&h.container_status!=="n/a")bits.push("docker "+h.container_status);
    return bits.join(" · ");
  }
  const rows=(projects||[]).filter(x=>!x.hidden);
  const checkedAt=st&&(st.projects_checked_at||st.updated_at);
  return(
   <div>
    <div className="auto-health-freq">
      <span className="lbl">
        <span className="t">⏱ Check every</span>
        <span className="d">How often Heygents re-checks each project — Reachable · Port · PM2 · Container. Applies to all projects.</span>
      </span>
      <span style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
        <input type="number" min={1} max={1440} value={mins} aria-label={t("Project health check interval in minutes")}
          onChange={e=>setMins(e.target.value)} onBlur={e=>setMins(clamp(e.target.value))}
          style={{width:72,textAlign:"end"}}/>
        <span className="muted" style={{fontSize:12.5}}>min</span>
        <button className="btn sm primary" disabled={saved==="saving"} onClick={save}>{saved==="saving"?"Saving…":"Save"}</button>
      </span>
    </div>
    <div className="muted" style={{fontSize:11.5,margin:"6px 0 10px"}}>
      {saved==="ok"?t("Saved ✓ — applies within a minute.")
       :saved==="err"?t("Couldn't save — try again.")
       :!loaded?t("Loading…")
       :checkedAt?(t("Last checked {rel} ago.",{rel:relTime(new Date(checkedAt).getTime())})):t("Waiting for the first check…")}
    </div>
    {rows.length===0
      ?<div className="muted" style={{fontSize:13}}>{t("No projects to monitor yet.")}</div>
      :<div className="auto-list">
        {rows.map(x=>{const h=health(x.slug,st);const d=detail(x.slug);return(
          <div className="auto-health-row" key={x.slug}>
            <span className="hdot" style={{background:h.c}}/>
            <span className="hname" dir={dirOf(x.nameHe||x.slug)}>{x.nameHe||x.slug}</span>
            <span className="hverdict" style={{color:h.c}}>{h.t}</span>
            {d&&<span className="hdet">{d}</span>}
          </div>
        );})}
      </div>}
   </div>);
}

function Automations({p,st,projects,openCount=0}){
  const{lang}=useLang();
  const[list,setList]=useState(null);
  const[draft,setDraft]=useState(null); // rule being created/edited, or null
  const[err,setErr]=useState("");
  const[busy,setBusy]=useState(false);
  const[tab,setTab]=useState("all"); // "all" | "scan" | "fixer" | "health" | "prompts"
  const[sched,setSched]=useState(null); // scheduled prompts for this project
  const[showAi,setShowAi]=useState(null); // AI settings modal scoped to a use key ("scan"|"fix"), or null
  function load(){
    return fetch("/api/automations?slug="+encodeURIComponent(p.slug))
      .then(r=>r.json()).then(d=>{if(d&&d.ok)setList(d.automations);}).catch(()=>{});
  }
  function loadSched(){
    return fetch("/api/scheduled-chats?slug="+encodeURIComponent(p.slug))
      .then(r=>r.json()).then(d=>{if(d&&d.ok)setSched(d.scheduledChats||[]);}).catch(()=>{});
  }
  useEffect(()=>{setList(null);setDraft(null);setSched(null);load();loadSched();},[p.slug]);
  // poll so running state + last-result stay fresh (runs are detached server-side)
  useEffect(()=>{const i=setInterval(()=>{load();loadSched();},15000);return()=>clearInterval(i);},[p.slug]);

  const targetOptions=[[p.slug,"📁 This project"]];
  function save(){
    const d=draft;
    setBusy(true);setErr("");
    apiSaveAuto(d).then(res=>{setBusy(false);if(!res||!res.ok){setErr((res&&res.error)||"Save failed.");return;}setDraft(null);load();})
      .catch(e=>{setBusy(false);setErr("Save failed: "+String(e));});
  }
  function toggle(a){setList(prev=>(prev||[]).map(x=>x.id===a.id?{...x,enabled:!a.enabled}:x));apiToggleAuto(a.id,!a.enabled).then(load).catch(load);}
  function del(a){if(!window.confirm(t("Delete the automation “{name}”?",{name:autoName(a)})))return;apiDeleteAuto(a.id).then(load);}
  function runNow(a){setList(prev=>(prev||[]).map(x=>x.id===a.id?{...x,running:true}:x));apiRunAuto(a.id).then(res=>{if(res&&!res.ok&&res.error&&res.error!=="Already running.")setErr(res.error);setTimeout(load,1200);}).catch(load);}

  const isInherited=a=>a.target==="*"||a.target===AUTO_TARGET_MULTI;
  // Hide inactive cross-project rules entirely: only active globals are inherited here.
  const rules=(list||[]).filter(a=>!(isInherited(a)&&!a.enabled));
  const scans=rules.filter(a=>a.kind==="scan");
  const fixers=rules.filter(a=>a.kind!=="scan");
  // Site Health has no stored rules, so the whole rule pipeline below (create,
  // list, empty-state) is skipped on that tab.
  const isHealth=tab==="health";
  const isPrompts=tab==="prompts";
  // "All" lists every rule regardless of kind; the per-row colour theme keeps
  // 🔍 scans and 🛠 fixes tellable apart in the mixed list.
  const shown=tab==="all"?rules:tab==="scan"?scans:fixers;
  const emptyMsg=tab==="all"
    ?t("No automations yet. A scan has Claude read this project and file the tasks it finds; a fixer picks an open task and completes it — both on a schedule.")
    :tab==="scan"
    ?t("No scans yet. A scan has Claude read this project and file the tasks it finds — on a schedule.")
    :t("No fixers yet. A fixer picks an open task and has Claude complete it — on a schedule.");
  return(
   <div className="sec">
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
      <span className="muted" style={{fontSize:12,flex:1}}>{t("Scan to generate tasks · Fix to complete them · Site Health to watch the site — on a schedule, hands-off.")}</span>
    </div>
    {err&&!draft&&<div className="muted" style={{fontSize:12,color:"var(--err)",margin:"4px 0"}}>{err}</div>}

    <div className="seg" style={{margin:"8px 0 4px"}} role="tablist">
      <button className={"seg-b"+(tab==="all"?" on":"")} role="tab" aria-selected={tab==="all"} onClick={()=>setTab("all")}>📋 {t("All")}{rules.length?" ("+rules.length+")":""}</button>
    </div>
    <div className="seg" style={{margin:"0 0 4px"}} role="tablist">
      <button className={"seg-b seg-scan"+(tab==="scan"?" on":"")} role="tab" aria-selected={tab==="scan"} onClick={()=>setTab("scan")}>🔍 {t("Scan")}{scans.length?" ("+scans.length+")":""}</button>
      <button className={"seg-b seg-fixer"+(tab==="fixer"?" on":"")} role="tab" aria-selected={tab==="fixer"} onClick={()=>setTab("fixer")}>🛠 {t("Fix")}{fixers.length?" ("+fixers.length+")":""}</button>
      <button className={"seg-b"+(isHealth?" on":"")} role="tab" aria-selected={isHealth} onClick={()=>setTab("health")}>❤️ {t("Site Health")}</button>
      <button className={"seg-b"+(isPrompts?" on":"")} role="tab" aria-selected={isPrompts} onClick={()=>setTab("prompts")}>🕐 {t("Prompts")}{(()=>{const n=(sched||[]).filter(x=>!x.status||x.status==="pending").length;return n?" ("+n+")":"";})()}</button>
    </div>
    {showAi&&<AiSettings only={showAi} onClose={()=>setShowAi(null)}/>}

    {/* Create button sits inside its own tab — scan on Scan, auto-fix on Fix.
        The mixed All tab has no single kind, so it offers both. */}
    {!draft&&!isHealth&&!isPrompts&&<div style={{margin:"10px 0 2px",display:"flex",gap:8}}>
      {tab!=="fixer"&&<button className="btn sm primary" onClick={()=>{setErr("");setDraft(blankAuto("scan",p.slug));}}>+ {t("Schedule scan")}</button>}
      {tab!=="fixer"&&<button type="button" className="btn sm" title={t("Scan AI options")} aria-label={t("Scan AI options")} onClick={()=>setShowAi("scan")}>⚙</button>}
      {tab!=="scan"&&<button className="btn sm primary" disabled={!openCount} title={!openCount?t("No open tasks to fix. Run a scan first."):null} onClick={()=>{setErr("");setDraft(blankAuto("fixer",p.slug));}}>+ {t("Schedule fix")}</button>}
      {tab!=="scan"&&<button type="button" className="btn sm" title={t("Fix AI options")} aria-label={t("Fix AI options")} onClick={()=>setShowAi("fix")}>⚙</button>}
    </div>}

    {isPrompts&&<ScheduledPromptsPanel p={p} rows={sched} reload={loadSched}/>}

    {isHealth&&<SiteHealth st={st} projects={(projects||[]).filter(x=>x.slug===p.slug)}/>}

    {!isHealth&&!isPrompts&&draft&&<AutomationForm draft={draft} setDraft={setDraft} projects={projects} targetOptions={targetOptions} allowMulti={false} busy={busy} err={err} onSave={save} onCancel={()=>{setDraft(null);setErr("");}}/>}

    {isHealth||isPrompts?null
      :list===null
      ?<div className="muted" style={{fontSize:13}}>{t("Loading…")}</div>
      :shown.length===0&&!draft
        ?<div className="muted" style={{fontSize:13}}>{emptyMsg}</div>
        :<div className="auto-list">
          {shown.map(a=>{const inherited=isInherited(a);return(
            <AutomationRow key={a.id} a={a} projects={projects} showTarget={inherited} global={inherited}
              onToggle={toggle} onEdit={(x)=>{setErr("");setDraft({...x});}} onDelete={del} onRun={runNow}/>
          );})}
        </div>}
    {!isHealth&&!isPrompts&&shown.some(isInherited)&&<div className="muted" style={{fontSize:11,marginTop:8}}>🌐 {t("Cross-project rules run here too. Editing one here changes it everywhere it applies.")}</div>}
   </div>);
}

// Group a list of rules by their target project so each project's automations
// render together. Order: 🌐 All projects, then 📍 Multiple projects, then each
// single project alphabetically by name.
function groupAutosByProject(rules,projects){
  const groups=new Map(); // key -> {key,label,order,items}
  const bucket=(key,label,order)=>{let g=groups.get(key);if(!g){g={key,label,order,items:[]};groups.set(key,g);}return g;};
  for(const a of rules){
    if(a.target==="*")bucket("*","🌐 All projects",0).items.push(a);
    else if(a.target===AUTO_TARGET_MULTI)bucket(AUTO_TARGET_MULTI,"📍 Multiple projects",1).items.push(a);
    else bucket(a.target,projName(a.target,projects),2).items.push(a);
  }
  return[...groups.values()].sort((x,y)=>x.order-y.order||x.label.localeCompare(y.label,"he"));
}
// "in 12m" / "in 3h" style countdown to a future ISO timestamp
function relUntil(iso){const s=Math.floor((new Date(iso).getTime()-Date.now())/1000);if(s<=0)return"now";const m=Math.floor(s/60);if(m<60)return m+"m";const h=Math.floor(m/60);if(h<24)return h+"h";return Math.floor(h/24)+"d";}

// Small file explorer opened from the chat's working-dir link: browse the
// project's files (rooted at its working dir) and preview text/images/PDFs.
// All reads go through /api/fs/* which clamp every path to within `root`.
function FileBrowser({root,initialPath,onClose}){
  const{lang}=useLang();
  const initialFile=initialPath||"";
  const[dir,setDir]=useState(initialFile?initialFile.slice(0,initialFile.lastIndexOf("/"))||root:root);
  const[data,setData]=useState(null); // {parent,entries}
  const[err,setErr]=useState("");
  const[loading,setLoading]=useState(true);
  const[sel,setSel]=useState(null);   // selected file name in the current dir
  const[prev,setPrev]=useState(null); // {kind,name,content,size,path,mime}
  const[prevLoading,setPrevLoading]=useState(false);
  const[draft,setDraft]=useState("");        // live textarea content for the open text file
  const[saving,setSaving]=useState(false);
  const[saveMsg,setSaveMsg]=useState("");     // transient "Saved ✓" / error note
  // Load the current directory's entries whenever `dir` changes.
  useEffect(()=>{let live=true;setLoading(true);setErr("");
    fetch("/api/fs/list?root="+encodeURIComponent(root)+"&dir="+encodeURIComponent(dir))
      .then(r=>r.json()).then(d=>{if(!live)return;
        if(d.ok){setData(d);}else{setErr(d.error||"Failed to list.");setData(null);}
      }).catch(e=>{if(live){setErr(String(e));setData(null);}}).finally(()=>{if(live)setLoading(false);});
    return()=>{live=false;};
  },[dir,root]);
  // Close on Escape.
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[onClose]);
  function openFile(name){
    const full=(dir==="/"?"":dir)+"/"+name;
    setSel(name);setPrev(null);setPrevLoading(true);setSaveMsg("");setDraft("");
    fetch("/api/fs/file?root="+encodeURIComponent(root)+"&path="+encodeURIComponent(full))
      .then(r=>r.json()).then(d=>{
        setPrev(d.ok?d:{kind:"err",name,content:d.error||"Failed to read."});
        if(d.ok&&d.kind==="text")setDraft(d.content||""); // open straight into an editable buffer
      })
      .catch(e=>setPrev({kind:"err",name,content:String(e)})).finally(()=>setPrevLoading(false));
  }
  // A file link opens the browser with that file already selected and previewed.
  useEffect(()=>{if(!initialFile)return;const slash=initialFile.lastIndexOf("/");const parent=slash>0?initialFile.slice(0,slash):root;setDir(parent);openFile(initialFile.slice(slash+1));},[initialFile,root]);
  function goDir(next){setSel(null);setPrev(null);setSaveMsg("");setDraft("");setDir(next);}
  function saveFile(){
    if(!prev||prev.kind!=="text"||saving)return;
    setSaving(true);setSaveMsg("");
    fetch("/api/fs/write",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({root,path:prev.path,content:draft})})
      .then(r=>r.json()).then(d=>{
        if(d.ok){
          setPrev(p=>({...p,content:draft,size:d.size}));setSaveMsg("Saved ✓");
          setData(dd=>dd?{...dd,entries:dd.entries.map(e=>e.name===prev.name?{...e,size:d.size}:e)}:dd);
        }else setSaveMsg(d.error||"Save failed.");
      }).catch(e=>setSaveMsg(String(e))).finally(()=>setSaving(false));
  }
  const dirty=prev&&prev.kind==="text"&&draft!==prev.content;
  const rawUrl=(p)=>"/api/fs/raw?root="+encodeURIComponent(root)+"&path="+encodeURIComponent(p);
  return <div className="fbrow-scrim" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="fbrow">
      <div className="fhead">
        <b style={{fontSize:14,flexShrink:0}}>📂 {t("Files")}</b>
        <span className="fpath" title={dir}>{dir}</span>
        <button className="btn sm" onClick={onClose}>✕ {t("Close")}</button>
      </div>
      {err&&<div className="ferr">{err}</div>}
      <div className="fbody">
        <div className="flist">
          {data&&data.parent&&<button className="fitem up" onClick={()=>goDir(data.parent)}>
            <span>↑</span><span className="fn">..</span></button>}
          {loading&&!data&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{t("Loading…")}</div>}
          {data&&data.entries.map(e=>(
            <button key={e.name} className={"fitem"+(!e.dir&&sel===e.name?" on":"")}
              onClick={()=>e.dir?goDir((dir==="/"?"":dir)+"/"+e.name):openFile(e.name)}
              title={e.name}>
              <span>{e.dir?"📁":"📄"}</span>
              <span className="fn">{e.name}</span>
              {!e.dir&&<span className="fsz">{fmtBytes(e.size)}</span>}
            </button>))}
          {data&&!data.entries.length&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{t("Empty folder")}</div>}
        </div>
        <div className="fprev">
          {prevLoading&&<div className="fmsg">{t("Loading…")}</div>}
          {!prevLoading&&!prev&&<div className="fmsg">{t("Select a file to preview.")}</div>}
          {!prevLoading&&prev&&prev.kind==="text"&&<div className="ftext">
            <div className="fprev-head">
              <span className="ftitle" title={prev.name}>{prev.name}{dirty?" •":""}</span>
              {saveMsg&&<span className={"fsavemsg"+(/fail|error|Error/.test(saveMsg)?" bad":"")}>{saveMsg}</span>}
              {dirty&&<button className="btn sm" onClick={()=>{setDraft(prev.content);setSaveMsg("");}} disabled={saving} title={t("Discard unsaved changes")}>↩ {t("Revert")}</button>}
              <button className="btn sm primary" onClick={saveFile} disabled={saving||!dirty}>{saving?t("Saving…"):"💾 "+t("Save")}</button>
            </div>
            <textarea className="fedit" value={draft} spellCheck={false} onChange={e=>setDraft(e.target.value)}
              onKeyDown={e=>{if((e.metaKey||e.ctrlKey)&&e.key==="s"){e.preventDefault();if(dirty&&!saving)saveFile();}}}/>
          </div>}
          {!prevLoading&&prev&&prev.kind==="image"&&(prev.dataUrl||!CLOUD
            ?<img className="fimg" src={prev.dataUrl||rawUrl(prev.path)} alt={prev.name}/>
            :<div className="fmsg">{t("Image preview needs a newer agent - re-run the install command to update it.")}</div>)}
          {!prevLoading&&prev&&prev.kind==="pdf"&&<iframe src={rawUrl(prev.path)} title={prev.name}/>}
          {!prevLoading&&prev&&(prev.kind==="binary"||prev.kind==="toobig")&&<div className="fmsg">
            {prev.kind==="toobig"?"File is too large to preview":"Binary file"} ({fmtBytes(prev.size)}).
            <a className="dl btn sm" href={rawUrl(prev.path)} target="_blank" download>⬇ Download</a></div>}
          {!prevLoading&&prev&&prev.kind==="err"&&<div className="fmsg" style={{color:"var(--err)"}}>{prev.content}</div>}
        </div>
      </div>
    </div>
  </div>;
}

// Browse / search the server's Google Drive (rclone "gdrive:" remote) and import
// a chosen file into this chat as an attachment. Mirrors FileBrowser's look; on
// pick it downloads the file server-side and hands the staged attachment back.
function DrivePicker({slug,onClose,onPick}){
  const[dir,setDir]=useState("");        // current Drive folder ("" = root)
  const[data,setData]=useState(null);    // {entries:[…]}
  const[err,setErr]=useState("");
  const[loading,setLoading]=useState(true);
  const[q,setQ]=useState("");            // search box text
  const[searching,setSearching]=useState(false); // showing search results, not a folder
  const[busyPath,setBusyPath]=useState("");       // path currently importing
  const parent=dir?dir.split("/").slice(0,-1).join("/"):null;
  // Load a folder's contents whenever `dir` changes (unless we're in search view).
  useEffect(()=>{if(searching)return;let live=true;setLoading(true);setErr("");
    fetch("/api/drive/list?path="+encodeURIComponent(dir))
      .then(r=>r.json()).then(d=>{if(!live)return;
        if(d.ok)setData(d);else{setErr(d.error||"Failed to list Drive.");setData(null);}
      }).catch(e=>{if(live){setErr(String(e));setData(null);}}).finally(()=>{if(live)setLoading(false);});
    return()=>{live=false;};
  },[dir,searching]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[onClose]);
  function runSearch(){const term=q.trim();if(term.length<2){setErr("Type at least 2 characters to search.");return;}
    setSearching(true);setLoading(true);setErr("");setData(null);
    fetch("/api/drive/search?q="+encodeURIComponent(term))
      .then(r=>r.json()).then(d=>{if(d.ok)setData({entries:d.entries,truncated:d.truncated});else{setErr(d.error||"Search failed.");setData(null);}})
      .catch(e=>{setErr(String(e));setData(null);}).finally(()=>setLoading(false));
  }
  function exitSearch(){setSearching(false);setQ("");setErr("");}
  function openEntry(e){
    if(e.isDir){if(searching)setSearching(false);setDir(e.path);return;}
    if(e.isGoogleDoc){setErr("“"+e.name+"” is a Google Doc/Sheet/Slide — export it to PDF or Office in Drive first, then pick that file.");return;}
    setBusyPath(e.path);setErr("");
    fetch("/api/drive/import",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({slug,id:e.id,name:e.name,mime:e.mime,size:e.size})})
      .then(r=>r.json()).then(d=>{if(d&&d.ok)onPick(d.file);else setErr((d&&d.error)||"Import failed.");})
      .catch(err=>setErr(String(err))).finally(()=>setBusyPath(""));
  }
  const entries=data?data.entries:[];
  return <div className="fbrow-scrim" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="fbrow">
      <div className="fhead">
        <b style={{fontSize:14,flexShrink:0}}>☁️ Google Drive</b>
        <span className="fpath" title={searching?"Search results":("/"+dir)}>{searching?"🔎 “"+q+"”":("/"+dir)}</span>
        <button className="btn sm" onClick={onClose}>✕ Close</button>
      </div>
      <div className="dsearch">
        <input placeholder="Search all of Drive by name…" value={q}
          onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")runSearch();}}/>
        <button className="btn sm" onClick={runSearch} disabled={loading}>Search</button>
        {searching&&<button className="btn sm" onClick={exitSearch}>← Back to folders</button>}
      </div>
      {err&&<div className="ferr">{err}</div>}
      <div className="fbody">
        <div className="flist" style={{width:"100%",borderRight:"none"}}>
          {!searching&&parent!==null&&<button className="fitem up" onClick={()=>setDir(parent)}>
            <span>↑</span><span className="fn">..</span></button>}
          {loading&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{searching?"Searching Drive… (can take a moment)":"Loading…"}</div>}
          {!loading&&entries.map(e=>(
            <button key={e.path} className={"fitem"+(busyPath===e.path?" on":"")} disabled={!!busyPath}
              onClick={()=>openEntry(e)} title={e.isGoogleDoc?"Google Doc — export to PDF/Office in Drive first":e.path}>
              <span>{e.isDir?"📁":e.isGoogleDoc?"📝":e.mime&&e.mime.startsWith("image/")?"🖼":"📄"}</span>
              <span className="fn">{e.name}{searching&&e.path.indexOf("/")>=0&&<span className="dpath"> — {e.path.split("/").slice(0,-1).join("/")}</span>}</span>
              {busyPath===e.path?<span className="fsz">importing…</span>:!e.isDir&&e.size!=null&&<span className="fsz">{fmtBytes(e.size)}</span>}
            </button>))}
          {!loading&&!entries.length&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{searching?"No matches.":"Empty folder"}</div>}
          {data&&data.truncated&&<div className="fmsg" style={{padding:"10px 14px",fontSize:11,color:"var(--text-4)"}}>Showing first 200 matches — narrow your search to see more.</div>}
        </div>
      </div>
    </div>
  </div>;
}

// Cloud Google Drive picker. Per-tenant OAuth (server routes /api/gdrive/*).
// Browse folders by id, search by name, pick a file (downloaded server-side into
// the project's uploads) or a folder (attached as a Drive link). Handles the not
// -connected state by kicking off the OAuth flow.
function CloudDrivePicker({slug,onClose,onPickFile,onPickFolder,importPath,allowFolder=true}){
  const impPath=importPath||"/api/gdrive/import";
  const[st,setSt]=useState(null);       // /api/gdrive/status
  const[stack,setStack]=useState([{id:"root",name:"My Drive"}]);
  const[data,setData]=useState(null);   // {entries:[…]}
  const[err,setErr]=useState("");
  const[loading,setLoading]=useState(true);
  const[q,setQ]=useState("");
  const[searching,setSearching]=useState(false);
  const[busy,setBusy]=useState("");
  const cur=stack[stack.length-1];
  useEffect(()=>{fetch("/api/gdrive/status").then(r=>r.json()).then(setSt).catch(()=>setSt({configured:false,connected:false}));},[]);
  useEffect(()=>{if(!st||!st.connected||searching)return;let live=true;setLoading(true);setErr("");
    fetch("/api/gdrive/list?folder="+encodeURIComponent(cur.id))
      .then(r=>r.json()).then(d=>{if(!live)return;if(d.ok)setData(d);else{setErr(d.error||"Failed to list Drive.");setData(null);}})
      .catch(e=>{if(live){setErr(String(e));setData(null);}}).finally(()=>{if(live)setLoading(false);});
    return()=>{live=false;};
  },[st,cur.id,searching]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape")onClose();};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[onClose]);
  function connect(){fetch("/api/gdrive/login").then(r=>r.json()).then(d=>{if(d&&d.url)window.location.href=d.url;else setErr((d&&d.error)||"Google Drive isn't configured.");}).catch(e=>setErr(String(e)));}
  function runSearch(){const term=q.trim();if(term.length<2){setErr("Type at least 2 characters to search.");return;}
    setSearching(true);setLoading(true);setErr("");setData(null);
    fetch("/api/gdrive/search?q="+encodeURIComponent(term)).then(r=>r.json())
      .then(d=>{if(d.ok)setData({entries:d.entries});else{setErr(d.error||"Search failed.");setData(null);}})
      .catch(e=>{setErr(String(e));setData(null);}).finally(()=>setLoading(false));
  }
  function exitSearch(){setSearching(false);setQ("");setErr("");}
  function openEntry(e){
    if(e.isDir&&!e.pick){if(searching)setSearching(false);setStack(s=>[...s,{id:e.id,name:e.name}]);return;}
    if(e.isGoogleDoc){setErr("“"+e.name+"” is a Google Doc/Sheet/Slide — export it to PDF or Office in Drive first, then pick that file.");return;}
    setBusy(e.id);setErr("");
    fetch(impPath,{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({slug,id:e.id,name:e.name,mime:e.mime,size:e.size,isDir:e.isDir,webViewLink:e.webViewLink})})
      .then(r=>r.json()).then(d=>{if(d&&d.ok){if(d.folder)onPickFolder(d.folder);else onPickFile(d.file);}else setErr((d&&d.error)||"Import failed.");})
      .catch(err2=>setErr(String(err2))).finally(()=>setBusy(""));
  }
  const entries=data?data.entries:[];
  const notReady=st&&(!st.configured||!st.connected);
  return <div className="fbrow-scrim" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="fbrow">
      <div className="fhead">
        <b style={{fontSize:14,flexShrink:0}}>☁️ Google Drive</b>
        <span className="fpath">{searching?"🔎 “"+q+"”":stack.map(x=>x.name).join(" / ")}</span>
        <button className="btn sm" onClick={onClose}>✕ {t("Close")}</button>
      </div>
      {!st&&<div className="fmsg" style={{padding:16,fontSize:13}}>Loading…</div>}
      {st&&!st.configured&&<div className="ferr">Google Drive isn't configured on the server.</div>}
      {st&&st.configured&&!st.connected&&<div style={{padding:20,textAlign:"center"}}>
        <div style={{fontSize:13,color:"var(--text-2)",marginBottom:12}}>Connect your Google account to browse and attach Drive files and folders.</div>
        <button className="btn primary" onClick={connect}>Connect Google Drive</button>
      </div>}
      {st&&st.connected&&<>
        <div className="dsearch">
          <input placeholder="Search all of Drive by name…" value={q}
            onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")runSearch();}}/>
          <button className="btn sm" onClick={runSearch} disabled={loading}>Search</button>
          {searching&&<button className="btn sm" onClick={exitSearch}>← Back to folders</button>}
        </div>
        {err&&<div className="ferr">{err}</div>}
        <div className="fbody">
          <div className="flist" style={{width:"100%",borderRight:"none"}}>
            {!searching&&stack.length>1&&<button className="fitem up" onClick={()=>setStack(s=>s.slice(0,-1))}><span>↑</span><span className="fn">..</span></button>}
            {loading&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{searching?"Searching Drive…":"Loading…"}</div>}
            {!loading&&entries.map(e=>(
              <div key={e.id} className="fitem" style={{display:"flex",alignItems:"center"}}>
                <button className={"fitem"+(busy===e.id?" on":"")} style={{flex:1,border:"none",background:"none",padding:0}} disabled={!!busy}
                  onClick={()=>openEntry(e)} title={e.isGoogleDoc?"Google Doc — export to PDF/Office in Drive first":e.name}>
                  <span>{e.isDir?"📁":e.isGoogleDoc?"📝":e.mime&&e.mime.startsWith("image/")?"🖼":"📄"}</span>
                  <span className="fn">{e.name}</span>
                  {busy===e.id?<span className="fsz">importing…</span>:!e.isDir&&e.size?<span className="fsz">{fmtBytes(e.size)}</span>:null}
                </button>
                {e.isDir&&allowFolder&&<button className="btn sm" style={{marginInlineEnd:6}} disabled={!!busy} title="Attach this folder as a Drive link"
                  onClick={()=>openEntry({...e,pick:true})}>Attach folder</button>}
              </div>))}
            {!loading&&!entries.length&&<div className="fmsg" style={{padding:"14px",fontSize:12,color:"var(--text-4)"}}>{searching?"No matches.":"Empty folder"}</div>}
          </div>
        </div>
      </>}
    </div>
  </div>;
}

// Available Claude skills for the "/" picker — fetched once, shared across
// project switches (skills are global, so there's no need to refetch per dock).
let SKILLS_CACHE=null;
// Interactive prompt popup — the browser side of the Agent SDK's canUseTool
// bridge. Renders either an AskUserQuestion (multiple-choice, like Claude Code /
// Desktop, with a free-text "Other") or a tool-permission Allow/Deny. Submitting
// POSTs the answer and the model's turn resumes.
// Wrong-project guard: the opening message of a chat looked like it belongs to a
// different project. Purely local detection (src/project-match.js) — no AI call.
function WrongProjectModal({match,fromName,onTransfer,onSendAnyway,onAlwaysIgnore}){
  const{lang}=useLang();
  return (
    <div className="ask-overlay">
      <div className="card ask-modal" dir="ltr" style={{maxWidth:460,width:"100%",padding:18,boxShadow:"0 12px 40px var(--shadow-color)"}}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>{t("Wrong project?")}</div>
        <div style={{fontSize:13,marginBottom:10,lineHeight:1.5}}>
          {t("This message looks like it belongs to")} <strong>{match.name}</strong>{t(", but you are writing in")} <strong>{fromName}</strong>.
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
          {match.tokens.map(tok=><span key={tok} className="chip" style={{fontSize:11,opacity:.8}} dir="ltr">{tok}</span>)}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <button className="btn primary" onClick={onTransfer}>{t("Move to")} {match.name}</button>
          <button className="btn" onClick={onSendAnyway}>{t("Send here anyway")}</button>
          <button className="btn ghost" style={{fontSize:12,opacity:.75}} onClick={onAlwaysIgnore}>{t("Send, and stop asking about this pair")}</button>
        </div>
      </div>
    </div>);
}

function AskModal({ask,onAnswer,onDismiss}){
  const{lang}=useLang();
  const isQ=ask.kind==="question";
  const questions=(isQ&&Array.isArray(ask.questions))?ask.questions:[];
  const[sel,setSel]=useState(()=>questions.map(q=>({picked:q.multiSelect?[]:null,other:""})));
  const[step,setStep]=useState(0); // which question is on screen (one at a time)
  const advanceRef=useRef(null);   // pending auto-advance timer, so unmount/re-pick cancels it
  useEffect(()=>()=>{if(advanceRef.current)clearTimeout(advanceRef.current);},[]);
  function pick(qi,label,multi){
    setSel(prev=>prev.map((s,i)=>{
      if(i!==qi)return s;
      if(multi){const has=s.picked.includes(label);return{...s,picked:has?s.picked.filter(x=>x!==label):[...s.picked,label]};}
      return{...s,picked:label,other:""};
    }));
    // Single-select: picking IS the answer, so walk to the next question on its own.
    // Multi-select needs Next (we can't know when the user is done ticking boxes), and
    // the last question always waits for an explicit Send answer.
    if(!multi&&qi<questions.length-1){
      if(advanceRef.current)clearTimeout(advanceRef.current);
      advanceRef.current=setTimeout(()=>{advanceRef.current=null;setStep(s=>s===qi?qi+1:s);},180); // brief beat so the tick is visible
    }
  }
  function setOther(qi,val){setSel(prev=>prev.map((s,i)=>i!==qi?s:(questions[i].multiSelect?{...s,other:val}:{...s,other:val,picked:null})));}
  function submitQ(){
    const answers={};
    questions.forEach((q,i)=>{
      const s=sel[i];
      if(q.multiSelect){const vals=[...s.picked];if(s.other.trim())vals.push(s.other.trim());answers[q.question]=vals;}
      else answers[q.question]=s.other.trim()?s.other.trim():(s.picked||"");
    });
    onAnswer({answers});
  }
  const stepOk=i=>{const q=questions[i],s=sel[i];if(!q||!s)return false;return q.multiSelect?(s.picked.length>0||!!s.other.trim()):(!!s.picked||!!s.other.trim());};
  const canSubmit=questions.every((q,i)=>stepOk(i));
  const isLast=step>=questions.length-1;
  const input=ask.input||{};
  const detail=input.command||input.file_path||input.path||input.url||input.pattern||input.description||"";
  return (
    <div className="ask-overlay">
      <div className="card ask-modal" style={{maxWidth:560,width:"100%",padding:18,boxShadow:"0 12px 40px var(--shadow-color)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <strong style={{fontSize:15}}>{isQ?t("Claude has a question"):t("Approve this action?")}</strong>
        </div>
        {isQ ? (()=>{
          const qi=Math.min(step,questions.length-1);
          const q=questions[qi]||{};
          return (
          <div className="ask-modal-inner">
            {questions.length>1&&(
              <div className="ask-progress">
                {questions.map((_,i)=><span key={i} className={"dot"+(i<qi?" done":i===qi?" cur":"")}/>)}
              </div>
            )}
            {questions.length>1&&<div style={{fontSize:12,opacity:.6,marginBottom:4}}>{t("Question")} {qi+1} {t("of")} {questions.length}</div>}
            <div key={qi} className="ask-modal-body">
              <div className="ask-q" dir={dirOf(q.question)} style={{fontWeight:600,marginBottom:2}}>{q.question}</div>
              <div style={{fontSize:12,opacity:.6,marginBottom:10}}>{q.multiSelect?t("Choose one or more"):t("Choose one")}</div>
              <div className="ask-opts" style={{display:"flex",flexDirection:"column",gap:8}}>
                {(q.options||[]).map((o,oi)=>{
                  const active=q.multiSelect?sel[qi].picked.includes(o.label):sel[qi].picked===o.label;
                  return (
                    <div key={oi} role="button" tabIndex={0}
                      className={"ask-opt "+(q.multiSelect?"ask-opt-check":"ask-opt-radio")+(active?" active":"")}
                      onClick={()=>pick(qi,o.label,q.multiSelect)}
                      onKeyDown={e=>{if(e.key===" "||e.key==="Enter"){e.preventDefault();pick(qi,o.label,q.multiSelect);}}}>
                      <span className="ask-mark">{active?(q.multiSelect?"✓":"●"):""}</span>
                      <span className="ask-opt-body">
                        <span dir={dirOf(o.label)} style={{fontWeight:600,display:"block"}}>{o.label}</span>
                        {o.description&&<span dir={dirOf(o.description)} style={{fontSize:12,opacity:.7,display:"block"}}>{o.description}</span>}
                      </span>
                    </div>
                  );
                })}
                <input className="input" placeholder={t("Other…")} value={sel[qi].other} onChange={e=>setOther(qi,e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"&&sel[qi].other.trim()){e.preventDefault();if(isLast){if(canSubmit)submitQ();}else if(stepOk(qi))setStep(s=>s+1);}}}
                  style={{marginTop:2}}/>
              </div>
            </div>
            <div className="ask-modal-foot" style={{display:"flex",gap:8,alignItems:"center",marginTop:16}}>
              <button className="btn" onClick={onDismiss}>{t("Dismiss")}</button>
              {qi>0&&<button className="btn" style={{marginInlineStart:"auto"}} onClick={()=>setStep(s=>s-1)}>{t("Back")}</button>}
              {isLast
                ? <button className="btn primary" style={qi>0?{}:{marginInlineStart:"auto"}} disabled={!canSubmit} onClick={submitQ}>{t("Send answer")}</button>
                : <button className="btn primary" style={qi>0?{}:{marginInlineStart:"auto"}} disabled={!stepOk(qi)} onClick={()=>setStep(s=>s+1)}>{t("Next")}</button>}
            </div>
          </div>
          );
        })() : (
          <div className="ask-modal-inner">
            <div className="ask-modal-body">
              <div dir={dirOf(ask.title||"")} style={{marginBottom:8}}>{ask.title||(t("Claude wants to use")+" "+(ask.tool||t("a tool")))}</div>
              {detail&&<pre style={{whiteSpace:"pre-wrap",wordBreak:"break-word",background:"var(--code-bg,#0002)",padding:"8px 10px",borderRadius:8,fontSize:12,maxHeight:200,overflowY:"auto"}}>{detail}</pre>}
              {ask.description&&<div className="note" dir={dirOf(ask.description)} style={{fontSize:12,marginTop:6}}>{ask.description}</div>}
            </div>
            <div className="ask-modal-foot" style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button className="btn" onClick={()=>onAnswer({allow:false,message:"User denied this action."})}>{t("Deny")}</button>
              <button className="btn primary" onClick={()=>onAnswer({allow:true})}>{t("Allow")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// The real failure reason from the server, shown inline. Short reasons render as
// plain text; long or multi-line ones collapse into a <details> disclosure (same
// pattern used elsewhere in this file). Hebrew renders RTL per project convention.
const FAIL_REASON_MAX=160;
function FailReason({text,inline}){
  const t=String(text||"").trim();
  if(!t)return null;
  const long=t.length>FAIL_REASON_MAX||t.indexOf("\n")>=0;
  const head=t.split("\n")[0].slice(0,FAIL_REASON_MAX);
  const style={whiteSpace:"pre-wrap",wordBreak:"break-word",fontSize:11,marginTop:4,fontFamily:"var(--mono, ui-monospace, monospace)"};
  if(!long)return <span dir={dirOf(t)} style={{whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{t}</span>;
  return <details style={inline?{marginTop:2,width:"100%"}:{marginTop:2}}>
    <summary style={{cursor:"pointer"}}><span dir={dirOf(head)}>{head+(t.length>head.length?"…":"")}</span></summary>
    <pre dir={dirOf(t)} style={style}>{t}</pre>
  </details>;
}

function ClaudePanel({p,projects,onSwitchProject,usage,ask,openChat,openRun,newChatReq,tab,headSlot,providerSlot,onActivateChat,onTipDone,onTipStart,onRunCmd,onChatSeen,onChatRunning,onChatDone,paneVisible,localDone,onAddNote}){
  const{lang}=useLang(); // subscribe so chat chrome re-renders on language change
  const[sessions,setSessions]=useState([]); // most-recent-first list of chat sessions
  const[guard,setGuard]=useState(null); // pending wrong-project confirmation: {match,raw,opts}
  const[provider,setProvider]=useState(()=>{try{const p=localStorage.getItem("hub.aiProvider");return["claude","codex","gemini"].includes(p)?p:"claude";}catch{return"claude";}});
  const[model,setModelRaw]=useState(()=>{try{const p=localStorage.getItem("hub.aiProvider")||"claude";return canonModel(p,localStorage.getItem("hub."+p+"Model")||defaultModelFor(p));}catch{return defaultModelFor("claude");}}); // chat model, remembered across reloads
  // Every chat model write goes through canonModel so a legacy pinned ID restored
  // from a saved chat/localStorage (claude-opus-5) becomes its alias (opus).
  const setModel=useCallback(v=>setModelRaw(canonModel(undefined,v)),[]);
  const[executionMode,setExecutionMode]=useState(()=>{try{const p=localStorage.getItem("hub.aiProvider")||"claude";return safeMode(localStorage.getItem("hub."+p+"ExecutionMode"));}catch{return"auto";}});
  const[effort,setEffort]=useState(()=>{try{const p=localStorage.getItem("hub.aiProvider")||"claude";return safeEffortClient(localStorage.getItem("hub."+p+"Effort"));}catch{return"medium";}});
  const[modeMenuOpen,setModeMenuOpen]=useState(false); // agent-controls popover (mode + effort)
  const modeMenuRef=useRef(null);
  const[fontScale,setFontScale]=useState(()=>{try{return +localStorage.getItem("hub.chatFontScale")||1;}catch{return 1;}}); // chat log font size, remembered across reloads
  // Reading direction for the whole chat pane. "auto" keeps the per-message
  // detection (any Hebrew ⇒ RTL); "rtl"/"ltr" force every bubble, the composer and
  // the edit box one way, for mixed conversations where the guess reads wrong.
  const[dirMode,setDirMode]=useState(()=>{try{return localStorage.getItem("hub.chatDir")||"auto";}catch{return "auto";}});
  const cycleDirMode=()=>setDirMode(m=>{
    const next=m==="auto"?"rtl":m==="rtl"?"ltr":"auto";
    try{localStorage.setItem("hub.chatDir",next);}catch{}
    return next;
  });
  const[activeId,setActiveId]=useState(null);
  const[input,setInput]=useState("");
  const[quickPrompts,setQuickPrompts]=useState(loadQuickPrompts); // user-editable quick-prompt chips
  const[quickEdit,setQuickEdit]=useState(false); // quick-prompt editor popover open
  const commitQuickPrompts=useCallback(list=>{setQuickPrompts(list);saveQuickPrompts(list);},[]);
  const[runningIds,setRunningIds]=useState([]); // ids of chats with a live stream — each keeps working when you switch away
  const[opStates,setOpStates]=useState({}); // chatId -> explicit async lifecycle state for its current/last run
  const[err,setErr]=useState("");
  const[dir,setDir]=useState("");           // working directory Claude runs in
  const[dirErr,setDirErr]=useState("");
  const[browseOpen,setBrowseOpen]=useState(false); // false, or the file to preview
  const[localInfo,setLocalInfo]=useState(null); // {online,note} for isLocal projects
  const[loaded,setLoaded]=useState(false);
  const[showHist,setShowHist]=useState(false);
  const[histQuery,setHistQuery]=useState(""); // filter text for the history dropdown
  const[histTab,setHistTab]=useState("user"); // history dropdown tab: "user" (your chats) | "task" (chats created from tasks)
  const[closedTabs,setClosedTabs]=useState([]); // ids closed off the tab strip but KEPT in history (reopen from 🕘 History) — persisted per slug in localStorage so the arrangement survives project switches/reloads
  const[openedTabs,setOpenedTabs]=useState([]); // ids of OLD chats the user reopened from 🕘 History — pins them back onto the strip past the recency window; persisted per slug in localStorage
  const[tabOrder,setTabOrder]=useState(null); // {ids,stamp} manual drag arrangement of the strip; persisted per slug, discarded by orderTabs() as soon as any chat sees new activity
  const[dragTab,setDragTab]=useState(null);   // id of the tab being dragged
  const[dropAt,setDropAt]=useState(null);     // {id,side} — where the dragged tab would land
  const[tabMenu,setTabMenu]=useState(null); // right-click menu for conversation tabs; delete here means permanent history deletion
  const[histCtx,setHistCtx]=useState(null); // right-click menu for chat-history rows (same actions as the tab menu)
  const[suggestions,setSuggestions]=useState([]); // tips → starter prompts
  const[attach,setAttach]=useState([]); // files/images staged for the next message
  // Messages typed while a chat is still working. They are saved (not injected into
  // the live turn) and sent as ONE new turn the moment the current run finishes.
  const[queued,setQueued]=useState({}); // chatId -> [text,...] awaiting the current run's end
  const queuedRef=useRef(queued);queuedRef.current=queued;
  function enqueueMsg(id,text){setQueued(q=>{const next={...q,[id]:[...(q[id]||[]),text]};queuedRef.current=next;return next;});}
  // Canceling a queued chip returns its text to the composer so it's never lost.
  function dropQueued(id,idx){
    const removed=(queuedRef.current[id]||[])[idx];
    setQueued(q=>{const list=(q[id]||[]).slice();list.splice(idx,1);const next={...q};if(list.length)next[id]=list;else delete next[id];queuedRef.current=next;return next;});
    if(removed!=null&&id===activeIdRef.current)setInput(prev=>prev?prev+"\n\n"+removed:removed);
  }
  // Send everything queued for `id` as a single follow-up turn. Called at the true
  // end of a run (the `done` event), so runTurn just chains the next turn on.
  function flushQueued(id){
    const list=(queuedRef.current[id]||[]);
    if(!list.length)return;
    setQueued(q=>{const next={...q};delete next[id];queuedRef.current=next;return next;});
    const text=list.join("\n\n");
    patchSession(id,s=>({...s,updatedAt:Date.now(),messages:[...s.messages,{role:"user",text,ts:Date.now()},{role:"assistant",parts:[],ts:Date.now(),retryPrompt:text}]}));
    runTurn(id,text);
  }
  const[fileDrag,setFileDrag]=useState(false); // native files hovering anywhere over the chat
  const[driveOpen,setDriveOpen]=useState(false); // Google Drive picker modal
  const[attachMenuOpen,setAttachMenuOpen]=useState(false); // combined local/Drive attachment menu
  const attachBtnRef=useRef(null),attachPopRef=useRef(null);
  useMenuFocus(attachMenuOpen,attachPopRef,attachBtnRef);
  const[skills,setSkills]=useState(()=>SKILLS_CACHE||[]); // available "/" skills
  const[slashIdx,setSlashIdx]=useState(0);  // highlighted row in the "/" menu
  const[slashHide,setSlashHide]=useState(false); // Esc dismisses the menu until edited
  const[editingLast,setEditingLast]=useState(false);
  const[editVal,setEditVal]=useState("");
  const[notedIdx,setNotedIdx]=useState(null); // message index that just got saved to Plan > Notes (shows ✓ briefly)
  const[scheduleOpen,setScheduleOpen]=useState(false);
  const[pendingAsk,setPendingAsk]=useState({}); // chatId -> the interactive prompt (AskUserQuestion / tool-permission) awaiting an answer
  const[followups,setFollowups]=useState({}); // chatId -> {ts,text}: suggested next question for that chat's last answer (ghost text)
  const inlineFollowupRef=useRef(new Map()); // chatId -> suggestion the live turn wrote inline (<<followup: ...>>), consumed on done
  const[followupOn,setFollowupOn]=useState(followupEnabled);
  useEffect(()=>{const h=()=>setFollowupOn(followupEnabled());window.addEventListener("hub:followup-pref",h);window.addEventListener("storage",h);
    return()=>{window.removeEventListener("hub:followup-pref",h);window.removeEventListener("storage",h);};},[]);
  const[scheduleAt,setScheduleAt]=useState("");
  const[scheduleTitle,setScheduleTitle]=useState("Scheduled chat");
  const[scheduleFreq,setScheduleFreq]=useState("0");
  const[scheduledChats,setScheduledChats]=useState([]);
  const[scheduleBusy,setScheduleBusy]=useState(false);
  const[hubTodoOpen,setHubTodoOpen]=useState(false);
  const[hubTodoText,setHubTodoText]=useState("");
  const[hubTodoRows,setHubTodoRows]=useState([]);
  const[hubTodoBusy,setHubTodoBusy]=useState(false);
  const[hubTodoErr,setHubTodoErr]=useState("");
  const[hubTelegram,setHubTelegram]=useState({loaded:false,connected:false});
  const[hubTelegramToken,setHubTelegramToken]=useState("");
  const[hubTelegramChat,setHubTelegramChat]=useState("");
  const[hubTelegramBusy,setHubTelegramBusy]=useState(false);
  const logRef=useRef(null);
  const tabsRef=useRef(null);  // horizontal chat-tabs strip (for scroll/paging buttons)
  const historyMenuRef=useRef(null);
  const histBtnRef=useRef(null);
  const[histPos,setHistPos]=useState(null); // viewport-clamped fixed position for the history menu
  const tabMenuRef=useRef(null);
  const histCtxRef=useRef(null);
  // Keep the active tab fully visible after every selection/reorder. The helper
  // scrolls only this strip, avoiding scrollIntoView() moving the whole page.
  React.useLayoutEffect(()=>{
    const strip=tabsRef.current;if(!strip)return;
    const selected=Array.from(strip.children).find(el=>el.classList&&el.classList.contains("active"));
    scrollTabFullyIntoView(strip,selected,"smooth");
  },[activeId,sessions,closedTabs,openedTabs,tabOrder]);
  const taRef=useRef(null);    // chat textarea (refocus after picking a skill)
  const fileRef=useRef(null);
  const attachMenuRef=useRef(null);
  const fileDragDepth=useRef(0); // nested dragenter/leaves must not flicker the overlay
  const abortersRef=useRef(new Map()); // sessionId -> AbortController for that chat's live stream
  const manuallyStoppedRef=useRef(new Set()); // manual Stop never creates a failure indicator
  // Runs this browser watched END: runId -> terminal state. The server's copy of a
  // run can stay `done:false` long after the conversation finished (the agent's
  // exit POST is the only thing that flips it, and an agent that dies mid-run
  // never sends one), and EVERY project switch re-seeds the per-chat op states -
  // the tab lights - from that copy. That is how a finished chat's tab went back
  // to orange "Working…" and stayed there across reloads. A run we saw end with
  // our own eyes is over, whatever a later poll claims.
  const endedRunsRef=useRef(new Map());
  const runningRef=useRef(runningIds);runningRef.current=runningIds;
  const opStatesRef=useRef(opStates);opStatesRef.current=opStates;
  const sessionsRef=useRef(sessions);sessionsRef.current=sessions;
  const activeIdRef=useRef(activeId);activeIdRef.current=activeId;
  const attachRef=useRef(attach);attachRef.current=attach;
  const inputRef=useRef(input);inputRef.current=input;
  // Persist the composer to sessionStorage on every change so a page reload
  // (e.g. the automatic ServerReconnect reload after a hub restart) restores it.
  // chatId -> unsent composer text, kept across chat switches AND across page
  // reloads (the ServerReconnect auto-reload used to wipe whatever was typed).
  // Seeded from sessionStorage so a reload restores every pending draft.
  const draftsRef=useRef((()=>{try{return JSON.parse(sessionStorage.getItem("hub.chatDrafts")||"{}")||{};}catch{return {};}})());
  // Snapshot the live composer (current input under the active chat) into the
  // persisted draft map. Called on every keystroke so a reload never loses text.
  const persistDrafts=useCallback(()=>{
    const d=stashDraft(draftsRef.current,activeIdRef.current,inputRef.current);
    draftsRef.current=d;
    try{sessionStorage.setItem("hub.chatDrafts",JSON.stringify(d));}catch{}
  },[]);
  useEffect(()=>{persistDrafts();},[input,activeId,persistDrafts]);
  useEffect(()=>{
    if(!hubTodoOpen)return;
    loadHubTodos();loadHubTelegram();
    const timer=setInterval(loadHubTodos,4000);
    return()=>clearInterval(timer);
  },[hubTodoOpen,p.slug]);
  // Escape closes the docked composer popovers (parity with every other dialog).
  useEffect(()=>{
    if(!hubTodoOpen&&!scheduleOpen)return;
    const h=e=>{if(e.key==="Escape"){e.stopPropagation();if(hubTodoOpen)setHubTodoOpen(false);if(scheduleOpen)setScheduleOpen(false);}};
    document.addEventListener("keydown",h);
    return()=>document.removeEventListener("keydown",h);
  },[hubTodoOpen,scheduleOpen]);
  const lastAsk=useRef(0);
  const lastOpenChat=useRef(0); // nonce of the last "Open chat" jump we honored
  const lastOpenRun=useRef(0); // nonce of the last "Open AI chat" (run-as-chat) request we honored
  const lastNewChat=useRef(0); // nonce of the last Home "New chat with provider" request we honored
  const pendingTipRef=useRef(null); // {chatId,tipId,slug} — a tip's run we should nudge "mark solved?" on completion
  const saveTimer=useRef(null);
  // chatId -> {error, stderr:[...last few lines], exit} — the LAST REAL failure reason
  // reported by the server for the current run. Without this the specific error is
  // lost and every failure reads as the generic "stream closed without a final result".
  const failReasonRef=useRef(new Map());
  const loadingFullRef=useRef(new Set()); // chat ids whose full transcript is being lazy-loaded, to dedupe fetches
  const sessionCacheRef=useRef(new Map()); // slug -> last-known sessions snapshot; lets a switch back to an already-visited project paint instantly (no blank/spinner) while it revalidates in the background
  const loadedSlug=useRef(null); // slug whose history is currently loaded — guards the auto-saver
  const deletedIdsRef=useRef([]); // ids this client explicitly deleted; sent on every save so the union-merge server drops them (and can't resurrect them from a stale copy)
  const closedTabsRef=useRef(closedTabs);closedTabsRef.current=closedTabs; // synchronous mirror for closeTab/openSession
  const openedTabsRef=useRef(openedTabs);openedTabsRef.current=openedTabs; // synchronous mirror for closeTab/openSession

  const active=sessions.find(s=>s.id===activeId)||null;
  const agentName=providerName(provider);
  const providerModels=AI_MODELS[provider]||AI_MODELS.claude;
  const msgs=active?active.messages:[];
  const lastUserIdx=msgs.map(m=>m.role).lastIndexOf("user");
  const history=sessions.slice(); // all chats (incl. a fresh empty New chat) show in history
  // A chat is "running" while its client stream is live. This is per-chat now, so
  // several chats can work at once and you can freely switch between them; `busy`
  // is just the ACTIVE chat's state (drives Send/Stop and the bottom status panel).
  const isRunning=(id)=>id!=null&&runningRef.current.indexOf(id)>=0;
  const opFor=(id)=>id?opStatesRef.current[id]||null:null;
  const bgWaitText=(n)=>n===1?"Waiting for a background agent to finish.":"Waiting for "+n+" background agents to finish.";
  // `isRunning` tracks the client STREAM, which stays open for a moment after the
  // run's own `done` event (the fetch body has to close and unwind before
  // setRunning(false) lands). For the indicators that is too late: the chat is
  // ready, so it must already read as ready. Ask the run's terminal state instead.
  const isWorking=(id)=>isRunning(id)&&((opFor(id)||{}).state!=="completed");
  // "New finished conversation" indication for every display (sidebar badge, this
  // panel's History button + chat dots, Home Last Chats). Single source of truth:
  // the PERSISTED session.doneUnseen flag — set the moment a turn finishes,
  // cleared once the chat has actually been looked at. A chat whose NEXT turn is
  // already working again is never "unseen".
  const unseenCount=sessions.filter(s=>s&&s.doneUnseen&&!isWorking(s.id)).length;
  // A task chat "needs you to act" while it sits parked on an approval/question.
  const taskNeedsAct=(id)=>!!pendingAsk[id]||((opFor(id)||{}).state==="waiting");
  // Per-tab status light. Colour = what the conversation is doing; `live` = a
  // running stream, which makes the dot blink. Returns null when there's nothing
  // worth flagging on the tab.
  //   orange  working (processing/streaming)
  //   blue    waiting for my approval or a survey answer
  //   green   completed and not viewed yet
  //   red     unexpectedly failed and not viewed yet
  const tabStatus=(id,unseen,failedUnseen)=>{
    const running=isWorking(id);
    const st=(opFor(id)||{}).state;
    if(st==="waiting")return{tone:"waiting",live:running,label:"Waiting for your approval or answer"};
    if(st==="cancelled")return{tone:"error",live:false,label:"Stopped before finishing"};
    if(failedUnseen||(st==="stuck"||st==="failed"))return{tone:"error",live:false,label:"Stuck or disconnected"};
    if(running||st==="processing"||st==="streaming")return{tone:"working",live:true,label:"Working…"};
    if(unseen)return{tone:"done",live:false,label:"Finished"};
    return null;
  };
  const runToOp=(rn)=>rn&&rn.chatId?{
    runId:rn.runId,
    // A ghost run (still `done:false` on the server although it ended here) must
    // not repaint this chat as working — use the terminal state we observed.
    state:(!rn.done&&endedRunsRef.current.get(rn.runId))||rn.state||(!rn.done?"processing":(rn.code===0?"completed":"failed")),
    provider:rn.provider||"claude",
    startedAt:rn.startedAt||Date.now(),
    lastEventAt:rn.lastEventAt||rn.startedAt||Date.now(),
    endedAt:rn.endedAt||null,
    detail:rn.error||"",
  }:null;
  // Keep only the newest run per chat: several runs of the same chat may still be
  // listed, and re-attaching to more than one replays an older buffer over the
  // newest assistant message.
  const newestPerChat=(list)=>{
    const by=new Map();
    for(const r of (list||[])){
      if(!r||!r.chatId)continue;
      const cur=by.get(r.chatId);
      const t=(x)=>x.lastEventAt||x.startedAt||0;
      if(!cur||t(r)>=t(cur))by.set(r.chatId,r);
    }
    return Array.from(by.values());
  };
  const busy=runningIds.indexOf(activeId)>=0;
  // The active chat is parked on an approval/question popup. You can still reply in
  // the box: the text becomes the answer (a denial carrying your words as feedback)
  // and the run continues, so a parked run is never a dead end.
  const parked=activeId!=null&&!!pendingAsk[activeId];
  // Ghost follow-up: only for the chat's CURRENT last answer, only while the box
  // is empty and nothing else (a run, a parked question, staged files) owns it.
  const lastMsg=msgs[msgs.length-1];
  const fu=activeId!=null?followups[activeId]:null;
  const ghost=(followupOn&&fu&&fu.text&&lastMsg&&lastMsg.role==="assistant"&&(lastMsg.ts||0)===fu.ts
    &&!input&&!busy&&!parked&&!attach.length)?fu.text:"";
  function acceptGhost(){
    if(!ghost)return;
    setInput(ghost);
    const ta=taRef.current;
    if(ta)requestAnimationFrame(()=>{try{ta.focus();ta.setSelectionRange(ghost.length,ghost.length);}catch{}});
  }
  function dismissGhost(){if(activeId!=null)setFollowups(f=>{const n={...f};delete n[activeId];return n;});}
  const activeStatus=busy?claudeStatus(msgs[msgs.length-1]&&msgs[msgs.length-1].parts):"";
  const activeOp=opFor(activeId);
  const lastAssistant=[...msgs].reverse().find(m=>m.role==="assistant")||null;
  const lastActivity=active&&(active.updatedAt||active.createdAt);
  const conversationState=(()=>{
    if(!loaded)return{kind:"loading",label:"Loading conversation",detail:"Restoring chat history and run state."};
    if(activeOp&&activeOp.state==="waiting")return{kind:"waiting",label:activeOp.agentOffline?"Agent offline":"Waiting for input",detail:activeOp.detail||"Paused for your confirmation — answer the popup, or just reply in the box below.",action:"Force Reset"};
    // Reconnecting is NOT an error: the run is still alive server-side and every
    // event is durably sequenced, so we say so quietly and keep the turn open.
    // `sub` carries WHY the last attempt failed (network error vs stream
    // timeout) as small secondary text, so the state is never unexplained.
    if(activeOp&&activeOp.state==="reconnecting")return{kind:"reconnecting",label:t("Reconnecting..."),detail:activeOp.detail||t("The connection dropped - reconnecting to the same run."),sub:activeOp.lastErrorText?"Last attempt failed: "+activeOp.lastErrorText:null};
    if(activeOp&&activeOp.state==="stuck")return{kind:"stuck",label:"Disconnected or silent",detail:activeOp.detail||"No stream events have arrived recently. The run may have disconnected or exited silently.",action:lastAssistant&&lastAssistant.retryPrompt?"Retry":"Force Reset"};
    // A server-reported stall is NOT an error: the run is alive (heartbeats
    // flow) but has produced no output for a while - say exactly that, quietly,
    // instead of letting the chat look silently dead.
    if(busy&&activeOp&&activeOp.stalled)return{kind:"processing",label:agentName+" is "+(activeStatus||"thinking"),detail:"Still running - no output for "+Math.max(1,Math.round((activeOp.silentForMs||0)/1000))+"s"};
    if(busy)return{kind:"processing",label:agentName+" is "+(activeStatus||"thinking"),detail:"Working — type below and your message is saved, then sent as a new turn when this finishes."};
    if(lastAssistant&&lastAssistant.interrupted)return{
      kind:"stuck",
      label:lastAssistant.resumeRunId?"Execution paused":"Connection lost",
      detail:lastAssistant.interruptedReason||("The stream ended before "+agentName+" sent a completion signal."),
      action:lastAssistant.resumeRunId?"Resume":(lastAssistant.retryPrompt?"Retry":"Force Reset")
    };
    if(lastAssistant&&lastAssistant.meta&&lastAssistant.meta.error)return{
      kind:"error",
      label:"Run ended with an error",
      detail:(lastAssistant.meta.resultText||lastAssistant.interruptedReason||agentName+" reported an error before completing this turn."),
      action:lastAssistant.retryPrompt?"Retry":null
    };
    // A turn the user stopped, or one the server cancelled/failed, ended abruptly
    // - it must NOT read as a calm green "Ready". Surface it as a red alert with a
    // resume/retry so the premature end is visible.
    if(activeOp&&(activeOp.state==="cancelled"||activeOp.state==="failed"))return{
      kind:activeOp.state==="cancelled"?"stopped":"error",
      label:activeOp.state==="cancelled"?"Stopped":"Run ended with an error",
      detail:activeOp.detail||(activeOp.state==="cancelled"
        ?"This turn was stopped before it finished. Send a new message, or resume/retry."
        :agentName+" ended this turn with an error before completing it."),
      action:lastAssistant&&lastAssistant.resumeRunId?"Resume":(lastAssistant&&lastAssistant.retryPrompt?"Retry":null)
    };
    return{
      kind:"idle",
      label:"Ready",
      detail:msgs.length
        ?("Last activity "+(lastActivity?relTime(lastActivity):"just now")+". Review timestamps, details, or choose a quick prompt.")
        :"Start a new conversation or choose a quick start below."
    };
  })();
  // Which chats carry local edits the server hasn't been told about. Only these
  // are uploaded by the debounced save: the light list and the transcripts the
  // hydration loop pulls back down came FROM the server, and echoing them back
  // meant every project switch ended in a multi-megabyte POST from the phone
  // (which then had to be re-merged and rewritten server-side). Ids are never
  // un-marked while the project stays open — resending a chat you actually
  // touched is cheap; dropping one of its saves is not.
  const dirtyRef=useRef(new Set());
  function commitSessions(nextOrFn,opts){
    const prev=sessionsRef.current;
    const next=typeof nextOrFn==="function"?nextOrFn(prev):nextOrFn;
    const mode=(opts&&opts.dirty)||"diff";
    if(mode==="reset"){dirtyRef.current=new Set();              // a fresh load from the server
      // Old turns never show an inline follow-up marker (only a live finish turns it into ghost text).
      if(Array.isArray(next))for(let k=0;k<next.length;k++)next[k]=stripFollowupFromSession(next[k]);}
    else if(mode==="diff"){
      // A session that appears out of nowhere (new chat, chat built from a task
      // run) is local by definition and must reach the server.
      const before=new Set(prev.map(s=>s.id));
      for(const s of next)if(!before.has(s.id))dirtyRef.current.add(s.id);
    }                                                          // "none": the caller tracked it itself
    sessionsRef.current=next;setSessions(next);
    // Snapshot the loaded project's sessions (including hydrated transcripts) so a
    // later switch back to it repaints instantly from cache instead of blanking
    // out and waiting on the server's full-store parse.
    if(loadedSlug.current)sessionCacheRef.current.set(loadedSlug.current,next);
    return next;
  }
  function commitRunning(nextOrFn){
    const next=typeof nextOrFn==="function"?nextOrFn(runningRef.current):nextOrFn;
    runningRef.current=next;setRunningIds(next);return next;
  }
  function commitOps(nextOrFn){
    const next=typeof nextOrFn==="function"?nextOrFn(opStatesRef.current):nextOrFn;
    opStatesRef.current=next;setOpStates(next);return next;
  }
  function setOp(id,patch){
    if(!id)return;
    commitOps(prev=>({...prev,[id]:{...(prev[id]||{}),...patch,lastEventAt:patch.lastEventAt||Date.now()}}));
  }
  function clearOp(id){if(id)commitOps(prev=>{const next={...prev};delete next[id];return next;});}
  // Record that a run reached a terminal state here, so a server snapshot that
  // still calls it live can never drag the chat back to "working". Bounded: only
  // the newest few hundred matter (an older run is long gone from /api/runs).
  function noteRunEnded(id,state){
    const rid=(opFor(id)||{}).runId||(sessionsRef.current.find(s=>s&&s.id===id)||{}).runId||null;
    if(!rid)return;
    const m=endedRunsRef.current;
    m.delete(rid);m.set(rid,state||"completed");
    while(m.size>300)m.delete(m.keys().next().value);
  }
  // A run this browser saw end, by runId — used to drop ghosts out of the resume
  // list as well as out of the tab lights.
  const runEnded=(rn)=>!!(rn&&rn.runId&&endedRunsRef.current.has(rn.runId));
  // Stream-resume feedback: a subtle "reconnecting" state, never the red error.
  // The hard "Connection lost" banner is reserved for a genuinely dead run.
  function applyResumeStatus(id,st){
    if(!id||!st)return;
    if(st.state==="reconnecting")setOp(id,{state:"reconnecting",detail:t("The connection dropped - reconnecting to the same run.")+(st.attempt>1?" ("+st.attempt+")":""),lastErrorText:st.lastError||null});
    else if(st.state==="resumed")setOp(id,{state:"processing",detail:t("Reconnected - continuing this turn where it stopped."),lastErrorText:null});
  }
  function setRunning(id,on){commitRunning(prev=>{const has=prev.indexOf(id)>=0;if(on&&!has)return[...prev,id];if(!on&&has)return prev.filter(x=>x!==id);return prev;});}

  // A task card / Home chat row's "open this chat" request races the history load
  // that happens on a project switch: if the request's chat isn't in `list` yet, it
  // hasn't loaded, NOT gone missing — leave the nonce unconsumed so whichever load
  // path finishes with the right list still honors it, instead of falling through
  // to the default/live-run chat. Returns true once actually honored.
  function jumpToRequestedChat(list){
    if(!openChat||!openChat.nonce||openChat.nonce===lastOpenChat.current)return false;
    if(openChat.slug&&openChat.slug!==p.slug)return false;
    if(!list.some(s=>s.id===openChat.chatId))return false;
    lastOpenChat.current=openChat.nonce;
    openSession(openChat.chatId);
    return true;
  }

  // Load this project's saved chats, its working dir, and tip-based suggestions.
  // Then check whether a turn is STILL RUNNING for this project (started earlier
  // and left going when we switched away) and resume streaming it.
  useEffect(()=>{
    let live=true;
    loadedSlug.current=null;deletedIdsRef.current=[];
    const savedClosed=loadTabPrefs("closedTabs",p.slug),savedOpened=loadTabPrefs("openedTabs",p.slug),savedOrder=loadTabOrder(p.slug);
    setLoaded(false);setErr("");setInput("");draftsRef.current={};commitRunning([]);commitOps({});setShowHist(false);setHistQuery("");setHistTab("user");setTabMenu(null);setTabOrder(savedOrder);setDragTab(null);setDropAt(null);closedTabsRef.current=savedClosed;setClosedTabs(savedClosed);openedTabsRef.current=savedOpened;setOpenedTabs(savedOpened);
    setDir("");setDirErr("");setLocalInfo(null);setSuggestions([]);setAttach([]);
    // Stale-while-revalidate: if we already loaded this project this session, paint
    // its cached sessions right away (no blank list, no spinner) and mark it loaded
    // so the switch feels instant. The fetch below still runs and overwrites this
    // with authoritative data one round-trip later. First-ever visit falls through
    // to the normal blank-then-load path.
    const cachedSessions=sessionCacheRef.current.get(p.slug);
    if(cachedSessions&&cachedSessions.length){loadedSlug.current=p.slug;commitSessions(cachedSessions,{dirty:"reset"});setLoaded(true);}
    // …and keep those transcripts when the authoritative light list lands: a
    // light session whose updatedAt still matches the copy we already hydrated
    // adds nothing over it, so swiping back to a project doesn't re-download
    // every chat it downloaded last time.
    const keepHydrated=(light)=>{
      const have=cachedSessions&&cachedSessions.length?new Map(cachedSessions.map(s=>[s.id,s])):null;
      if(!have)return light;
      return light.map(s=>{
        const old=have.get(s.id);
        if(!old||old.partial||(old.updatedAt||0)!==(s.updatedAt||0))return s;
        return{...s,messages:old.messages,msgCount:old.messages.length,partial:false};
      });
    };
    // Fire history + runs together (not chained) — runs doesn't depend on the
    // history response, so waiting for one round trip before starting the next
    // just adds latency to every project switch for no reason.
    const runsReq=fetch("/api/claude/runs?slug="+encodeURIComponent(p.slug)).then(r=>r.json());
    fetch("/api/claude/history?slug="+encodeURIComponent(p.slug)+"&light=1").then(r=>r.json()).then(d=>{
      if(!live)return;
      const ss=keepHydrated((d&&d.sessions)||[]);
      commitSessions(ss,{dirty:"reset"});
      loadedSlug.current=p.slug;
      setLoaded(true);
      // Resume EVERY run still live for a chat we just loaded (they kept working
      // while we were away), so switching projects and back shows them all still
      // streaming. Open a chat that is actively running first; otherwise DON'T
      // reopen the last conversation — default the view to a fresh new chat so the
      // user always lands on an empty prompt ready to type.
      runsReq.then(rd=>{
        if(!live)return;
        const list=(rd&&rd.runs)||[];
        const resumable=list.filter(x=>x.chatId&&ss.some(s=>s.id===x.chatId));
        commitOps(Object.fromEntries(resumable.map(rn=>[rn.chatId,runToOp(rn)]).filter(x=>x[1])));
        // NEVER re-attach to a finished run: its buffer would be replayed over the
        // newest assistant message and then persisted, resurrecting an old answer.
        // Only genuinely live runs are resumed, newest one per chat.
        const liveOnes=newestPerChat(resumable.filter(x=>!x.done&&!runEnded(x)));
        liveOnes.forEach(rn=>{if(!abortersRef.current.has(rn.chatId))resumeRun(rn);});
        if(jumpToRequestedChat(ss)){/* an explicit "open this chat" request wins over the default/live-run pick */}
        else if(liveOnes.length)setActiveId(liveOnes[0].chatId);else openFreshDefault(ss);
      }).catch(()=>{if(live&&!jumpToRequestedChat(ss))openFreshDefault(ss);});
    }).catch(()=>{if(live){commitSessions([],{dirty:"reset"});setActiveId(null);loadedSlug.current=p.slug;setLoaded(true);}});
    fetch("/api/claude/dir?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).then(d=>{
      // `local` says the project runs on another machine (its own agent); `agentOnline`
      // is reported whenever an agent is what executes this project at all (always on
      // Heygents). Track both so the chip can stay honest about which one it means.
      if(!live)return;if(d&&d.ok){setDir(d.dir||"");if(d.local||d.agentOnline!==undefined)setLocalInfo({online:!!d.agentOnline,note:d.agentNote||"",local:!!d.local});}else setDirErr((d&&d.error)||"No working directory set.");
    }).catch(()=>{});
    fetch("/api/tips?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).then(d=>{
      if(live&&d&&d.tips)setSuggestions(d.tips);
    }).catch(()=>{});
    // Leaving this project (switch or dock close): detach every in-flight stream so
    // none can keep writing into — or drop a "network error" onto — another project's
    // chat. The server-side runs stay alive, so we re-attach to them all on return.
    return()=>{live=false;detachAllStreams();};
  },[p.slug]);

  // A hand-dragged tab arrangement is a snapshot: activity wins. orderTabs()
  // already ignores it once any chat sees newer activity, so retire the stale
  // prefs too rather than leaving a dead arrangement in localStorage to be
  // reloaded on the next project switch.
  useEffect(()=>{
    if(!loaded||!tabOrder)return;
    if(latestActivity(sessions)>(tabOrder.stamp||0)){setTabOrder(null);saveTabOrder(p.slug,null);}
  },[sessions,tabOrder,loaded,p.slug]);

  // Phones routinely suspend JavaScript and tear down long-lived fetch streams
  // when the app is backgrounded. Detach deliberately, then reload durable chat
  // history and reattach every server run when the page becomes active/online.
  // Detaching never stops the server-side model process.
  //
  // But a BRIEF background (notification shade, quick app-switch, glance at
  // another app) must NOT tear a healthy stream down: aborting on the first
  // `hidden` forces a full reconnect+replay that reads as the chat "breaking".
  // So detaching is DELAYED — only if the page is still hidden long enough that
  // the OS is likely to freeze the tab do we actually abort. Come back before
  // then and the live streams are left untouched (streamWithResume self-heals a
  // socket the OS may have quietly dropped), so nothing flickers. A real
  // teardown signal (`pagehide`/`freeze`) still detaches immediately.
  useEffect(()=>{
    let recovering=false,detached=false,graceT=null;
    const DETACH_GRACE_MS=20000;
    const detach=()=>{
      detached=true;
      detachAllStreams();commitRunning([]);
    };
    const clearGrace=()=>{if(graceT){clearTimeout(graceT);graceT=null;}};
    const onHidden=()=>{clearGrace();graceT=setTimeout(()=>{graceT=null;if(document.visibilityState==="hidden")detach();},DETACH_GRACE_MS);};
    const recover=async()=>{
      clearGrace();
      // Nothing was torn down (returned within the grace window) — the live
      // streams are intact, so skip the history reload that would clobber the
      // in-flight bubble. A stream the OS silently killed reconnects on its own.
      if(!detached)return;
      if(recovering||document.visibilityState==="hidden"||loadedSlug.current!==p.slug)return;
      detached=false;
      recovering=true;
      try{
        const [hd,rd]=await Promise.all([
          fetch("/api/claude/history?slug="+encodeURIComponent(p.slug)).then(r=>r.json()),
          fetch("/api/claude/runs?slug="+encodeURIComponent(p.slug)).then(r=>r.json()),
        ]);
        if(loadedSlug.current!==p.slug)return;
        const ss=(hd&&hd.sessions)||[];
        commitSessions(ss,{dirty:"reset"});
        const runs=((rd&&rd.runs)||[]).filter(r=>r.chatId&&ss.some(s=>s.id===r.chatId));
        commitOps(Object.fromEntries(runs.map(rn=>[rn.chatId,runToOp(rn)]).filter(x=>x[1])));
        const liveRuns=newestPerChat(runs.filter(r=>!r.done&&!runEnded(r)));
        liveRuns.forEach(rn=>{if(!abortersRef.current.has(rn.chatId))resumeRun(rn);});
        const current=activeIdRef.current;
        if(!current||!ss.some(s=>s.id===current)){
          if(liveRuns.length)setActiveId(liveRuns[0].chatId);else openFreshDefault(ss);
        }
      }catch{}finally{recovering=false;}
    };
    const onVisibility=()=>{if(document.visibilityState==="hidden")onHidden();else recover();};
    const onTeardown=()=>{clearGrace();detach();};
    document.addEventListener("visibilitychange",onVisibility);
    window.addEventListener("pagehide",onTeardown);
    window.addEventListener("freeze",onTeardown);
    window.addEventListener("online",recover);
    window.addEventListener("pageshow",recover);
    return()=>{clearGrace();document.removeEventListener("visibilitychange",onVisibility);window.removeEventListener("pagehide",onTeardown);window.removeEventListener("freeze",onTeardown);window.removeEventListener("online",recover);window.removeEventListener("pageshow",recover);};
  },[p.slug]);

  // The project-switch load pulls a LIGHT chat list (metadata + last message only)
  // so the sidebar refreshes instantly instead of transferring every transcript.
  // When a `partial` chat becomes active, fetch its full transcript on demand and
  // splice it in. A chat with a live stream is skipped — its messages are being
  // written by the stream and must not be clobbered.
  useEffect(()=>{
    if(!loaded||activeId==null)return;
    const s=sessionsRef.current.find(x=>x.id===activeId);
    if(!s||!s.partial||loadingFullRef.current.has(activeId)||isRunning(activeId))return;
    const id=activeId,slug=p.slug;
    loadingFullRef.current.add(id);
    fetch("/api/claude/chat?slug="+encodeURIComponent(slug)+"&id="+encodeURIComponent(id))
      .then(r=>r.json()).then(d=>{
        if(loadedSlug.current!==slug||!d||!d.ok||!d.session)return;
        // Skip if a stream started for this chat while we were fetching.
        if(isRunning(id))return;
        patchSession(id,x=>stripFollowupFromSession({...d.session,partial:false,doneUnseen:x.doneUnseen,failedUnseen:x.failedUnseen}),{fromServer:true});
      }).catch(()=>{}).finally(()=>{loadingFullRef.current.delete(id);});
  },[activeId,loaded,p.slug,sessions]);

  // Background-hydrate the FEW most recent partial chats after the light load so
  // that switching to one of the chats on the tab strip is instant instead of
  // paying a round-trip. Fetches one at a time at idle, most-recent-first, reuses
  // the same de-dupe guard as the on-demand load above, skips live-streaming
  // chats, and bails the moment the project changes.
  //
  // Bounded on purpose: hydrating ALL of them pulled the project's entire chat
  // store (megabytes, one request per chat) on every project switch, which on a
  // phone queued ahead of — and starved — the next switch's own history load.
  // Anything past the window still loads on demand the moment it is opened.
  const HYDRATE_AHEAD=8;
  useEffect(()=>{
    if(!loaded)return;
    const slug=p.slug;
    let cancelled=false,handle=null;
    const ric=window.requestIdleCallback||(cb=>setTimeout(()=>cb(),300));
    const cic=window.cancelIdleCallback||clearTimeout;
    function next(){
      if(cancelled||loadedSlug.current!==slug)return;
      const s=sessionsRef.current.slice(0,HYDRATE_AHEAD).find(x=>x.partial&&!loadingFullRef.current.has(x.id)&&!isRunning(x.id));
      if(!s){handle=null;return;}
      const id=s.id;
      loadingFullRef.current.add(id);
      fetch("/api/claude/chat?slug="+encodeURIComponent(slug)+"&id="+encodeURIComponent(id))
        .then(r=>r.json()).then(d=>{
          if(cancelled||loadedSlug.current!==slug||!d||!d.ok||!d.session||isRunning(id))return;
          patchSession(id,x=>stripFollowupFromSession({...d.session,partial:false,doneUnseen:x.doneUnseen,failedUnseen:x.failedUnseen}),{fromServer:true});
        }).catch(()=>{}).finally(()=>{loadingFullRef.current.delete(id);if(!cancelled)handle=ric(next);});
    }
    handle=ric(next);
    return()=>{cancelled=true;if(handle)cic(handle);};
  },[loaded,p.slug]);

  // never persist empty drafts, and drop the transient runId (it only matters live).
  // `running` goes with it: the server STAMPS that flag on the sessions it serves
  // (it means "this chat has a live run right now"), so echoing it back on the next
  // save wrote a live-only fact into stored history and the chat rail then showed a
  // permanent "running" dot on a finished chat. The server re-derives it per read.
  // `partial` chats are the lazy-loaded stubs whose full transcript was never
  // pulled in — excluding them keeps a stripped copy from overwriting the stored
  // full transcript (the server union-merge preserves what the client omits).
  // The dirty filter is the same idea one step further: a chat we only ever READ
  // from the server is already stored exactly as we have it, so uploading it
  // again is pure cost (see dirtyRef).
  const stripSessions=(arr)=>arr.map(({runId,partial,msgCount,running,...s})=>s);
  const stripForSave=(arr)=>stripSessions(arr.filter(s=>!s.partial&&dirtyRef.current.has(s.id)));
  // A save that carries deletions ALWAYS ships the full set of loaded chats, not
  // just the dirty ones. Every route we post to unions today, so absence is not a
  // delete — but that has been broken before (an old cached shim.js routed this
  // POST to /api/chats, which replaced the stored array, and a delete posting an
  // empty list emptied whole projects; see CHANGELOG 2026-08-16). A delete is the
  // one save where being wrong is unrecoverable, so it never relies on merge
  // semantics holding. Ordinary saves keep the dirty-only optimisation.
  const postHistory=(toSave)=>{
    const deleting=deletedIdsRef.current.length>0;
    if(!toSave.length&&!deleting)return;                        // nothing local to report
    const sessions=deleting?stripSessions(sessionsRef.current.filter(s=>!s.partial)):toSave;
    fetch("/api/claude/history",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,sessions,deletedIds:deletedIdsRef.current})}).catch(()=>{});
  };
  // Write the current chats to the server right now (not just on the debounce) —
  // used the moment a turn starts, so a fast project switch can't lose the user's
  // message before the server-side run needs it for fallback persistence.
  function saveNow(){
    if(loadedSlug.current!==p.slug)return;
    postHistory(stripForSave(sessionsRef.current));
  }
  // Persist chats (debounced). Only saves once this project's history has loaded,
  // so switching projects can't write the previous project's chats to this one.
  useEffect(()=>{
    if(!loaded||loadedSlug.current!==p.slug)return;
    if(saveTimer.current)clearTimeout(saveTimer.current);
    const toSave=stripForSave(sessions);
    if(!toSave.length&&!deletedIdsRef.current.length)return;
    saveTimer.current=setTimeout(()=>{postHistory(toSave);},600);
    return()=>{if(saveTimer.current)clearTimeout(saveTimer.current);};
  },[sessions,loaded,p.slug]);

  // Auto-scroll only while the user is "stuck" to the bottom. The moment they
  // scroll up themselves we stop following new messages, and we re-attach as
  // soon as they scroll back down to the bottom.
  const stickRef=useRef(true);
  const refocusRef=useRef(null);
  const autoScrollAtRef=useRef(0);
  useEffect(()=>{
    const el=logRef.current;if(!el)return;
    const onScroll=()=>{
      const e=logRef.current;if(!e)return;
      stickRef.current=(e.scrollHeight-e.scrollTop-e.clientHeight)<80;
      // Keep the keyboard on the composer: after the user finishes scrolling the
      // chat log, always put focus back on the textarea so they can just type.
      // Only for scrolls the user made: auto-follow of streaming output fires
      // scroll events too, and refocusing on those yanked focus to the chat on
      // every new message (stealing it from other fields, popping the mobile keyboard).
      if(Date.now()-autoScrollAtRef.current<300)return;
      clearTimeout(refocusRef.current);
      refocusRef.current=setTimeout(()=>{
        // Never pull focus out of something else the user is working in.
        const a=document.activeElement;if(a&&a!==document.body&&!el.contains(a)&&a!==taRef.current)return;
        // Don't steal focus back to the composer while the user is selecting
        // text in the log — focusing the textarea collapses their selection and
        // makes it impossible to scroll-then-copy a message.
        try{const sel=window.getSelection();if(sel&&!sel.isCollapsed&&el.contains(sel.anchorNode))return;}catch(_){}
        const t=taRef.current;if(!t)return;
        t.focus({preventScroll:true});
        const n=t.value.length;try{t.setSelectionRange(n,n);}catch(_){}
      },220);
    };
    el.addEventListener("scroll",onScroll,{passive:true});
    return()=>{el.removeEventListener("scroll",onScroll);clearTimeout(refocusRef.current);};
  },[]);
  useEffect(()=>{const el=logRef.current;if(!el)return;if(stickRef.current){autoScrollAtRef.current=Date.now();el.scrollTop=el.scrollHeight;}},[msgs,busy]);
  // On opening/switching a chat, jump straight to the latest message. Without
  // this, a tall chat opens scrolled to the top because the nearBottom guard
  // above never fires on first render (scrollTop starts at 0).
  useEffect(()=>{if(activeId==null)return;const el=logRef.current;if(!el)return;stickRef.current=true;const jump=()=>{const e=logRef.current;if(e){autoScrollAtRef.current=Date.now();e.scrollTop=e.scrollHeight;}};jump();requestAnimationFrame(jump);},[activeId]);
  useEffect(()=>{
    const t=setInterval(()=>{
      const now=Date.now();
      commitOps(prev=>{
        let changed=false;
        const next={...prev};
        for(const [id,op] of Object.entries(prev)){
          if(!op||op.state==="completed"||op.state==="failed"||op.state==="stuck")continue;
          if(runningRef.current.indexOf(id)>=0&&(now-(op.lastEventAt||op.startedAt||now))>CHAT_STUCK_MS){
            next[id]={...op,state:"stuck",detail:"No heartbeat or output has arrived for over a minute. The socket may be disconnected or the process may be silent."};
            changed=true;
          }
        }
        return changed?next:prev;
      });
    },5000);
    return()=>clearInterval(t);
  },[]);
  useEffect(()=>{
    const el=taRef.current;if(!el)return;
    el.style.height="auto";
    const cs=getComputedStyle(el);
    const min=parseFloat(cs.minHeight)||40;
    const max=parseFloat(cs.maxHeight)||160;
    const h=Math.min(max,Math.max(min,el.scrollHeight));
    el.style.height=h+"px";
    el.style.overflowY=el.scrollHeight>max?"auto":"hidden";
  },[input]);
  useEffect(()=>{
    if(!attachMenuOpen)return;
    const onDown=e=>{if(attachMenuRef.current&&!attachMenuRef.current.contains(e.target))setAttachMenuOpen(false);};
    const onKey=e=>{if(e.key==="Escape")setAttachMenuOpen(false);};
    document.addEventListener("mousedown",onDown);
    document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDown);document.removeEventListener("keydown",onKey);};
  },[attachMenuOpen]);
  useEffect(()=>{
    if(!tabMenu)return;
    const onDown=e=>{if(tabMenuRef.current&&!tabMenuRef.current.contains(e.target))setTabMenu(null);};
    const onKey=e=>{if(e.key==="Escape")setTabMenu(null);};
    document.addEventListener("mousedown",onDown);
    document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDown);document.removeEventListener("keydown",onKey);};
  },[tabMenu]);
  useEffect(()=>{
    if(!histCtx)return;
    const onDown=e=>{if(histCtxRef.current&&!histCtxRef.current.contains(e.target))setHistCtx(null);};
    const onKey=e=>{if(e.key==="Escape")setHistCtx(null);};
    document.addEventListener("mousedown",onDown);
    document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDown);document.removeEventListener("keydown",onKey);};
  },[histCtx]);
  useEffect(()=>{
    if(!showHist)return;
    const onDown=e=>{if(historyMenuRef.current&&!historyMenuRef.current.contains(e.target))setShowHist(false);};
    document.addEventListener("mousedown",onDown);
    return()=>document.removeEventListener("mousedown",onDown);
  },[showHist]);
  // Keep the history dropdown fully on-screen: measure the trigger and place the
  // menu as a viewport-clamped fixed element instead of an absolutely anchored,
  // fixed-width popover (which spills off the near edge when the button sits close
  // to a viewport margin, e.g. in RTL / narrow chat docks).
  React.useLayoutEffect(()=>{
    if(!showHist){setHistPos(null);return;}
    const place=()=>{
      // The menu must be position:fixed so it escapes the chat header's
      // overflow:hidden (styles.css .dock-head) — otherwise it gets clipped and
      // is invisible. This applies on phones too, where an absolutely-anchored
      // menu inside the clipped header couldn't be seen at all.
      const b=histBtnRef.current;if(!b)return;
      const r=b.getBoundingClientRect();
      // Phones: pin the fixed menu to BOTH viewport edges (left:10, right:10) so
      // it can never run past a border regardless of button position or width.
      if(window.innerWidth<=760){setHistPos({phone:true,top:r.bottom+6});return;}
      const w=Math.min(440,window.innerWidth-20);
      const rtl=(document.dir==="rtl")||(document.documentElement&&document.documentElement.dir==="rtl");
      let left=rtl?r.right-w:r.left; // align to the button's inline-start edge
      left=Math.max(10,Math.min(left,window.innerWidth-w-10));
      setHistPos({left,top:r.bottom+6,width:w});
    };
    place();
    window.addEventListener("resize",place);window.addEventListener("scroll",place,true);
    return()=>{window.removeEventListener("resize",place);window.removeEventListener("scroll",place,true);};
  },[showHist]);
  useEffect(()=>{try{localStorage.setItem("hub.aiProvider",provider);localStorage.setItem("hub."+provider+"Model",model);localStorage.setItem("hub."+provider+"ExecutionMode",executionMode);localStorage.setItem("hub."+provider+"Effort",effort);}catch{}},[provider,model,executionMode,effort]);
  // Close the agent-controls popover on outside click / Escape.
  useEffect(()=>{
    if(!modeMenuOpen)return;
    const onDown=e=>{if(modeMenuRef.current&&!modeMenuRef.current.contains(e.target))setModeMenuOpen(false);};
    const onKey=e=>{if(e.key==="Escape")setModeMenuOpen(false);};
    document.addEventListener("mousedown",onDown);
    document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDown);document.removeEventListener("keydown",onKey);};
  },[modeMenuOpen]);
  useEffect(()=>{
    const s=sessionsRef.current.find(x=>x.id===activeId);
    if(!s)return;
    setProvider(s.provider||"claude");setModel(s.model||"");
  },[activeId]);
  useEffect(()=>{try{localStorage.setItem("hub.chatFontScale",String(fontScale));}catch{}},[fontScale]);
  // Is this browser tab in the foreground? Half of the "has this chat been seen?"
  // test (the other half is paneVisible — the dock actually showing the chat pane).
  const[docVisible,setDocVisible]=useState(()=>{try{return document.visibilityState!=="hidden";}catch{return true;}});
  useEffect(()=>{
    const onVis=()=>setDocVisible(document.visibilityState!=="hidden");
    document.addEventListener("visibilitychange",onVis);
    return()=>document.removeEventListener("visibilitychange",onVis);
  },[]);
  // Actually SEEING a chat marks it seen — clear its persisted "finished, unseen"
  // flag so every indicator (sidebar badge, History dot, tab dot, Home Last Chats)
  // drops. "Seen" means the conversation is genuinely in front of you: it is the
  // active one AND its pane is on screen (dock open on the Chat tab, the chat view
  // on a phone) AND the browser tab itself is visible. Being the active chat is not
  // enough on its own — a run finishing while you're on the Terminal tab, on Home,
  // or in another browser tab has not been seen, and its green light has to stay up
  // until you come back. Keyed on the session's OWN flag and on visibility, so a
  // chat flagged while hidden clears the moment its pane is shown again.
  const chatOnScreen=paneVisible!==false&&docVisible;
  const activeUnseen=sessions.find(s=>s.id===activeId&&(s.doneUnseen||s.failedUnseen));
  // The App's in-memory "run ended here" overlay (localChatDone) wins over session
  // flags in the rail, and only markChatSeen downgrades it. Sessions reloaded from a
  // store whose copy was already cleared (persistChatRun, /seen, another device,
  // project switch) carry no flag, so key "unseen" off the overlay too - otherwise
  // the rail dot stays green until a full reload. markChatSeen flips it to "seen",
  // which no longer matches, so this cannot loop.
  const activeLocalDone=activeId!=null&&localDone?localDone[activeId]:null,activeLocalUnseen=activeLocalDone==="done"||activeLocalDone==="failed"?activeLocalDone:null;
  useEffect(()=>{if(activeId==null||!(activeUnseen||activeLocalUnseen)||!chatOnScreen)return;const kind=(activeUnseen&&activeUnseen.failedUnseen)||activeLocalUnseen==="failed"?"failed":"done";commitSessions(prev=>prev.map(s=>s.id===activeId?{...s,doneUnseen:false,failedUnseen:false}:s));onChatSeen&&onChatSeen(p.slug,activeId,kind);},[activeId,!!activeUnseen,activeLocalUnseen,chatOnScreen]);
  // clamp the chat font scale to a sensible range and round to avoid drift
  const bumpFont=d=>setFontScale(s=>Math.min(1.6,Math.max(0.8,Math.round((s+d)*100)/100)));
  // Load the available skills once so the chat input can autocomplete them on "/".
  useEffect(()=>{
    if(SKILLS_CACHE){setSkills(SKILLS_CACHE);return;}
    let live=true;
    fetch("/api/claude/skills").then(r=>r.json()).then(d=>{
      if(live&&d&&d.ok&&Array.isArray(d.skills)){SKILLS_CACHE=d.skills;setSkills(d.skills);}
    }).catch(()=>{});
    return()=>{live=false;};
  },[]);

  // An "Ask Claude" button (a tip card or a host-health popover) pushes a ready-made
  // prompt here. Each one opens in a FRESH chat session so it never lands in the
  // middle of an existing conversation. Wait for history to load, then send — a
  // fresh chat is always idle, so other chats already working don't block it.
  useEffect(()=>{
    if(!ask||!ask.nonce||ask.nonce===lastAsk.current||!loaded)return;
    if(ask.slug&&ask.slug!==p.slug)return;
    lastAsk.current=ask.nonce;
    const id=sendPrompt(ask.prompt,{fresh:true,title:ask.title,model:ask.model,provider:ask.provider,target:ask.target});
    // remember which tip this fresh run belongs to so its `result` event can nudge the card
    pendingTipRef.current=(id&&ask.tipId)?{chatId:id,tipId:ask.tipId,slug:ask.slug}:null;
    // the run actually launched → tell the card to move from "open" to "in progress"
    // and hand it this chat's id, so the card can link straight back to this thread.
    if(id&&ask.tipId)onTipStart&&onTipStart(ask.slug,ask.tipId,id);
  },[ask,loaded,p.slug]);

  // A task card's "Open chat" link asks to jump to a specific past conversation.
  // Wait for THIS project's own history to actually be loaded (loadedSlug.current,
  // not the `loaded` flag — that flag can still read stale/true from the project we
  // just switched away from, which would consume the nonce against the wrong
  // project's session list and silently drop the request), then jump to that chat.
  useEffect(()=>{
    if(loadedSlug.current!==p.slug)return;
    jumpToRequestedChat(sessionsRef.current);
  },[openChat,loaded,p.slug]);

  // "Open AI chat" on a task card: turn the saved autonomous-run transcript into a
  // real chat session (seeded with what the automation wrote and ran) and open it,
  // so the user can keep chatting from where the automation left off. The session's
  // recorded sessionId lets the next turn resume the same conversation with context.
  useEffect(()=>{
    if(!openRun||!openRun.nonce||openRun.nonce===lastOpenRun.current)return;
    if(openRun.slug&&openRun.slug!==p.slug)return;
    if(loadedSlug.current!==p.slug||!loaded)return;
    lastOpenRun.current=openRun.nonce;
    const chatId="airun_"+openRun.taskId;
    // Already materialised? Just open it.
    if(sessionsRef.current.some(s=>s.id===chatId)){openSession(chatId);return;}
    let live=true;
    fetch("/api/tips/run?slug="+encodeURIComponent(openRun.slug)+"&id="+encodeURIComponent(openRun.taskId))
      .then(r=>r.json()).then(d=>{
        if(!live)return;
        const run=d&&d.ok&&d.run;
        const parts=(run&&run.parts)||[];
        const now=Date.now();
        const seedUser="This is an automated run that completed the task: "+(openRun.title||"")+(openRun.detail?("\n\n"+openRun.detail):"");
        const session={
          id:chatId,fromTask:true,taskId:openRun.taskId,title:openRun.title||"AI run",provider:"claude",model:"",lang:null,
          createdAt:(run&&run.startedAt?Date.parse(run.startedAt)||now:now),updatedAt:now,
          sessionId:(run&&run.sessionId)||null,
          messages:[
            {role:"user",text:seedUser,ts:now},
            {role:"assistant",parts:parts.length?parts:[{type:"text",text:"(The automation produced no visible output for this run.)"}],ts:now},
          ],
        };
        commitSessions(prev=>[session,...prev.filter(s=>s.id!==chatId)]);
        openSession(chatId);
        // Link the task to this chat so its card's "Open chat" button lands here too.
        fetch("/api/tips/chat",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({slug:openRun.slug,id:openRun.taskId,chatId})}).catch(()=>{});
      }).catch(()=>{if(live)lastOpenRun.current=0;});
    return()=>{live=false;};
  },[openRun,loaded,p.slug]);

  // Home can request a blank Claude/Codex chat without sending a prompt. Keep the
  // provider-specific session creation here so it follows the same reuse rules as
  // the dock's own New chat button.
  useEffect(()=>{
    if(!newChatReq||!newChatReq.nonce||newChatReq.nonce===lastNewChat.current||!loaded)return;
    if(newChatReq.slug&&newChatReq.slug!==p.slug)return;
    lastNewChat.current=newChatReq.nonce;
    const next=["claude","codex"].includes(newChatReq.provider)?newChatReq.provider:"claude";
    stashActiveDraft(); // keep the outgoing chat's unsent text before jumping away
    const id=freshSession(next);
    const saved=(()=>{try{return localStorage.getItem("hub."+next+"Model")||defaultModelFor(next);}catch{return defaultModelFor(next);}})();
    patchSession(id,s=>({...s,provider:next,model:s.model||saved||"",sessionId:null}));
    setProvider(next);setModel(saved);setErr("");restoreDraft(id);setShowHist(false);setAttach([]);setEditingLast(false);setEditVal("");
    onActivateChat&&onActivateChat();
  },[newChatReq,loaded,p.slug]);

  // Ensure an active session exists to receive a message; returns its id.
  function ensureActive(){
    const cur=activeIdRef.current;
    if(cur&&sessionsRef.current.some(s=>s.id===cur))return cur;
    const s=newSession(provider);
    commitSessions(prev=>[s,...prev]);setActiveId(s.id);
    return s.id;
  }
  // Default landing view: open a fresh blank chat (reusing an existing empty draft
  // if one is already loaded) instead of reopening the most recent conversation.
  // `loadedList` is the just-loaded session array so we don't race the state setter.
  function openFreshDefault(loadedList){
    const list=loadedList||sessionsRef.current;
    const blank=list.find(s=>s.messages.length===0&&(s.provider||"claude")===provider);
    if(blank){setActiveId(blank.id);return;}
    const s=newSession(provider);
    commitSessions(prev=>[s,...prev]);setActiveId(s.id);
  }
  // Start a fresh chat session (reusing an existing blank one) and make it active;
  // returns its id. Lets each "Ask Claude" prompt open in its own new chat.
  function freshSession(nextProvider=provider){
    const blank=sessionsRef.current.find(s=>s.messages.length===0&&(s.provider||"claude")===nextProvider);
    if(blank){setActiveId(blank.id);return blank.id;}
    const s=newSession(nextProvider);
    commitSessions(prev=>[s,...prev]);setActiveId(s.id);
    return s.id;
  }
  function chooseProvider(next){
    if(next===provider)return;
    const saved=(()=>{try{return localStorage.getItem("hub."+next+"Model")||"";}catch{return"";}})();
    const savedMode=(()=>{try{return localStorage.getItem("hub."+next+"ExecutionMode")==="plan"?"plan":"auto";}catch{return"auto";}})();
    setProvider(next);setModel(saved);setExecutionMode(savedMode);setErr("");setAttach([]);
    // Picking a different provider must not eat what you typed: stash it against
    // the chat we're leaving, then restore whatever the chat we land on had.
    // When we stay on the same (blank) chat, this restores the text unchanged.
    stashActiveDraft();
    const cur=sessionsRef.current.find(s=>s.id===activeIdRef.current);
    if(cur&&cur.messages.length){
      const blank=sessionsRef.current.find(s=>s.messages.length===0&&(s.provider||"claude")===next);
      let toId;
      if(blank){toId=blank.id;setActiveId(blank.id);}
      else{const s=newSession(next);toId=s.id;commitSessions(prev=>[s,...prev]);setActiveId(s.id);}
      restoreDraft(toId);
    }else{
      if(cur)patchSession(cur.id,s=>({...s,provider:next,model:"",sessionId:null}));
      restoreDraft(activeIdRef.current);
    }
  }
  function chooseModel(next){
    setModel(next);
    const cur=sessionsRef.current.find(s=>s.id===activeIdRef.current);
    if(cur&&((cur.provider||"claude")===provider))patchSession(cur.id,s=>({...s,model:next}));
  }
  function chooseExecutionMode(next){
    const mode=safeMode(next);
    setExecutionMode(mode);
    const cur=sessionsRef.current.find(s=>s.id===activeIdRef.current);
    if(cur&&((cur.provider||"claude")===provider))patchSession(cur.id,s=>({...s,executionMode:mode}));
  }
  function chooseEffort(next){
    const eff=safeEffortClient(next);
    setEffort(eff);
    const cur=sessionsRef.current.find(s=>s.id===activeIdRef.current);
    if(cur&&((cur.provider||"claude")===provider))patchSession(cur.id,s=>({...s,effort:eff}));
  }
  // opts.fromServer marks a patch that merely writes back what the server just
  // sent us (transcript hydration), so it does not make the chat dirty.
  function patchSession(id,fn,opts){
    if(!opts||!opts.fromServer)dirtyRef.current.add(id);
    commitSessions(prev=>prev.map(s=>s.id===id?fn(s):s),{dirty:"none"});
  }

  // ---- last-real-failure-reason bookkeeping -------------------------------
  // A server-side failure ({type:"hub",event:"error"/"stderr"/"done" with a
  // non-zero code) used to only touch the op banner; the interrupted bubble then
  // overwrote the detail with a generic "stream closed" line and the actual cause
  // was lost. Record it per chat so markInterrupted can show the real thing.
  const STDERR_KEEP=6;
  function noteFailReason(id,patch){const m=failReasonRef.current;m.set(id,{...(m.get(id)||{}),...patch});}
  function noteStderr(id,chunk){
    const lines=String(chunk||"").split(/\r?\n/).map(s=>s.replace(/\s+$/,"")).filter(s=>s.trim());
    if(!lines.length)return;
    const m=failReasonRef.current;const cur=m.get(id)||{};
    m.set(id,{...cur,stderr:(cur.stderr||[]).concat(lines).slice(-STDERR_KEEP)});
  }
  function clearFailReason(id){failReasonRef.current.delete(id);}
  function failReasonText(id){
    const r=failReasonRef.current.get(id);if(!r)return"";
    const out=[];
    if(r.error)out.push(String(r.error).trim());
    else if(r.exit)out.push(String(r.exit).trim());
    const se=(r.stderr||[]).join("\n").trim();
    if(se&&out.indexOf(se)<0)out.push(se);
    return out.filter(Boolean).join("\n").trim();
  }
  function handle(id,ev){
    if(ev.type==="hub"&&ev.event==="stderr")noteStderr(id,ev.text||ev.data||"");
    // Real output vs our own heartbeat noise: pings and the stall notices are
    // server-generated, so only every OTHER event refreshes lastOutputAt. Real
    // output also clears a shown stall notice locally, without waiting for the
    // server's one-shot stall-clear to arrive.
    const synthetic=ev.type==="hub"&&(ev.event==="ping"||ev.event==="stall"||ev.event==="stall-clear");
    if(!synthetic){const cur=opFor(id)||{};setOp(id,cur.stalled?{lastOutputAt:Date.now(),stalled:false,silentForMs:null}:{lastOutputAt:Date.now()});}
    if(ev.type==="hub"){
      if(ev.event==="start"){if(!ev._replay)clearFailReason(id);setOp(id,{runId:ev.runId,state:"processing",provider:ev.provider||provider,detail:"Run started.",startedAt:Date.now()});}
      else if(ev.event==="ping")setOp(id,{state:(opFor(id)&&opFor(id).state)||"processing"});
      // The server noticed the run produced no real output for a while (its
      // heartbeat keeps flowing, so this is a status notice, NOT an error and
      // NOT the end of the run - long tool calls look exactly like this).
      else if(ev.event==="stall")setOp(id,{stalled:true,silentForMs:ev.silentForMs||0});
      else if(ev.event==="stall-clear")setOp(id,{stalled:false,silentForMs:null});
      // Background agents/tasks still running after the turn's result: the run is
      // NOT finished (the server keeps it open), so say what it is waiting on.
      else if(ev.event==="background"){const n=ev.count||0;setOp(id,n>0?{state:"processing",bgCount:n,detail:bgWaitText(n)}:{bgCount:0});}
      else if(ev.event==="waiting"||ev.event==="paused"){
        // reason "agent-offline" is a token from the cloud shim (the paired Mac
        // agent is unreachable), not display text - map it to a real sentence.
        const off=ev.reason==="agent-offline";
        setOp(id,{state:"waiting",agentOffline:off,detail:off?"Agent offline - waiting for it to reconnect...":(ev.reason||"Waiting for user confirmation or manual input.")});
      }
      else if(ev.event==="error"){const em=String(ev.error||ev.message||ev.text||"").trim();if(em)noteFailReason(id,{error:em});noteRunEnded(id,"failed");setOp(id,{state:"failed",detail:failReasonText(id)||em||"Run error."});}
      else if(ev.event==="gone"){noteRunEnded(id,"stuck");setOp(id,{state:"stuck",detail:"The server no longer has this run. It may have expired after a disconnect or restart."});}
      // Terminal marker from a RESUMED stream: the durable log replayed the tail,
      // but the process itself died with the previous server. Nothing to wait
      // for: stop reconnecting and show the retry banner.
      else if(ev.event==="interrupted"){
        const why=interruptReasonText(ev.reason);
        noteFailReason(id,{error:why});
        noteRunEnded(id,"stuck");
        setOp(id,{state:"stuck",endedAt:Date.now(),detail:why});
      }
      else if(ev.event==="done"){
        if(ev.code===0)clearFailReason(id);
        else noteFailReason(id,{exit:"Process exited with code "+ev.code+"."});
        noteRunEnded(id,ev.code===0?"completed":"failed");
        setOp(id,{state:ev.code===0?"completed":"failed",endedAt:Date.now(),detail:ev.code===0?"Run completed.":(failReasonText(id)||("Process exited with code "+ev.code+"."))});
        // A finished run raises its "ready" light RIGHT HERE, on every chat — the
        // one on screen included. Persisted via doneUnseen so the indicator
        // survives a reload and shows up on the sidebar badge + Home Last Chats
        // too; taking it down is the seen-effect's job, and it only does that once
        // the chat is really in front of you. Set on `done` (END OF RUN), never on
        // a per-turn `result`, so a chat with a queued follow-up turn is not
        // flagged "finished" while still working.
        const manuallyStopped=manuallyStoppedRef.current.delete(id);
        if(!manuallyStopped){
          patchSession(id,s=>({...s,doneUnseen:ev.code===0,failedUnseen:ev.code!==0}));
          // Drop the live/orange state now instead of when the stream socket
          // unwinds, and hand the finished state to the rail + Home lists, which
          // read the server's copy and would otherwise stay orange until the
          // debounced save round-tripped. Push that save immediately too.
          if(onChatRunning)onChatRunning(p.slug,id,false);
          if(onChatDone)onChatDone(p.slug,id,ev.code===0?"done":"failed");
          if(!ev._replay)saveNow();
          // Let the final text part settle into sessionsRef before reading it.
          if(ev.code===0&&!ev._replay)setTimeout(()=>inlineFollowupOrRequest(id),250);
          else inlineFollowupRef.current.delete(id);
        }
        // audible "Claude is done" cue on end of run (task-card fix or any other
        // chat turn — both use the single "Task finished" sound). A failed run
        // plays the fixed error buzz. Deduped against the Web Push echo of the same
        // finish (and against a replayed buffer on reconnect, skipped via _replay).
        if(!ev._replay)fireCue(id,"runFinished",ev.code===0,(p&&(p.nameHe||p.slug))||"");
        // the "mark this task solved?" nudge belongs to the END OF THE RUN, not the
        // end of a turn — a multi-turn chat kept firing it while still working.
        const dpt=pendingTipRef.current;
        if(dpt&&dpt.chatId===id&&!ev._replay){pendingTipRef.current=null;if(ev.code===0)onTipDone&&onTipDone(dpt.slug,dpt.tipId);}
        // Any messages typed while this run worked are sent now as one new turn.
        if(!ev._replay&&!manuallyStopped)flushQueued(id);}
      // The model paused to ask us something (AskUserQuestion popup) or to get
      // approval for a tool (permission prompt). Surface it as a modal; the run
      // stays parked server-side until we POST an answer.
      else if(ev.event==="ask"&&ev.ask){setOp(id,{state:"waiting",detail:ev.ask.kind==="question"?"Waiting for your answer.":"Waiting for your approval."});setPendingAsk(p=>({...p,[id]:ev.ask}));
        // audible "needs you" cue; if the user opted into repeat, replay it every
        // 60s until answered. Name the project when the spoken cue is chosen.
        const plabel=(p&&(p.nameHe||p.slug))||"";
        if(!ev._replay)fireCue(id,"needsApproval",true,plabel);
        if(loadSoundPrefs().needsApprovalRepeat!==false)startRepeat("needsApproval",id,plabel);}
      else if(ev.event==="ask_resolved"){stopRepeat(id);setPendingAsk(p=>{const cur=p[id];if(!cur||cur.id!==ev.id)return p;const n={...p};delete n[id];return n;});}
    }
    // A run that ends (done/gone/error) can't answer a parked prompt anymore.
    if(ev.type==="hub"&&(ev.event==="done"||ev.event==="gone"||ev.event==="interrupted"||ev.event==="error")){stopRepeat(id);setPendingAsk(p=>{if(!p[id])return p;const n={...p};delete n[id];return n;});}
    if(ev.type==="hub"&&ev.event==="start"&&ev.dir)setDir(ev.dir);
    // Persist the actual provider reported by the run (always matches what was
    // requested — the server no longer auto-switches providers by usage).
    if(ev.type==="hub"&&ev.event==="start"&&ev.provider)patchSession(id,s=>({...s,provider:ev.provider}));
    // remember the server-side run id so Stop can target it and a switch-away can resume it
    if(ev.type==="hub"&&ev.event==="start"&&ev.runId)patchSession(id,s=>({...s,runId:ev.runId}));
    if(ev.type==="hub"&&(ev.event==="done"||ev.event==="gone"||ev.event==="interrupted"))patchSession(id,s=>({...s,runId:null}));
    if(ev.type==="system"&&ev.subtype==="init"&&ev.session_id)patchSession(id,s=>({...s,sessionId:ev.session_id}));
    if(ev.type==="assistant"&&ev.message&&Array.isArray(ev.message.content)){
      setOp(id,{state:"streaming",detail:"Streaming response."});
      const blocks=ev.message.content;
      // each assistant message reports its prompt size in usage — the latest one
      // is the live context-window fill for the conversation.
      const ctx=ev.message.usage?turnTokens(ev.message.usage).in:null;
      patchSession(id,s=>{const c=s.messages.slice();const a=c[c.length-1];if(!a||a.role!=="assistant")return s;const parts=a.parts.slice();
        for(const b of blocks){
          // ev.delta marks an incremental streaming chunk (Gemini): append to the
          // current text part instead of pushing a new one, so a single reply
          // renders as one continuous bubble rather than fragmented pieces.
          if(b.type==="text"&&b.text){const last=parts[parts.length-1];
            if(ev.delta&&last&&last.type==="text")parts[parts.length-1]={type:"text",text:last.text+b.text};
            else parts.push({type:"text",text:b.text});}
          else if(b.type==="tool_use"){
            // The Skill tool's input is {skill, args} — none of toolDesc's keys match, so
            // it would fall through to JSON.stringify and render a raw {"skill":"…"} blob.
            // Carry the id and set desc explicitly so that path is never reached.
            const pt={type:"tool",name:b.name,desc:toolDesc(b.input)};
            if(b.name==="Skill"&&b.input&&b.input.skill){
              pt.skill=String(b.input.skill);
              pt.desc=b.input.args?String(b.input.args):"";
            }
            parts.push(pt);
            // "Using skill" cue — same Notifications settings as the other events, toggled per-row.
            if(b.name==="Skill"&&!ev._replay)playEventSound("skillUsed",true,(b.input&&b.input.skill)||"");}
        }
        c[c.length-1]={...a,parts};return{...s,messages:c,updatedAt:Date.now(),ctx:ctx!=null?ctx:s.ctx};});
    }
    if(ev.type==="result"){
      // `result` ends a TURN, not the run. Marking it "completed" here froze the
      // chat as done while it kept working (and skipped the stuck-watchdog).
      // The run is only completed on hub done.
      // Background agents/tasks still live when the turn's result lands: the run is
      // not done and not waiting on the user either, so keep it "processing".
      const bgLive=!ev.is_error&&((opFor(id)||{}).bgCount||0);
      setOp(id,bgLive?{state:"processing",detail:bgWaitText(bgLive)}:{state:ev.is_error?"failed":"waiting",detail:ev.is_error?(typeof ev.result==="string"?ev.result:"Run ended with an error."):"Turn finished."});
      if(ev.session_id)patchSession(id,s=>({...s,sessionId:ev.session_id}));
      const tk=turnTokens(ev.usage);
      // The turn is over: strip the inline follow-up marker from the STORED parts
      // (so copy/export/history never see it) and keep the suggestion aside. Only
      // a live finish arms it as ghost text; a replayed buffer just gets cleaned.
      if(!ev._replay)inlineFollowupRef.current.delete(id);
      patchSession(id,s=>{const c=s.messages.slice();const a0=c[c.length-1];if(!a0||a0.role!=="assistant")return s;
        const cl=stripFollowupFromMessage({...a0,meta:{cost:ev.total_cost_usd,turns:ev.num_turns,ms:ev.duration_ms,error:ev.is_error,tin:tk&&tk.in,tout:tk&&tk.out,resultText:typeof ev.result==="string"?ev.result:null}});
        const a=cl.message;
        if(!ev._replay&&!ev.is_error&&cl.suggestion)inlineFollowupRef.current.set(id,cl.suggestion);
        c[c.length-1]={...a,ts:Date.now()};
        // NOTE: do NOT set ctx from ev.usage here — the result usage is cumulative
        // across every internal agentic step (cache_read re-counts the same context
        // each round-trip), so it balloons past the window. The live context-window
        // fill is the last single assistant request's input, set on the assistant event.
        return{...s,messages:c};});
      // NOTE: the "finished, unseen" badge (doneUnseen) AND the "run finished"
      // sound/notification both moved to the hub `done` handler — like the "mark
      // this task solved?" nudge, they mark END OF RUN, not end of a turn. A
      // multi-turn run (a message queued mid-turn via sendLive) emits a `result`
      // per turn; firing them here flagged the chat "finished / ready" and rang
      // the alert while it was still working on the queued turn.
    }
    // only surface a chat's error banner while that chat is on screen — a background
    // chat's error shouldn't overwrite the panel error for the one you're viewing.
    if(ev.type==="hub"&&ev.event==="error"&&id===activeIdRef.current)setErr(ev.error||"error");
  }

  // Answer (or dismiss) an interactive prompt: POST to the run so its parked turn
  // resumes, and clear the popup optimistically. `answer` shape matches the
  // server: {answers:{…}} for a question, {allow:bool,message?} for a permission.
  function answerAsk(chatId,ask,answer){
    stopRepeat(chatId);
    setPendingAsk(p=>{const n={...p};delete n[chatId];return n;});
    setOp(chatId,{state:"processing",detail:"Sent — Claude is continuing."});
    fetch("/api/claude/answer",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({runId:ask.runId,id:ask.id,answer})}).catch(()=>{});
    // A task chat exists only to get your approval. Once you approve, close its tab
    // (it stays in History) so the strip clears itself of finished task work.
    if(answer&&answer.allow===true&&isFromTaskChat(sessionsRef.current.find(s=>s.id===chatId)))closeTab(chatId);
  }

  // Deliver `text` into chat `id`'s ALREADY-RUNNING turn, so a live run stays a
  // conversation instead of a read-only transcript. The server decides what that
  // means: answer a parked approval (the agent is blocked in canUseTool and can't
  // read a queued message), or queue for the next turn boundary.
  //
  // Appends the user bubble AND a fresh assistant bubble: every stream handler
  // appends to the LAST message and bails unless it's an assistant one, so
  // leaving a user bubble last would silently swallow the rest of the run.
  function sendLive(id,text){
    const cur=sessionsRef.current.find(s=>s.id===id);
    const runId=cur&&cur.runId;
    if(!runId)return false;
    const bubbles=[{role:"user",text,ts:Date.now()},{role:"assistant",parts:[],ts:Date.now()}];
    patchSession(id,s=>({...s,updatedAt:Date.now(),messages:[...s.messages,...bubbles]}));
    setOp(id,{state:"processing",detail:"Sent — "+agentName+" is continuing."});
    // The run closed between the keystroke and the POST, OR the network dropped —
    // either way undo the optimistic bubbles and hand the text back so the turn
    // never silently wedges as "…is continuing" while the agent got nothing.
    const recover=(msg)=>{
      patchSession(id,s=>({...s,messages:s.messages.filter(m=>bubbles.indexOf(m)<0)}));
      setInput(prev=>prev||text);
      setOp(id,null);
      setErr(msg);
    };
    fetch("/api/claude/message",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({runId,text})})
      .then(r=>r.json().catch(()=>({})).then(j=>({ok:r.ok,j:j||{}})))
      .then(({ok,j})=>{
        if(ok&&j.ok)return;
        recover("That run just finished — press Send to start a new turn.");
      })
      .catch(()=>recover("Couldn't reach the server — your message wasn't sent. Press Send to try again."));
    return true;
  }
  // Pairs the user has told us to stop warning about, as "fromSlug>toSlug".
  function loadIgnorePairs(){
    try{const v=JSON.parse(localStorage.getItem(IGNORE_KEY)||"[]");return Array.isArray(v)?v:[];}catch{return[];}
  }
  function addIgnorePair(fromSlug,toSlug){
    try{
      const key=ignorePairKey(fromSlug,toSlug),list=loadIgnorePairs();
      if(list.indexOf(key)<0){list.push(key);localStorage.setItem(IGNORE_KEY,JSON.stringify(list));}
    }catch{}
  }
  // The three answers to the wrong-project modal. All of them clear the prompt
  // from the guard; {guarded:true} makes the re-send skip the check.
  function guardSendAnyway(alsoIgnore){
    const g=guard;setGuard(null);if(!g)return;
    if(alsoIgnore)addIgnorePair(p&&p.slug,g.match.slug);
    sendPrompt(g.raw,Object.assign({},g.opts,{guarded:true}));
  }
  function guardTransfer(){
    const g=guard;setGuard(null);if(!g)return;
    // Switch the hub to the suggested project, then open a FRESH chat there with
    // the same text. The dock reloads for the new project, so the send has to
    // wait a tick for that state to land.
    if(onSwitchProject)onSwitchProject(g.match.slug);
    setTimeout(()=>{try{setInput(g.raw);}catch{}},0);
  }
  // Append an inline system note to a session (used by runtime commands). A note
  // is display-only: it is never sent to the model and carries no parts.
  function pushNote(id,text){
    patchSession(id,s=>({...s,messages:[...s.messages,{role:"note",text:String(text),ts:Date.now()}],updatedAt:Date.now()}));
  }
  // Execute a client-side runtime command. Returns true when handled so the
  // caller skips the normal model dispatch. Only /compact dispatches a turn.
  function runRuntimeCommand(cmd,args){
    const id=activeIdRef.current;
    if(id==null)return false;
    const cur=sessionsRef.current.find(s=>s.id===id)||null;
    switch(cmd){
      case"context":{
        const r=contextReport(cur);
        pushNote(id,"Context: "+fmtTokens(r.used)+" / "+fmtTokens(r.window)+" tokens ("+r.pct+"% used, "+r.freePct+"% free).\nThis is the last request's context size; a category breakdown is not available in this chat.");
        return true;
      }
      case"cost":{
        const c=sessionCost(cur);
        pushNote(id, c>0 ? "Session cost so far: $"+c.toFixed(4)+"." : "No cost recorded yet for this session.");
        return true;
      }
      case"usage":{
        pushNote(id,"Fetching your Claude usage...");
        fetch("/api/claude/usage").then(r=>r.json()).then(j=>{
          const pct=j&&j.usage&&typeof j.usage.percent==="number"?j.usage.percent:(j&&typeof j.percent==="number"?j.percent:null);
          pushNote(id, pct!=null ? "Claude subscription usage: "+Math.round(pct)+"% of the current window used." : "Usage: "+JSON.stringify(j&&(j.usage||j)||{}));
        }).catch(()=>pushNote(id,"Could not read your Claude usage right now."));
        return true;
      }
      case"clear":{
        // Start a fresh conversation: drop the resumable sessionId and reset the
        // context readout (ctx:0). Visible history stays, marked by a divider
        // note; per-message costs persist, so the running cost is not reset.
        patchSession(id,s=>({...s,sessionId:null,ctx:0}));
        pushNote(id,"- new session started (previous context cleared) -");
        return true;
      }
      case"model":{
        const tiers=AI_MODELS.claude;
        if(args){
          const pick=tiers.find(m=>m.label.toLowerCase()===args.toLowerCase()||m.value.toLowerCase()===args.toLowerCase());
          if(pick){setModel(pick.value);patchSession(id,s=>({...s,model:pick.value}));pushNote(id,"Model switched to "+pick.label+".");}
          else pushNote(id,"Unknown model \""+args+"\". Options: "+tiers.map(m=>m.label).join(", ")+".");
        }else{
          const curLabel=(tiers.find(m=>m.value===(cur&&cur.model||model))||tiers[0]).label;
          pushNote(id,"Current model: "+curLabel+".\nSwitch with /model <tier>. Options: "+tiers.map(m=>m.label).join(", ")+".");
        }
        return true;
      }
      case"resume":{
        setShowHist(true);
        pushNote(id,"Opened chat history - pick a previous session to resume.");
        return true;
      }
      case"compact":{
        // Best-effort: a headless run has no native compaction, so we send ONE
        // real turn asking the model to summarize the conversation and continue
        // from that summary. This is the only runtime command that dispatches.
        pushNote(id,"Compacting - asking "+agentName+" to summarize the conversation so far...");
        runTurn(id,"Summarize our conversation so far into a concise running summary I can rely on going forward. Keep all decisions, file paths, and open tasks. Then continue helping from that summary.",provider,cur&&cur.model,executionMode,effort);
        return true;
      }
      default:
        return false;
    }
  }
  function sendPrompt(raw,opts){
    const prompt=String(raw==null?"":raw).trim();
    // only attachments that finished uploading (have an absolute path) can be sent
    const files=(attachRef.current||[]).filter(a=>a.path);
    if(!prompt&&!files.length)return;
    // Runtime commands (/context, /cost, ...) are client-side terminal-parity
    // commands: handle them here and never dispatch a model turn. Claude only -
    // other providers get no such commands and the text falls through.
    if(provider==="claude"&&!files.length){
      const rc=parseSlash(prompt);
      if(rc&&runRuntimeCommand(rc.cmd,rc.args)){setInput("");clearDraft(activeIdRef.current);return activeIdRef.current;}
    }
    // Sending is an explicit intent to follow the conversation again.
    stickRef.current=true;
    // Wrong-project guard. Only the OPENING message of a chat is checked — that
    // is where a project switch gets mixed up — and only against local project
    // metadata, so this costs nothing and never blocks on the network. Anything
    // that throws or is less than decisive falls through and sends normally.
    if(!(opts&&opts.guarded)){
      try{
        const curSess=sessionsRef.current.find(s=>s.id===activeIdRef.current);
        const firstTurn=!!(opts&&opts.fresh)||!curSess||curSess.messages.length===0;
        const hit=guardPrompt({prompt,isFirstTurn:firstTurn,projects,currentSlug:p&&p.slug,ignore:loadIgnorePairs()});
        if(hit){setGuard({match:hit,raw,opts:opts||null});return null;}
      }catch(e){/* guard is advisory only — never let it stop a send */}
    }
    // A normal send reuses the open chat. If THAT chat is already working: a reply
    // to a parked approval/question goes straight into the live run (it IS the
    // answer); otherwise the message is SAVED and sent as a new turn the moment the
    // current run finishes. A {fresh:true} send always opens a new, idle chat, so
    // other chats still running never block it.
    if(!(opts&&opts.fresh)&&isRunning(activeIdRef.current)){
      const rid=activeIdRef.current;
      // Attachments need a fresh turn to carry their paths — text only here.
      if(prompt){
        if(pendingAsk[rid]){if(!sendLive(rid,prompt))return rid;}
        else enqueueMsg(rid,prompt);
        setErr("");clearDraft(rid);setInput("");
      }
      return rid;
    }
    setErr("");clearDraft(activeIdRef.current);setInput("");setShowHist(false);setAttach([]);
    // {fresh:true} (from the "Ask Claude" buttons) opens a new chat; a normal send reuses the open one.
    const runProvider=(opts&&opts.provider)||provider;
    const runModel=opts&&opts.model!=null?opts.model:(runProvider===provider?model:(()=>{try{return localStorage.getItem("hub."+runProvider+"Model")||"";}catch{return"";}})());
    const id=(opts&&opts.fresh)?freshSession(runProvider):ensureActive();
    // give Claude the absolute paths so it can Read the files/images itself
    const fullPrompt=files.length
      ?"[Attached files — you MUST open each one with the Read tool before answering"+(files.some(f=>f.isImage)?"; the Read tool renders images as well, so use it to view every image":"")+"]\n"
        +files.map(f=>"- "+f.path+(f.isImage?" (image)":"")).join("\n")+"\n\n"+(prompt||"Please look at the attached file(s).")
      :prompt;
    const userFiles=files.map(f=>({name:f.name,isImage:f.isImage,url:f.url,path:f.path}));
    // a tip's "Ask Claude" passes the tip title so the chat is named after the tip,
    // not the long context-laden prompt; otherwise fall back to the prompt text.
    const titleOverride=opts&&opts.title?opts.title:null;
    const runExecutionMode=(opts&&opts.executionMode)||executionMode;
    const runEffort=(opts&&opts.effort)||effort;
    // This is the chat's opening message when the target session has no messages
    // yet — that's the only turn that names the chat, and the only one we ask the
    // AI to summarize into a short subject.
    const curBefore=sessionsRef.current.find(s=>s.id===id);
    const isFirstTurn=!curBefore||curBefore.messages.length===0;
    // opts.target ("server", from a host-health Ask Claude) sticks to the chat so
    // every later turn in it is routed to the same machine.
    patchSession(id,s=>({...s,...(opts&&opts.target?{target:opts.target}:{}),provider:runProvider,model:runModel,executionMode:runExecutionMode,effort:runEffort,title:s.messages.length===0?(titleOverride||prompt||(files[0]&&files[0].name)||"Files").slice(0,140):s.title,updatedAt:Date.now(),
      messages:[...s.messages,{role:"user",text:prompt,files:userFiles,ts:Date.now()},{role:"assistant",parts:[],ts:Date.now(),retryPrompt:fullPrompt}]}));
    setEditingLast(false);setEditVal("");
    // First turn carries the interpolated chat system preamble ({name}/{context}
    // filled from the project) so the conversation opens with project context.
    // Later turns resume the same session and don't need it re-sent.
    const turnPrompt=isFirstTurn?(()=>{const pre=chatSystemPreamble(p);return pre?pre+"\n\n---\n\n"+fullPrompt:fullPrompt;})():fullPrompt;
    runTurn(id,turnPrompt,runProvider,runModel,runExecutionMode,runEffort);
    // Auto-name the chat from its first message (unless a tip already gave it a
    // title). Always on. Fire-and-forget: the short subject replaces the raw
    // first-prompt title a moment later.
    if(isFirstTurn&&!titleOverride){
      const seed=(prompt&&prompt.trim())||(files[0]&&files[0].name)||"";
      if(seed)generateSubject(id,seed);
    }
    return id;
  }
  function openSchedule(){
    const d=new Date(Date.now()+60*60*1000);d.setSeconds(0,0);
    setScheduleAt(new Date(d.getTime()-d.getTimezoneOffset()*60000).toISOString().slice(0,16));
    setScheduleTitle((input.trim().split("\n")[0]||"Scheduled chat").slice(0,80));
    setScheduleFreq("0");
    setScheduleOpen(true);setErr("");
    fetch("/api/scheduled-chats?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).then(j=>{if(j&&j.ok)setScheduledChats((j.scheduledChats||[]).filter(x=>x.status==="pending"));}).catch(()=>{});
  }
  function scheduleChat(){
    if(!input.trim()||!scheduleAt)return;
    setScheduleBusy(true);setErr("");
    // credentials:"same-origin" is the default, but stating it guards against the
    // request being sent cookie-less (which returns 401 and reads as a failure).
    fetch("/api/scheduled-chats",{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,prompt:input.trim(),title:scheduleTitle,runAt:new Date(scheduleAt).toISOString(),frequency:+scheduleFreq,provider,model})})
      .then(r=>r.json()).then(j=>{setScheduleBusy(false);if(!j||!j.ok){setErr((j&&j.error)||"Could not schedule chat.");return;}setInput("");setScheduleOpen(false);})
      // A rejected fetch (TypeError "Load failed"/"Failed to fetch") means the
      // request never completed at the network layer — a dropped connection or a
      // server restart mid-flight, NOT a rejected schedule. Don't auto-retry (the
      // POST is non-idempotent and may have already been created); the prompt and
      // chosen time are still in the composer, so tell the user to try again.
      .catch(e=>{setScheduleBusy(false);const net=e&&(e.name==="TypeError"||/load failed|failed to fetch|network/i.test(String(e&&e.message||e)));setErr(net?"Connection dropped before the chat could be scheduled — your prompt is kept, please press Schedule again.":"Could not schedule chat: "+String(e));});
  }
  function cancelScheduled(id){
    fetch("/api/scheduled-chats/delete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id})})
      .then(r=>r.json()).then(j=>{if(j&&j.ok)setScheduledChats(xs=>xs.filter(x=>x.id!==id));}).catch(()=>{});
  }
  function loadHubTodos(){
    fetch("/api/hub-todos?slug="+encodeURIComponent(p.slug)).then(r=>r.json()).then(j=>{if(j&&j.ok)setHubTodoRows(j.todos||[]);}).catch(()=>{});
  }
  function loadHubTelegram(){
    fetch("/api/hub-todos/telegram").then(r=>r.json()).then(j=>{if(j&&j.ok)setHubTelegram({...j,loaded:true});}).catch(()=>setHubTelegram({loaded:true,connected:false}));
  }
  async function connectHubTelegram(replaceWebhook){
    if(!hubTelegramToken.trim()||hubTelegramBusy)return;
    setHubTelegramBusy(true);setHubTodoErr("");
    try{
      const call=async replace=>{const r=await fetch("/api/hub-todos/telegram",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token:hubTelegramToken.trim(),chatId:hubTelegramChat.trim(),replaceWebhook:!!replace})});const j=await r.json().catch(()=>({}));return{r,j};};
      let{r,j}=await call(replaceWebhook);
      if(r.status===409&&j.code==="webhook_in_use"&&confirm(j.error||t("Replace the bot's existing webhook?")))({r,j}=await call(true));
      if(!r.ok||!j.ok)throw new Error(j.error||("HTTP "+r.status));
      setHubTelegram({...j,loaded:true});setHubTelegramToken("");setHubTelegramChat("");
    }catch(e){setHubTodoErr(String(e&&e.message||e));}
    finally{setHubTelegramBusy(false);}
  }
  function disconnectHubTelegram(){
    if(!confirm(t("Disconnect this Telegram bot from Heygents?")))return;
    setHubTelegramBusy(true);setHubTodoErr("");
    fetch("/api/hub-todos/telegram",{method:"DELETE"}).then(async r=>{const j=await r.json().catch(()=>({}));if(!r.ok||!j.ok)throw new Error(j.error||("HTTP "+r.status));setHubTelegram({loaded:true,connected:false});})
      .catch(e=>setHubTodoErr(String(e&&e.message||e))).finally(()=>setHubTelegramBusy(false));
  }
  function addHubTodo(){
    const note=hubTodoText.trim();if(!note||hubTodoBusy)return;
    setHubTodoBusy(true);setHubTodoErr("");
    fetch("/api/hub-todos",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,note,provider,model})})
      .then(async r=>{const j=await r.json().catch(()=>({}));if(!r.ok||!j.ok)throw new Error(j.error||("HTTP "+r.status));return j;})
      .then(j=>{setHubTodoText("");setHubTodoRows(xs=>[j.todo,...xs]);})
      .catch(e=>setHubTodoErr(String(e&&e.message||e))).finally(()=>setHubTodoBusy(false));
  }
  function dismissHubTodo(id){
    fetch("/api/hub-todos/dismiss",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id})})
      .then(r=>r.json()).then(j=>{if(j&&j.ok)setHubTodoRows(xs=>xs.map(x=>x.id===id?j.todo:x));}).catch(()=>{});
  }
  // Turn a saved note into an AI fix prompt on demand (the per-note ▶ button).
  function prepareHubTodo(id){
    setHubTodoErr("");
    setHubTodoRows(xs=>xs.map(x=>x.id===id?{...x,status:"translating"}:x));
    fetch("/api/hub-todos/prepare",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({id,provider,model})})
      .then(async r=>{const j=await r.json().catch(()=>({}));if(!r.ok||!j.ok)throw new Error(j.error||("HTTP "+r.status));return j;})
      .then(j=>setHubTodoRows(xs=>xs.map(x=>x.id===id?j.todo:x)))
      .catch(e=>{setHubTodoErr(String(e&&e.message||e));setHubTodoRows(xs=>xs.map(x=>x.id===id?{...x,status:"saved"}:x));});
  }
  // Stream one turn into session `id`'s last (assistant) bubble. Shared by a fresh
  // send and by Retry. If the stream ends or drops before Claude's final result
  // (a dropped connection / "network error", or the CLI dying mid-turn), mark the
  // bubble interrupted and remember the prompt so it can be retried — the retry
  // resumes the conversation via the session's recorded sessionId.
  function runTurn(id,fullPrompt,providerOverride,modelOverride,executionModeOverride,effortOverride){
    const cur=sessionsRef.current.find(s=>s.id===id);
    const resumeId=cur?cur.sessionId:null;
    const runProvider=providerOverride||(cur&&cur.provider)||provider;
    const runModel=modelOverride!=null?modelOverride:(cur&&cur.model!=null?cur.model:model);
    const runExecutionMode=executionModeOverride||(cur&&cur.executionMode)||executionMode;
    const runEffort=effortOverride||(cur&&cur.effort)||effort;
    // First message decides the chat's language for good. Fall back to the UI
    // language when the opener carries no strong signal (e.g. just a file path).
    // Already-set langs are frozen — toggling the UI later must not retarget a
    // conversation already in flight. A session predating this feature has NO
    // `lang` property at all (vs. a post-feature session's explicit `lang:null`
    // awaiting its first message) — use property presence, not truthiness, to
    // tell the two apart, so a legacy chat never gets pinned to a language.
    const session=cur;
    let chatLang=session&&session.lang;
    if(!session||(("lang" in session)&&session.lang==null)){
      chatLang=HEB_RE.test(String(fullPrompt||""))?"he":lang;
      if(session&&("lang" in session))patchSession(id,s=>(("lang" in s)&&s.lang)?s:({...s,lang:chatLang}));
    }
    const sentPrompt=fullPrompt;
    const ac=new AbortController();abortersRef.current.set(id,ac);
    setRunning(id,true);
    if(onChatRunning)onChatRunning(p.slug,id,true);
    setOp(id,{state:"processing",provider:runProvider,detail:"Request sent. Waiting for first stream event.",startedAt:Date.now(),lastEventAt:Date.now()});
    const markInterrupted=(rawReason)=>{
      let marked=false;
      // Prefer the REAL server-reported failure (error / stderr / non-zero exit)
      // over the generic "stream closed" text — otherwise every failure looks the same.
      const real=failReasonText(id);
      const reason=real||rawReason||("The stream ended before "+providerName(runProvider)+" sent a completion signal.");
      // a run that ends without a clean result must not leave a tip armed for the
      // "mark solved?" nudge, or a later turn in this chat would fire it spuriously
      if(pendingTipRef.current&&pendingTipRef.current.chatId===id)pendingTipRef.current=null;
      patchSession(id,s=>{const c=s.messages.slice();const a=c[c.length-1];
        if(!a||a.role!=="assistant"||a.meta||a.interrupted)return s;
        const resumeRunId=s.runId||null;
        // Same rule as a clean finish: the light goes up on every chat, the one on
        // screen included, and only a real look takes it down again.
        c[c.length-1]={...a,interrupted:true,retryPrompt:fullPrompt,resumeRunId,interruptedReason:reason};marked=true;return{...s,messages:c,...(!manuallyStoppedRef.current.has(id)?{failedUnseen:true,doneUnseen:false}:{})};});
      if(marked){setOp(id,{state:"stuck",detail:reason});if(onChatDone&&!manuallyStoppedRef.current.has(id))onChatDone(p.slug,id,"failed");}
    };
    // persist the user turn immediately so a fast switch-away can't lose it before
    // the server-side run finishes (the server saves the answer into this chat).
    setTimeout(saveNow,0);
    // The stream is resumable: a dropped connection reconnects with the last seq
    // it saw and keeps filling THIS bubble. markInterrupted (and the red banner)
    // only run once the run really ended or the retries were exhausted.
    streamWithResume({
      start:(onEv)=>runClaude({slug:p.slug,prompt:sentPrompt,sessionId:resumeId,model:runModel,provider:runProvider,executionMode:runExecutionMode,effort:runEffort,chatId:id,chatSession:cur,chatLang,target:cur&&cur.target,followupHint:(runProvider||"claude")==="claude"&&followupEnabled(),onEvent:onEv,signal:ac.signal}),
      onEvent:(ev)=>handle(id,ev),signal:ac.signal,onStatus:(st)=>applyResumeStatus(id,st),
    })
      .then(r=>{
        const gaveUp=resumeGaveUpText(r&&r.retriedMs);
        // A run that never got a runId never actually entered streamWithResume's
        // retry loop (curRunId was still null, so `while(!terminal&&curRunId)`
        // never ran) - it failed once, immediately, before any run existed. r.error
        // is the real cause (e.g. a 409 "Set this project's Agent host" from
        // pickAgent), and "kept retrying for 0s" would misreport it as a dropped
        // connection. Only take this branch when no runId ever existed; a genuine
        // mid-stream drop (runId established, retries actually happened) keeps the
        // existing gaveUp banner unchanged.
        const realErr=(r&&!r.runId&&r.error)?String((r.error&&r.error.message)||r.error).trim():"";
        if(r&&!r.terminal&&id===activeIdRef.current)setErr(failReasonText(id)||realErr||gaveUp);
        markInterrupted(r&&r.terminal?null:(realErr||gaveUp));
      })
      .catch(e=>{if(e.name==="AbortError")return;const msg=String(e&&e.message||e||"Connection interrupted");const real=failReasonText(id);if(id===activeIdRef.current)setErr(real||"Connection interrupted - the response stopped before finishing. Resume or retry from the status banner.");markInterrupted(msg);})
      .then(()=>{if(abortersRef.current.get(id)===ac){abortersRef.current.delete(id);setRunning(id,false);if(onChatRunning)onChatRunning(p.slug,id,false);
        // The run ended without a clean finish (stopped/failed/dropped) while
        // messages sat queued: a successful `done` would have flushed them and
        // chained a new turn (new AbortController, so this guard is false). Since
        // we're here with the queue intact, hand it back to the composer so
        // nothing the user typed is silently lost.
        const q=queuedRef.current[id];
        if(q&&q.length){setQueued(m=>{const n={...m};delete n[id];queuedRef.current=n;return n;});if(id===activeIdRef.current)setInput(prev=>[...q,prev].filter(Boolean).join("\n\n"));}
      }});
  }
  // Re-attach to a turn that is STILL RUNNING on the server (we left this project
  // mid-turn and came back, or reloaded the page). Reset the assistant bubble and
  // replay the buffered + live events into it — the work itself never stopped.
  function resumeRun(rn){
    const id=rn.chatId;
    // Never resume into a chat whose last assistant turn is already COMPLETE
    // (has meta): the replayed buffer would overwrite that finished answer and
    // get persisted, resurrecting a stale response.
    const sess=(sessionsRef.current||[]).find(s=>s.id===id);
    if(sess&&sess.messages&&sess.messages.length){
      const last=sess.messages[sess.messages.length-1];
      if(last&&last.role==="assistant"&&last.meta)return;
    }
    const ac=new AbortController();abortersRef.current.set(id,ac);
    setRunning(id,true);setErr("");
    setOp(id,{...(runToOp(rn)||{}),state:rn.state==="waiting"?"waiting":"processing",detail:"Reconnected to the server-side run."});
    patchSession(id,s=>{const c=s.messages.slice();const a=c[c.length-1];
      if(a&&a.role==="assistant")c[c.length-1]={role:"assistant",parts:[],ts:a.ts};return{...s,provider:rn.provider||s.provider||"claude",messages:c};});
    const markInterrupted=(rawReason)=>{
      let marked=false;
      const real=failReasonText(id);
      const reason=real||rawReason||"The resumed stream ended before a final result arrived.";
      if(pendingTipRef.current&&pendingTipRef.current.chatId===id)pendingTipRef.current=null;
      patchSession(id,s=>{const c=s.messages.slice();const a=c[c.length-1];
        if(!a||a.role!=="assistant"||a.meta||a.interrupted)return s;
        const resumeRunId=s.runId||rn.runId||null;
        c[c.length-1]={...a,interrupted:true,retryPrompt:rn.prompt||"",resumeRunId,interruptedReason:reason};marked=true;return{...s,messages:c,...(!manuallyStoppedRef.current.has(id)?{failedUnseen:true,doneUnseen:false}:{})};});
      if(marked){setOp(id,{state:"stuck",detail:reason});if(onChatDone&&!manuallyStoppedRef.current.has(id))onChatDone(p.slug,id,"failed");}
    };
    streamWithResume({
      runId:rn.runId,
      start:(onEv)=>reconnectClaude({runId:rn.runId,onEvent:onEv,signal:ac.signal}),
      onEvent:(ev)=>handle(id,ev),signal:ac.signal,onStatus:(st)=>applyResumeStatus(id,st),
    })
      .then(r=>{markInterrupted(r&&r.terminal?null:resumeGaveUpText(r&&r.retriedMs));})
      .catch(e=>{if(e.name==="AbortError")return;markInterrupted(String(e&&e.message||e||"Connection interrupted"));})
      .then(()=>{if(abortersRef.current.get(id)===ac){abortersRef.current.delete(id);setRunning(id,false);if(onChatRunning)onChatRunning(p.slug,id,false);}});
  }
  // A run can sit in `stuck` while its background resume loop is still alive (still
  // in runningRef). The banner/bubble offer Retry/Resume, so those actions must
  // tear the in-flight stream down first rather than silently bail on isRunning.
  // Detach every stream this panel owns (project switch, dock close, a phone
  // freezing the tab). The server-side runs are untouched and we re-attach on
  // return; what we must give up is the local "running in THIS browser" overlay,
  // because no stream is left to retract it. Hand the rail and the badges back
  // to the server snapshot, which keeps reporting the truth while we're away —
  // otherwise a run that finishes while detached leaves the chat orange forever.
  function detachAllStreams(){
    for(const[id,ac]of abortersRef.current){
      try{ac.abort();}catch{}
      if(onChatRunning)onChatRunning(p.slug,id,false);
    }
    abortersRef.current.clear();
  }
  function abortInflight(id){
    const ac=abortersRef.current.get(id);if(ac){try{ac.abort();}catch{}abortersRef.current.delete(id);}
    setRunning(id,false);
    // Detaching removes the very stream whose terminal `.then()` would have
    // reported the chat idle, so release the live overlay right here. Without
    // this, Stop left the rail dot and the per-project badge stuck orange for
    // good while the chat itself already read "Cancelled by you".
    if(onChatRunning)onChatRunning(p.slug,id,false);
  }
  function resumeInterruptedLast(){
    abortInflight(activeIdRef.current);
    const a=sessionsRef.current.find(s=>s.id===activeIdRef.current);if(!a)return;
    const last=a.messages[a.messages.length-1];
    if(!last||last.role!=="assistant"||!last.interrupted)return;
    if(last.resumeRunId){
      setErr("");
      patchSession(a.id,s=>{const c=s.messages.slice();const old=c[c.length-1]||{};c[c.length-1]={role:"assistant",parts:old.parts||[],ts:old.ts||Date.now()};return{...s,messages:c,runId:last.resumeRunId,updatedAt:Date.now()};});
      resumeRun({runId:last.resumeRunId,chatId:a.id,prompt:last.retryPrompt||"",provider:a.provider||provider});
    }else retryLast();
  }
  // Re-run the last interrupted turn, streaming back into the SAME bubble WITHOUT
  // discarding the work already done (text, tool calls, bash output, previews).
  // runTurn appends into the last bubble, so keeping old.parts continues the turn
  // from where it stopped instead of restarting from the user's message.
  function retryLast(){
    abortInflight(activeIdRef.current);
    const a=sessionsRef.current.find(s=>s.id===activeIdRef.current);if(!a)return;
    const last=a.messages[a.messages.length-1];
    if(!last||last.role!=="assistant"||!(last.interrupted||(last.meta&&last.meta.error))||!last.retryPrompt)return;
    const fullPrompt=last.retryPrompt;
    setErr("");
    patchSession(a.id,s=>{const c=s.messages.slice();const old=c[c.length-1]||{};c[c.length-1]={role:"assistant",parts:old.parts||[],ts:old.ts||Date.now(),retryPrompt:fullPrompt};return{...s,messages:c,updatedAt:Date.now()};});
    runTurn(a.id,fullPrompt);
  }
  function forceResetActive(){
    const a=sessionsRef.current.find(s=>s.id===activeIdRef.current);if(!a)return;
    abortInflight(a.id);
    // Mark the chat cancelled rather than wiping its op state, so the tab keeps a
    // red status dot until the chat is reopened (opening clears it).
    noteRunEnded(a.id,"cancelled");
    setOp(a.id,{state:"cancelled",detail:"Cancelled by you.",endedAt:Date.now()});setErr("");
    patchSession(a.id,s=>({...s,runId:null,updatedAt:Date.now()}));
  }
  function send(){sendPrompt(input);}
  // Ask the hub for ONE likely next question once a Claude turn really finished
  // in this browser (live `done`, exit 0 — never a replay, a scheduled chat or an
  // automation run). Keyed to the answer's timestamp, so the ghost text disappears
  // by itself as soon as the conversation moves on. Best-effort: any failure just
  // leaves the normal placeholder.
  // End of a live run: the main turn already wrote its own suggestion (inline
  // <<followup: ...>> marker, agent v2.11.0+) - show it instantly, no extra model
  // call. Otherwise fall back to the Haiku round-trip in requestFollowup.
  function inlineFollowupOrRequest(id){
    const inline=inlineFollowupRef.current.get(id);inlineFollowupRef.current.delete(id);
    if(!followupEnabled())return;
    const s=(sessionsRef.current||[]).find(x=>x.id===id);
    const last=s&&s.messages?s.messages[s.messages.length-1]:null;
    if(inline&&last&&last.role==="assistant"&&!last.interrupted&&!(last.meta&&last.meta.error)&&(s.provider||"claude")==="claude"){
      const ts=last.ts||0;setFollowups(f=>({...f,[id]:{ts,text:inline}}));return;
    }
    requestFollowup(id);
  }
  function requestFollowup(id){
    if(!followupEnabled())return;
    const s=(sessionsRef.current||[]).find(x=>x.id===id);if(!s||!s.messages)return;
    if((s.provider||"claude")!=="claude")return;
    const m=s.messages;const last=m[m.length-1];
    if(!last||last.role!=="assistant"||last.interrupted||(last.meta&&last.meta.error))return;
    const answer=(last.parts||[]).filter(pt=>pt.type==="text").map(pt=>pt.text).join("\n\n").trim();
    if(!answer)return;
    const u=[...m].reverse().find(x=>x.role==="user");
    const ts=last.ts||0;
    fetch("/api/claude/followup",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({slug:p.slug,question:u?String(u.text||""):"",answer:answer.slice(-4000)})})
      .then(r=>r.ok?r.json():null)
      .then(j=>{const text=j&&typeof j.suggestion==="string"?j.suggestion.trim():"";if(text)setFollowups(f=>({...f,[id]:{ts,text}}));})
      .catch(()=>{});
  }
  function saveAndRunLast(){
    const prompt = editVal.trim();
    const id = activeIdRef.current;
    const cur = sessionsRef.current.find(s=>s.id===id);
    if(!cur) return;
    const lastUserIdx = cur.messages.map(m=>m.role).lastIndexOf("user");
    if(lastUserIdx===-1) return;
    const lastUser = cur.messages[lastUserIdx];
    if(!prompt && (!lastUser.files || !lastUser.files.length)) return;

    setErr("");
    setEditingLast(false);
    setEditVal("");

    const files = lastUser.files || [];
    const fullPrompt = files.length
      ? "[Attached files — read them with the Read tool" + (files.some(f=>f.isImage)?" (images are readable too)":"") + "]\n"
        + files.map(f=>"- "+f.path + (f.isImage?" (image)":"")).join("\n") + "\n\n" + (prompt || "Please look at the attached file(s).")
      : prompt;

    patchSession(id, s=>{
      const msgs = s.messages.slice(0, lastUserIdx);
      const updatedUser = { ...lastUser, text: prompt, ts: Date.now() };
      return {
        ...s,
        updatedAt: Date.now(),
        messages: [...msgs, updatedUser, { role: "assistant", parts: [], ts: Date.now(), retryPrompt: fullPrompt }]
      };
    });

    runTurn(id, fullPrompt);
    setTimeout(saveNow, 0);
  }
  // Stop is optimistic in the browser: detach the stream and clear the running
  // state immediately while the server performs the hard cancellation. The
  // partial assistant bubble remains available in chat history.
  function stop(){
    const a=sessionsRef.current.find(s=>s.id===activeIdRef.current);
    const runId=a&&a.runId;
    if(a)manuallyStoppedRef.current.add(a.id);
    if(runId)fetch("/api/claude/stop",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({runId})}).catch(()=>{});
    if(a){
      abortInflight(a.id);
      // Stopped is terminal here even though the server keeps the run until the
      // agent's exit POST lands, so a later poll can't re-light this tab.
      noteRunEnded(a.id,"cancelled");
      setOp(a.id,{state:"cancelled",detail:"Cancelled by you.",endedAt:Date.now()});
      setPendingAsk(p=>{if(!p[a.id])return p;const n={...p};delete n[a.id];return n;});
      stopRepeat(a.id);
      patchSession(a.id,s=>({...s,runId:null,updatedAt:Date.now()}));
    }
  }

  // ── attach files / pasted images to the next message ──
  // Upload to data/uploads/<slug>/chat/ and stage the absolute path so the prompt
  // can point Claude at it (it reads the file itself, images included).
  function attachBlob(blob,name){
    const isImage=String(blob.type||"").startsWith("image/");
    const aid=rid("a");
    setAttach(prev=>[...prev,{id:aid,name:name||blob.name||"file",isImage,uploading:true}]);
    const r=new FileReader();
    r.onerror=()=>{setAttach(prev=>prev.filter(a=>a.id!==aid));setErr("Could not read file.");};
    r.onload=()=>{
      fetch("/api/claude/attach",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({slug:p.slug,filename:name||blob.name||"file",dataUrl:r.result})})
        .then(x=>x.json()).then(d=>{
          if(d&&d.ok)setAttach(prev=>prev.map(a=>a.id===aid?{...a,uploading:false,path:d.file.path,url:d.file.url,mime:d.file.mime,size:d.file.size}:a));
          else{setAttach(prev=>prev.filter(a=>a.id!==aid));setErr("Attach failed: "+((d&&d.error)||"unknown error"));}
        }).catch(e=>{setAttach(prev=>prev.filter(a=>a.id!==aid));setErr("Attach failed: "+String(e));});
    };
    r.readAsDataURL(blob);
  }
  function onPickFiles(fl){Array.from(fl||[]).forEach(f=>attachBlob(f,f.name));}
  function removeAttach(aid){setAttach(prev=>prev.filter(a=>a.id!==aid));}
  function hasDraggedFiles(e){
    const dt=e&&e.dataTransfer;
    const types=dt&&dt.types;
    return !!(dt&&dt.files&&dt.files.length)||(!!types&&Array.prototype.indexOf.call(types,"Files")!==-1);
  }
  function onChatDragEnter(e){
    if(!hasDraggedFiles(e))return;
    e.preventDefault();e.stopPropagation();
    fileDragDepth.current+=1;
    setFileDrag(true);
  }
  function onChatDragOver(e){
    if(!hasDraggedFiles(e))return;
    e.preventDefault();e.stopPropagation();
    if(e.dataTransfer)e.dataTransfer.dropEffect="copy";
  }
  function onChatDragLeave(e){
    if(fileDragDepth.current===0)return;
    e.preventDefault();e.stopPropagation();
    fileDragDepth.current=Math.max(0,fileDragDepth.current-1);
    if(fileDragDepth.current===0)setFileDrag(false);
  }
  function onChatDrop(e){
    if(!hasDraggedFiles(e))return;
    e.preventDefault();e.stopPropagation();
    fileDragDepth.current=0;setFileDrag(false);
    onPickFiles(e.dataTransfer&&e.dataTransfer.files);
  }
  // A Drive file the picker already downloaded server-side — stage it as a ready
  // attachment (it arrives with an absolute path, so it can be sent immediately).
  function stageDriveFile(f){
    setAttach(prev=>[...prev,{id:rid("a"),name:f.name,isImage:String(f.mime||"").startsWith("image/"),uploading:false,path:f.path,url:f.url,mime:f.mime,size:f.size}]);
  }
  function onChatPaste(e){
    const items=e.clipboardData&&e.clipboardData.items;if(!items)return;
    let took=false;
    for(const it of items){if(it.kind==="file"){const f=it.getAsFile();if(f){const ext=(String(f.type).split("/")[1]||"png");attachBlob(f,f.name||("pasted-"+(String(f.type).startsWith("image/")?"image."+ext:"file")));took=true;}}}
    if(took)e.preventDefault(); // keep pasted files out of the textarea; plain text still pastes normally
  }

  // Save what's in the composer under the chat we're leaving, and load whatever
  // the chat we're opening had. Called on EVERY activeId change so an unsent
  // message is never lost by switching away from it.
  function stashActiveDraft(){draftsRef.current=stashDraft(draftsRef.current,activeIdRef.current,inputRef.current);persistDrafts();}
  function restoreDraft(id){const t=readDraft(draftsRef.current,id);setInput(t);return t;}
  // A sent (or discarded) message is no longer a draft — drop it so it can't be
  // restored later by switching back to that chat.
  function clearDraft(id){if(id==null)return;const next={...draftsRef.current};delete next[id];draftsRef.current=next;try{sessionStorage.setItem("hub.chatDrafts",JSON.stringify(next));}catch{}}

  // Start a fresh chat — reuse an existing blank draft instead of stacking them.
  function newChat(){
    // No busy guard: other chats keep working in the background while you start a new one.
    // `defaultProvider` used to exist before provider selection moved into this
    // panel. Referencing it now throws on click, leaving New chat apparently dead.
    const nextProvider=provider||"claude";
    stashActiveDraft();
    const blank=sessionsRef.current.find(s=>s.messages.length===0&&(s.provider||"claude")===nextProvider);
    let nextId;
    if(blank){nextId=blank.id;setActiveId(blank.id);setProvider(nextProvider);setModel(blank.model||"");}
    else{const s=newSession(nextProvider);nextId=s.id;commitSessions(prev=>[s,...prev]);setActiveId(s.id);setProvider(nextProvider);setModel("");}
    // Returning to an existing blank draft brings its own unsent text back with it.
    setErr("");restoreDraft(nextId);setShowHist(false);setAttach([]);setEditingLast(false);setEditVal("");
    // A click on New chat should leave the composer immediately ready, including
    // when it simply returns to the existing blank draft.
    setTimeout(()=>taRef.current&&taRef.current.focus(),0);
  }
  // Switch chats freely — the one you leave keeps streaming; clear the panel-wide
  // error since it belongs to the chat you're leaving, not the one you're opening.
  function openSession(id){
    const s=sessionsRef.current.find(x=>x.id===id);
    // A late click from a tab that was just deleted/reloaded must not blank the
    // provider or disturb the chat the user is currently viewing.
    if(!s)return;
    const nextProvider=(s&&s.provider)||"claude";
    setProvider(nextProvider);setModel((s&&s.model)||"");setExecutionMode(safeMode(s&&s.executionMode));if(s&&s.effort)setEffort(safeEffortClient(s.effort));
    // Reopening a chat (from History or elsewhere) brings its tab back onto the strip:
    // clear any explicit close, and pin it so an OLD chat stays past the recency window.
    if(closedTabsRef.current.includes(id)){closedTabsRef.current=closedTabsRef.current.filter(x=>x!==id);setClosedTabs(closedTabsRef.current);saveTabPrefs("closedTabs",p.slug,closedTabsRef.current);}
    if(!openedTabsRef.current.includes(id)){openedTabsRef.current=[...openedTabsRef.current,id];setOpenedTabs(openedTabsRef.current);saveTabPrefs("openedTabs",p.slug,openedTabsRef.current);}
    // Keep the outgoing chat's unsent text, and bring back the opened chat's.
    stashActiveDraft();
    // Viewing a chat clears its terminal status dot (finished/cancelled/stuck/failed).
    if(!isRunning(id)){const os=opFor(id);if(os&&["completed","cancelled","stuck","failed"].indexOf(os.state)>=0)clearOp(id);}
    // Opening it counts as seeing it only if the pane is actually up. When the open
    // comes from somewhere that still has to bring the chat view forward (mobile,
    // a minimized dock), leave the green light alone — the seen-effect above takes
    // it down as soon as the conversation is really on screen.
    setActiveId(id);setErr("");restoreDraft(id);setShowHist(false);setAttach([]);if(chatOnScreen&&(s.doneUnseen||s.failedUnseen)){const kind=s.failedUnseen?"failed":"done";patchSession(id,x=>({...x,doneUnseen:false,failedUnseen:false}));onChatSeen&&onChatSeen(p.slug,id,kind);}setEditingLast(false);setEditVal("");
    // Opening a chat from History must also bring the chat view forward on
    // mobile, otherwise the dock stays hidden/off-screen behind the current view.
    onActivateChat&&onActivateChat();
  }
  // Left/Right arrows switch to the neighbouring chat from ANYWHERE in the pane
  // (not just when a tab is focused), as long as focus isn't in a text field
  // where the arrows move the caret. The tab strip's own handler preventDefaults
  // first, so a focused tab never double-switches here.
  function paneArrowSwitch(e){
    if(e.defaultPrevented)return;
    if(e.key!=="ArrowRight"&&e.key!=="ArrowLeft")return;
    if(e.altKey||e.ctrlKey||e.metaKey||e.shiftKey)return;
    const el=e.target;
    if(el&&(el.isContentEditable||/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)))return;
    const cur=sessionsRef.current.filter(s=>{
      if(s.id===activeId)return true;
      if(s.messages.length===0)return false;
      if(closedTabsRef.current.includes(s.id))return false;
      return true;
    });
    const list=orderTabs(cur,tabOrder);
    if(list.length<=1)return;
    const idx=list.findIndex(s=>s.id===activeId);
    if(idx<0)return;
    const rtl=getComputedStyle(e.currentTarget).direction==="rtl";
    const fwd=rtl?e.key==="ArrowLeft":e.key==="ArrowRight";
    const ni=idx+(fwd?1:-1);
    if(ni<0||ni>=list.length)return;
    e.preventDefault();
    setTabMenu(null);openSession(list[ni].id);
  }
  // The ✕ on a chat TAB does not delete the chat — it just removes it from the tab
  // strip. The chat stays in 🕘 History (and keeps streaming if it's running);
  // reopen it from History to bring its tab back.
  function closeTab(id){
    const nextClosed=closedTabsRef.current.includes(id)?closedTabsRef.current:[...closedTabsRef.current,id];
    closedTabsRef.current=nextClosed;setClosedTabs(nextClosed);saveTabPrefs("closedTabs",p.slug,nextClosed);
    if(openedTabsRef.current.includes(id)){openedTabsRef.current=openedTabsRef.current.filter(x=>x!==id);setOpenedTabs(openedTabsRef.current);saveTabPrefs("openedTabs",p.slug,openedTabsRef.current);} // drop any recency pin
    if(id===activeIdRef.current){
      // Don't leave the pane on a now-hidden chat: jump to the newest still-open
      // tab, or start a fresh chat if this was the last one open.
      const open=sessionsRef.current.filter(s=>s.id!==id&&s.messages.length>0&&!nextClosed.includes(s.id));
      if(open.length)openSession(open[0].id);else newChat();
    }
  }
  function closeTabs(ids){
    // Batch-close a set of tabs off the strip (they stay in History), then fix up
    // the active pane once so we don't thrash openSession per closed tab.
    const set=new Set(ids);
    if(!set.size)return;
    const nextClosed=[...new Set([...closedTabsRef.current,...set])];
    closedTabsRef.current=nextClosed;setClosedTabs(nextClosed);saveTabPrefs("closedTabs",p.slug,nextClosed);
    const keptOpened=openedTabsRef.current.filter(x=>!set.has(x));
    if(keptOpened.length!==openedTabsRef.current.length){openedTabsRef.current=keptOpened;setOpenedTabs(keptOpened);saveTabPrefs("openedTabs",p.slug,keptOpened);}
    if(set.has(activeIdRef.current)){
      const open=sessionsRef.current.filter(s=>!set.has(s.id)&&s.messages.length>0&&!nextClosed.includes(s.id));
      if(open.length)openSession(open[0].id);else newChat();
    }
  }
  function renameSession(id){
    const cur=sessionsRef.current.find(s=>s.id===id);if(!cur)return;
    const newTitle=window.prompt(t("Rename chat"),cur.title||"");
    if(newTitle==null)return;
    // A manual rename wins over auto-naming: lock the title so a still-pending
    // subject request can't overwrite what the user just typed.
    patchSession(id,s=>({...s,title:newTitle.trim()||s.title,titleLocked:true}));
  }
  // Ask the server for a very short 3–4 word subject for a new chat and swap it in
  // as the title. Best-effort: a failure or an empty result just leaves the
  // first-prompt title in place, and a manual rename (titleLocked) is never touched.
  function generateSubject(id,seed){
    const{provider:sp,model:sm}=loadAiUsePrefs().subject||{};
    fetch("/api/chats/subject",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({text:String(seed).slice(0,2000),provider:sp,model:sm})})
      .then(r=>r.json()).then(j=>{
        if(!j||!j.ok||!j.subject)return;
        patchSession(id,s=>s.titleLocked?s:{...s,title:j.subject});
      }).catch(()=>{});
  }
  function deleteSession(id){
    // If the deleted chat was streaming, detach its client stream and drop its
    // running flag (the server run may finish and fallback-persist, harmlessly).
    abortInflight(id);clearOp(id);
    const next=sessionsRef.current.filter(s=>s.id!==id);
    commitSessions(next);
    // The server now unions saves (a stale tab can't wipe history), so deletion
    // must be explicit: record the id and flush it right away so the union-merge
    // removes it and never resurrects it from another tab's stale copy.
    if(!deletedIdsRef.current.includes(id))deletedIdsRef.current=[...deletedIdsRef.current,id];
    saveNow();
    clearDraft(id); // a deleted chat's draft dies with it
    if(id===activeIdRef.current){const to=next.length?next[0].id:null;setActiveId(to);restoreDraft(to);}
  }

  // ── "/" skill picker ───────────────────────────────────────────────────────
  // Skills resolve in the headless chat as /skill-name, so typing "/" offers an
  // autocomplete. The menu shows while the input is just "/" + a command token
  // (no space yet); typing a space begins the skill's arguments and closes it.
  const slashQ = /^\/(\S*)$/.test(input) ? input.slice(1).toLowerCase() : null;
  // Runtime commands (/context, /cost, ...) are client-side, not harvested
  // skills, so merge them in first (deduped by label) so the picker always
  // offers them. They carry {name,label,desc,runtime:true}.
  const slashSource = [...RUNTIME_COMMANDS.map(c=>({name:c.name,label:c.name,description:c.desc,runtime:true})), ...skills];
  const slashMatches = slashQ==null ? [] : slashSource.filter(s=>
    s.label.toLowerCase().includes(slashQ) || String(s.name).toLowerCase().includes(slashQ));
  const slashOpen = provider==="claude" && slashQ!=null && slashMatches.length>0 && !busy && !slashHide;
  const slashSel = slashOpen ? Math.min(slashIdx, slashMatches.length-1) : 0;
  function pickSkill(s){
    if(!s)return;
    // Insert the short label (e.g. /last30days, not /last30days:last30days) — skills
    // resolve via /skill-name and labels are unique in the list.
    setInput("/"+s.label+" ");setSlashIdx(0);setSlashHide(false);
    setTimeout(()=>{const t=taRef.current;if(t){t.focus();const n=t.value.length;t.setSelectionRange(n,n);}},0);
  }
  const runStateAction=()=>{
    if(conversationState.action==="Resume")return resumeInterruptedLast;
    if(conversationState.action==="Retry")return retryLast;
    if(conversationState.action==="Force Reset")return forceResetActive;
    return null;
  };

  return(
   <div className={"claudepane provider-"+provider} data-dirmode={dirMode} onKeyDown={paneArrowSwitch}>
    {providerSlot&&ReactDOM.createPortal(
      <React.Fragment>
        <button className={"provider-claude"+(tab==="chat"&&provider==="claude"?" active":"")}
          onClick={()=>{chooseProvider("claude");onActivateChat&&onActivateChat();}} disabled={busy&&provider!=="claude"}
          aria-label={t("Claude")} title={t("Claude Code")}><ProviderLogo provider="claude"/></button>
        <button className={"provider-codex"+(tab==="chat"&&provider==="codex"?" active":"")}
          onClick={()=>{chooseProvider("codex");onActivateChat&&onActivateChat();}} disabled={busy&&provider!=="codex"}
          aria-label={t("Codex")} title={t("Codex CLI")}><ProviderLogo provider="codex"/></button>
        <button className={"provider-gemini"+(tab==="chat"&&provider==="gemini"?" active":"")}
          onClick={()=>{chooseProvider("gemini");onActivateChat&&onActivateChat();}} disabled={busy&&provider!=="gemini"}
          aria-label={t("Gemini")} title={t("Gemini CLI")}><ProviderLogo provider="gemini"/></button>
      </React.Fragment>,providerSlot)}
    {headSlot&&tab==="chat"&&ReactDOM.createPortal(
     <React.Fragment>
      <ChatAiOptions/>
      <div className="chist" ref={historyMenuRef}>
        <button ref={histBtnRef} className={"btn sm"+(unseenCount?" hasnew":"")} onClick={()=>setShowHist(v=>!v)}
          title={unseenCount?unseenCount+" chat"+(unseenCount>1?"s":"")+" finished — open to view":"Chat history"}>
          🕘 <span className="chist-lbl">{t("History")}</span>{history.length?" ("+history.length+")":""}
          {unseenCount>0&&<span className="chist-dot" title={t("Finished chats waiting")}/>}</button>
        {showHist&&<div className="chist-menu" style={histPos?(histPos.phone?{position:"fixed",top:histPos.top,insetInlineStart:"auto",insetInlineEnd:"auto",left:10,right:10,width:"auto",maxWidth:"none",maxHeight:"calc(100dvh - "+(histPos.top+12)+"px)"}:{position:"fixed",left:histPos.left,top:histPos.top,insetInlineStart:"auto",insetInlineEnd:"auto",right:"auto",width:histPos.width,maxWidth:"none",maxHeight:"calc(100dvh - "+(histPos.top+12)+"px)"}):undefined}>
          {(()=>{const uN=history.filter(s=>!isFromTaskChat(s)).length,tN=history.filter(s=>isFromTaskChat(s)).length;return(
            <div className="chist-tabs" onClick={e=>e.stopPropagation()}>
              <button className={"chist-tab"+(histTab==="user"?" on":"")} onClick={()=>setHistTab("user")}>{t("My chats")} <span className="chist-tab-n">{uN}</span></button>
              <button className={"chist-tab"+(histTab==="task"?" on":"")} onClick={()=>setHistTab("task")}>{t("Task chats")} <span className="chist-tab-n">{tN}</span></button>
            </div>);})()}
          {history.length>=6&&<input className="chist-search" placeholder={t("Search chats…")} value={histQuery}
            onChange={e=>setHistQuery(e.target.value)} onClick={e=>e.stopPropagation()}/>}
          {(()=>{
            const q=histQuery.trim().toLowerCase();
            const scoped=history.filter(s=>histTab==="task"?isFromTaskChat(s):!isFromTaskChat(s));
            const items=q?scoped.filter(s=>chatSearchText(s).includes(q)):scoped;
            if(!items.length)return <div className="chist-empty">{history.length===0?t("No saved chats yet. Your conversations are kept here per project."):q?t("No chats match your search."):histTab==="task"?t("No chats from tasks yet."):t("No chats yet.")}</div>;
            return groupByRecency(items).map(g=>(
              <div key={g.label} className="chist-group">
                <div className="chist-glabel">{g.label}</div>
                {g.items.map(s=>{
                  const running=isRunning(s.id);
                  const status=tabStatus(s.id,!running&&!!s.doneUnseen,!!s.failedUnseen);
                  const sessionProvider=["codex","gemini"].includes(s.provider)?s.provider:"claude";
                  const sessionProviderName=providerName(sessionProvider);
                  return(
                  <div key={s.id} className={"chist-item "+sessionProvider+(s.id===activeId?" active":"")}
                    onContextMenu={e=>{
                      e.preventDefault();
                      e.stopPropagation();
                      const x=Math.min(e.clientX,window.innerWidth-220);
                      const y=Math.min(e.clientY,window.innerHeight-200);
                      setHistCtx({id:s.id,title:s.title||t("Untitled chat"),provider:sessionProvider,x:Math.max(8,x),y:Math.max(8,y)});
                    }}>
                    <span className={"ci-bar "+sessionProvider} title={t("Chat with {agent}",{agent:sessionProviderName})} aria-label={sessionProviderName}/>
                    <button className="ci-t" title={t("{agent} chat: {title}",{agent:sessionProviderName,title:s.title||t("Untitled chat")})} onClick={()=>openSession(s.id)}>
                      {status&&<span className={"chat-status-dot dot-"+status.tone+(status.live?" live":"")} title={status.label}>●</span>}
                      {isFromTaskChat(s)&&(()=>{const act=taskNeedsAct(s.id);return <span className={"ci-task"+(act?" act":"")} title={act?t("Needs your action"):t("Created from a task")} aria-label={act?t("Needs your action"):t("Created from a task")}>{act?"!":"✔"}</span>;})()}
                      <span className="ci-tt" dir={dirOf(s.title||t("Untitled chat"))}>{s.title||t("Untitled chat")}</span>
                    </button>
                    <span className="ci-w">{relTime(s.updatedAt)}</span>
                    <span className="ci-actions">
                      <button className="ci-d" title={t("Delete chat")} aria-label={t("Delete chat")} onClick={()=>deleteSession(s.id)}>✕</button>
                    </span>
                  </div>);
                })}
              </div>));
          })()}
        </div>}
      </div>
      <button type="button" className={"btn sm new-chat-btn " + provider} onClick={newChat}>+ {t("New chat")}</button>
      {histCtx&&<div className="ctab-menu" ref={histCtxRef} style={{position:"fixed",left:histCtx.x,top:histCtx.y}} onContextMenu={e=>e.preventDefault()}>
        <button onClick={()=>{const id=histCtx.id;setHistCtx(null);renameSession(id);}}>{t("Rename chat")}</button>
        <button onClick={()=>{const keep=histCtx.id;setHistCtx(null);closeTabs(tabs.filter(s=>s.id!==keep).map(s=>s.id));}}>{t("Close all chats except this")}</button>
        <button onClick={()=>{setHistCtx(null);closeTabs(tabs.map(s=>s.id));}}>{t("Close all chats")}</button>
        <button className="ctab-menu-danger" onClick={()=>{const id=histCtx.id;setHistCtx(null);deleteSession(id);}}>{t("Delete conversation")}</button>
      </div>}
     </React.Fragment>,headSlot)}
    <div className="claudepane-head">
      <span className="title" title={active?active.title:""}>{active&&active.messages.length?active.title:t("New chat")}</span>
      <button type="button" className={"dirmode-btn"+(dirMode==="auto"?"":" on")} onClick={cycleDirMode}
        aria-label={t("Text direction")} title={dirMode==="auto"
          ?t("Text direction: Auto (Hebrew ⇒ RTL). Click to force RTL.")
          :dirMode==="rtl"?t("Text direction: RTL — forced on every message. Click to force LTR.")
          :t("Text direction: LTR — forced on every message. Click for Auto.")}>
        {dirMode==="auto"?"⇄ "+t("Auto"):dirMode==="rtl"?"⇐ RTL":"⇒ LTR"}
      </button>
      <div className="fontbar" title={t("Chat text size")}>
        <button className="fbtn" onClick={()=>bumpFont(-0.05)} disabled={fontScale<=0.8} title={t("Smaller text")}>A−</button>
        <button className="fbtn fpct" onClick={()=>setFontScale(1)} title={t("Reset text size")}>{Math.round(fontScale*100)}%</button>
        <button className="fbtn" onClick={()=>bumpFont(0.05)} disabled={fontScale>=1.6} title={t("Larger text")}>A+</button>
      </div>
      {msgs.length>0&&<CopyBtn cls="copychat-btn" label="📋" doneLabel="✓" title={t("Copy the whole conversation to the clipboard")}
        text={chatToText(msgs,agentName,active&&active.title)}/>}
    </div>
    {providerModels && providerModels.length > 0 && (
      <div className="chat-model-selector" style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:"6px 8px",padding:"8px 14px",borderBottom:"1px solid var(--line)",background:"var(--bar-soft)",flexShrink:0}}>
        <div className="modelbar" style={{margin:0,padding:0,marginInlineStart:0}}>
          {providerModels.map(m=>(
            <button key={m.value} className={"mpill"+(model===m.value?" active":"")}
              onClick={()=>chooseModel(m.value)}
              title={(busy?"Use "+m.label+" for the next message. Current run keeps its starting model.":(m.desc||(m.hint?"Default model ("+m.label+")":m.label)))}>
              {m.label}{m.hint&&<span className="mhint">{m.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    )}
    {(()=>{
      // Browser-style tabs for switching between conversations without opening the
      // history dropdown. The strip carries every conversation with history —
      // running, finished-and-viewed, or finished-and-not — regardless of age. Only
      // empty drafts and chats you've explicitly ✕-closed are kept off it; a closed
      // chat still lives in 🕘 History and returns to the strip the moment you reopen
      // it (openSession clears its closedTabs entry). The ● "new" dot (below) — not
      // visibility — is what's gated on finished-and-unseen.
      //
      // Order: newest-first, so a new chat — or one that just replied — floats to
      // the left. Drag a tab to rearrange the strip by hand; that arrangement holds
      // (and persists per project) until any chat sees new activity, at which point
      // recency takes the strip back. See orderTabs().
      const visible=sessions.filter(s=>{
        if(s.id===activeId)return true;                       // the chat you're viewing
        if(s.messages.length===0)return false;                // skip other empty drafts
        if(closedTabs.includes(s.id))return false;            // explicitly closed off the strip
        return true;                                          // every other chat with history stays on the strip
      });
      const tabs=orderTabs(visible,tabOrder);
      if(tabs.length===0)return null;
      const scrollBy=(d)=>{if(tabsRef.current)tabsRef.current.scrollBy({left:d,behavior:"smooth"});};
      // Commit a drag: snapshot the strip as it now reads, stamped with the current
      // activity clock so the next reply anywhere hands the strip back to recency.
      const dropTab=(toId,side)=>{
        const from=dragTab;
        setDragTab(null);setDropAt(null);
        if(!from||from===toId)return;
        const next={ids:moveTab(tabs.map(s=>s.id),from,toId,side),stamp:latestActivity(visible)};
        setTabOrder(next);saveTabOrder(p.slug,next);
      };
      return(
      <div className="chat-tabs-bar">
        <button type="button" className="ctab-nav" title={t("Previous chats")} onClick={()=>scrollBy(-180)}>‹</button>
        <div className="chat-tabs" ref={tabsRef}>
          {tabs.map((s,ti)=>{
            const sp=["codex","gemini"].includes(s.provider)?s.provider:"claude";
            const running=isRunning(s.id);
            const unseen=!running&&s.doneUnseen;
            const status=tabStatus(s.id,unseen,!!s.failedUnseen);
            const title=s.title||t("Untitled chat");
            const dz=dropAt&&dropAt.id===s.id?" drop-"+dropAt.side:"";
            return(
            <div key={s.id} tabIndex={0} className={"ctab "+sp+(s.id===activeId?" active":"")+(dragTab===s.id?" dragging":"")+dz}
              title={providerName(sp)+" chat: "+title} onClick={e=>{setTabMenu(null);openSession(s.id);if(s.id===activeId)scrollTabFullyIntoView(tabsRef.current,e.currentTarget,"smooth");}}
              // Arrow keys swipe to the neighbouring tab when the strip has focus
              // (mirrored for an RTL strip). Home/End jump to the ends.
              onKeyDown={e=>{
                if(e.key!=="ArrowRight"&&e.key!=="ArrowLeft"&&e.key!=="Home"&&e.key!=="End")return;
                e.preventDefault();
                const rtl=getComputedStyle(e.currentTarget).direction==="rtl";
                let ni;
                if(e.key==="Home")ni=0;
                else if(e.key==="End")ni=tabs.length-1;
                else{const fwd=rtl?e.key==="ArrowLeft":e.key==="ArrowRight";ni=ti+(fwd?1:-1);}
                if(ni<0||ni>=tabs.length)return;
                const nx=tabs[ni];
                setTabMenu(null);openSession(nx.id);
                const el=e.currentTarget.parentNode&&e.currentTarget.parentNode.children[ni];
                if(el&&el.focus)el.focus();
              }}
              // Touch devices: no HTML5 drag. Native drag handling on a draggable
              // element swallows the first tap, so switching a tab took two taps —
              // the strip is reorderable with a mouse only.
              draggable={!isTouchDevice()} onDragStart={e=>{
                setTabMenu(null);setDragTab(s.id);
                e.dataTransfer.effectAllowed="move";
                try{e.dataTransfer.setData("text/plain",s.id);}catch{} // Firefox needs a payload to start a drag
              }}
              onDragOver={e=>{
                if(!dragTab||dragTab===s.id)return;
                e.preventDefault();                    // allow the drop
                e.dataTransfer.dropEffect="move";
                const r=e.currentTarget.getBoundingClientRect();
                // Drop on the half you're pointing at; mirrored for an RTL strip.
                const before=getComputedStyle(e.currentTarget).direction==="rtl"
                  ?e.clientX>r.left+r.width/2
                  :e.clientX<r.left+r.width/2;
                const side=before?"before":"after";
                if(!dropAt||dropAt.id!==s.id||dropAt.side!==side)setDropAt({id:s.id,side});
              }}
              onDrop={e=>{e.preventDefault();e.stopPropagation();dropTab(s.id,dropAt&&dropAt.id===s.id?dropAt.side:"before");}}
              onDragEnd={()=>{setDragTab(null);setDropAt(null);}}
              onContextMenu={e=>{
                e.preventDefault();
                e.stopPropagation();
                const bar=e.currentTarget.closest(".chat-tabs-bar");
                const r=bar&&bar.getBoundingClientRect?bar.getBoundingClientRect():{left:0,top:0,width:window.innerWidth};
                const x=Math.max(8,Math.min(e.clientX-r.left,(r.width||window.innerWidth)-220));
                const y=Math.max(8,e.clientY-r.top);
                setTabMenu({id:s.id,title,provider:sp,x,y});
              }}>
              {status&&<span className={"chat-status-dot dot-"+status.tone+(status.live?" live":"")} title={status.label}>●</span>}
              {isFromTaskChat(s)&&(()=>{const act=taskNeedsAct(s.id);return <span className={"ci-task"+(act?" act":"")} title={act?t("Needs your action"):t("Created from a task")} aria-label={act?t("Needs your action"):t("Created from a task")}>{act?"!":"✔"}</span>;})()}
              <span className="ctab-t" dir={dirOf(title)}>{title}</span>
              <button className="ctab-x" title={t("Close tab (stays in History)")} aria-label={t("Close tab")} onClick={e=>{e.stopPropagation();setTabMenu(null);closeTab(s.id);}}>✕</button>
            </div>);
          })}
        </div>
        {tabMenu&&<div className="ctab-menu" ref={tabMenuRef} style={{left:tabMenu.x,top:tabMenu.y}} onContextMenu={e=>e.preventDefault()}>
          <button onClick={()=>{
            const id=tabMenu.id;
            setTabMenu(null);
            renameSession(id);
          }}>{t("Rename chat")}</button>
          <button onClick={()=>{
            const keep=tabMenu.id;
            setTabMenu(null);
            closeTabs(tabs.filter(s=>s.id!==keep).map(s=>s.id));
          }}>{t("Close all chats except this")}</button>
          <button onClick={()=>{
            setTabMenu(null);
            closeTabs(tabs.map(s=>s.id));
          }}>{t("Close all chats")}</button>
          <button className="ctab-menu-danger" onClick={()=>{
            const id=tabMenu.id;
            setTabMenu(null);
            deleteSession(id);
          }}>{t("Delete conversation")}</button>
        </div>}
        <button type="button" className="ctab-nav" title={t("Next chats")} onClick={()=>scrollBy(180)}>›</button>
      </div>);
    })()}
    <div className={"chat"+(fileDrag?" file-drag":"")}
      onDragEnter={onChatDragEnter} onDragOver={onChatDragOver}
      onDragLeave={onChatDragLeave} onDrop={onChatDrop}>
      {fileDrag&&<div className="chat-file-drop" role="status" aria-live="polite">
        <span className="chat-file-drop-icon" aria-hidden="true">📎</span>
        <strong>{t("Drop files to attach")}</strong>
        <span>{t("They will be added to your next message.")}</span>
      </div>}
      <div className="log" ref={logRef} style={{zoom:fontScale}}>
        {msgs.length===0&&<div className="empty">
          <div>{t("Tell {agent} what to do — it runs in this project's folder.",{agent:agentName})} {t("Press")} <b>{t("Enter")}</b> {t("to send")} · <b>{t("Shift+Enter")}</b> {t("for a new line.")}</div>
          {provider==="claude"&&<div style={{marginTop:4}}>{t("Type")} <b>/</b> {t("to run a Claude skill — e.g.")} <b>/deep-research</b>, <b>/code-review</b>, <b>/summary</b>.</div>}
          {history.length>0
            &&<div className="csugg-empty">
              <div className="csugg-empty-lbl">{t("Recent chats")}</div>
              <div className="last-chats-list">
                {history.slice(0,5).map(s=>{
                  const sp=["codex","gemini"].includes(s.provider)?s.provider:"claude";
                  const running=isRunning(s.id);
                  const status=tabStatus(s.id,!running&&!!s.doneUnseen,!!s.failedUnseen);
                  return(
                  <button key={s.id} className={"lc-row "+sp} title={t("{agent} chat: {title}",{agent:providerName(sp),title:s.title||t("Untitled chat")})} onClick={()=>openSession(s.id)}>
                    <span className={"ci-bar "+sp}/>
                    {status&&<span className={"chat-status-dot dot-"+status.tone+(status.live?" live":"")} title={status.label}>●</span>}
                    {isFromTaskChat(s)&&(()=>{const act=taskNeedsAct(s.id);return <span className={"ci-task"+(act?" act":"")} title={act?t("Needs your action"):t("Created from a task")} aria-label={act?t("Needs your action"):t("Created from a task")}>{act?"!":"✔"}</span>;})()}
                    <span className="lc-t" dir={dirOf(s.title||t("Untitled chat"))}>{s.title||t("Untitled chat")}</span>
                    <span className="lc-w">{relTime(s.updatedAt)}</span>
                  </button>);
                })}
              </div>
            </div>}
          {CLOUD&&localInfo&&!localInfo.online&&<div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6,alignItems:"flex-start"}}>
            <div className="muted" style={{fontSize:12}}>{t("No agent is connected, so {agent} can't run yet.",{agent:agentName})}</div>
            <InstallAgentBtn label={t("Install the agent to start")}/>
          </div>}
        </div>}
        {msgs.map((m,i)=>{
          const liveMsg=busy&&i===msgs.length-1;
          const msgText=m.role==="user"
            ?String(m.text||"").trim()
            :stripFollowupForDisplay((m.parts||[]).filter(pt=>pt.type==="text").map(pt=>pt.text).join("\n\n"),liveMsg).trim();
          if(m.role==="note"){
            return <div key={i} className="cmsg note">
              <div className="bubble note-bubble" dir={dirOf(m.text)}>{m.text}</div>
            </div>;
          }
          return (
          <div key={i} className={"cmsg "+m.role}>
            {/* The one copy button for this message: floats at the message's top-right on
                hover and carries the full text (all parts joined), so assistant messages
                get exactly one regardless of how many text/tool parts they render. */}
            {msgText&&<CopyBtn cls="cmsg-copy-btn" label="📋" doneLabel="✓" title={t("Copy message")} text={msgText} html={mdToHtml(msgText)}/>}
            {(m.role==="assistant"||m.role==="user")&&msgText&&onAddNote&&p&&p.slug&&
              <button className="cmsg-note-btn" title={t("Save to plan notes")} aria-label={t("Save to plan notes")}
                onClick={()=>{onAddNote(p.slug,msgText);setNotedIdx(i);setTimeout(()=>setNotedIdx(v=>v===i?null:v),1400);}}>
                {notedIdx===i?"✓":"+"}
              </button>}
            <div className="cmsg-head">
              <span className="who">{m.role==="user"?"you":agentName.toLowerCase()}</span>
              {m.ts&&<span className="ctime" title={fmtWhen(m.ts)}>{fmtTime(m.ts)}</span>}
              {m.role==="user" && i===lastUserIdx && !busy && !editingLast && (
                <button className="cmsg-edit-btn" title={t("Edit message & run again")} onClick={()=>{setEditingLast(true);setEditVal(m.text||"");}}>
                  ✎ Edit
                </button>
              )}
            </div>
            {m.role==="user"
              ?<div>
                 {m.files&&m.files.length>0&&<div className="catts">{m.files.map((f,k)=>(
                   f.isImage&&f.url
                     ?<a key={k} className="catt catt-img" href={f.url} target="_blank" rel="noopener noreferrer" title={t("Open")+": "+f.name}>
                        <img className="catt-thumb" src={f.url} alt={f.name}/> <span className="catt-n">{f.name}</span></a>
                     :<span key={k} className="catt" title={f.name}>📎 <span className="catt-n">{f.name}</span></span>))}</div>}
                 {i===lastUserIdx && editingLast ? (
                   <div className="msg-edit-container">
                     <textarea className="msg-edit-textarea" dir={dirOf(editVal)} value={editVal} onChange={e=>setEditVal(e.target.value)}
                       onKeyDown={e=>{
                         if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();saveAndRunLast();}
                         if(e.key==="Escape"){e.preventDefault();setEditingLast(false);}
                       }} autoFocus/>
                     <div className="msg-edit-actions">
                       <button className="btn sm primary" onClick={saveAndRunLast} disabled={!editVal.trim() && (!m.files || !m.files.length)}>{t("Save & Run")}</button>
                       <button className="btn sm" onClick={()=>setEditingLast(false)}>{t("Cancel")}</button>
                     </div>
                   </div>
                 ) : (
                   m.text&&<div className="bubble" dir={dirOf(m.text)}>{m.text}</div>
                 )}
               </div>
              :<div style={{display:"flex",flexDirection:"column",gap:6}}>
                 {groupParts(m.parts,liveMsg).map((pt,j)=>pt.type==="text"
                   ?<AssistantText key={j} text={pt.text} onRunCmd={onRunCmd}/>
                   :pt.type==="tools"
                     ?<ToolGroup key={j} items={pt.items}/>
                     :<div key={j} className="ctool"><span>⚙</span><span className="nm">{pt.name}</span>{pt.desc&&<span className="dsc">{pt.desc}</span>}</div>)}
                 {m.interrupted&&!busy&&<div className="cinterrupt"><span style={{flex:"1 1 200px",minWidth:0}}>⚠ {m.interruptedReason
                     ?<FailReason text={m.interruptedReason} inline/>
                     :(m.resumeRunId?"Execution paused — reconnect to continue this run.":"Response interrupted — the connection dropped before "+agentName+" finished.")}</span>{i===msgs.length-1&&conversationState.action&&<button className="btn sm" onClick={runStateAction()}>{"↻ "+conversationState.action}</button>}</div>}
                 {i===msgs.length-1&&!busy&&<div className="cquick">
                   {quickPrompts.map((q,k)=>{
                     const prompt=typeof q.prompt==="function"?q.prompt(p):q.prompt;
                     return <button key={k} title={q.hint||("Send “"+prompt+"” to "+agentName)} onClick={()=>sendPrompt(prompt)}>{q.label}</button>;
                   })}
                   <button className="cquick-edit" title={t("Edit quick prompts")} aria-label={t("Edit quick prompts")} onClick={()=>setQuickEdit(true)}>✎</button>
                 </div>}
                 {/* The stats strip reports a FINISHED generation, so it only renders while
                     the chat is idle. `result` ends a TURN, not the run: a multi-turn run
                     (a queued/live mid-run reply, or an agentic chain) would otherwise show
                     "done · N turns · Xs" on an earlier bubble while Claude is still working.
                     `busy` flips true the instant a turn starts (setRunning in runTurn) and
                     false only when the stream reaches its terminal state, so the strip
                     clears the moment the conversation continues and comes back once the
                     new operation has completed. The numbers stay on the message (m.meta) —
                     this hides them, it doesn't drop them. */}
                 {m.meta&&!busy&&<div className="cfoot" style={{zoom:1/fontScale}}>
                   {m.ts&&<span className="cfoot-time" title={fmtWhen(m.ts)}>{fmtTime(m.ts)}</span>}
                   {m.meta.error
                     ?<Stat color="var(--err)" tip={"This turn ended with an error — "+agentName+" didn't finish cleanly. Hover the message above for details."}>error</Stat>
                     :<Stat tip="This turn completed successfully.">done</Stat>}
                   {m.meta.turns!=null&&<Stat tip="Agent turns — how many model ↔ tool round-trips Claude made before answering. More turns usually means more tool calls (reading files, running commands).">{m.meta.turns} turns</Stat>}
                   {m.meta.ms!=null&&<Stat tip="Response time — wall-clock seconds this turn took, end to end.">{(m.meta.ms/1000).toFixed(1)}s</Stat>}
                   {m.meta.tin!=null&&<Stat tip="Input tokens (↑) — your prompt plus the cached conversation context sent to the model this turn. Grows as the chat gets longer."><span className="tok">↑{fmtTokens(m.meta.tin)}</span></Stat>}
                   {m.meta.tout!=null&&<Stat tip="Output tokens (↓) — the text Claude generated in this turn."><span className="tok">↓{fmtTokens(m.meta.tout)}</span></Stat>}
                 </div>}
               </div>}
          </div>);})}
      </div>
      {err&&<div className="err" style={{padding:"0 12px 8px"}}>{err}</div>}
      {browseOpen&&dir&&<FileBrowser root={dir} initialPath={typeof browseOpen==="string"?browseOpen:""} onClose={()=>setBrowseOpen(false)}/>}
      {quickEdit&&<QuickPromptsEditor list={quickPrompts} onSave={commitQuickPrompts} onClose={()=>setQuickEdit(false)}/>}
      {attach.length>0&&<div className="cattach-row">
        {attach.map(a=>(
          <span key={a.id} className={"catt"+(a.uploading?" up":"")} title={a.name}>
            {a.isImage&&a.url&&!a.uploading
              ?<a href={a.url} target="_blank" rel="noopener noreferrer" title={t("Open")+": "+a.name} className="catt-open"><img className="catt-thumb" src={a.url} alt={a.name}/> <span className="catt-n">{a.name}</span></a>
              :<React.Fragment>{a.isImage?"🖼":"📎"} <span className="catt-n">{a.name}</span></React.Fragment>}
            {a.uploading?<span>…</span>:<button className="catt-x" title={t("Remove")} aria-label={t("Remove")} onClick={()=>removeAttach(a.id)}>✕</button>}
          </span>))}
      </div>}
      {activeId!=null&&(queued[activeId]||[]).length>0&&<div className="cqueued-row" role="list" aria-label={t("Queued messages")}>
        <span className="cq-hint">🕓 {t("Queued — sent when {agent} finishes",{agent:agentName})}</span>
        {(queued[activeId]||[]).map((qm,qi)=>(
          <span key={qi} className="cqueued" role="listitem" title={qm} dir={dirOf(qm)}>
            <span className="cq-n">{qm}</span>
            <button className="cq-x" title={t("Remove")} aria-label={t("Remove")} onClick={()=>dropQueued(activeId,qi)}>✕</button>
          </span>))}
      </div>}
      <div className="chatdir">
        <span className="lbl">📁</span>
        {dir?<span className="pth" role="button" tabIndex={0} aria-label={"Browse files in "+dir} title={"Browse files in "+dir} style={{cursor:"pointer"}} onClick={()=>setBrowseOpen(true)} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();setBrowseOpen(true);}}}>{shortPath(dir)}</span>:<span className="none">{dirErr||"resolving…"}</span>}
        {/* An offline agent still warns, since nothing can run without one. The
            online-agent chip is intentionally not shown. */}
        {localInfo&&!localInfo.online&&
          <span className={"agchip off"} {...(CLOUD?{role:"button",tabIndex:0,onKeyDown:e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();openAgentSetup();}}}:{})} style={CLOUD?{cursor:"pointer"}:null} title={CLOUD?"Click to install the Heygents agent":localInfo.note} onClick={CLOUD?openAgentSetup:undefined}>🔴 no agent{CLOUD?" · install":""}</span>}
        <div className="ss-meta">
          {/* The current model is already named on the composer's unified selector
              (.agent-controls-trigger) right below — no second copy up here. */}
          {active&&active.ctx?(()=>{const pct=Math.min(100,Math.round(active.ctx/CTX_WINDOW*100));
            const col=pct>=85?"var(--err)":pct>=60?"var(--warn)":"var(--accent)";
            return <button type="button" className={"ctxchip ctxpie-btn"+(pct>=85?" hot":pct>=60?" warn":"")}
              title={"Context window: "+active.ctx.toLocaleString()+" / "+CTX_WINDOW.toLocaleString()+" tokens ("+pct+"%) used.\nClick to /compact this conversation."}
              onClick={()=>sendPrompt("/compact")}>
              <span className="ctxpie" style={{background:"conic-gradient("+col+" "+(pct*3.6)+"deg, var(--line) 0)"}} aria-hidden="true"/>
              {fmtTokens(active.ctx)} · {pct}%</button>;})():null}
        </div>
      </div>
      {/* Interactive question/permission popup — docked directly above the
         composer (never covering it), so you can answer with the buttons or
         just reply in the box while the run stays live. */}
      {guard&&<WrongProjectModal match={guard.match} fromName={(p&&(p.nameHe||p.nameEn||p.slug))||""}
        onTransfer={guardTransfer} onSendAnyway={()=>guardSendAnyway(false)} onAlwaysIgnore={()=>guardSendAnyway(true)}/>}
      {activeId!=null&&pendingAsk[activeId]&&<AskModal ask={pendingAsk[activeId]}
        onAnswer={ans=>answerAsk(activeId,pendingAsk[activeId],ans)}
        onDismiss={()=>answerAsk(activeId,pendingAsk[activeId],pendingAsk[activeId].kind==="question"?{}:{allow:false,message:"Dismissed."})}/>}
      {/* Status indicator — ALWAYS at the very bottom of the chat stack, below the
         folder path and any active question, directly above the composer. It is
         emitted LAST in source (after .chatdir and the AskModal) so desktop's
         non-flex .chat — where CSS `order` is ignored and DOM source order wins —
         places it at the bottom, and given the highest flex order (32, just under
         the composer's 33) so the dock/mobile flex layouts agree. */}
      {!(conversationState.kind==="idle"&&!msgs.length)&&<div className={"chat-state state-"+conversationState.kind} aria-live="polite">
        <span className="state-dot"/>
        <span className="state-copy">
          <b>{conversationState.label}</b>
          {(conversationState.kind==="stuck"||conversationState.kind==="error")
            ?<FailReason text={conversationState.detail}/>
            :<span>{conversationState.detail}</span>}
          {conversationState.sub&&<span className="muted" style={{display:"block",fontSize:11}}>{conversationState.sub}</span>}
        </span>
        {conversationState.action&&<button className="btn sm" onClick={runStateAction()}>{conversationState.action}</button>}
      </div>}
      <div className="inp" style={{background:"var(--surface)",borderTop:"1px solid var(--line)"}}>
        {(()=>{
          // Unified selector — one button, pinned left, that opens a single popover
          // for provider, model, agent mode and reasoning effort. Mode & effort only
          // apply to Claude/Codex; Gemini shows provider + model alone.
          const pn=providerName(provider);
          const hasModes=provider==="claude"||provider==="codex";
          const curMode=EXECUTION_MODES.find(m=>m.value===executionMode)||EXECUTION_MODES[EXECUTION_MODES.length-1];
          const curEff=EFFORTS.find(e=>e.value===effort)||EFFORTS[1];
          const curModel=providerModels.find(m=>m.value===model)||providerModels[0]||{label:pn};
          return(
          <div className="agent-controls" ref={modeMenuRef}>
            <button type="button" className={"agent-controls-trigger"+(modeMenuOpen?" open":"")}
              aria-haspopup="dialog" aria-expanded={modeMenuOpen}
              onClick={()=>setModeMenuOpen(o=>!o)}
              title={busy
                ? "Model, agent mode & effort for the next message. Current run keeps its starting settings."
                : ("AI model, agent mode & reasoning effort · "+(curModel.label||pn)+(hasModes?(" · "+curMode.title+" · "+curEff.label+" effort"):""))}>
              <span className="acx-ic"><ProviderLogo provider={provider} size={14}/></span>
              <span className="acx-mode">{curModel.label||pn}</span>
              {hasModes&&<React.Fragment>
                <span className="acx-sep"/>
                <span className="acx-submode" title={curMode.title+" mode"}>{curMode.icon}</span>
                <span className="acx-eff"><span className="acx-eff-dots" data-eff={effort}><i/><i/><i/></span></span>
              </React.Fragment>}
              <span className="acx-caret">▴</span>
            </button>
            {modeMenuOpen&&<div className="agent-controls-pop" role="dialog" aria-label={t("AI model & agent controls")}>
              <div className="acp-section">
                <div className="acp-h">{t("Provider")}</div>
                <div className="acp-efforts acp-providers" role="group" aria-label={t("AI provider")}>
                  {ASK_PROVIDERS.map(pr=>(
                    <button key={pr.key} type="button"
                      className={"acp-eff-opt"+(provider===pr.key?" active":"")}
                      onClick={()=>{if(pr.key!==provider)chooseProvider(pr.key);}}
                      title={t("Switch to {name}",{name:pr.name})}>
                      <ProviderLogo provider={pr.key} size={14}/> {pr.name}
                    </button>
                  ))}
                </div>
              </div>
              {providerModels.length>1&&<div className="acp-section">
                <div className="acp-h">{pn} model</div>
                <div className="acp-modes acp-models">
                  {providerModels.map(m=>(
                    <button key={m.value} type="button"
                      className={"acp-mode"+(model===m.value?" active":"")}
                      onClick={()=>chooseModel(m.value)}
                      title={m.desc||m.label}>
                      <span className="acp-mode-body">
                        <span className="acp-mode-t">{m.label}{m.hint&&<span className="acp-model-hint">{m.hint}</span>}</span>
                        {m.desc&&<span className="acp-mode-d">{m.desc}</span>}
                      </span>
                      {model===m.value&&<span className="acp-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>}
              {hasModes&&<React.Fragment>
                <div className="acp-section">
                  <div className="acp-h">{t("Agent mode")}</div>
                  <div className="acp-efforts acp-modeseg" role="group" aria-label={t("Agent mode")}>
                    {EXECUTION_MODES.map(m=>(
                      <button key={m.value} type="button"
                        className={"acp-eff-opt"+(executionMode===m.value?" active":"")}
                        onClick={()=>chooseExecutionMode(m.value)}
                        title={m.title} aria-label={m.title} aria-pressed={executionMode===m.value}>
                        <span className="acp-modeseg-ic" aria-hidden="true">{m.icon}</span>
                        <span className="acp-modeseg-t">{m.short}</span>
                      </button>
                    ))}
                  </div>
                  <div className="acp-eff-note">{curMode.desc(pn)}</div>
                </div>
                <div className="acp-section">
                  <div className="acp-h">{t("Reasoning effort")}</div>
                  <div className="acp-efforts" role="group" aria-label={t("Reasoning effort")}>
                    {EFFORTS.map(e=>(
                      <button key={e.value} type="button"
                        className={"acp-eff-opt"+(effort===e.value?" active":"")}
                        onClick={()=>chooseEffort(e.value)} title={e.desc}>{e.label}</button>
                    ))}
                  </div>
                  <div className="acp-eff-note">{curEff.desc}</div>
                </div>
              </React.Fragment>}
            </div>}
          </div>);
        })()}
        {CLOUD&&<div className="hub-todo-control">
          <button type="button" className={"hub-todo-trigger"+(hubTodoOpen?" open":"")}
            aria-label={t("Quick-fix to-do list")} title={t("Quick-fix to-do list")}
            aria-haspopup="dialog" aria-expanded={hubTodoOpen}
            onClick={()=>{setHubTodoOpen(v=>!v);setHubTodoErr("");}}>☑</button>
          {hubTodoOpen&&<div className="hub-todo-pop" role="dialog" aria-label={t("Quick-fix to-do list")}>
            <div className="hub-todo-head"><div><b>{t("Quick fixes for {project}",{project:p.nameHe||p.nameEn||p.name||p.slug})}</b><small>{t("Add notes here, then press ▶ Prompt on a note to turn it into an AI fix prompt. Approve or dismiss each one from your Telegram bot.")}</small></div><button className="btn xs" onClick={()=>setHubTodoOpen(false)}>✕</button></div>
            {hubTelegram.loaded&&hubTelegram.connected
              ?<div className="hub-telegram-connected"><span>✓ @{hubTelegram.botUsername||t("Telegram bot")} · {hubTelegram.chatIdMasked}</span><button className="hub-todo-dismiss" disabled={hubTelegramBusy} onClick={disconnectHubTelegram}>{t("Disconnect")}</button></div>
              :<div className="hub-telegram-connect">
                <b>{t("Connect your Telegram bot")}</b>
                <small>{t("Create a bot with BotFather, send it /start, then paste its token. Chat ID is optional when /start is the bot's latest message.")}</small>
                <input type="password" autoComplete="off" value={hubTelegramToken} onChange={e=>setHubTelegramToken(e.target.value)} placeholder={t("Bot token")}/>
                <input inputMode="numeric" value={hubTelegramChat} onChange={e=>setHubTelegramChat(e.target.value)} placeholder={t("Chat ID (optional)")}/>
                <button className="btn sm primary" disabled={hubTelegramBusy||!hubTelegramToken.trim()} onClick={()=>connectHubTelegram(false)}>{hubTelegramBusy?t("Connecting…"):t("Connect Telegram")}</button>
              </div>}
            {hubTelegram.connected&&<React.Fragment>
              <textarea dir="ltr" rows={3} autoFocus value={hubTodoText}
                placeholder={t("What needs to be done or fixed in this project?")}
                onChange={e=>setHubTodoText(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&(e.ctrlKey||e.metaKey)){e.preventDefault();addHubTodo();}}}/>
              <div className="hub-todo-addrow"><span className="muted">{t("Ctrl/⌘ + Enter to add")}</span><button className="btn sm primary" disabled={hubTodoBusy||!hubTodoText.trim()} onClick={addHubTodo}>{hubTodoBusy?t("Preparing…"):t("Add note")}</button></div>
            </React.Fragment>}
            {hubTodoErr&&<div className="err hub-todo-error">{hubTodoErr}</div>}
            <div className="hub-todo-list">
              {hubTodoRows.length===0&&hubTelegram.connected&&<div className="muted hub-todo-empty">{t("No quick-fix notes for this project yet.")}</div>}
              {hubTodoRows.slice(0,20).map(row=>{
                const labels={saved:t("Saved — not prompted yet"),translating:t("Preparing prompt…"),sending:t("Sending to Telegram…"),awaiting_approval:t("Awaiting Telegram approval"),telegram_failed:t("Telegram delivery failed"),approved:t("Approved — waiting for agent"),running:t("Fixing now…"),completed:t("Completed"),failed:t("Fix failed"),dismissed:t("Dismissed")};
                const open=["saved","translating","sending","awaiting_approval","telegram_failed","approved"].includes(row.status);
                const canPrompt=row.status==="saved"||row.status==="telegram_failed";
                return <div className={"hub-todo-item status-"+row.status} key={row.id}>
                  <div className="hub-todo-meta"><span>{labels[row.status]||row.status}</span><time>{fmtWhen(row.createdAt)}</time></div>
                  <div className="hub-todo-note" dir="ltr">{row.note}</div>
                  {row.prompt&&<details><summary>{t("AI fix prompt")}</summary><div dir="ltr">{row.prompt}</div></details>}
                  <div className="hub-todo-item-actions">
                    {canPrompt&&<button className="btn xs primary" disabled={!hubTelegram.connected} title={hubTelegram.connected?t("Prompt this note"):t("Connect Telegram first")} onClick={()=>prepareHubTodo(row.id)}>▶ {t("Prompt")}</button>}
                    {row.chatId&&<a className="btn xs" href={"#/"+encodeURIComponent(row.slug)+"/chat/"+encodeURIComponent(row.chatId)}>{t("Open fix chat")}</a>}
                    {open&&<button className="hub-todo-dismiss" onClick={()=>dismissHubTodo(row.id)}>{t("Dismiss")}</button>}
                  </div>
                  {row.error&&<div className="err hub-todo-error">{row.error}</div>}
                </div>;
              })}
            </div>
          </div>}
        </div>}
        {scheduleOpen&&<div className="schedule-chat-pop" role="dialog" aria-modal="true" aria-label={t("Schedule this chat")}>
          <div className="schedule-chat-head"><b>🕒 {t("Schedule this chat")}</b><button className="btn xs" onClick={()=>setScheduleOpen(false)}>✕</button></div>
          <label><span>{t("Chat title")}</span><input dir="ltr" value={scheduleTitle} onChange={e=>setScheduleTitle(e.target.value)}/></label>
          <label><span>{t("Run at")}</span><input type="datetime-local" value={scheduleAt} min={new Date(Date.now()-new Date().getTimezoneOffset()*60000).toISOString().slice(0,16)} onChange={e=>setScheduleAt(e.target.value)}/></label>
          <label><span>{t("Repeat")}</span><select value={scheduleFreq} onChange={e=>setScheduleFreq(e.target.value)}>{SCHED_FREQ.map(([v])=><option key={v} value={v}>{trSchedFreq(v)}</option>)}</select></label>
          <div className="schedule-chat-actions"><button className="btn sm" onClick={()=>setScheduleOpen(false)}>{t("Cancel")}</button><button className="btn sm primary" disabled={scheduleBusy||!input.trim()||!scheduleAt} onClick={scheduleChat}>{scheduleBusy?t("Scheduling…"):t("Schedule")}</button></div>
          {scheduledChats.length>0&&<div className="schedule-chat-list"><b>{t("Upcoming")}</b>{scheduledChats.map(x=><div key={x.id}><span dir={dirOf(x.title)}>{x.title}</span><small>{new Date(x.runAt).toLocaleString()}</small><button title={t("Cancel scheduled chat")} onClick={()=>cancelScheduled(x.id)}>✕</button></div>)}</div>}
        </div>}
        {slashOpen&&<div className="slashmenu">
          <div className="slash-h">{slashMatches.length} skill{slashMatches.length===1?"":"s"} · ↑↓ then Enter to insert · hover for details · Esc to dismiss</div>
          {slashMatches.map((s,i)=>(
            <SlashRow key={s.name} s={s} active={i===slashSel} onEnter={()=>setSlashIdx(i)} onPick={pickSkill}/>))}
        </div>}
        <input ref={fileRef} type="file" multiple style={{display:"none"}}
          onChange={e=>{onPickFiles(e.target.files);e.target.value="";}}/>
        {<div className="attach-menu" ref={attachMenuRef}>
          <button type="button" ref={attachBtnRef} className="iconbtn" title={t("Add attachment")} aria-label={t("Add attachment")}
            aria-haspopup="menu" aria-expanded={attachMenuOpen} onClick={()=>setAttachMenuOpen(o=>!o)} disabled={busy}>＋</button>
          {attachMenuOpen&&<div ref={attachPopRef} className="attach-pop" role="menu">
            <button type="button" role="menuitem" onClick={()=>{setAttachMenuOpen(false);fileRef.current&&fileRef.current.click();}}>📎 {t("Upload File")}</button>
            <button type="button" role="menuitem" onClick={()=>{setAttachMenuOpen(false);setDriveOpen(true);}}>☁️ {t("Google Drive")}</button>
          </div>}
        </div>}
        <textarea dir={dirOf(input||ghost||(!parked&&!busy?projLabel(p):""))} ref={taRef} rows={1}
          aria-label={t("Message")} className={ghost?"has-ghost":undefined}
          title={ghost?t("Suggested follow-up — press Tab to use it, Esc to dismiss"):undefined}
          placeholder={ghost||(parked?t("Reply to {agent}…",{agent:agentName}):busy?t("Queue a message for when {agent} finishes…",{agent:agentName}):t("Ask about {name}…",{name:projLabel(p)||t("this project")}))}
          value={input}
          onChange={e=>{setInput(e.target.value);setSlashIdx(0);if(slashHide)setSlashHide(false);}} onPaste={onChatPaste}
          onKeyDown={e=>{
            if(slashOpen){
              if(e.key==="ArrowDown"){e.preventDefault();setSlashIdx(i=>(Math.min(i,slashMatches.length-1)+1)%slashMatches.length);return;}
              if(e.key==="ArrowUp"){e.preventDefault();setSlashIdx(i=>(Math.min(i,slashMatches.length-1)-1+slashMatches.length)%slashMatches.length);return;}
              if(e.key==="Enter"||e.key==="Tab"){e.preventDefault();pickSkill(slashMatches[slashSel]);return;}
              if(e.key==="Escape"){e.preventDefault();setSlashHide(true);return;}
            }
            if(ghost&&e.key==="Tab"&&!e.shiftKey&&!e.altKey&&!e.ctrlKey&&!e.metaKey){e.preventDefault();acceptGhost();return;}
            if(ghost&&e.key==="Escape"){e.preventDefault();dismissGhost();return;}
            if(e.key==="Enter"&&!e.shiftKey&&!(e.nativeEvent&&e.nativeEvent.isComposing)){e.preventDefault();send();}
          }}/>
        <div className="inp-actions">
          {/* Icon only so it sits flush next to Send; the label lives in the tooltip. */}
          {/* Touch devices have no Tab key: the ↹ button accepts the ghost suggestion. */}
          {ghost&&<button type="button" className="btn sm ghost-accept" title={t("Use suggested follow-up (Tab)")} aria-label={t("Use suggested follow-up (Tab)")} onClick={acceptGhost}>↹</button>}
          {!busy&&<button type="button" className="btn sm" title={t("Schedule")} aria-label={t("Schedule")} disabled={!input.trim()||attach.some(a=>a.path)} onClick={openSchedule}>🕒</button>}
          {busy&&<button className="btn sm danger" onClick={stop}>{t("Stop")}</button>}
          <button className="btn sm primary" onClick={send}
            disabled={!input.trim()&&!(!busy&&attach.some(a=>a.path))}
            title={parked?t("Send as your answer — {agent} continues with this instead",{agent:agentName}):busy?t("Saved and sent as a new turn when {agent} finishes",{agent:agentName}):t("Send")}>{t("Send")}</button>
        </div>
      </div>
    </div>
    {driveOpen&&(CLOUD
      ?<CloudDrivePicker slug={p.slug} importPath="/api/gdrive/chat-import" allowFolder={false}
        onClose={()=>setDriveOpen(false)} onPickFile={f=>{stageDriveFile(f);setDriveOpen(false);}} onPickFolder={()=>{}}/>
      :<DrivePicker slug={p.slug} onClose={()=>setDriveOpen(false)}
        onPick={f=>{stageDriveFile(f);setDriveOpen(false);}}/>)}
   </div>);
}

// Shared xterm.js view: opens a pty over the /ws/term WebSocket for one project.
// Reports connection status / cwd back to the parent via onMeta({status,cwd}).
const TermView=forwardRef(function TermView({slug,mode,nonce,onMeta,injectCmd,onInjected},ref){
  const hostRef=useRef(null);
  const metaRef=useRef(onMeta);metaRef.current=onMeta;
  const injectedRef=useRef(onInjected);injectedRef.current=onInjected;
  const wsRef=useRef(null);
  const termRef=useRef(null);
  const readyRef=useRef(false);
  const injectRef=useRef(injectCmd);injectRef.current=injectCmd;
  const sentRef=useRef(0);
  // Type a queued command into the live pty (left at the prompt, not auto-run, so
  // it can be reviewed/edited first — important for placeholders & destructive ops).
  const sendPending=()=>{
    const ic=injectRef.current,ws=wsRef.current;
    if(!ic||!ic.cmd||ic.nonce===sentRef.current)return;
    if(!ws||ws.readyState!==1||!readyRef.current)return;
    sentRef.current=ic.nonce;
    ws.send(JSON.stringify({t:"i",d:ic.cmd}));
    try{injectedRef.current&&injectedRef.current(ic);}catch{}
    try{termRef.current&&termRef.current.focus();}catch{}
  };
  useImperativeHandle(ref,()=>({
    // Explicit end-of-session request (Restart ↻ button) - tells the server to
    // actually kill this project+mode's persistent pty, unlike a plain close.
    kill:()=>{try{const ws=wsRef.current;if(ws&&ws.readyState===1)ws.send(JSON.stringify({t:"kill"}));}catch{}}
  }));
  useEffect(()=>{
    const host=hostRef.current;if(!host||!window.Terminal)return;
    const emit=(o)=>{try{metaRef.current&&metaRef.current(o);}catch{}};
    // xterm.js theme is consumed by the library's canvas renderer, not CSS, so a
    // var(--…) token can't resolve here — literal hex only, picked to match the
    // design system's near-black/near-white extremes (--term-bg / dark --text).
    const term=new window.Terminal({fontSize:13,fontFamily:"ui-monospace,Menlo,Consolas,monospace",cursorBlink:true,theme:{background:"#18191B",foreground:"#F5F6F7"}});
    termRef.current=term;
    const fit=new window.FitAddon.FitAddon();term.loadAddon(fit);
    term.open(host);try{fit.fit();}catch{}
    emit({status:"connecting",cwd:""});
    const proto=location.protocol==="https:"?"wss":"ws";
    // Idle sockets get dropped by NATs/proxies/backgrounded tabs, so a closed socket
    // is usually transient — reconnect on the same backoff ladder the chat stream uses
    // instead of leaving the pane sitting on "closed" until the user hits ↻.
    const BACKOFF=[500,1000,2000,4000,8000,10000];
    let attempt=0,disposed=false,retryT=null,exited=false;
    const connect=()=>{
      if(disposed)return;
      const qs=new URLSearchParams({slug:slug||"",mode,cols:String(term.cols),rows:String(term.rows)});
      // Cloud: WebSockets can't send an Authorization header, so hand the tenant's
      // Supabase JWT (gate.js stores it here) to the server via the query string.
      if(CLOUD){try{const tk=localStorage.getItem("hubcloud.token");if(tk)qs.set("access_token",tk);}catch(e){}}
      const ws=new WebSocket(proto+"://"+location.host+"/ws/term?"+qs.toString());
      wsRef.current=ws;
      ws.onopen=()=>{
        if(attempt>0)term.write("\r\n\x1b[90m[reconnected]\x1b[0m\r\n");
        attempt=0;emit({status:"connected"});readyRef.current=true;setTimeout(sendPending,150);};
      ws.onmessage=e=>{let m;try{m=JSON.parse(e.data);}catch{return;}
        if(m.t==="clear"){try{term.reset();}catch(_){}}// reattach: wipe before the server replays this shell's scrollback
        else if(m.t==="o")term.write(m.d);
        else if(m.t==="meta")emit({cwd:m.cwd||""});
        else if(m.t==="exit"){exited=true;term.write("\r\n\x1b[90m[process exited — switch project/mode or hit ↻ to restart]\x1b[0m\r\n");emit({status:"exited"});}};
      ws.onerror=()=>{if(!disposed&&attempt===0)emit({status:"error"});};
      ws.onclose=()=>{
        readyRef.current=false;
        if(disposed||exited)return;
        if(attempt>=BACKOFF.length*4){emit({status:"closed"});return;}
        const wait=BACKOFF[Math.min(attempt,BACKOFF.length-1)];
        attempt++;
        emit({status:"reconnecting"});
        if(attempt===1)term.write("\r\n\x1b[90m[connection lost — reconnecting…]\x1b[0m\r\n");
        retryT=setTimeout(connect,wait);
      };
    };
    connect();
    const onData=term.onData(d=>{const ws=wsRef.current;if(ws&&ws.readyState===1)ws.send(JSON.stringify({t:"i",d}));});
    const onResize=()=>{try{fit.fit();}catch{}const ws=wsRef.current;if(ws&&ws.readyState===1)ws.send(JSON.stringify({t:"r",cols:term.cols,rows:term.rows}));};
    window.addEventListener("resize",onResize);
    // A tab coming back to the foreground is the most common moment to discover a
    // dead socket — retry immediately instead of waiting out the backoff timer.
    const onWake=()=>{
      if(disposed||exited||document.hidden)return;
      const ws=wsRef.current;
      if(ws&&(ws.readyState===0||ws.readyState===1))return;
      clearTimeout(retryT);attempt=0;connect();
    };
    document.addEventListener("visibilitychange",onWake);
    window.addEventListener("online",onWake);
    const ro=setTimeout(onResize,80);
    term.focus();
    return()=>{disposed=true;clearTimeout(retryT);window.removeEventListener("resize",onResize);document.removeEventListener("visibilitychange",onWake);window.removeEventListener("online",onWake);clearTimeout(ro);readyRef.current=false;const ws=wsRef.current;wsRef.current=null;termRef.current=null;try{ws&&ws.close();}catch{}onData.dispose();term.dispose();};
  },[slug,mode,nonce]);
  // Command queued while already connected (no mode switch) — send it right away.
  useEffect(()=>{sendPending();},[injectCmd]);
  return <div className="term" ref={hostRef}/>;
});

// Dock terminal: an interactive shell/claude session in the selected project.
function TerminalPane({slug,onExpand,runCmd}){
  const{lang}=useLang();
  const[mode,setMode]=useState("shell");
  const[nonce,setNonce]=useState(0);
  const[status,setStatus]=useState("connecting");
  const[cwd,setCwd]=useState("");
  const[inject,setInject]=useState(null); // {cmd,nonce} to type into the terminal
  const runSeen=useRef(0);
  const viewRef=useRef(null);
  // A command or handoff clicked elsewhere: pick the target mode and queue text for TermView.
  useEffect(()=>{
    if(!runCmd||!runCmd.nonce||runCmd.nonce===runSeen.current)return;
    runSeen.current=runCmd.nonce;
    setMode(runCmd.mode||"shell");
    setInject({cmd:runCmd.cmd,nonce:runCmd.nonce});
  },[runCmd]);
  const onMeta=(o)=>{
    if(o.status!==undefined)setStatus(s=>(o.status==="closed"&&s==="exited")?s:o.status);
    if(o.cwd!==undefined)setCwd(o.cwd);
  };
  const ok=status==="connected";
  return(
   <div className="termpane">
    <div className="termpane-head">
      <span className={"tstat "+(ok?"on":"off")}>● {status}</span>
      <div className="sp"/>
      {cwd&&<span className="muted mono" style={{fontSize:11,maxWidth:170,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={cwd}>{cwd}</span>}
      <button className="btn sm" title={t("Restart")} onClick={()=>{viewRef.current&&viewRef.current.kill();setNonce(n=>n+1);}}>↻</button>
      {onExpand&&<button className="btn sm" title={t("Full screen")} onClick={()=>onExpand(mode)}>⤢</button>}
    </div>
    <TermView ref={viewRef} slug={slug} mode={mode} nonce={nonce} onMeta={onMeta} injectCmd={inject}/>
   </div>);
}

function TerminalModal({projects,initialSlug,initialMode,initialCmd,onClose}){
  const{lang}=useLang();
  const[slug,setSlug]=useState(initialSlug||(projects[0]&&projects[0].slug)||"");
  const[mode,setMode]=useState(initialMode||"shell");
  const[nonce,setNonce]=useState(0);
  const[status,setStatus]=useState("connecting");
  const[cwd,setCwd]=useState("");
  const[inject,setInject]=useState(initialCmd||null);
  const viewRef=useRef(null);
  const onMeta=(o)=>{
    if(o.status!==undefined)setStatus(s=>(o.status==="closed"&&s==="exited")?s:o.status);
    if(o.cwd!==undefined)setCwd(o.cwd);
  };
  const ok=status==="connected";
  return(
   <div className="modal" role="dialog" aria-modal="true" aria-labelledby="dlg-terminal-title">
    <div className="bar">
      <h2 id="dlg-terminal-title" style={{fontSize:14,margin:0,fontWeight:700}}>{t("Terminal")}</h2>
      <select value={slug} onChange={e=>{setSlug(e.target.value);setInject(null);}}>
        {projects.map(x=><option key={x.slug} value={x.slug}>{x.nameHe} ({x.slug})</option>)}
      </select>
      {cwd&&<span className="muted mono" style={{fontSize:12}}>{cwd}</span>}
      <span className={"tstat "+(ok?"on":"off")}>● {status}</span>
      <div className="sp"/>
      <button className="btn sm" title={t("Restart")} onClick={()=>{viewRef.current&&viewRef.current.kill();setNonce(n=>n+1);}}>↻ {t("Restart")}</button>
      <button className="btn sm" onClick={onClose}>✕ {t("Close")}</button>
    </div>
    <TermView ref={viewRef} slug={slug} mode={mode} nonce={nonce} onMeta={onMeta} injectCmd={inject} onInjected={()=>setInject(null)}/>
   </div>);
}

// ── Resizable dock sizing ──────────────────────────────────────────────────
// The dock can be dragged wider/narrower (left edge), taller/shorter (bottom
// edge), or both at once (bottom-left corner). Size is clamped to the viewport
// so it can never be dragged off-screen or shrunk past usability, and persisted
// to localStorage so it survives reloads. Only active on wide screens — on phones
// the dock is full-width via the max-width:700px media query.
const DOCK_MIN_W=320, DOCK_MIN_H=280, DOCK_SIZE_KEY="hub-dock-size";
// Below this the project detail column is too cramped to read, so instead of
// squishing it we close it entirely (dock goes maximized: .main is display:none).
const DETAIL_MIN_W=250;
const mainWidth=()=>{const el=document.querySelector(".appbody>.main");return el?el.getBoundingClientRect().width:0;};
// Cap so .main keeps at least DETAIL_MIN_W; below that it's hidden and the chat takes the space.
const dockMaxW=()=>Math.max(DOCK_MIN_W,window.innerWidth-DETAIL_MIN_W);
// leave only a small gap below the dock so it can be dragged nearly to the
// bottom of the viewport (the shell is height-locked, so this space is real).
// Fallback cap used before the dock is mounted (loadDockSize runs at module
// init, so there's no element to measure yet). Once mounted we clamp against
// the dock's REAL top offset instead — see availDockH() in Dock — because the
// dock sits below a wrapping .apphead (+ optional AgentBanner), so a fixed
// "innerHeight - 64" guess lets a tall dock run past the viewport bottom and
// clip the composer's action buttons.
const dockMaxH=()=>Math.max(DOCK_MIN_H,window.innerHeight-64);
function clampDockSize(s,maxH){
  const hCap=maxH||dockMaxH();
  return{
    w:Math.round(Math.max(DOCK_MIN_W,Math.min(dockMaxW(),s.w))),
    h:Math.round(Math.max(DOCK_MIN_H,Math.min(hCap,s.h))),
  };
}
function loadDockSize(){
  let v=null;try{v=JSON.parse(localStorage.getItem(DOCK_SIZE_KEY));}catch(e){}
  if(!v||!v.w||!v.h)v={w:window.innerWidth<=1100?380:460,h:Math.min(720,window.innerHeight-96)};
  return clampDockSize(v);
}

// Persistent right-side dock: Claude chat + interactive terminal for the
// selected project, living in the empty space beside the project detail.
function Dock({p,projects,onSwitchProject,open,setOpen,maxed,setMaxed,tab,setTab,usage,ask,openChat,openRun,newChatReq,runCmd,onTipDone,onTipStart,onExpandTerminal,onRunCmd,onChatSeen,onChatRunning,onChatDone,visible,onAddNote,onFocusChat,localChatDone}){
  const{lang}=useLang(); // subscribe so the dock chrome re-renders on language change
  const[size,setSize]=useState(loadDockSize);
  const[wide,setWide]=useState(()=>{try{return window.innerWidth>760;}catch(e){return true;}});
  const[dragging,setDragging]=useState(null); // "x" | "y" | "xy" while a handle is held
  const[providerSlot,setProviderSlot]=useState(null); // dock-tabs node hosting Claude/Codex controls (portaled from ClaudePanel)
  const providerSlotRef=useRef(n=>setProviderSlot(n)).current;
  const[headSlot,setHeadSlot]=useState(null); // dock-head node hosting the chat's History + New chat controls (portaled from ClaudePanel)
  const headSlotRef=useRef(n=>setHeadSlot(n)).current; // stable ref callback so the portal target isn't torn down each render
  const dockRef=useRef(null); // the .dock element — measured to cap height against real available space
  // Measured dock width, so the header can compact itself when the PANEL (not the
  // viewport) is narrow: History collapses to an icon and the maximize/collapse/new-chat
  // buttons keep their space instead of being clipped away.
  const[dockW,setDockW]=useState(0);

  // Max height the dock may take WITHOUT its bottom (the composer + action
  // buttons) sliding past the viewport. The dock sits below a wrapping .apphead
  // (and sometimes an AgentBanner), so its top offset is dynamic — measure it
  // live instead of guessing. Falls back to the pre-mount cap until the ref is set.
  const availDockH=()=>{
    const el=dockRef.current;
    if(!el)return dockMaxH();
    const top=el.getBoundingClientRect().top;
    return Math.max(DOCK_MIN_H,window.innerHeight-top-10); // 10px breathing gap below
  };

  useEffect(()=>{
    const el=dockRef.current;
    if(!el||typeof ResizeObserver==="undefined")return;
    const ro=new ResizeObserver(entries=>{
      const w=entries[0]&&entries[0].contentRect?entries[0].contentRect.width:el.clientWidth;
      setDockW(Math.round(w));
    });
    ro.observe(el);
    setDockW(Math.round(el.clientWidth));
    return()=>ro.disconnect();
  },[open]);

  // xterm sizes to 0 while its tab is display:none — refit once it's shown, and
  // again whenever the dock is maximized/restored (its width jumps).
  useEffect(()=>{
    if(!open||tab!=="term")return;
    const t=setTimeout(()=>window.dispatchEvent(new Event("resize")),60);
    return()=>clearTimeout(t);
  },[open,tab,maxed,p&&p.slug]);

  // Re-clamp on viewport change and switch custom sizing off at phone widths.
  // Growing the window hands the EXTRA width to the dock (chat), not to .main —
  // shrinking it takes it back — so upscaling the window enlarges the chatbox.
  const lastWinW=useRef(typeof window!=="undefined"?window.innerWidth:0);
  useEffect(()=>{
    const onWinResize=()=>{
      const w=window.innerWidth, dx=w-lastWinW.current;
      lastWinW.current=w;
      setWide(w>760);
      setSize(s=>{
        const n=clampDockSize(dx&&w>760?{w:s.w+dx,h:s.h}:s,availDockH());
        try{localStorage.setItem(DOCK_SIZE_KEY,JSON.stringify(n));}catch(e){}
        return n;
      });
      // Shrinking the viewport can squeeze the detail column too — close it too.
      if(w>760&&!maxed){
        const mw=mainWidth();
        if(mw&&mw<DETAIL_MIN_W)setMaxed(true);
      }
    };
    window.addEventListener("resize",onWinResize);
    onWinResize(); // clamp the persisted size against the ACTUAL layout on mount
    return()=>window.removeEventListener("resize",onWinResize);
  },[maxed]);

  // Drag a resize handle. axis: "x" (width), "y" (height), "xy" (both). The left
  // edge widens as it's dragged left. The dock is anchored to the bottom, so its
  // free edge is the TOP: the top handle grows the panel as it's dragged UP.
  // Pointer events cover mouse, touch and pen; a rAF-throttled resize event keeps
  // xterm fitted live without flooding the layout.
  function startResize(e){
    if(!wide)return;
    e.preventDefault();
    const start={x:e.clientX,w:size.w,h:size.h,mainW:mainWidth()};
    let last={w:size.w,h:size.h},raf=0,closed=false;
    setDragging("x");
    document.body.style.userSelect="none";
    document.body.style.cursor="ew-resize";
    const onMove=(ev)=>{
      // Width-only: the dock is anchored bottom-right, so only the inline-start
      // edge drags. Height tracks the viewport clamp, not the pointer.
      const w=start.w+(start.x-ev.clientX);
      last=clampDockSize({w,h:start.h},availDockH());
      // Dragged the detail column below readable width → close it, keep only chat.
      if(!closed&&!maxed&&start.mainW&&(start.mainW-(last.w-start.w))<DETAIL_MIN_W){
        closed=true;last={w:start.w,h:last.h};
        setSize(last);setMaxed(true);onUp();
        return;
      }
      setSize(last);
      if(!raf)raf=requestAnimationFrame(()=>{raf=0;window.dispatchEvent(new Event("resize"));});
    };
    const onUp=()=>{
      window.removeEventListener("pointermove",onMove);
      window.removeEventListener("pointerup",onUp);
      document.body.style.userSelect="";document.body.style.cursor="";
      setDragging(null);
      try{localStorage.setItem(DOCK_SIZE_KEY,JSON.stringify(last));}catch(e){}
      window.dispatchEvent(new Event("resize"));
    };
    window.addEventListener("pointermove",onMove);
    window.addEventListener("pointerup",onUp);
  }

  if(!open)return(
   <div className="dock collapsed">
    <button className="dock-open" title={t("Open Claude panel")} onClick={()=>setOpen(true)}>🤖<span>{t("Claude ‹")}</span></button>
   </div>);

  // Custom width/height only on wide screens; phones fall back to the full-width
  // CSS. When maximized the dock fills the middle via CSS, so drop the inline size.
  // maxHeight:"100%" caps the dock to its parent (.appbody) — a hard safety net
  // so the composer/action buttons stay on screen even if the measured clamp
  // lags a layout change (apphead re-wrapping, an AgentBanner appearing).
  const dockStyle=(wide&&!maxed)?{width:size.w+"px",height:size.h+"px",maxHeight:"100%"}:null;
  return(
   <div ref={dockRef} className={"dock"+(maxed?" maxed":"")+(dockW&&dockW<700?" dock-narrow":"")+(dockW&&dockW<560?" dock-xnarrow":"")} style={dockStyle}>
    {wide&&!maxed&&
      <div className={"dock-rsz x"+(dragging==="x"?" active":"")} onPointerDown={e=>startResize(e)} title={t("Drag to resize width")}/>
    }
    <div className="dock-head">
      <div className="dock-tabs">
        <span className="dock-provider-tabs" ref={providerSlotRef}/>
        <button className={tab==="term"?"active":""} onClick={()=>setTab("term")} title={t("Terminal")}>🖥️</button>
      </div>
      <div className="dock-head-chat" ref={headSlotRef}/>
      <div className="sp"/>
      {wide&&<button className={"btn sm dock-size-btn"+(maxed?" on":"")} title={maxed?t("Restore panel width"):t("Open wide over the middle")} aria-label={maxed?t("Restore panel width"):t("Open wide over the middle")} onClick={()=>setMaxed(m=>!m)}>
        {maxed
          ?<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12H14"/><path d="M17 8l-4 4 4 4"/><path d="M3 12h7"/><path d="M7 8l4 4-4 4"/></svg>
          :<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 12H3"/><path d="M6 8l-4 4 4 4"/><path d="M14 12h7"/><path d="M18 8l4 4-4 4"/></svg>}
      </button>}
      <button className="btn sm dock-size-btn" title={t("Minimize panel")} aria-label={t("Minimize panel")} onClick={()=>setOpen(false)}>
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M15 4v16"/><path d="M11 9l-3 3 3 3"/></svg>
      </button>
    </div>
    {!p
      ?<div className="dock-body"><div className="muted" style={{padding:16,fontSize:13}}>{t("Select a project to chat with Claude or open a terminal in its folder.")}</div></div>
      :<div className="dock-body">
         <div className={"dock-pane"+(tab==="chat"?"":" hide")}><ClaudePanel p={p} projects={projects} onSwitchProject={onSwitchProject} usage={usage} ask={ask} openChat={openChat} openRun={openRun} newChatReq={newChatReq} tab={tab} headSlot={headSlot}
           providerSlot={providerSlot} onActivateChat={()=>{setTab("chat");onFocusChat&&onFocusChat();}} onTipDone={onTipDone} onTipStart={onTipStart} onRunCmd={onRunCmd} onChatSeen={onChatSeen} onChatRunning={onChatRunning} onChatDone={onChatDone} onAddNote={onAddNote} localDone={(localChatDone&&p&&localChatDone[p.slug])||null}
           paneVisible={visible!==false&&tab==="chat"}/></div>
         <div className={"dock-pane"+(tab==="term"?"":" hide")}><TerminalPane slug={p.slug} onExpand={onExpandTerminal} runCmd={runCmd}/></div>
       </div>}
   </div>);
}

function Field({label,hint,tip,children}){
  return(<label className="fld"><span className="lbl">{label}{hint&&<span className="hint"> - {hint}</span>}{tip&&<span className="hint-i" title={tip} aria-label={tip}> ⓘ</span>}</span>{children}</label>);
}

// Inline directory picker for the Working-dir field. Browses a filesystem
// read-only — this server's via /api/fs/browse, or, for a project that lives on
// another machine, that machine's via its hub agent (/api/fs/browse-agent).
// "Use this folder" writes the path back.
function DirBrowser({start,onPick,onClose,agent,agentHost}){
  const{lang}=useLang();
  const[cur,setCur]=useState("");
  const[parent,setParent]=useState(null);
  const[dirs,setDirs]=useState([]);
  const[err,setErr]=useState("");
  const[loading,setLoading]=useState(false);
  const box=useRef(null);
  // The picker opens inline BELOW the field, inside the project's scrollable pane.
  // With the Working-dir row low in that pane the panel lands past the fold, so
  // clicking Browse looks like it did nothing. Pull it into view — once on open
  // and again after the listing arrives, since the box only reaches its real
  // height then. The timeout lets React paint before we measure.
  function reveal(){setTimeout(()=>{try{box.current&&box.current.scrollIntoView({block:"nearest",behavior:"smooth"});}catch(e){}},0);}
  // Home/root for this filesystem: the agent picks its own home when we ask with
  // an empty dir; the server side has no home to infer, so start at "/".
  const homeDir=agent?"":"/";
  // Preferred default folder for local-machine browsing. If it doesn't exist on
  // this machine the failed-load fallback recovers at home, so it's safe to seed.
  const defaultLocalDir=agent?"/Users/tzahidagan/Documents/GitHub":"";
  // `fallback` marks the automatic retry at home so it can't loop. A seeded
  // `start` is often a path that only exists on ANOTHER machine (e.g. a server
  // path on a project just switched to "Local machine"). The agent then answers
  // "Not found on <host>: <dir>" and, with no parent and no folders, the picker
  // would dead-end. So on the first failed load we retry at home instead of
  // stranding the user.
  function load(dir,fallback){
    setLoading(true);setErr("");
    const url=agent
      ?"/api/fs/browse-agent?agentHost="+encodeURIComponent(agentHost||"")+"&dir="+encodeURIComponent(dir||"")
      :"/api/fs/browse?agentHost="+encodeURIComponent(agentHost||"")+"&local=0&dir="+encodeURIComponent(dir||"");
    fetch(url).then(r=>r.json()).then(d=>{
      if(d&&d.ok){setCur(d.dir);setParent(d.parent);setDirs(d.dirs||[]);setLoading(false);reveal();return;}
      if(!fallback&&dir){load(homeDir,true);return;} // seeded path unreadable here — recover at home
      setErr((d&&d.error)||t("Could not read directory."));setLoading(false);reveal();
    }).catch(e=>{
      if(!fallback&&dir){load(homeDir,true);return;}
      setErr(String(e));setLoading(false);reveal();
    });
  }
  // No path yet: start at "/" here, but let the agent choose (its home dir) —
  // a laptop's projects live under ~, not at the root.
  useEffect(()=>{reveal();load(start||defaultLocalDir||homeDir);},[]);
  return(
   <div className="dirbrowse" ref={box}>
    <div className="db-head">
      <button type="button" className="btn sm" onClick={()=>parent&&load(parent)} disabled={!parent||loading} title={t("Up one level")}>↑</button>
      <span className="db-path" title={cur}>{cur||"/"}</span>
      <button type="button" className="btn sm" onClick={onClose} aria-label={t("Close")}>✕</button>
    </div>
    {err&&<div className="err" style={{margin:"6px 0"}}>{err}</div>}
    <div className="db-list">
      {loading?<div className="muted" style={{padding:8,fontSize:12}}>{t("Loading…")}</div>
       :dirs.length===0?<div className="muted" style={{padding:8,fontSize:12}}>{t("No sub-folders here.")}</div>
       :dirs.map(name=>(
        <button type="button" key={name} className="db-item" onClick={()=>load((cur==="/"?"":cur)+"/"+name)}>
          <span style={{opacity:.7}}>📁</span> {name}
        </button>))}
    </div>
    <div className="db-foot">
      <button type="button" className="btn sm primary" onClick={()=>onPick(cur)} disabled={!cur}>{t("Use this folder")}</button>
    </div>
   </div>);
}

// Which connected hub-agent runs this project. Was a free-text hostname input,
// which meant guessing the exact machine name; now a dropdown of the tenant's
// currently-connected agents (/api/agents). "Auto" (blank) lets the server pick
// when only one is online. A stored host that's offline right now is still shown
// (marked offline) so editing a project never silently drops it. The old inline
// explanation moved to a hover tooltip on the label (Field `tip`).
function AgentHostField({value,onChange}){
  const{lang}=useLang();
  const[hosts,setHosts]=useState(null); // null=loading, []=none
  useEffect(()=>{let live=true;
    fetch("/api/agents").then(r=>r.json()).then(d=>{if(live)setHosts(d&&d.ok?(d.agents||[]).map(a=>a.host):[]);}).catch(()=>{if(live)setHosts([]);});
    return()=>{live=false;};
  },[]);
  const list=hosts||[];
  const cur=value||"";
  const opts=cur&&!list.includes(cur)?[cur,...list]:list; // keep an offline stored host selectable
  const tip=t(CLOUD?"The machine whose hub-agent runs this project. Leave on Auto when only one agent is connected; pick a machine when several are.":"The machine's hub-agent that runs this. Leave on Auto when only one is connected; pick a machine when several are.");
  return(
   <Field label={t("Agent host")} tip={tip}>
    <select value={cur} onChange={e=>onChange(e.target.value)}>
      <option value="">{t("Auto (single agent)")}</option>
      {opts.map(h=><option key={h} value={h}>{h}{!list.includes(h)?" "+t("(offline)"):""}</option>)}
    </select>
   </Field>);
}

// `isLocal` projects live on another machine, so Browse there asks that
// machine's hub agent for the listing instead of reading this server's disk.
// The button stays available either way; if no (or too old an) agent is
// connected the picker says so, which beats hiding the control.
function WorkingDirField({value,onChange,isLocal,agentHost}){
  const{lang}=useLang();
  const[open,setOpen]=useState(false);
  // Browse always goes through the agent-backed DirBrowser, which returns a REAL
  // absolute path (d.dir) from the target filesystem. The browser's native folder
  // picker (showDirectoryPicker/webkitdirectory) can only expose a bare folder
  // NAME, never an absolute path, so it can't produce a usable project.dir and is
  // rejected by the /api/run absolute-path guard. For "My machine" (isLocal)
  // projects we ask that machine's hub agent (agent=true); for on-server projects
  // we read the server disk (agent=false).
  return(
   <Field label={t("Working dir")} hint={isLocal?t("folder on your own computer — pick it with Browse"):t("absolute path Claude runs in")}>
    <div style={{display:"flex",gap:6}}>
      <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder={isLocal?"/Users/…":"/var/www/…"} style={{flex:1}}/>
      <button type="button" className="btn sm" onClick={()=>setOpen(o=>!o)}>{t("Browse")}</button>
    </div>
    {open&&<DirBrowser start={value} agent={isLocal} agentHost={agentHost} onClose={()=>setOpen(false)} onPick={p=>{onChange(p);setOpen(false);}}/>}
   </Field>);
}

// Repo URL field with a one-click "Detect" that reads the git origin remote +
// branch from the project's working dir (/api/git/detect) and fills both, so a
// project can be connected to its GitHub repo without pasting anything.
function RepoUrlField({dir,repo,canDetect,onChange,onDetect}){
  const{lang}=useLang();
  const[busy,setBusy]=useState(false);
  const[msg,setMsg]=useState(null); // {ok,text}
  const[ghOpen,setGhOpen]=useState(false);
  function detect(){
    if(!dir){setMsg({ok:false,text:t("Set a working dir first.")});return;}
    setBusy(true);setMsg(null);
    fetch("/api/git/detect?dir="+encodeURIComponent(dir)).then(r=>r.json()).then(d=>{
      if(d&&d.ok){
        onDetect(d.url,d.branch);
        const short=(d.url||"").replace(/^https?:\/\/(www\.)?github\.com\//,"").replace(/\.git$/,"");
        setMsg({ok:true,text:"✓ "+short+(d.branch?" ("+d.branch+")":"")});
      } else setMsg({ok:false,text:(d&&d.error)||t("No git repo found.")});
    }).catch(e=>setMsg({ok:false,text:String(e)})).finally(()=>setBusy(false));
  }
  return(
   <Field label={t("Repo URL")} hint={canDetect?t("or auto-detect from the working dir"):undefined}>
    <div style={{display:"flex",gap:6}}>
      <input value={repo||""} onChange={e=>onChange(e.target.value)} placeholder={t("https://github.com/…")} style={{flex:1}}/>
      <button type="button" className="btn sm" onClick={()=>setGhOpen(true)} title={t("Pick a repo from your connected GitHub account")}>🐙 {t("From GitHub")}</button>
      {canDetect&&<button type="button" className="btn sm" onClick={detect} disabled={busy} title={t("Read the git remote from the working dir")}>{busy?"…":"🔎 "+t("Detect")}</button>}
    </div>
    {msg&&<div style={{marginTop:4,fontSize:11,color:msg.ok?"var(--ok)":"var(--text-3)"}}>{msg.text}</div>}
    {ghOpen&&<GithubRepoDialog onClose={()=>setGhOpen(false)} onPick={(r)=>{
      // Pass the whole repo so the editor can also fill the details GitHub knows
      // (description, language, name) into any still-empty project fields.
      onDetect(r.htmlUrl,r.defaultBranch,r);
      const short=(r.htmlUrl||"").replace(/^https?:\/\/(www\.)?github\.com\//,"").replace(/\.git$/,"");
      setMsg({ok:true,text:"✓ "+short+(r.defaultBranch?" ("+r.defaultBranch+")":"")});
    }}/>}
   </Field>);
}

function editableProjectPatch(d){
  return{
    nameHe:d.nameHe||"",
    isLocal:!!d.isLocal,
    agentHost:d.agentHost||"",
    localPort:d.localPort||"",
    repo:d.repo||"",
    repoBranch:d.repoBranch||"",
    dir:d.dir||"",
    urls:Array.isArray(d.urls)?d.urls:[],
  };
}

function ProjectFields({draft:d,setDraft,isNew=false}){
  const{lang}=useLang();
  const set=(k,v)=>setDraft({...d,[k]:v});
  const urls=d.urls||[];
  const setUrl=(i,k,v)=>{const u=urls.slice();u[i]={...u[i],[k]:v};set("urls",u);};
  const addUrl=()=>set("urls",[...urls,{label:"",url:""}]);
  const delUrl=(i)=>set("urls",urls.filter((_,j)=>j!==i));

  return(
   <div className="project-fields-grid">
    <Field label={t("Name")} hint={t("kept as-is, not translated")}><input dir="ltr" value={d.nameHe||""} onChange={e=>setDraft(isNew?{...d,nameHe:e.target.value,slug:slugFromProjectName(e.target.value)}:{...d,nameHe:e.target.value})}/></Field>

    <div className="grid2">
      <Field label={t("Where it runs")} hint={t("local machine hides server-only fields")}>
        <div className="seg">
          <button className={"seg-b"+(!d.isLocal?" on":"")} onClick={()=>set("isLocal",false)}>🖥️ {t(CLOUD?"Server agent":"On this server")}</button>
          <button className={"seg-b"+(d.isLocal?" on":"")} onClick={()=>set("isLocal",true)}>💻 {t(CLOUD?"My machine":"Local machine")}</button>
        </div>
      </Field>
      {(CLOUD||d.isLocal)&&<AgentHostField value={d.agentHost} onChange={v=>set("agentHost",v)}/>}
    </div>
    {d.isLocal&&<Field label={t("Local port")} hint={t("dev-server port on your machine — adds an Open button next to the project name")}><input type="number" min="1" max="65535" value={d.localPort||""} onChange={e=>set("localPort",e.target.value.replace(/[^0-9]/g,""))} placeholder={t("e.g. 3000")}/></Field>}

    <RepoUrlField dir={d.dir} repo={d.repo} canDetect={!d.isLocal} onChange={v=>set("repo",v)} onDetect={(url,branch,meta)=>{
        // Always take the repo URL + branch. When the pick came from GitHub (meta),
        // also backfill the details GitHub knows into fields the user hasn't set —
        // never overwriting anything already filled in.
        const next={...d,repo:url,...(branch?{repoBranch:branch}:{})};
        const empty=v=>!String(v||"").trim();
        // Repo name: from GitHub's metadata when we have it, otherwise the last
        // path segment of the URL (the "Detect" path).
        const repoName=(meta&&meta.name)||String(url||"").replace(/\.git$/,"").replace(/\/+$/,"").split("/").pop()||"";
        // The Hebrew name / slug default to "New project" / "new-project" on a fresh
        // draft — treat those placeholders as empty so the repo name wins.
        if(repoName&&(empty(d.nameHe)||d.nameHe==="New project"))next.nameHe=repoName;
        if(repoName&&(empty(d.slug)||d.slug==="new-project"))
          next.slug=repoName.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"").slice(0,40)||d.slug;
        if(meta){
          if(meta.description){
            if(empty(d.oneLiner))next.oneLiner=meta.description;
            if(empty(d.description))next.description=meta.description;
          }
          if(meta.language){
            const cur=Array.isArray(d.stack)?d.stack:[];
            if(!cur.some(s=>String(s).toLowerCase()===meta.language.toLowerCase()))next.stack=[...cur,meta.language];
          }
          if(meta.fullName&&empty(d.nameEn))next.nameEn=meta.fullName;
        }
        setDraft(next);
      }}/>
    <div className="grid2">
      <WorkingDirField value={d.dir} onChange={v=>set("dir",v)} isLocal={!!d.isLocal} agentHost={d.agentHost}/>
    </div>

    <h3>{t("Links")}</h3>
    {urls.map((u,i)=>(
      <div className="urlrow" key={i}>
        <input dir="ltr" placeholder={t("Label")} value={u.label||""} onChange={e=>setUrl(i,"label",e.target.value)} style={{maxWidth:180}}/>
        <input placeholder={t("https://…")} value={u.url||""} onChange={e=>setUrl(i,"url",e.target.value)}/>
        <button className="btn sm danger" onClick={()=>delUrl(i)}>✕</button>
      </div>
    ))}
    <button className="btn sm" onClick={addUrl}>+ {t("Add link")}</button>
   </div>);
}

function Editor({draft,setDraft,isNew,saving,saveErr,savedTick}){
  const{lang}=useLang();
  return(
   <div>
    <h2 style={{margin:"0 0 14px",fontSize:22}}>{t("Add project")}</h2>

    <ProjectFields draft={draft} setDraft={setDraft} isNew={true}/>

    <div className="toolbar project-fields-status">
      <span className="muted" style={{display:"flex",alignItems:"center",gap:6}}>
        {saving?t("Saving…"):(saveErr?"":(savedTick>0?t("Saved ✓"):t("Auto-saves as you type")))}
      </span>
    </div>
    {saveErr&&<div className="err">{saveErr}</div>}
   </div>);
}

// CLOUD: a render crash must never leave a paying user staring at a blank
// page — show the error and a reload button instead of an empty #root.
class CloudErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err){ return { err }; }
  render(){
    if (!this.state.err) return this.props.children;
    return <div style={{maxWidth:560,margin:"80px auto",padding:24,fontFamily:"system-ui,sans-serif"}}>
      <h2>{t("Something went wrong")}</h2>
      <p className="muted">{t("The app hit an unexpected error. Reloading usually fixes it — if it keeps happening, contact support.")}</p>
      <pre style={{whiteSpace:"pre-wrap",fontSize:12,opacity:.7}}>{String(this.state.err && this.state.err.message || this.state.err)}</pre>
      <button className="btn" onClick={()=>window.location.reload()}>{t("Reload")}</button>
    </div>;
  }
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <LangProvider>{CLOUD ? <CloudErrorBoundary><App/></CloudErrorBoundary> : <App/>}</LangProvider>
);
